import React, { useState, useEffect, useRef, useCallback, useMemo, useId } from 'react'
import { CalendarPicker } from './components/ui/CalendarPicker'
import { DAYS_ES, MONTHS_ES, ACT_EMOJI, ACT_OPTS, ACT_COLOR, ACT_GERUND } from './lib/constants'
import { apiBase } from './lib/api'
import { createPortal } from 'react-dom'
import PlanCards from './PlanCards'
import './App.css'
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom'
import { usePolarH10 } from './hooks/usePolarH10'
import { usePolarOffline } from './hooks/usePolarOffline'
import { useBridge } from './hooks/useBridge'
import { useAuth, uploadSession, uploadSessionCheckpoint, uploadAccRecording, fetchAccRecording, authFetch } from './hooks/useAuth'
import { useLiveStream, deviceIdentity } from './hooks/useLiveStream'
import { saveDraft, getDraft, clearDraft, addToOutbox, getOutbox, removeFromOutbox, saveAccRecording, getAccRecording } from './db'
import { packAndCompressAcc, unpackAndDecompressAcc, exportAccToCsv } from './utils/accCompression'
import { saveDayOffline, loadDayOffline, saveSessionsOffline, loadSessionsOffline, pruneDaysOffline, uidOf } from './lib/offlineStore'
import { toBlob as domToBlob, toPng as domToPng } from 'html-to-image'
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea, LineChart, Line,
  ScatterChart, Scatter, Brush,
} from 'recharts'

import { NavigateContext } from './contexts/NavigateContext'
import { SectionHeader } from './components/ui/SectionHeader'
import { MetricCalculationAudit } from './components/ui/MetricCalculationAudit'
import { MeditacionTab as MeditacionTabImported } from './features/meditation/MeditacionTab'
import { AmigosTab } from './features/friends/AmigosTab'
import { AlertasTab, SidebarIconAlertas } from './features/alerts/AlertasTab'
import { classifyInstantPosture, computePostureBreakdown, analyzeSessionPosture, POSTURE_TYPES, getPostureColor, getPostureLabel } from './utils/posture'
import { PostureDistribution } from './components/ui/PostureDistribution'
import {
  IconSun, IconMoon, IconHeart, IconSteps, IconBattery,
  IconBatteryLow, IconAlert, IconRadio, IconEye, IconChat,
  IconPin, IconFolder, IconCheck, IconLock, IconStar,
  IconStethoscope, IconBarChart, IconZap, IconActivity, IconMeditation,
  IconClock, IconDroplet, IconPostureSitting, IconPostureLying,
  IconPostureWalking, IconPostureStandSit, renderPostureIcon
} from './components/ui/Icons'

import {
  STREAK_MIN_MIN,
  fmtH,
  fmtDate,
  fmtTime,
  fmtDurationMs,
  fmtDuration,
  fmtLocalTs,
  fmtHMS,
  fmtLatidos,
  friendlyErr,
  localDateKey,
  localMonthKey,
  weekKeyOf,
  weekFullLabel,
  nightOverlapMin
} from './utils/formatters'

import {
  buildTrainingZones,
  getTrainingZone,
  stressInfo,
  computeStats
} from './utils/stats'

import {
  computeHRV,
  computeHRDistribution,
  computeHRVTrend,
  computeArrhythmiaRisk,
  computeSpectralHRV,
  computeDFA,
  computeSampEn,
  computeReadinessAndWorkoutAdvice,
  TRAINING_SCENARIOS
} from './utils/hrv'
import { analyzeSleepArchitecture } from './utils/sleepStaging'
import { SleepHypnogram } from './components/ui/SleepHypnogram'
import { JuegosTab } from './features/games/JuegosTab'

// ── Selector de período compartido (Resumen + Análisis usan el mismo) ────────
function PeriodToggle({ value, onChange }) {
  return (
    <div style={{ display: 'flex', background: 'var(--chip-bg)', borderRadius: 9, padding: 2.5, gap: 2 }}>
      {[{ k: 'day', l: 'Diaria' }, { k: 'week', l: 'Semanal' }, { k: 'month', l: 'Mensual' }].map(p => (
        <button key={p.k} onClick={() => onChange(p.k)} style={{
          flex: 1, padding: '5px 0', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
          background: value === p.k ? 'var(--seg-active)' : 'transparent',
          color: value === p.k ? 'var(--text)' : 'var(--text-faint)',
          boxShadow: value === p.k ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
        }}>{p.l}</button>
      ))}
    </div>
  )
}

// ── In-memory cache (RAM, module scope) ─────────────────────────────────────
// Survives tab switches / component remounts for the life of the page and has
// NO storage quota — unlike sessionStorage, which silently fails to write when a
// day's readings exceed ~5 MB (that was why data "always reloaded"). The small
// session list is ALSO mirrored to sessionStorage so it stays instant after a
// full page reload; large per-day reading sets live in RAM only.
const _vcMem = new Map()   // key -> { ts, data }

// ── Import progress channel (module scope) ──────────────────────────────────
// Lets a running import publish progress that survives the panel unmounting when
// you switch tabs — the nav badge subscribes here, and the upload loop keeps
// running in the background because it's a closure, not tied to the mounted view.
const importBus = {
  state: null,                                   // null | { importing, done, total }
  subs: new Set(),
  set(s) { this.state = s; this.subs.forEach(fn => { try { fn(s) } catch {} }) },
  subscribe(fn) { this.subs.add(fn); return () => this.subs.delete(fn) },
}

// Mirror of the import panel's UI state. The panel unmounts when you switch
// tabs, but the parse/upload loops keep running in the background — on remount
// the panel restores itself from here so nothing looks "lost".
const importMem = { sessions: [], importing: false, batchDone: false, step: 'idle', isOpen: false }

function aggregateReadings(readings, view) {
  if (!readings.length) return []
  if (view === 'second') {
    const beats = readings.slice(-120)
    return beats.map((p, i) => {
      const posture = p.posture || 'sentado'
      const postureColor = getPostureColor(posture)
      const postureLabel = getPostureLabel(posture)
      if (p.hr === null) return { hr: null, time: p.time, label: fmtHMS(p.time), posture, postureColor, postureLabel }
      // 6-beat rolling average: smooths short-term HR spikes without adding much lag
      const win = []
      for (let j = i; j >= 0 && win.length < 6; j--) {
        if (beats[j].hr === null) break   // a disconnect gap resets the rolling average
        win.push(beats[j].hr)
      }
      return {
        hr: Math.round(win.reduce((a, b) => a + b, 0) / win.length),
        time: p.time,
        label: fmtHMS(p.time),
        posture,
        postureColor,
        postureLabel
      }
    })
  }
  const groups = {}
  readings.forEach(p => {
    if (p.hr == null) return   // skip disconnect-gap markers so they don't drag the average
    const d   = new Date(p.time)
    const key = view === 'minute'
      ? `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
      : `${String(d.getHours()).padStart(2,'0')}:00`
    if (!groups[key]) groups[key] = { sum: 0, count: 0, time: p.time, postureCounts: {} }
    groups[key].sum += p.hr
    groups[key].count++
    const post = p.posture || 'sentado'
    groups[key].postureCounts[post] = (groups[key].postureCounts[post] || 0) + 1
  })
  return Object.entries(groups).map(([label, v]) => {
    let domPosture = 'sentado', maxC = 0
    for (const [k, c] of Object.entries(v.postureCounts)) {
      if (c > maxC) { maxC = c; domPosture = k }
    }
    return {
      label,
      time: v.time,
      hr:   Math.round(v.sum / v.count),
      posture: domPosture,
      postureColor: getPostureColor(domPosture),
      postureLabel: getPostureLabel(domPosture),
    }
  })
}

// Expand packet readings into one data point per heartbeat using RR intervals.
// Each beat gets an instantaneous HR (60000/rr) and a precise reconstructed timestamp.
function expandToBeats(readings) {
  const beats = []
  readings.forEach(p => {
    if (p.rr?.length) {
      let cursor = p.time - p.rr.reduce((a, b) => a + b, 0)
      p.rr.forEach(rr => {
        cursor += rr
        if (rr >= 250 && rr <= 2000) {
          beats.push({ time: cursor, hr: Math.round(60000 / rr), rr })
        }
      })
    } else if (p.hr && p.hr >= 30 && p.hr <= 240) {
      beats.push({ time: p.time, hr: p.hr, rr: null })
    }
  })
  return beats
}

function injectGaps(data, thresholdMs = 5 * 60 * 1000) {
  const out = []
  data.forEach((p, i) => {
    if (i > 0 && p.time - data[i - 1].time > thresholdMs) {
      out.push({ ...p, hr: null, isGap: true })
    }
    out.push(p)
  })
  return out
}

// Live HR chart: when the live stream is cut (sensor disconnect / signal lost), leave a
// clearly visible blank space instead of bridging the two ends with a straight line.
// A single null already breaks the line, but we insert a short run of nulls placed inside
// the gap so a real cut is unmistakable and its width roughly reflects how long the
// connection was lost.
function injectLiveGaps(beats, thresholdMs = 8000) {
  const out = []
  beats.forEach((b, i) => {
    if (i > 0) {
      const dt = b.time - beats[i - 1].time
      if (dt > thresholdMs) {
        const n = Math.min(8, Math.max(2, Math.round(dt / 1000)))
        for (let k = 1; k <= n; k++) {
          out.push({ time: beats[i - 1].time + (dt * k) / (n + 1), hr: null, isGap: true })
        }
      }
    }
    out.push(b)
  })
  return out
}

function downloadCSV(readings, filename) {
  const esc  = v => `"${String(v ?? '').replace(/"/g, '""')}"`
  const rows = [
    ['Timestamp ISO (latido)', 'Timestamp local (latido)', 'HR (bpm)', 'RR (ms)', 'Tipo'].map(esc)
  ]

  readings.forEach(p => {
    if (p.rr?.length) {
      // Reconstruct exact timestamp of each individual heartbeat from RR intervals
      // The packet arrived at p.time; work backwards through the RR intervals
      let cursor = p.time
      const beats = []
      for (let i = p.rr.length - 1; i >= 0; i--) {
        cursor -= p.rr[i]
        beats.unshift({ ts: cursor, rr: p.rr[i] })
      }
      beats.forEach(b => {
        rows.push([
          new Date(b.ts).toISOString(),
          fmtLocalTs(b.ts),
          p.hr,
          b.rr,
          'latido',
        ].map(esc))
      })
    } else {
      // No RR data — use packet receipt time
      rows.push([
        new Date(p.time).toISOString(),
        fmtLocalTs(p.time),
        p.hr,
        '',
        'paquete',
      ].map(esc))
    }
  })

  const blob = new Blob(['﻿' + rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' })
  const a    = document.createElement('a')
  a.download = filename ?? `polar-${new Date().toISOString().slice(0, 10)}.csv`
  a.href     = URL.createObjectURL(blob)
  a.click()
  URL.revokeObjectURL(a.href)
}

// Render a chart (SVG-based like Recharts, or <canvas>-based like Poincaré/ECG)
// onto a fresh white-background canvas. Returns a Promise<canvas|null>.
function chartToCanvas(ref) {
  return new Promise(resolve => {
    const el = ref?.current
    if (!el) return resolve(null)
    // Canvas-based charts: copy the existing canvas onto a white background.
    const srcCanvas = el.tagName === 'CANVAS' ? el : el.querySelector('canvas')
    if (srcCanvas) {
      const c = document.createElement('canvas')
      c.width = srcCanvas.width; c.height = srcCanvas.height
      const cx = c.getContext('2d')
      cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, c.width, c.height)
      cx.drawImage(srcCanvas, 0, 0)
      return resolve(c)
    }
    // SVG-based charts: serialize the SVG and rasterize it.
    const svg = el.querySelector('svg')
    if (!svg) return resolve(null)
    const { width, height } = svg.getBoundingClientRect()
    const scale = 2
    const c = document.createElement('canvas')
    c.width = width * scale; c.height = height * scale
    const cx = c.getContext('2d')
    cx.scale(scale, scale)
    cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, width, height)
    const img = new Image()
    img.onload  = () => { cx.drawImage(img, 0, 0); resolve(c) }
    img.onerror = () => resolve(null)
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg))
  })
}

async function downloadChartPNG(ref, filename) {
  const canvas = await chartToCanvas(ref)
  if (!canvas) return
  const a = document.createElement('a')
  a.download = filename ?? 'chart.png'
  a.href = canvas.toDataURL('image/png')
  a.click()
}

// Copy a chart to the clipboard as a PNG image. Returns true on success.
async function copyChartPNG(ref) {
  try {
    const canvas = await chartToCanvas(ref)
    if (!canvas || !navigator.clipboard || !window.ClipboardItem) return false
    const blob = await new Promise(res => canvas.toBlob(res, 'image/png'))
    if (!blob) return false
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
    return true
  } catch { return false }
}

// Small toolbar (copy + download) shown on chart cards.
function ChartActions({ chartRef, filename }) {
  const [copied, setCopied] = useState(false)
  const btn = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', fontFamily: 'inherit', fontSize: 13, lineHeight: 1 }
  return (
    <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <button title="Copiar imagen" style={{ ...btn, color: copied ? '#34c759' : 'var(--text-fainter)' }}
        onClick={async () => { const ok = await copyChartPNG(chartRef); if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1400) } else { downloadChartPNG(chartRef, filename) } }}>
        {copied ? '✓' : '⧉'}
      </button>
      <button title="Descargar PNG" style={{ ...btn, color: 'var(--text-fainter)' }}
        onClick={() => downloadChartPNG(chartRef, filename ?? `chart-${Date.now()}.png`)}>↓</button>
    </div>
  )
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

function useClock() {
  const [t, setT] = useState(new Date())
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id) }, [])
  return t
}

function useSessionTimer(running, resetTrigger = 0) {
  const [sec, setSec] = useState(0)
  useEffect(() => {
    if (resetTrigger === 0) setSec(0)
  }, [resetTrigger])
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setSec(s => s + 1), 1000)
    return () => clearInterval(id)
  }, [running])
  return { sec, fmt: fmtDuration(sec) }
}

function buildDemoHistory(minutes = 8) {
  const now = Date.now()
  const readings = []
  let hr = 68
  // Simulate a realistic resting→light activity pattern
  const phases = [
    { dur: minutes * 0.3, target: 65, spread: 3 },   // resting
    { dur: minutes * 0.4, target: 88, spread: 6 },   // light activity
    { dur: minutes * 0.3, target: 72, spread: 4 },   // recovery
  ]
  let elapsed = 0
  for (const phase of phases) {
    const steps = Math.round(phase.dur * 60)
    for (let i = 0; i < steps; i++) {
      hr = Math.round(Math.max(48, Math.min(130,
        hr * 0.92 + phase.target * 0.08 + (Math.random() - 0.5) * phase.spread
      )))
      const rr = [Math.round(60000 / hr + (Math.random() - 0.5) * 40)]
      readings.push({ time: now - (minutes * 60 - elapsed) * 1000, hr, rr })
      elapsed++
    }
  }
  return readings
}

function useDemoMode() {
  const [active,   setActive]   = useState(false)
  const [hr,       setHr]       = useState(72)
  const [history,  setHistory]  = useState([])
  const [steps,    setSteps]    = useState(0)
  const [posture,  setPosture]  = useState('sentado')
  const [moisture, setMoisture] = useState({ pct: 92, label: 'Excelente', color: '#30d158' })
  const hrRef = useRef(72)
  const tickRef = useRef(0)

  useEffect(() => {
    if (!active) return
    const id = setInterval(() => {
      tickRef.current++
      const t = tickRef.current

      // Ciclo completo de 120s para demostración ágil e interactiva de todas las posturas:
      // 0 - 35s: Sentado (reposo, HR 65-72)
      // 35 - 65s: Parado (activo leve, HR 78-95)
      // 65 - 100s: En movimiento (caminando, pasos incrementando a ~95 ppm, HR 105-135)
      // 100 - 120s: Regreso a Sentado (recuperación, HR descendiendo a 70)
      const phaseSec = t % 120
      let currentPosture = 'sentado'
      let baseTarget = 68
      let isWalking = false

      if (phaseSec < 35) {
        currentPosture = 'sentado'
        baseTarget = 68 + Math.sin(t * 0.1) * 3
      } else if (phaseSec < 65) {
        currentPosture = 'parado'
        baseTarget = 82 + (phaseSec - 35) * 0.4
      } else if (phaseSec < 100) {
        currentPosture = 'en_movimiento'
        baseTarget = 112 + Math.sin((phaseSec - 65) * 0.2) * 12
        isWalking = true
      } else {
        currentPosture = 'sentado'
        const rec = (phaseSec - 100) / 20
        baseTarget = 110 - rec * 42
      }

      if (isWalking) {
        setSteps(s => s + Math.round(1 + Math.random()))
      }

      setPosture(currentPosture)

      // Variación sutil y fisiológica de ajuste entre 88% y 94%
      const moistPct = Math.round(91 + Math.sin(t * 0.08) * 3)
      setMoisture({
        pct: moistPct,
        label: 'Excelente',
        color: '#30d158',
      })

      // Arritmia sinusal respiratoria (RSA) natural cada ~4 segundos
      const breathing = Math.sin(t * (Math.PI / 2)) * 3.2
      const jitter = (Math.random() - 0.5) * 2.5
      const target = baseTarget + breathing + jitter

      // Inercia cardíaca suave
      const next = Math.round(Math.max(50, Math.min(182, hrRef.current * 0.88 + target * 0.12)))
      hrRef.current = next

      const meanRr = 60000 / next
      const rrVariation = breathing * 8 + (Math.random() - 0.5) * 25
      const rr = [Math.round(meanRr + rrVariation)]

      setHr(next)
      setHistory(prev => [...prev.slice(-1800), {
        time: Date.now(),
        hr: next,
        rr,
        posture: currentPosture,
      }])
    }, 1000)
    return () => clearInterval(id)
  }, [active])

  const start = (withHistory = false) => {
    tickRef.current = 0
    const seed = withHistory ? buildDemoHistory(8) : []
    const initHr = seed.length ? seed[seed.length - 1].hr : 70
    hrRef.current = initHr
    setHr(initHr)
    setHistory(seed)
    setSteps(0)
    setPosture('sentado')
    setMoisture({ pct: 92, label: 'Excelente', color: '#30d158' })
    setActive(true)
  }
  const stop  = () => {
    setActive(false)
    tickRef.current = 0
  }
  const clear = () => {
    setHistory([])
    setSteps(0)
  }
  return {
    active,
    hr: active ? hr : null,
    history,
    steps,
    posture,
    moisture,
    start,
    stop,
    clearHistory: clear,
  }
}

function useAlerts(history, posture = 'sentado') {
  const [alerts,    setAlerts]    = useState([])
  const lastFired = useRef({})

  useEffect(() => {
    if (history.length < 20) return
    // Suppress alerts during the first 60 seconds to avoid false positives on startup
    if (Date.now() - history[0].time < 60_000) return
    const recent = history.slice(-30)
    const avg    = recent.reduce((a, b) => a + b.hr, 0) / recent.length
    const now    = Date.now()

    // Leer umbrales configurados por el usuario
    const userThresholds = (() => {
      try { return JSON.parse(localStorage.getItem('vc_alert_thresholds') || '{}') } catch (_) { return {} }
    })()
    const tachyThreshold = userThresholds.resting_tachycardia || 100
    const bradyThreshold = userThresholds.resting_bradycardia || 45

    const fire = (type, value, details = {}) => {
      // La misma alerta no se repite hasta pasados 5 min
      if (now - (lastFired.current[type] ?? 0) < 300_000) return
      lastFired.current[type] = now
      const id = `${type}-${now}`
      setAlerts(prev => [...prev, { id, type, value, time: now, dismissed: false, ...details }])

      // Guardar en el log de eventos del día para la pestaña de Alertas
      try {
        const todayKey = new Date().toISOString().slice(0, 10)
        const storageKey = `vc_alerts_log_${todayKey}`
        const existing = JSON.parse(localStorage.getItem(storageKey) || '[]')
        const titleMap = {
          tachycardia: 'Taquicardia en Reposo',
          bradycardia: 'Bradicardia en Vigilia',
          irregular: 'Inestabilidad de Ritmo (Poincaré)'
        }
        existing.unshift({
          id,
          ruleId: type === 'tachycardia' ? 'resting_tachycardia' : type === 'bradycardia' ? 'resting_bradycardia' : 'irregular_rhythm',
          time: new Date(now).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          title: titleMap[type] || 'Desvío Fisiológico',
          val: `${value} ${type === 'irregular' ? 'ms' : 'bpm'}`,
          posture: posture || 'Sentado',
          severity: type === 'tachycardia' && value > 115 ? 'alta' : 'moderada',
          desc: details.desc || `Alerta detectada durante la sesión activa.`
        })
        localStorage.setItem(storageKey, JSON.stringify(existing.slice(0, 50)))
      } catch (_) {}

      // El mensaje se va solo a los 10 segundos
      setTimeout(() => setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: true } : a)), 10_000)
    }

    // Taquicardia contextual (solo en posturas de reposo)
    const isAtRest = !posture || posture === 'sentado' || posture === 'acostado' || posture === 'reposo'
    if (avg > tachyThreshold && isAtRest) {
      fire('tachycardia', Math.round(avg), { desc: `Frecuencia elevada (${Math.round(avg)} bpm) en postura de reposo (${posture || 'sentado'}).` })
    }

    if (avg < bradyThreshold) {
      fire('bradycardia', Math.round(avg), { desc: `Frecuencia mínima de ${Math.round(avg)} bpm en vigilia.` })
    }

    const rr = recent.flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)
    if (rr.length > 10) {
      const diffs  = rr.slice(1).map((v, i) => Math.abs(v - rr[i]))
      const stddev = Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length)
      if (stddev > 80) fire('irregular', Math.round(stddev), { desc: `Dispersión atípica de intervalos RR (desvío de ${Math.round(stddev)} ms).` })
    }
  }, [history.length, posture])

  const dismiss = id => setAlerts(prev => prev.map(a => a.id === id ? { ...a, dismissed: true } : a))
  const dismissAll = () => setAlerts(prev => prev.map(a => ({ ...a, dismissed: true })))
  return { alerts, active: alerts.filter(a => !a.dismissed), dismiss, dismissAll }
}

// ── Base UI ───────────────────────────────────────────────────────────────────

function Clock() {
  const t = useClock()
  const p = n => String(n).padStart(2, '0')
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--text-faint)' }}>
      {p(t.getHours())}:{p(t.getMinutes())}:{p(t.getSeconds())}
    </span>
  )
}

// ── Metric definitions (plain language) ───────────────────────────────────────

import { MetricCard } from './components/ui/MetricCard'
import { Pill } from './components/ui/Pill'
import { SegControl } from './components/ui/SegControl'
import { Btn } from './components/ui/Btn'



const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const hrVal = payload.find(p => p.name === 'hr' || p.dataKey === 'hr')?.value
  const stepsVal = payload.find(p => p.name === 'steps' || p.dataKey === 'steps')?.value
  const itemPayload = payload[0]?.payload || {}
  const posture = itemPayload.posture
  const postureLabel = itemPayload.postureLabel
  const postureColor = itemPayload.postureColor || '#ff3b30'

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.09)', textAlign: 'left' }}>
      {hrVal !== undefined && hrVal !== null && <p style={{ color: postureColor, fontSize: 15, fontWeight: 700, margin: 0 }}>{hrVal} bpm</p>}
      {stepsVal !== undefined && stepsVal !== null && <p style={{ color: '#ff9500', fontSize: 14, fontWeight: 700, margin: '2px 0 0' }}>{stepsVal} pasos</p>}
      {posture && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
          {renderPostureIcon(posture, postureColor, 12)}
          <span style={{ fontSize: 11, fontWeight: 700, color: postureColor }}>{postureLabel || posture}</span>
        </div>
      )}
      <p style={{ color: 'var(--text-faint)', fontSize: 11, margin: '4px 0 0' }}>{itemPayload.label}</p>
    </div>
  )
}

// ── Alert strip ───────────────────────────────────────────────────────────────

const ALERT_CFG = {
  tachycardia: { label: 'Taquicardia',      color: '#ff3b30', bg: 'rgba(255, 59, 48, 0.12)', border: 'rgba(255, 59, 48, 0.28)', desc: 'FC promedio >100 bpm (30s)' },
  bradycardia: { label: 'Bradicardia',      color: '#007aff', bg: 'rgba(0, 122, 255, 0.12)', border: 'rgba(0, 122, 255, 0.28)', desc: 'FC promedio <50 bpm (30s)' },
  irregular:   { label: 'Ritmo irregular',  color: '#ff9f0a', bg: 'rgba(255, 159, 10, 0.12)', border: 'rgba(255, 159, 10, 0.28)', desc: 'Alta variabilidad RR detectada' },
}

function AlertStrip({ active, onDismissAll }) {
  if (!active.length) return null
  const latest = active[active.length - 1]
  const cfg    = ALERT_CFG[latest.type]
  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10,
      padding: '9px 16px', marginBottom: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cfg.desc}</span>
        {active.length > 1 && <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>+{active.length - 1} más</span>}
        <span style={{ fontSize: 11, color: 'var(--text-fainter)' }}>· No es diagnóstico médico</span>
      </div>
      <button onClick={onDismissAll} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-fainter)', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
    </div>
  )
}

// ── HRV panel ─────────────────────────────────────────────────────────────────

function HRVPanel({ hrv }) {
  if (!hrv) return null
  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 600 }}>HRV · Sistema nervioso autónomo</p>
        {hrv.tone
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: hrv.toneColor }} />
              <span style={{ fontSize: 12, color: hrv.toneColor, fontWeight: 600 }}>{hrv.tone}</span>
            </div>
          : <span style={{ fontSize: 11, color: 'var(--text-fainter)' }}>Acumulando RR…</span>
        }
      </div>

      {/* Dominio temporal */}
      <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Dominio temporal</p>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Pill label="RMSSD"      value={hrv.rmssd}    unit="ms"  color={hrv.rmssd ? hrv.toneColor : 'var(--text)'}
              sub="tono vagal" tooltip="Root Mean Square of Successive Differences. Mide la variación entre latidos consecutivos. Alto = relajación/recuperación. Bajo = estrés/fatiga." guideKey="rmssd" />
        <Pill label="SDNN"       value={hrv.sdnn}     unit="ms"
              sub="variabilidad total" tooltip="Desviación estándar de todos los intervalos RR. Refleja la actividad total del sistema nervioso autónomo." guideKey="sdnn" />
        <Pill label="pNN50"      value={hrv.pnn50}    unit="%"
              sub="Δ > 50 ms" tooltip="% de pares consecutivos con diferencia > 50 ms. Similar al RMSSD." guideKey="pnn50" />
        <Pill label="Resp. est." value={hrv.respRate} unit="rpm"
              sub="por RSA" tooltip="Frecuencia respiratoria estimada por arritmia sinusal respiratoria. Normal: 12–20 rpm." guideKey="resp" />
      </div>

      {/* Poincaré */}
      {hrv.sd1 !== null && (
        <>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Poincaré (no-lineal)</p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <Pill label="SD1" value={hrv.sd1} unit="ms" sub="corto plazo"
                  tooltip="Variabilidad latido a latido (perpendicular al eje de identidad). Equivale al RMSSD/√2. Alto = buena actividad parasimpática." />
            <Pill label="SD2" value={hrv.sd2} unit="ms" sub="largo plazo"
                  tooltip="Variabilidad continua del ritmo (sobre el eje de identidad). Similar al SDNN. Refleja la adaptación total del SNA." />
            <Pill label="SD1/SD2" value={hrv.sd1sd2} sub="balance SNA"
                  tooltip="Ratio ideal en reposo: 0.5–0.7. Muy alto (> 0.75) indica que la variabilidad a corto plazo domina — patrón de estrés agudo o arritmia."
                  color={hrv.sd1sd2 > 0.75 ? '#ff9f0a' : hrv.sd1sd2 < 0.3 ? '#ff3b30' : '#34c759'} />
          </div>
        </>
      )}

      {/* DFA α1 + SampEn */}
      {(hrv.dfa1 !== null || hrv.sampen !== null) && (
        <>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Fractal y complejidad</p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            {hrv.dfa1 !== null && (
              <Pill label="DFA α1" value={hrv.dfa1} sub="correlación fractal"
                    color={hrv.dfa1 < 0.75 ? '#ff3b30' : hrv.dfa1 > 1.0 ? '#ff9f0a' : '#34c759'}
                    tooltip={`Exponente de fluctuaciones sin tendencia a corto plazo. Rango saludable: 0.75–1.0. Actual: ${hrv.dfa1}. Bajo (<0.75) = fatiga/sobreentrenamiento. Alto (>1.0) = rigidez autonómica.`} />
            )}
            {hrv.sampen !== null && (
              <Pill label="SampEn" value={hrv.sampen} sub="entropía muestral"
                    color={hrv.sampen < 0.5 ? '#ff3b30' : hrv.sampen > 2.0 ? '#ff9f0a' : '#34c759'}
                    tooltip={`Complejidad del ritmo cardíaco. Rango típico: 1.0–2.5. Bajo (<0.5) = ritmo muy regular/rígido (estrés o patología). Alto (>2.5) = puede indicar arritmia. Actual: ${hrv.sampen}.`} />
            )}
          </div>
        </>
      )}

      <div style={{ background: 'var(--field-bg)', borderRadius: 8, padding: '8px 12px' }}>
        <p style={{ fontSize: 11, color: 'var(--text-faint)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-muted)' }}>Cómo leer esto:</strong> RMSSD alto = calma y recuperación. DFA α1 en 0.75–1.0 = sistema nervioso adaptable.{' '}
          {hrv.rrCount < 30
            ? `Acumulando datos (${hrv.rrCount} intervalos RR — necesito 30+ para DFA, 50+ para SampEn).`
            : `Basado en ${hrv.rrCount} intervalos RR.`}
        </p>
      </div>
    </div>
  )
}

// ── Spectral HRV panel (LF / HF / VLF) ───────────────────────────────────────

function SpectralHRVPanel({ readings }) {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  const rr = useMemo(() => {
    const raw = readings.flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)
    if (raw.length < 3) return raw
    const sorted = [...raw].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    return raw.filter(v => Math.abs(v - median) / median < 0.4)
  }, [readings])

  const spectral = useMemo(() => computeSpectralHRV(rr), [rr])
  const specCardRef = useRef(null)

  if (!spectral) {
    return (
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Espectro HRV · LF / HF</p>
        <p style={{ fontSize: 11, color: 'var(--text-fainter)' }}>
          Acumulando intervalos RR… ({rr.length}/60 mínimos para análisis espectral)
        </p>
        <div style={{ height: 4, background: 'var(--chip-bg)', borderRadius: 2, marginTop: 8 }}>
          <div style={{ height: '100%', width: `${Math.min(100, Math.round(rr.length / 60 * 100))}%`, background: '#007aff', borderRadius: 2, transition: 'width 0.5s' }} />
        </div>
      </div>
    )
  }

  const { vlf, lf, hf, lfNu, hfNu, lfHf, specPoints } = spectral
  const total = vlf + lf + hf || 1

  // Interpretation
  let balanceLabel = '', balanceColor = '#34c759'
  if (lfHf !== null) {
    if      (lfHf > 3.0)  { balanceLabel = 'Simpático dominante';   balanceColor = '#ff3b30' }
    else if (lfHf > 2.0)  { balanceLabel = 'Simpático elevado';     balanceColor = '#ff9f0a' }
    else if (lfHf < 0.5)  { balanceLabel = 'Parasimpático dominante'; balanceColor = '#007aff' }
    else                  { balanceLabel = 'Balance equilibrado';    balanceColor = '#34c759' }
  }

  // Downsample spectrum for chart (keep ≤ 80 points)
  const chartData = specPoints.filter((_, i) => i % Math.ceil(specPoints.length / 80) === 0)

  // Band color helper for area shading
  const getBandColor = freq => {
    if (freq < 0.04)  return '#8e8e9340'
    if (freq < 0.15)  return '#007aff40'
    if (freq < 0.40)  return '#34c75940'
    return 'transparent'
  }

  return (
    <div ref={specCardRef} style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '16px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={{ fontSize: 13, fontWeight: 600 }}>Espectro HRV · LF / HF</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lfHf !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: balanceColor }} />
              <span style={{ fontSize: 12, color: balanceColor, fontWeight: 600 }}>{balanceLabel}</span>
            </div>
          )}
          <ChartActions chartRef={specCardRef} filename={`espectro-hrv-${Date.now()}.png`} />
        </div>
      </div>

      {/* Band pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <Pill label="VLF" value={`${Math.round(vlf / total * 100)}%`} sub="0.003–0.04 Hz"
              color="#8e8e93"
              tooltip="Very Low Frequency. Regulación vasomotora y termorregulatoria. No tiene interpretación autonómica directa en registros cortos." />
        <Pill label="LF" value={`${lfNu}%`} sub="0.04–0.15 Hz"
              color="#007aff"
              tooltip="Low Frequency. Mezcla de actividad simpática y parasimpática. En reposo refleja principalmente el barorreflejo." />
        <Pill label="HF" value={`${hfNu}%`} sub="0.15–0.40 Hz"
              color="#34c759"
              tooltip="High Frequency. Principalmente actividad parasimpática / vagal. Se corresponde con la frecuencia respiratoria. Alto en reposo = buena recuperación." />
        {lfHf !== null && (
          <Pill label="LF/HF" value={lfHf} sub="balance SNA"
                color={balanceColor}
                tooltip={`Ratio de actividad simpática sobre parasimpática. Valores típicos en reposo: 0.5–2.0. Actual: ${lfHf}. Alto = más simpático (estrés/ejercicio). Bajo = más parasimpático (recuperación).`} />
        )}
      </div>

      {/* Bar proportion */}
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ width: `${Math.round(vlf / total * 100)}%`, background: 'var(--text-faint)', transition: 'width 0.5s' }} />
        <div style={{ width: `${lfNu}%`,                          background: '#007aff', transition: 'width 0.5s' }} />
        <div style={{ width: `${hfNu}%`,                          background: '#34c759', transition: 'width 0.5s' }} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {[['VLF', 'var(--text-faint)'], ['LF', '#007aff'], ['HF', '#34c759']].map(([l, c]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
            <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Power spectrum chart */}
      <ResponsiveContainer width="100%" height={100}>
        <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="specGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="8%"   stopColor="#8e8e93" stopOpacity={0.6} />
              <stop offset="30%"  stopColor="#007aff" stopOpacity={0.7} />
              <stop offset="80%"  stopColor="#34c759" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#34c759" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <XAxis dataKey="freq" tick={{ fontSize: 8, fill: 'var(--text-fainter)' }} tickFormatter={v => v === 0.04 || v === 0.15 || v === 0.40 ? v : ''} axisLine={false} tickLine={false} />
          <YAxis hide />
          <ReferenceLine x={0.04}  stroke="#8e8e9360" strokeDasharray="3 2" />
          <ReferenceLine x={0.15}  stroke="#007aff60" strokeDasharray="3 2" />
          <ReferenceLine x={0.40}  stroke="#34c75960" strokeDasharray="3 2" />
          <Area type="monotone" dataKey="power" stroke={isDark ? '#35e08c' : '#007aff'} strokeWidth={1.5} fill={isDark ? 'rgba(53,224,140,0.12)' : 'rgba(0,122,255,0.12)'} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>

      <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 4 }}>
        Basado en {spectral.rrCount} intervalos RR · Resolución frecuencial: 4 Hz · Ventana Hann
      </p>
    </div>
  )
}

// ── HR Distribution chart ────────────────────────────────────────────────────

const hrZoneColor = bpm => bpm < 60 ? '#007aff' : bpm < 100 ? '#34c759' : bpm < 140 ? '#ff9f0a' : '#ff3b30'

const HRDistTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.09)' }}>
      <p style={{ fontSize: 13, fontWeight: 700 }}>{d.label}–{d.bpmNum + 4} bpm</p>
      <p style={{ fontSize: 12, color: 'var(--text-faint)' }}>{d.pct}% del tiempo · {d.count} lecturas</p>
    </div>
  )
}

function HRDistributionChart({ readings }) {
  const isDark  = document.documentElement.getAttribute('data-theme') === 'dark'
  const data    = computeHRDistribution(readings)
  const chartRef = useRef(null)
  if (data.length < 2) return null
  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '18px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <p style={{ fontSize: 13, fontWeight: 600 }}>Distribución de FC</p>
        <ChartActions chartRef={chartRef} filename={`distribucion-fc-${Date.now()}.png`} />
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 14 }}>Cuánto tiempo pasaste en cada rango de latidos — el ancho de cada barra es 5 bpm</p>
      <div ref={chartRef}><ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false}
            label={{ value: 'bpm', position: 'insideBottom', offset: -8, fontSize: 10, fill: 'var(--text-fainter)' }} />
          <YAxis tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={28} tickFormatter={v => `${v}%`} />
          <Tooltip content={<HRDistTooltip />} />
          <Bar dataKey="pct" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((entry, i) => <Cell key={i} fill={hrZoneColor(entry.bpmNum)} fillOpacity={0.8} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer></div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
        {[['#007aff','<60 bpm','Reposo/sueño'],['#34c759','60–100','Normal'],['#ff9f0a','100–140','Esfuerzo mod.'],['#ff3b30','>140','Esfuerzo alto']].map(([c,r,l]) => (
          <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: c, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>{r} — {l}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── RMSSD trend chart ─────────────────────────────────────────────────────────

function makeHRVTooltip(personalAvg) {
  return function HRVTrendTooltip({ active, payload }) {
    if (!active || !payload?.length) return null
    const v = payload[0].value
    let tone, toneColor
    if (personalAvg) {
      const ratio = v / personalAvg
      if (ratio >= 1.2)      { tone = `↑ ${Math.round((ratio-1)*100)}% sobre tu promedio`; toneColor = '#34c759' }
      else if (ratio >= 0.9) { tone = 'En tu zona habitual';                                toneColor = '#007aff' }
      else if (ratio >= 0.7) { tone = `↓ ${Math.round((1-ratio)*100)}% bajo tu promedio`;  toneColor = '#ff9f0a' }
      else                   { tone = 'Muy bajo — estrés o esfuerzo';                       toneColor = '#ff3b30' }
    } else {
      tone = v > 50 ? 'Muy relajado' : v > 30 ? 'Equilibrado' : v > 15 ? 'Activo' : 'Alta activación'
      toneColor = v > 50 ? '#34c759' : v > 30 ? '#007aff' : v > 15 ? '#ff9f0a' : '#ff3b30'
    }
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.09)' }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#007aff' }}>{v} ms</p>
        <p style={{ fontSize: 11, color: toneColor, fontWeight: 600 }}>{tone}</p>
        {personalAvg && <p style={{ fontSize: 10, color: 'var(--text-soft)', marginTop: 2 }}>Promedio: {personalAvg} ms</p>}
      </div>
    )
  }
}

function HRVTrendChart({ readings, precomputed, personalAvg, embedded = false }) {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
  // Memoized so its identity is stable across re-renders — otherwise a new array
  // every render made Recharts reset the <Brush> zoom (the "se va al acortar" bug).
  const data     = useMemo(() => precomputed ?? computeHRVTrend(readings ?? []), [precomputed, readings])
  const chartRef = useRef(null)
  // Controlled, persistent zoom: only resets when the data actually changes length.
  const [brush, setBrush] = useState({ startIndex: undefined, endIndex: undefined })
  useEffect(() => { setBrush({ startIndex: undefined, endIndex: undefined }) }, [data.length])

  // Personal baseline: passed in, or compute from chart data if not provided
  const baseline = personalAvg ?? (
    data.length >= 3
      ? Math.round(data.reduce((s, d) => s + d.rmssd, 0) / data.length)
      : null
  )

  const HRVTip = useMemo(() => makeHRVTooltip(baseline), [baseline])

  if (data.length < 3) return null

  const yVals  = data.map(d => d.rmssd)
  const yMin   = Math.max(0,   Math.floor(Math.min(...yVals) * 0.85))
  const yMax   = Math.ceil(Math.max(...yVals) * 1.15)

  return (
    <div style={embedded ? { width: '100%' } : { background: 'var(--card-bg)', borderRadius: 14, padding: '18px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
          <p style={{ fontSize: 13, fontWeight: 600 }}>RMSSD a lo largo del tiempo</p>
          <ChartActions chartRef={chartRef} filename={`rmssd-trend-${Date.now()}.png`} />
        </div>
      )}
      <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 10 }}>
        Línea punteada = tu promedio ({baseline ?? '—'} ms). Arriba = más calma. <span style={{ color: 'var(--text-soft)' }}>Arrastrá el selector para hacer zoom.</span>
      </p>
      <div ref={chartRef}><ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
          <YAxis domain={[yMin, yMax]} tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={28} />
          {baseline && (
            <ReferenceLine y={baseline} stroke="#007aff" strokeDasharray="5 3" strokeWidth={1.5}
              label={{ value: `${baseline} ms`, fontSize: 8, fill: '#007aff', position: 'insideTopRight' }} />
          )}
          <Tooltip content={<HRVTip />} />
          <Line type="monotone" dataKey="rmssd" stroke="#007aff" strokeWidth={2.5} dot={false} isAnimationActive={false} />
          <Brush dataKey="label" height={24} stroke={isDark ? "rgba(255,255,255,0.15)" : "#e5e5ea"} fill={isDark ? "#161b22" : "#f9f9fb"} travellerWidth={8} tickFormatter={() => ''}
            startIndex={brush.startIndex == null ? undefined : Math.min(brush.startIndex, data.length - 1)}
            endIndex={brush.endIndex == null ? undefined : Math.min(brush.endIndex, data.length - 1)}
            onChange={r => setBrush({ startIndex: r.startIndex, endIndex: r.endIndex })}>
            <LineChart>
              <Line type="monotone" dataKey="rmssd" stroke="#007aff" strokeWidth={1} dot={false} isAnimationActive={false} />
            </LineChart>
          </Brush>
        </LineChart>
      </ResponsiveContainer></div>
    </div>
  )
}

// ── Poincaré plot — Canvas renderer (zero SVG elements per point) ─────────────

function PoincaréChart({ readings, precomputedRR }) {
  const canvasRef = useRef(null)

  const rrCapped = useMemo(() => {
    if (precomputedRR) return precomputedRR
    const raw = (readings ?? []).flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)
    if (raw.length < 3) return []
    const sorted = [...raw].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    const rr = raw.filter(v => Math.abs(v - median) / median < 0.4)
    const MAX = 800
    return rr.length > MAX ? rr.filter((_, i) => i % Math.ceil(rr.length / MAX) === 0) : rr
  }, [precomputedRR, readings])

  useEffect(() => {
    let raf
    const draw = () => {
    const canvas = canvasRef.current
    if (!canvas || rrCapped.length < 5) return

    const dpr = window.devicePixelRatio || 1
    const W   = canvas.offsetWidth
    const H   = 240
    // First paint can run before layout (offsetWidth = 0), leaving the chart
    // blank until something re-triggered a draw — retry on the next frame.
    if (!W) { raf = requestAnimationFrame(draw); return }

    canvas.width  = W * dpr
    canvas.height = H * dpr

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, W, H)

    const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'
    const textColor = isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
    const pointColor = isDark ? 'rgba(100, 190, 255, 0.75)' : 'rgba(0, 122, 255, 0.5)'

    // ── layout margins ──────────────────────────────────────────────────────
    const PAD_L = 42, PAD_R = 10, PAD_T = 10, PAD_B = 28
    const plotW = W - PAD_L - PAD_R
    const plotH = H - PAD_T - PAD_B

    const minVal = Math.max(200, Math.min(...rrCapped) - 60)
    const maxVal = Math.min(1800, Math.max(...rrCapped) + 60)
    const range  = maxVal - minVal

    const toX = v => PAD_L + ((v - minVal) / range) * plotW
    const toY = v => PAD_T + plotH - ((v - minVal) / range) * plotH

    // ── grid lines ──────────────────────────────────────────────────────────
    ctx.strokeStyle = gridColor
    ctx.lineWidth   = 1
    const gridStep = range > 600 ? 200 : 100
    const gridStart = Math.ceil(minVal / gridStep) * gridStep
    for (let v = gridStart; v <= maxVal; v += gridStep) {
      const x = toX(v), y = toY(v)
      ctx.beginPath(); ctx.moveTo(x, PAD_T); ctx.lineTo(x, PAD_T + plotH); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(PAD_L + plotW, y); ctx.stroke()
    }

    // ── axis labels ─────────────────────────────────────────────────────────
    ctx.fillStyle   = textColor
    ctx.font        = `${9 * dpr / dpr}px -apple-system, sans-serif`
    ctx.textAlign   = 'center'
    ctx.textBaseline = 'top'
    for (let v = gridStart; v <= maxVal; v += gridStep) {
      ctx.fillText(v, toX(v), PAD_T + plotH + 5)
    }
    ctx.textAlign    = 'right'
    ctx.textBaseline = 'middle'
    for (let v = gridStart; v <= maxVal; v += gridStep) {
      ctx.fillText(v, PAD_L - 5, toY(v))
    }

    // axis titles
    ctx.fillStyle = textColor
    ctx.font = `9px -apple-system, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText('RR[n] ms', PAD_L + plotW / 2, H - 14)

    ctx.save()
    ctx.translate(12, PAD_T + plotH / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.textBaseline = 'middle'
    ctx.fillText('RR[n+1] ms', 0, 0)
    ctx.restore()

    // ── identity line (y = x) ───────────────────────────────────────────────
    ctx.strokeStyle = textColor
    ctx.lineWidth   = 1
    ctx.setLineDash([5, 5])
    ctx.beginPath()
    ctx.moveTo(toX(minVal), toY(minVal))
    ctx.lineTo(toX(maxVal), toY(maxVal))
    ctx.stroke()
    ctx.setLineDash([])

    // ── scatter points ──────────────────────────────────────────────────────
    ctx.fillStyle = pointColor
    for (let i = 0; i < rrCapped.length - 1; i++) {
      const x = toX(rrCapped[i])
      const y = toY(rrCapped[i + 1])
      ctx.beginPath()
      ctx.arc(x, y, 2.5, 0, Math.PI * 2)
      ctx.fill()
    }
    }
    draw()
    // Redraw when the canvas changes size (layout settles, sidebar, resize)
    const ro = new ResizeObserver(() => draw())
    if (canvasRef.current) ro.observe(canvasRef.current)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [rrCapped])

  if (rrCapped.length < 5) {
    const hasRR = (readings ?? []).some(r => r.rr?.length)
    if (!hasRR && !precomputedRR) return null
    return (
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Diagrama de Poincaré</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)' }}>
          Se necesitan al menos 5 intervalos RR válidos. El H10 los provee durante el monitoreo en vivo —
          las sesiones grabadas internamente (botón) solo almacenan FC promedio por segundo, sin intervalos RR.
        </p>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '18px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Diagrama de Poincaré</p>
      <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 10 }}>
        Cada punto es un par de latidos seguidos: eje X = intervalo actual (ms), eje Y = intervalo siguiente. Nube apretada = ritmo regular. Elongada diagonal = variabilidad respiratoria normal. Puntos muy alejados = posibles anomalías.
      </p>
      <canvas ref={canvasRef} style={{ width: '100%', height: 240, display: 'block' }} />
      <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 6 }}>
        {rrCapped.length - 1} puntos · línea diagonal = latidos perfectamente iguales
      </p>
    </div>
  )
}

// ── Arrhythmia panel ──────────────────────────────────────────────────────────

// Plain-language, NON-diagnostic interpretation of the arrhythmia screening.
function arrhythmiaConclusion(risk) {
  const serious = risk.longPauses > 0 || risk.shortIntervals > 0
  if (risk.risk === 'Normal') {
    return { tone: '#34c759', title: 'Ritmo regular',
      body: 'El tiempo entre latidos se mantuvo parejo. No aparecen señales de irregularidad en esta sesión.' }
  }
  if (serious) {
    return { tone: '#ff3b30', title: 'Conviene revisarlo si se repite',
      body: 'Aparecen pausas largas o latidos muy cortos. La causa más común es ruido de la medición (la banda se movió o perdió contacto con la piel). Pero si lo ves en varias sesiones limpias o tenés síntomas (mareos, palpitaciones, falta de aire), mostráselo a un cardiólogo.' }
  }
  return { tone: '#ff9f0a', title: 'Mucha variación — casi siempre es normal',
    body: 'Hay bastante variación en el tiempo entre latidos. En reposo o con respiración lenta esto suele ser NORMAL (se llama “arritmia sinusal respiratoria”) y hasta es señal de buena recuperación. También puede venir de movimiento o ruido en la medición. Por sí solo NO significa que tengas una arritmia.' }
}

function arrhythmiaVerdict(risk) {
  const serious = risk.longPauses > 0 || risk.shortIntervals > 0
  if (serious) return { color: '#ff3b30', answer: 'Hay señales que conviene revisar',
    sub: 'Aparecieron pausas o latidos muy cortos. La causa más común es ruido de la medición (la banda se movió o perdió contacto). Si se repite en sesiones limpias o tenés síntomas (mareos, palpitaciones, falta de aire), mostráselo a un cardiólogo.' }
  if (risk.risk === 'Normal') return { color: '#34c759', answer: 'Probablemente NO tenés una arritmia',
    sub: 'El tiempo entre latidos se mantuvo regular durante toda la sesión.' }
  return { color: '#ff9f0a', answer: 'Probablemente no — variación alta, casi siempre normal',
    sub: 'Hubo bastante variación latido a latido. En reposo o con respiración lenta suele ser sana (se llama arritmia sinusal respiratoria); por sí sola NO indica que tengas una arritmia.' }
}

function ArrhythmiaPanel({ readings, precomputed }) {
  const risk = precomputed ?? computeArrhythmiaRisk(readings ?? [])
  const [showCalc, setShowCalc] = useState(false)
  if (!risk) return null
  const v = arrhythmiaVerdict(risk)
  const bpm = risk.meanRr ? Math.round(60000 / risk.meanRr) : null
  const calcRows = [
    ['Intervalos RR analizados', `${risk.rrCount}`, 'mínimo 10', false],
    ['RR promedio', `${risk.meanRr} ms`, bpm ? `≈ ${bpm} lpm` : '', false],
    ['SDNN (variabilidad total)', `${risk.sdnn} ms`, '', false],
    ['CV = SDNN ÷ RR', `${risk.cv}%`, 'alerta si > 15%', risk.cv > 15],
    ['Cambios bruscos (Δ > 20%)', `${risk.irregPct}%`, 'alerta si > 40%', risk.irregPct > 40],
    ['Pausas largas (> 1.5 s)', `${risk.longPauses}`, 'alerta si > 2', risk.longPauses > 0],
    ['Intervalos cortos (< 300 ms)', `${risk.shortIntervals}`, 'alerta si > 5', risk.shortIntervals > 0],
    ['Poincaré SD1 (corto plazo)', `${risk.sd1} ms`, '', false],
    ['Poincaré SD2 (largo plazo)', `${risk.sd2} ms`, '', false],
    ['SD1 / SD2', `${risk.sd1sd2 ?? '—'}`, 'alerta si > 0.75', risk.sd1sd2 !== null && risk.sd1sd2 > 0.75],
  ]
  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      {/* Veredicto directo */}
      <div style={{ background: v.color + '12', border: `1px solid ${v.color}30`, borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: v.color, flexShrink: 0 }} />
          <p style={{ fontSize: 13.5, fontWeight: 800, color: v.color, margin: 0 }}>{v.answer}</p>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--text)', margin: 0, lineHeight: 1.45 }}>{v.sub}</p>
      </div>

      {/* 2x2 Compact Grid de métricas de arritmias con formato de color claro */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
        <div style={{ background: 'var(--chip-bg)', borderRadius: 10, padding: '8px 10px' }}>
          <p style={{ fontSize: 8.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px', fontWeight: 700 }}>SD1</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{risk.sd1} <span style={{ fontSize: 9.5, fontWeight: 400, color: 'var(--text-faint)' }}>ms</span></p>
          <p style={{ fontSize: 8, color: 'var(--text-fainter)', margin: '1px 0 0' }}>latido a latido</p>
        </div>
        <div style={{ background: 'var(--chip-bg)', borderRadius: 10, padding: '8px 10px' }}>
          <p style={{ fontSize: 8.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px', fontWeight: 700 }}>SD2</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{risk.sd2} <span style={{ fontSize: 9.5, fontWeight: 400, color: 'var(--text-faint)' }}>ms</span></p>
          <p style={{ fontSize: 8, color: 'var(--text-fainter)', margin: '1px 0 0' }}>largo plazo</p>
        </div>
        <div style={{ background: 'var(--chip-bg)', borderRadius: 10, padding: '8px 10px' }}>
          <p style={{ fontSize: 8.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px', fontWeight: 700 }}>SD1/SD2</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: risk.sd1sd2 > 0.75 ? '#ff9f0a' : '#30d158', margin: 0 }}>{risk.sd1sd2 ?? '—'}</p>
          <p style={{ fontSize: 8, color: 'var(--text-fainter)', margin: '1px 0 0' }}>balance SNA</p>
        </div>
        <div style={{ background: 'var(--chip-bg)', borderRadius: 10, padding: '8px 10px' }}>
          <p style={{ fontSize: 8.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px', fontWeight: 700 }}>Irregularidad</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: risk.irregPct > 40 ? '#ff3b30' : '#30d158', margin: 0 }}>{risk.irregPct} <span style={{ fontSize: 9.5, fontWeight: 400, color: 'var(--text-faint)' }}>%</span></p>
          <p style={{ fontSize: 8, color: 'var(--text-fainter)', margin: '1px 0 0' }}>pares Δ&gt;20%</p>
        </div>
      </div>

      {risk.flags.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {risk.flags.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--inset-bg)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '6px 10px' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600 }}>{f.label}</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>{f.detail}</span>
            </div>
          ))}
        </div>
      )}

      {/* Cómo se calculó */}
      <button onClick={() => setShowCalc(s => !s)} style={{
        background: 'var(--chip-bg)', border: '1px solid var(--border)', color: 'var(--text)',
        fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        padding: '6px 12px', borderRadius: 8, marginBottom: showCalc ? 10 : 12,
        display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s'
      }}>
        <span>{showCalc ? '▲' : '▼'}</span>
        <span>{showCalc ? 'Ocultar desglose de cálculo' : 'Ver desglose de cálculo (RR y Poincaré)'}</span>
      </button>

      {showCalc && (
        <div style={{ background: 'var(--field-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {calcRows.map(([label, val, thr, flagged], i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 10px', borderRadius: 8, gap: 10,
                background: flagged ? 'rgba(255, 159, 10, 0.08)' : 'var(--card-bg)',
                border: `1px solid ${flagged ? 'rgba(255, 159, 10, 0.25)' : 'var(--border-soft)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: flagged ? '#ff9f0a' : '#30d158', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: flagged ? '#ff9f0a' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {val}
                  </span>
                  {thr && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
                      background: flagged ? 'rgba(255, 159, 10, 0.15)' : 'var(--chip-bg)',
                      color: flagged ? '#ff9f0a' : 'var(--text-faint)', whiteSpace: 'nowrap'
                    }}>
                      {thr}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '10px 2px 2px', lineHeight: 1.5 }}>
            Resultado global: <strong style={{ color: risk.riskColor }}>{risk.risk}</strong> — {risk.flags.length === 0 ? 'ningún umbral de alerta superado.' : `${risk.flags.length} señal(es) observadas: ${risk.flags.map(f => f.label.toLowerCase()).join(', ')}.`}
          </p>
        </div>
      )}

      {/* Dispositivo y derivaciones */}
      <div style={{ background: 'var(--tint-blue)', border: '1px solid var(--tint-blue-border)', borderRadius: 10, padding: '9px 12px', marginBottom: 10 }}>
        <p style={{ fontSize: 11.5, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
          <strong>Polar H10</strong> — ECG de <strong>1 derivación</strong> (un electrodo bipolar en el pecho, orientación aproximada a DI). Sirve para mirar el <strong>ritmo</strong> del corazón; no es un ECG clínico de <strong>12 derivaciones</strong> y no sirve para detectar isquemia o infarto.
        </p>
      </div>

      <p style={{ fontSize: 10, color: 'var(--text-fainter)', lineHeight: 1.5 }}>
        [AVISO] Análisis indicativo. No reemplaza diagnóstico médico. Consultá un cardiólogo ante síntomas o patrones persistentes.
      </p>
    </div>
  )
}

// ── ECG chart (Canvas-based, 130 Hz) ─────────────────────────────────────────
//
// Standards implemented:
//   Paper speed : 25 mm/s  → 1 large square = 200 ms (5 squares/s)
//   Gain        : 10 mm/mV → 1 large square = 0.5 mV (4 large = 2 mV)
//   Small square: 40 ms × 0.1 mV (1 mm × 1 mm on real paper)
//   Polar H10 ECG samples are raw µV integers at ~130 Hz.

import { ECG_FS, hpFilter, notchFilter50, detectRPeaks, computeMoistureIndex } from './utils/hrv'
const ECG_SAMPLES_PER_LARGE = 26      // 200 ms × 130 Hz = 26 samples per large square
const ECG_LARGE_MV     = 0.5          // mV per large square (standard 10 mm/mV)
const ECG_LARGE_UV     = 500          // µV per large square
const ECG_VERT_SQUARES = 4            // height in large squares (2 mV full range)
const ECG_CANVAS_H     = 220          // canvas CSS height — 4 large squares + margins

// Zoom levels in seconds (at 25 mm/s these map to real paper widths)
const ECG_ZOOM_SECS   = [2.5, 5, 10, 20]
const ECG_ZOOM_LABELS = ['2.5 s', '5 s', '10 s', '20 s']
const ECG_ZOOM_SAMPLES = ECG_ZOOM_SECS.map(s => Math.round(s * ECG_FS))

// Gain multipliers: ×0.5 = 5 mm/mV, ×1 = 10 mm/mV (standard), ×2 = 20 mm/mV
const ECG_GAIN_LEVELS = [0.5, 1, 2]
const ECG_GAIN_LABELS = ['5 mm/mV', '10 mm/mV', '20 mm/mV']

function drawECGGrid(ctx, W, H, windowSamples, calWidth = 0, theme = 'dark') {
  const isDark = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark') || theme === 'dark'
  const nLargeH = windowSamples / ECG_SAMPLES_PER_LARGE
  const largeW  = W / nLargeH
  const smallW  = largeW / 5
  const largeH  = H / ECG_VERT_SQUARES
  const smallH  = largeH / 5

  // Fondo estilo monitor en modo noche (#0d1117), papel clínico rosado en modo día
  ctx.fillStyle = isDark ? '#0d1117' : '#fff5f5'
  ctx.fillRect(0, 0, W, H)

  // Small grid (1 mm — 40 ms × 0.1 mV)
  ctx.strokeStyle = isDark ? 'rgba(140,165,190,0.10)' : 'rgba(224, 49, 49, 0.16)'
  ctx.lineWidth = 0.5
  for (let x = 0; x <= W + 0.5; x += smallW) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
  }
  for (let y = 0; y <= H + 0.5; y += smallH) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
  }

  // Large grid (5 mm — 200 ms × 0.5 mV)
  ctx.strokeStyle = isDark ? 'rgba(140,165,190,0.24)' : 'rgba(224, 49, 49, 0.36)'
  ctx.lineWidth = 0.9
  for (let x = 0; x <= W + 0.5; x += largeW) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
  }
  for (let y = 0; y <= H + 0.5; y += largeH) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
  }

  // Time axis labels — every large square = 200 ms, label every 1 s (5 squares)
  const labelEvery = 5   // large squares = 1 second
  ctx.fillStyle = isDark ? 'rgba(225,240,255,0.40)' : 'rgba(0,0,0,0.45)'
  ctx.font = '400 7px -apple-system, sans-serif'
  ctx.textAlign = 'center'
  const traceX = calWidth
  for (let sq = 0; sq * largeW + traceX <= W; sq++) {
    if (sq % labelEvery === 0 && sq > 0) {
      const x = traceX + sq * largeW
      const sec = (sq * ECG_SAMPLES_PER_LARGE / ECG_FS).toFixed(0)
      ctx.fillText(`${sec}s`, x, H - 2)
    }
  }
}

function ECGChart({ ecgRef, ecgTick, compact = false, theme = 'dark' }) {
  const canvasRef    = useRef(null)
  const gridCanvasRef = useRef(null)
  const gridKeyRef    = useRef('')
  const [offset,     setOffset]    = useState(0)
  const [zoomIdx,    setZoomIdx]   = useState(1)   // 1 = 5 s default
  const [gainIdx,    setGainIdx]   = useState(1)   // 1 = 10 mm/mV standard
  const [ecgInverted, setEcgInverted] = useState(false)
  const [isPaused,   setIsPaused]  = useState(false)
  const offsetRef    = useRef(0)
  const zoomIdxRef   = useRef(1)
  const gainIdxRef   = useRef(1)
  const ecgInvertedRef = useRef(false)
  const isPausedRef  = useRef(false)
  const frozenLenRef = useRef(null)
  const dragRef      = useRef(null)
  const touchRef     = useRef(null)

  // Reacción instantánea al cambio de tema del DOM ([data-theme="dark"])
  const [domTheme, setDomTheme] = useState(() => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark')
  useEffect(() => {
    if (typeof document === 'undefined') return
    const observer = new MutationObserver(() => {
      setDomTheme(document.documentElement.getAttribute('data-theme') || 'dark')
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  const isDark = domTheme === 'dark' || theme === 'dark'

  useEffect(() => { zoomIdxRef.current = zoomIdx }, [zoomIdx])
  useEffect(() => { ecgInvertedRef.current = ecgInverted }, [ecgInverted])
  useEffect(() => { offsetRef.current  = offset  }, [offset])
  useEffect(() => { gainIdxRef.current = gainIdx }, [gainIdx])
  useEffect(() => { isPausedRef.current = isPaused }, [isPaused])

  const togglePause = useCallback(() => {
    setIsPaused(p => {
      const next = !p
      if (next) {
        frozenLenRef.current = Math.max(0, ecgRef.current.length - offsetRef.current)
      } else {
        frozenLenRef.current = null
        setOffset(0)
      }
      return next
    })
  }, [ecgRef])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr     = window.devicePixelRatio || 1
    const cssW    = canvas.offsetWidth
    const cssH    = compact ? Math.max(140, canvas.offsetHeight || 160) : ECG_CANVAS_H
    // Resize canvas buffer if needed
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width  = Math.round(cssW * dpr)
      canvas.height = Math.round(cssH * dpr)
    }
    const W = canvas.width
    const H = canvas.height

    const ctx       = canvas.getContext('2d')
    const buf       = ecgRef.current
    const off       = offsetRef.current
    const winSamples = ECG_ZOOM_SAMPLES[zoomIdxRef.current]
    const gain      = ECG_GAIN_LEVELS[gainIdxRef.current]

    const totalLen = (isPausedRef.current && frozenLenRef.current != null)
      ? frozenLenRef.current
      : buf.length
    const end     = Math.max(0, totalLen - off)
    const raw     = buf.slice(Math.max(0, end - winSamples), end)

    // ── Fixed-scale amplitude mapping ────────────────────────────────────────
    const largeH  = cssH / ECG_VERT_SQUARES
    const pxPerUV = (largeH / ECG_LARGE_UV) * gain

    // ── 1 mV calibration pulse geometry (needed by grid for time labels) ─────
    const calSamples = ECG_SAMPLES_PER_LARGE
    const calPxW     = (calSamples / winSamples) * cssW
    const calX       = 4
    const traceStartX = calX + calPxW + 4

    ctx.save()
    ctx.scale(dpr, dpr)

    // Pre-rendered offscreen grid cache (invalidated reactively by isDark)
    const gridKey = `${Math.round(cssW)}x${Math.round(cssH)}_${winSamples}_${Math.round(traceStartX)}_${isDark ? 'dark' : 'light'}`
    if (!gridCanvasRef.current) gridCanvasRef.current = document.createElement('canvas')
    if (gridKeyRef.current !== gridKey) {
      gridCanvasRef.current.width = Math.round(cssW * dpr)
      gridCanvasRef.current.height = Math.round(cssH * dpr)
      const gCtx = gridCanvasRef.current.getContext('2d')
      gCtx.scale(dpr, dpr)
      drawECGGrid(gCtx, cssW, cssH, winSamples, traceStartX, isDark ? 'dark' : 'light')
      gridKeyRef.current = gridKey
    }
    ctx.drawImage(gridCanvasRef.current, 0, 0, cssW, cssH)

    if (raw.length < 2) {
      ctx.fillStyle = isDark ? 'rgba(225,240,255,0.5)' : 'rgba(0,0,0,0.5)'
      ctx.font      = '500 13px -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Esperando señal ECG…', cssW / 2, cssH / 2)
      ctx.restore()
      return
    }

    // HP filter → notch 50 Hz → signal pipeline
    const filtered = notchFilter50(hpFilter(raw))

    const midY = cssH / 2

    // ── Standard labels (top-left) ───────────────────────────────────────────
    ctx.fillStyle = isDark ? 'rgba(225,240,255,0.45)' : 'rgba(0,0,0,0.55)'
    ctx.font      = '500 9px -apple-system, sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText(ecgInvertedRef.current ? 'DII (Invertido)' : 'Derivación I (DI)', 6, 11)
    ctx.fillText(`${ECG_GAIN_LABELS[gainIdxRef.current]} · 25 mm/s`, 6, 22)

    // ── 1 mV calibration pulse ───────────────────────────────────────────────
    const calPxH = 1000 * pxPerUV
    const calY   = midY + calPxH / 2

    ctx.strokeStyle = isDark ? '#64d2ff' : '#000000'
    ctx.lineWidth   = 1.2
    ctx.lineJoin    = 'round'
    ctx.beginPath()
    ctx.moveTo(calX,          calY)
    ctx.lineTo(calX,          calY - calPxH)
    ctx.lineTo(calX + calPxW, calY - calPxH)
    ctx.lineTo(calX + calPxW, calY)
    ctx.stroke()

    ctx.fillStyle = isDark ? '#64d2ff' : '#000000'
    ctx.font      = '400 8px -apple-system, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('1 mV', calX + calPxW / 2, calY - calPxH - 3)

    // ── Draw ECG trace (verde monitor en noche, tinta médica negra en día) ────
    const traceW = cssW - traceStartX
    const n      = filtered.length
    const signalMultiplier = ecgInvertedRef.current ? -1 : 1

    ctx.strokeStyle = isDark ? '#35e08c' : '#000000'
    ctx.lineWidth   = isDark ? 1.6 : 1.8
    ctx.lineJoin    = 'round'
    ctx.lineCap     = 'round'
    ctx.beginPath()

    let started = false
    for (let i = 0; i < n; i++) {
      const x = traceStartX + (i / winSamples) * traceW
      const y = midY - filtered[i] * pxPerUV * signalMultiplier
      if (!started) { ctx.moveTo(x, y); started = true }
      else          { ctx.lineTo(x, y) }
    }
    ctx.stroke()

    // ── R-peak annotations ───────────────────────────────────────────────────
    const peaks = detectRPeaks(filtered, ECG_FS)
    ctx.fillStyle   = '#ff453a'
    ctx.strokeStyle = 'rgba(255,69,58,0.3)'
    ctx.lineWidth   = 0.8
    peaks.forEach(pi => {
      const x = traceStartX + (pi / winSamples) * traceW
      const y = midY - filtered[pi] * pxPerUV * signalMultiplier
      // Vertical dashed line from peak to top
      ctx.setLineDash([2, 3])
      ctx.beginPath()
      ctx.moveTo(x, y - 4)
      ctx.lineTo(x, 8)
      ctx.stroke()
      ctx.setLineDash([])
      // Small red dot on peak
      ctx.beginPath()
      ctx.arc(x, y, 2.5, 0, Math.PI * 2)
      ctx.fill()
    })

    // ── mV scale ruler (right edge) ──────────────────────────────────────────
    const rulerX  = cssW - 18
    const rulerH  = largeH * 2
    const rulerMv = (2 * ECG_LARGE_MV / gain).toFixed(1)
    const rulerY  = midY - rulerH / 2

    ctx.strokeStyle = 'rgba(225,240,255,0.35)'
    ctx.lineWidth   = 1
    ctx.beginPath(); ctx.moveTo(rulerX, rulerY);          ctx.lineTo(rulerX, rulerY + rulerH); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(rulerX - 4, rulerY);      ctx.lineTo(rulerX + 4, rulerY);      ctx.stroke()
    ctx.beginPath(); ctx.moveTo(rulerX - 4, rulerY + rulerH); ctx.lineTo(rulerX + 4, rulerY + rulerH); ctx.stroke()

    ctx.fillStyle = 'rgba(225,240,255,0.45)'
    ctx.font      = '400 8px -apple-system, sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText(`${rulerMv} mV`, rulerX - 6, midY + 3)

    ctx.restore()
  }, [ecgRef, isDark, theme])

  // Resize observer — redraw when container resizes
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ro = new ResizeObserver(() => draw())
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [draw])

  useEffect(() => { draw() }, [ecgTick, offset, gainIdx, zoomIdx, ecgInverted, draw, isDark, theme])

  // ── Mouse drag to pan ────────────────────────────────────────────────────────
  const onMouseDown  = useCallback(e => {
    dragRef.current = { startX: e.clientX, startOff: offsetRef.current }
  }, [])

  const onMouseMove  = useCallback(e => {
    if (!dragRef.current) return
    const canvas      = canvasRef.current
    if (!canvas) return
    const winSamples  = ECG_ZOOM_SAMPLES[zoomIdxRef.current]
    const pxPerSample = canvas.getBoundingClientRect().width / winSamples
    const delta       = Math.round((dragRef.current.startX - e.clientX) / pxPerSample)
    const maxOff      = Math.max(0, ecgRef.current.length - winSamples)
    setOffset(Math.max(0, Math.min(maxOff, dragRef.current.startOff + delta)))
  }, [ecgRef])

  const onMouseUp    = useCallback(() => { dragRef.current = null }, [])

  // ── Touch drag to pan ────────────────────────────────────────────────────────
  const onTouchStart = useCallback(e => {
    touchRef.current = { startX: e.touches[0].clientX, startOff: offsetRef.current }
  }, [])

  const onTouchMove  = useCallback(e => {
    if (!touchRef.current) return
    e.preventDefault()
    const canvas      = canvasRef.current
    if (!canvas) return
    const winSamples  = ECG_ZOOM_SAMPLES[zoomIdxRef.current]
    const pxPerSample = canvas.getBoundingClientRect().width / winSamples
    const delta       = Math.round((touchRef.current.startX - e.touches[0].clientX) / pxPerSample)
    const maxOff      = Math.max(0, ecgRef.current.length - winSamples)
    setOffset(Math.max(0, Math.min(maxOff, touchRef.current.startOff + delta)))
  }, [ecgRef])

  const onTouchEnd   = useCallback(() => { touchRef.current = null }, [])

  const winSamples = ECG_ZOOM_SAMPLES[zoomIdx]
  const maxOffset  = Math.max(0, ecgRef.current.length - winSamples)
  const isLive     = offset === 0

  function downloadECGPng() {
    const canvas = canvasRef.current
    if (!canvas) return
    const a = document.createElement('a')
    a.download = `ecg-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`
    a.href = canvas.toDataURL('image/png')
    a.click()
  }

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '14px 16px 10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>ECG</p>
          <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>Polar H10 · 130 Hz · 1 derivación</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Derivación Control */}
          <button onClick={() => setEcgInverted(v => !v)}
            style={{ background: 'var(--chip-bg)', color: 'var(--text-muted)', border: 'none', borderRadius: 7, padding: '5px 8px', fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            {ecgInverted ? 'DII (Inv)' : 'DI (Norm)'}
          </button>
          {/* Gain control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--chip-bg)', borderRadius: 7, padding: '2px 4px' }}>
            <span style={{ fontSize: 9, color: 'var(--text-faint)', paddingLeft: 2 }}>Gain</span>
            {ECG_GAIN_LABELS.map((lbl, i) => (
              <button key={i} onClick={() => setGainIdx(i)}
                style={{ background: gainIdx === i ? 'var(--btn-strong)' : 'transparent', color: gainIdx === i ? 'var(--seg-active-text-inv)' : 'var(--text-muted)', border: 'none', borderRadius: 5, padding: '2px 6px', fontSize: 9, fontWeight: gainIdx === i ? 700 : 400, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {ECG_GAIN_LEVELS[i]}×
              </button>
            ))}
          </div>
          {/* Zoom control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <button onClick={() => { setZoomIdx(i => Math.min(ECG_ZOOM_SECS.length - 1, i + 1)); setOffset(0) }}
              style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 28, textAlign: 'center' }}>{ECG_ZOOM_LABELS[zoomIdx]}</span>
            <button onClick={() => { setZoomIdx(i => Math.max(0, i - 1)); setOffset(0) }}
              style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
          <button
            onClick={togglePause}
            title={isPaused ? 'Reanudar visualización continua en vivo' : 'Congelar trazado para inspección minuciosa'}
            style={{
              background: isPaused ? 'rgba(255, 159, 10, 0.15)' : 'var(--chip-bg)',
              color: isPaused ? '#ff9f0a' : 'var(--text)',
              border: isPaused ? '1px solid rgba(255, 159, 10, 0.35)' : 'none',
              borderRadius: 7, padding: '3px 8px', fontSize: 10, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4
            }}
          >
            {isPaused ? 'Reanudar' : 'Pausar'}
          </button>
          {!isLive && (
            <button onClick={() => { setIsPaused(false); frozenLenRef.current = null; setOffset(0) }}
              style={{ fontSize: 10.5, color: '#007aff', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>
              → En vivo
            </button>
          )}
          <span style={{ fontSize: 10, color: (isLive && !isPaused) ? '#34c759' : '#ff9f0a', fontWeight: 700 }}>
            {(isLive && !isPaused) ? '● En vivo' : 'Pausado'}
          </span>
          <button onClick={downloadECGPng} title="Descargar PNG"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-fainter)', fontSize: 13, padding: '0 2px' }}>↓</button>
        </div>
      </div>

      {/* Canvas */}
      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'ew-resize', touchAction: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: ECG_CANVAS_H, display: 'block' }}
        />
      </div>

      {/* Scrubber */}
      {maxOffset > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 9, color: 'var(--text-fainter)', flexShrink: 0 }}>−{ECG_ZOOM_LABELS[zoomIdx]}</span>
          <input
            type="range" min={0} max={maxOffset}
            value={maxOffset - offset}
            onChange={e => setOffset(maxOffset - Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--text)' }}
          />
          <span style={{ fontSize: 9, color: '#34c759', flexShrink: 0 }}>en vivo</span>
        </div>
      )}

      <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 6 }}>
        Arrastrá para navegar · 25 mm/s · cuadro grande = 200 ms × 0.5 mV
      </p>
    </div>
  )
}

// ── Live HR chart ─────────────────────────────────────────────────────────────

function LiveChart({ readings, compact = false, posture = 'sentado', isDark = false }) {
  const [view,    setView]    = useState('second')
  const [window,  setWindow]  = useState(120)   // how many beats to show in 'second' view
  const chartRef  = useRef(null)
  const scrollRef = useRef(null)
  const prevCount = useRef(0)

  const postureColor = useMemo(() => getPostureColor(posture), [posture])
  const postureLabel = useMemo(() => getPostureLabel(posture), [posture])

  const strokeGradId = `${compact ? 'gradCompactStroke' : 'gradLiveStroke'}`
  const fillGradId   = `${compact ? 'gradCompactFill' : 'gradLiveFill'}`

  // Patch readings to respect window size in second view
  const windowedReadings = view === 'second' ? readings.slice(-window) : readings
  const data = aggregateReadings(windowedReadings, view)
  const avg  = data.length ? Math.round(data.filter(d => d.hr).reduce((a, b) => a + b.hr, 0) / data.filter(d => d.hr).length) : null

  // Gradiente horizontal que mapea en qué momentos estuviste sentado, parado o acostado a lo largo del tiempo
  const gradientStops = useMemo(() => {
    if (!data.length) return [{ offset: '0%', color: postureColor }, { offset: '100%', color: postureColor }]
    const N = data.length
    if (N === 1) {
      const c = data[0].postureColor || postureColor
      return [{ offset: '0%', color: c }, { offset: '100%', color: c }]
    }
    const stops = []
    let currentC = null
    let segStart = 0

    for (let i = 0; i < N; i++) {
      const c = data[i].postureColor || postureColor
      if (c !== currentC) {
        if (currentC !== null) {
          const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
          const p2 = Math.min(100, Math.round((i / (N - 1)) * 100))
          stops.push({ offset: `${p1}%`, color: currentC })
          stops.push({ offset: `${p2}%`, color: currentC })
        }
        currentC = c
        segStart = i
      }
    }
    if (currentC !== null) {
      const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
      stops.push({ offset: `${p1}%`, color: currentC })
      stops.push({ offset: '100%', color: currentC })
    }
    return stops
  }, [data, postureColor])

  // Scroll effect: when a new second-view data point arrives, slide the chart left
  useEffect(() => {
    if (view !== 'second') return
    if (readings.length <= prevCount.current) return
    prevCount.current = readings.length
    const el = scrollRef.current
    if (!el) return
    el.style.animation = 'none'
    void el.offsetHeight          // force reflow to restart animation
    el.style.animation = 'chartScroll 0.95s linear'
  }, [readings.length, view])

  // Stable Y-axis domain — updates only every 10 new readings to avoid jumps
  const yDomain = [40, 200]   // fixed scale so sessions are comparable

  return (
    <div style={compact ? { height: '100%', display: 'flex', flexDirection: 'column' } : { background: 'var(--card-bg)', borderRadius: 14, padding: '18px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: compact ? 6 : 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!compact && <p style={{ fontSize: 13, fontWeight: 600 }}>Frecuencia cardíaca</p>}
          {avg && <span style={{ fontSize: 10.5, color: '#007aff', background: '#eff6ff', borderRadius: 6, padding: '2px 7px', fontWeight: 700 }}>prom {avg} bpm</span>}
          {postureLabel && (
            <span style={{ fontSize: 10, color: postureColor, background: `${postureColor}18`, borderRadius: 6, padding: '2px 7px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {posture !== 'sentado' && renderPostureIcon(posture, postureColor, 12)}
              <span>{postureLabel}</span>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {view === 'second' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button onClick={() => setWindow(w => Math.max(30, w / 2))} title="Zoom in"
                style={{ background: compact && isDark ? 'rgba(255,255,255,0.12)' : 'var(--chip-bg)', color: compact && isDark ? '#ffffff' : 'inherit', border: 'none', borderRadius: 5, width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              <span style={{ fontSize: 9.5, color: compact && isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', minWidth: 28, textAlign: 'center' }}>{window}s</span>
              <button onClick={() => setWindow(w => Math.min(480, w * 2))} title="Zoom out"
                style={{ background: compact && isDark ? 'rgba(255,255,255,0.12)' : 'var(--chip-bg)', color: compact && isDark ? '#ffffff' : 'inherit', border: 'none', borderRadius: 5, width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            </div>
          )}
          <SegControl
            value={view}
            onChange={v => { setView(v); setWindow(120) }}
            options={[{ key: 'second', label: '1 s' }, { key: 'minute', label: '1 min' }, { key: 'hour', label: '1 h' }]}
            dark={compact && isDark}
          />
          {!compact && data.length >= 2 && <ChartActions chartRef={chartRef} filename={`frecuencia-cardiaca-${Date.now()}.png`} />}
        </div>
      </div>
      <div ref={chartRef} style={{ borderRadius: 8, flex: 1, minHeight: 0 }}>
        {!data.some(d => d.hr != null) ? (
          <div style={{
            height: compact ? 130 : 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            background: 'var(--field-bg)',
            borderRadius: 10,
            border: '1px dashed var(--border)',
            margin: '4px 0',
            padding: 16
          }}>
            <IconActivity size={24} color="var(--text-faint)" />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>
              Sin registros
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center' }}>
              No hay datos de frecuencia cardíaca para mostrar en este período.
            </span>
          </div>
        ) : (
          <div ref={scrollRef} style={{ overflow: 'hidden', height: '100%' }}>
            <ResponsiveContainer width="100%" height={compact ? 130 : 220}>
              <AreaChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={strokeGradId} x1="0" y1="0" x2="1" y2="0">
                    {gradientStops.map((s, idx) => (
                      <stop key={idx} offset={s.offset} stopColor={s.color} />
                    ))}
                  </linearGradient>
                  <linearGradient id={fillGradId} x1="0" y1="0" x2="1" y2="0">
                    {gradientStops.map((s, idx) => (
                      <stop key={idx} offset={s.offset} stopColor={s.color} stopOpacity={0.28} />
                    ))}
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={compact ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)') : 'var(--border-soft)'} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} interval={Math.max(0, Math.floor(data.length / 5) - 1)} axisLine={false} tickLine={false} />
                <YAxis domain={yDomain} tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={28} />
                {avg && <ReferenceLine y={avg} stroke="#007aff" strokeOpacity={compact ? 0.35 : 0.8} strokeDasharray="3 3" strokeWidth={1.2} />}
                <Tooltip content={<ChartTooltip />} />
                <Area type="linear" dataKey="hr" stroke={`url(#${strokeGradId})`} strokeWidth={2.5} fill={`url(#${fillGradId})`} dot={false} isAnimationActive={false} connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Línea de tiempo horizontal segmentada por postura bajo el gráfico */}
        {data.some(d => d.hr != null) && gradientStops.length > 2 && (
          <div style={{ height: 4, borderRadius: 2, display: 'flex', overflow: 'hidden', marginTop: 4, background: 'var(--track)', width: '100%' }}>
            {gradientStops.map((s, idx) => {
              if (idx % 2 !== 0) return null
              const next = gradientStops[idx + 1]
              const p1 = parseFloat(s.offset)
              const p2 = next ? parseFloat(next.offset) : 100
              const w = Math.max(0.5, p2 - p1)
              return (
                <div
                  key={idx}
                  style={{ width: `${w}%`, background: s.color, height: '100%' }}
                  title={`${s.color}`}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Offline recording panel ───────────────────────────────────────────────────

function OfflineSessionPanel({ offline, onSaveToHistory }) {
  const { status, statusMsg, hrSamples, progress, error, deviceName, fetchRecording, reset } = offline

  const stats = hrSamples.length ? computeStats(hrSamples) : null

  if (status === 'idle') return null

  const isLoading = ['connecting', 'scanning', 'downloading'].includes(status)

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>

      {/* Loading / progress */}
      {isLoading && (
        <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '24px 20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#007aff', animation: 'blink 0.8s ease-in-out infinite' }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{statusMsg || 'Procesando…'}</p>
          </div>
          {status === 'downloading' && (
            <div style={{ background: 'var(--chip-bg)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 100, background: '#007aff', width: `${progress}%`, transition: 'width 0.3s ease' }} />
            </div>
          )}
          <button onClick={reset} style={{ marginTop: 14, fontSize: 12, color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancelar
          </button>
        </div>
      )}

      {/* No session */}
      {status === 'nosession' && (
        <div style={{ background: '#fff8e6', border: '1.5px solid #ffd60a', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
          <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>No se encontró ninguna sesión grabada en el H10</p>

          {/* Step-by-step guide */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[
              { n: 1, text: 'En Nueva sesión, tocá "Grabar sin celular (memoria del H10)"' },
              { n: 2, text: 'Tocá "● Empezar a grabar" con el sensor puesto' },
              { n: 3, text: 'Dejá el celular y hacé tu actividad — el H10 guarda solo' },
              { n: 4, text: 'Al volver, tocá "■ Detener y traer la grabación"' },
            ].map(({ n, text }) => (
              <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#ffd60a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-soft)', flexShrink: 0 }}>{n}</div>
                <p style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.55, paddingTop: 2 }}>{text}</p>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 12, lineHeight: 1.5 }}>
            El H10 guarda hasta 30 hs de FC + intervalos RR. Solo puede haber una sesión a la vez.
          </p>
          <Btn onClick={reset}>Cerrar</Btn>
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div style={{ background: '#fff5f5', border: '1.5px solid #ffd6d6', borderRadius: 14, padding: '18px 20px', marginBottom: 14 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#ff3b30', marginBottom: 6 }}>No se pudo descargar la sesión</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.65, marginBottom: 14 }}>{error}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="primary" onClick={fetchRecording}>Reintentar</Btn>
            <Btn onClick={reset}>Cancelar</Btn>
          </div>
        </div>
      )}

      {/* Done — show downloaded session */}
      {status === 'done' && stats && (
        <>
          {/* Session header */}
          <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#34c759' }} />
                  <p style={{ fontSize: 14, fontWeight: 700 }}>{deviceName ?? 'Polar H10'} — Sesión grabada</p>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {fmtDate(hrSamples[0].time)} · {fmtTime(hrSamples[0].time)} → {fmtTime(hrSamples[hrSamples.length - 1].time)} · {fmtDurationMs(hrSamples[hrSamples.length - 1].time - hrSamples[0].time)} · {hrSamples.length} lecturas a 1 Hz
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="sync" onClick={() => onSaveToHistory(hrSamples, deviceName)}>Guardar en historial</Btn>
                <Btn variant="muted" onClick={reset}>Cerrar</Btn>
              </div>
            </div>
            <div style={{ marginTop: 10, background: '#f0f6ff', borderRadius: 8, padding: '8px 12px' }}>
              <p style={{ fontSize: 11, color: '#007aff' }}>
                Esta sesión fue grabada sin teléfono. Solo contiene FC (1 Hz) — los intervalos RR y ECG solo están disponibles en streaming en vivo.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
            <MetricCard defKey="avg" value={stats.avg} />
            <MetricCard defKey="min" value={stats.min} color="#007aff" />
            <MetricCard defKey="max" value={stats.max} color="#ff3b30" />
            <div style={{ background: 'var(--chip-bg)', borderRadius: 14, padding: '14px 16px', flex: 1, minWidth: 150 }}>
              <p style={{ fontSize: 10, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, marginBottom: 6 }}>Duración</p>
              <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
                {fmtDurationMs(hrSamples[hrSamples.length - 1].time - hrSamples[0].time)}
              </span>
            </div>
          </div>

          {/* HR over time */}
          <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '18px 18px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
            <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Frecuencia cardíaca</p>
            <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 14 }}>Un punto por segundo · línea azul = promedio</p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={injectGaps(aggregateReadings(hrSamples, 'minute'), 5 * 60_000)} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradOff" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3b30" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#ff3b30" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                <YAxis domain={[40, 170]} tick={{ fontSize: 9, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={28} />
                {stats.avg && <ReferenceLine y={stats.avg} stroke="#007aff" strokeDasharray="4 4" strokeWidth={1.5} />}
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="hr" stroke="#ff3b30" strokeWidth={2} fill="url(#gradOff)" dot={false} isAnimationActive={false} connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* HR Distribution */}
          <HRDistributionChart readings={hrSamples} />

          {/* Export */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <Btn onClick={() => downloadCSV(hrSamples, `polar-grabado-${fmtDate(hrSamples[0].time).replace(/ /g, '-')}.csv`)} disabled={!hrSamples.length}>
              ↓ Exportar CSV
            </Btn>
          </div>
        </>
      )}
    </div>
  )
}

// ── Record Mode Screen ────────────────────────────────────────────────────────

// La grabación la arranca y la trae VITALCORE, hablando directo con el H10
// (protocolo PS-FTP del SDK oficial de Polar). Sin Polar Beat.
function RecordModeScreen({ mode, setMode, offline, liveBusy }) {
  const { rec } = offline
  const recording = mode.phase === 'recording'
  const inShell = !!(window.__MANABI_SHELL__ || window.__VITALCORE_SHELL__ || window.ReactNativeWebView)
  const [stoppedNote, setStoppedNote] = useState(false)
  const [elapsed, setElapsed] = useState('00:00')
  useEffect(() => {
    if (!recording || !mode.startedAt) return
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - mode.startedAt) / 1000)
      const h = Math.floor(s / 3600)
      const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
      const sec = String(s % 60).padStart(2, '0')
      setElapsed(h > 0 ? `${h}:${m}:${sec}` : `${m}:${sec}`)
    }, 1000)
    return () => clearInterval(id)
  }, [recording, mode.startedAt])

  // La VERDAD la tiene el sensor, no el teléfono: al abrir el panel (solo en la
  // app — en la web abriría el selector de dispositivos) se le pregunta si hay
  // una grabación activa, y la pantalla se acomoda sola en ambos sentidos.
  useEffect(() => {
    if (!inShell) return
    offline.checkSensorRec({ quiet: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (rec.on === true && mode.phase !== 'recording') {
      setMode({ phase: 'recording', startedAt: mode.startedAt ?? null, kind: mode.kind ?? 'rr' })
    } else if (rec.on === false && mode.phase === 'recording') {
      setStoppedNote(true)
      setMode({ phase: 'menu' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.on, mode.phase])

  const start = async (kind) => {
    const ok = await offline.startSensorRec(kind)
    if (ok) setMode({ phase: 'recording', startedAt: Date.now(), kind })
  }
  const doImport = () => { setMode(null); offline.fetchRecording() }
  const stopAndImport = async () => {
    const ok = await offline.stopSensorRec()
    if (ok) doImport()
  }

  const primaryBtn = { background: '#34c759', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 700, width: '100%', marginBottom: 10, cursor: 'pointer', fontFamily: 'inherit', opacity: rec.busy ? 0.6 : 1 }
  const secondaryBtn = { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 0', fontSize: 13, fontWeight: 600, width: '100%', marginBottom: 10, cursor: 'pointer', fontFamily: 'inherit', opacity: rec.busy ? 0.6 : 1 }

  return (
    <div style={{ maxWidth: 440, margin: '0 auto', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ marginBottom: 12, display: 'flex' }}>
        <button
          onClick={() => setMode(mode?.phase === 'recording' ? { ...mode, minimized: true } : null)}
          style={{
            background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '6px 14px', fontSize: 13, fontWeight: 700, color: 'var(--text)',
            cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Volver a Nueva sesión
        </button>
      </div>
      <div style={{ background: 'var(--card-bg)', borderRadius: 20, padding: '24px 22px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', textAlign: 'center' }}>
        {recording ? (
          <>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: '#f0faf0', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 15, height: 15, borderRadius: '50%', background: '#34c759', animation: 'blink 1.4s ease-in-out infinite' }} />
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>El sensor está grabando solo</h2>
            {mode.startedAt
              ? <p style={{ fontSize: 30, fontWeight: 700, color: '#34c759', fontVariantNumeric: 'tabular-nums', margin: '4px 0' }}>{elapsed}</p>
              : <p style={{ fontSize: 13, fontWeight: 700, color: '#34c759', margin: '6px 0' }}>Grabación en curso (empezó antes de abrir esta pantalla)</p>}
            <p style={{ fontSize: 13, color: 'var(--text-faint)', marginBottom: 8, lineHeight: 1.6 }}>
              Podés dejar el celular y salir — el H10 guarda {mode.kind === 'hr' ? 'tu pulso' : 'pulso y HRV'} en su propia memoria.
              Cuando termines, volvé y tocá el botón verde.
            </p>
            <p style={{ fontSize: 11.5, color: 'var(--text-soft)', marginBottom: 18, lineHeight: 1.55 }}>
              Cerrar la app o volver a conectar el vivo <strong>no corta</strong> esta grabación — solo la corta el botón de detener.
              Eso sí: si medís en vivo mientras tanto, esos minutos van a aparecer dos veces al traer lo grabado.
            </p>
            <button disabled={rec.busy} onClick={stopAndImport} style={primaryBtn}>
              {rec.busy ? 'Hablando con el sensor…' : '■ Detener y traer la grabación'}
            </button>
            <button disabled={rec.busy} onClick={() => setMode({ ...mode, minimized: true })} style={secondaryBtn}>
              Volver (sigue grabando)
            </button>
            <button disabled={rec.busy} onClick={async () => { const ok = await offline.stopSensorRec(); if (ok) setMode(null) }} style={{ background: 'none', border: 'none', color: 'var(--text-fainter)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0' }}>
              Detener sin traer (queda en el sensor)
            </button>
          </>
        ) : (
          <>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: '#f0f4ff', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconRadio size={24} color="#007aff" />
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>Grabar en el sensor, sin celular</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
              El H10 graba solo, en su memoria interna. Empezá acá, dejá el teléfono
              donde quieras, y al volver traés todo a tu historial. Sin Polar Beat.
            </p>
            <p style={{ fontSize: 11.5, color: 'var(--text-soft)', marginBottom: 18, lineHeight: 1.55 }}>
              Si medís en vivo, esto ya pasa solo: al desconectar el sensor queda grabando,
              y al volver a conectar lo grabado se trae y se guarda en tu historial.
              Este panel es para manejarlo a mano.
            </p>
            {stoppedNote && (
              <p style={{ fontSize: 12, fontWeight: 600, color: '#b45309', background: '#fff8e6', border: '1px solid #ffe1a6', borderRadius: 10, padding: '8px 12px', margin: '0 0 12px', lineHeight: 1.5 }}>
                El sensor ya no está grabando. Lo que alcanzó a guardar lo traés con "⤓ Traer lo que ya está grabado".
              </p>
            )}
            {liveBusy && (
              <p style={{ fontSize: 12, fontWeight: 600, color: '#b45309', background: '#fff8e6', border: '1px solid #ffe1a6', borderRadius: 10, padding: '8px 12px', margin: '0 0 12px', lineHeight: 1.5 }}>
                Estás midiendo en vivo en esta pestaña y el H10 atiende una conexión a la vez.
                Cortá la sesión (✕ Desconectar) antes de usar la grabación sin celular.
              </p>
            )}
            <button disabled={rec.busy || liveBusy} onClick={() => start('rr')} style={{ ...primaryBtn, opacity: rec.busy || liveBusy ? 0.6 : 1 }}>
              {rec.busy ? 'Hablando con el sensor…' : '● Empezar a grabar (pulso + HRV)'}
            </button>
            <button disabled={rec.busy || liveBusy} onClick={() => start('hr')} style={{ ...secondaryBtn, opacity: rec.busy || liveBusy ? 0.6 : 1 }}>
              Solo pulso — memoria para hasta 30 h
            </button>
            <div style={{ borderTop: '1px solid var(--border-soft)', margin: '6px 0 12px' }} />
            <button disabled={rec.busy || liveBusy} onClick={doImport} style={{ ...secondaryBtn, opacity: rec.busy || liveBusy ? 0.6 : 1 }}>
              ⤓ Traer lo que ya está grabado
            </button>
            {!inShell && (
              <button disabled={rec.busy || liveBusy} onClick={() => offline.checkSensorRec()} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0 6px' }}>
                ¿El sensor ya estaba grabando? Consultar estado
              </button>
            )}
            <button onClick={() => setMode(null)} style={{ background: 'none', border: 'none', color: 'var(--text-fainter)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0' }}>
              ✕ Cerrar
            </button>
          </>
        )}
        {rec.error && <p style={{ fontSize: 12, color: '#ff3b30', margin: '10px 0 2px', lineHeight: 1.5 }}>{rec.error}</p>}
        {!recording && (
          <p style={{ fontSize: 10.5, color: 'var(--text-soft)', margin: '10px 0 0', lineHeight: 1.5 }}>
            Requiere firmware 3.x en el H10 (se actualiza gratis una vez desde Polar Beat).
            El sensor guarda una sesión a la vez · pulso+HRV varias horas · solo pulso hasta 30 h · sin ECG.
          </p>
        )}
      </div>
    </div>
  )
}

// ── Monitor tab ───────────────────────────────────────────────────────────────

function DisconnectedScreen({ onConnect, onDemo, onOffline, onImportFiles, status, error }) {
  const cancelled = error?.toLowerCase().includes('cancel')
  // Todo en UNA pantalla: conectar en vivo es la acción principal; la ayuda va
  // plegada y la importación del sensor queda al fondo como opción secundaria.
  const [showHelp, setShowHelp] = useState(false)
  return (
    <div style={{ maxWidth: 420, margin: '0 auto', width: '100%', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 22, padding: '24px 24px 16px', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        {/* Heartbeat icon */}
        <div style={{ margin: '0 auto 14px', width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #ff3b30, #ff6b6b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </div>
        <h2 style={{ fontSize: 17, fontWeight: 800, marginBottom: 4, color: 'var(--text)', letterSpacing: -0.3 }}>Nueva sesión</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
          Pulso, HRV y ECG en vivo desde tu Polar H10
        </p>

        <button onClick={onConnect} disabled={status === 'connecting'} style={{
          background: status === 'connecting' ? 'var(--track)' : '#ff3b30', color: status === 'connecting' ? 'var(--text-faint)' : '#fff',
          border: 'none', borderRadius: 14, padding: '14px 0', fontSize: 15, fontWeight: 700,
          width: '100%', cursor: status === 'connecting' ? 'default' : 'pointer', fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}>
          {status === 'connecting'
            ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ width: 14, height: 14, border: '2px solid #c7c7cc', borderTopColor: 'var(--text-faint)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Conectando con Polar…
              </span>
            : '⊕ Conectar Polar H10'}
        </button>

        {cancelled && <p style={{ fontSize: 12, color: 'var(--text-fainter)', margin: '10px 0 0' }}>Cerraste el diálogo — podés intentar de nuevo</p>}
        {error && !cancelled && <p style={{ fontSize: 12, color: '#ff3b30', margin: '10px 0 0' }}>{error}</p>}

        {/* Ayuda plegada: no roba pantalla si ya sabés conectar */}
        <button onClick={() => setShowHelp(h => !h)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '12px 0 0' }}>
          {showHelp ? '▴ Ocultar ayuda' : '¿Cómo conectar?'}
        </button>
        {showHelp && (
          <div style={{ textAlign: 'left', marginTop: 10 }}>
            {[
              ['1', 'Encendé tu Polar H10 y ponételo en el pecho'],
              ['2', 'Tocá "Conectar Polar H10" y esperá el diálogo Bluetooth'],
              ['3', 'Seleccioná tu sensor de la lista'],
            ].map(([n, text]) => (
              <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: n < '3' ? 8 : 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'var(--text-faint)' }}>{n}</div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45, paddingTop: 2, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        )}

        {/* Opciones secundarias, al fondo */}
        <div style={{ borderTop: '1px solid var(--border-soft)', marginTop: 14, paddingTop: 10 }}>
          {onImportFiles && (
            <button onClick={onImportFiles} style={{
              background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '10px 0', fontSize: 13, fontWeight: 600,
              width: '100%', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              ↑ Importar archivos
            </button>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <button onClick={onDemo} style={{ background: 'none', border: 'none', color: 'var(--text-soft)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0' }}>
              Ver demo
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Bridge recording panel ────────────────────────────────────────────────────

function BridgeRecordingPanel({ bridge, onSaveToHistory }) {
  const [showLog, setShowLog] = useState(false)

  if (!bridge.bridgeAvailable) return null

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14, border: '1.5px solid #34c759' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#34c759' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Bridge nativo activo</span>
          {bridge.connected && <span style={{ fontSize: 11, color: '#34c759', background: '#f0faf0', borderRadius: 6, padding: '2px 8px' }}>{bridge.deviceName}</span>}
        </div>
        <button onClick={() => setShowLog(l => !l)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-fainter)', fontFamily: 'inherit' }}>
          {showLog ? 'Ocultar log' : 'Ver log'}
        </button>
      </div>

      {!bridge.connected ? (
        <div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Escaneá y conectá tu H10 via bridge para grabar sesiones completas con intervalos RR.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <Btn variant="sync" onClick={bridge.scan}>Escanear H10</Btn>
          </div>
          {bridge.devices.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {bridge.devices.map(d => (
                <button key={d.id} onClick={() => bridge.connectDevice(d.id)} style={{
                  background: 'var(--chip-bg)', border: 'none', borderRadius: 8, padding: '10px 14px',
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{d.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{d.rssi} dBm</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : !bridge.recording ? (
        <div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            H10 conectado vía bridge. Iniciá una grabación — se guarda localmente con intervalos RR completos.
          </p>
          <Btn variant="primary" onClick={bridge.startRecording}>Iniciar grabación</Btn>
          <span style={{ marginLeft: 8 }}><Btn onClick={bridge.disconnect}>Desconectar</Btn></span>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff3b30', animation: 'blink 1s ease-in-out infinite' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#ff3b30' }}>Grabando…</span>
            <span style={{ fontSize: 12, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>{bridge.sampleCount} lecturas</span>
          </div>
          <Btn variant="primary" onClick={bridge.stopRecording}>Detener grabación</Btn>
        </div>
      )}

      {/* Session result */}
      {bridge.lastSamples?.samples?.length > 0 && (
        <div style={{ marginTop: 12, background: '#f0faf0', borderRadius: 10, padding: '12px 14px', border: '1px solid #c3f0ca' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            Sesión capturada · {bridge.lastSamples.samples.length} lecturas
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="sync" onClick={() => {
              onSaveToHistory(bridge.lastSamples.samples, bridge.deviceName ?? 'Polar H10 Bridge', 'bridge')
              bridge.clearLastSamples()
            }}>Guardar en historial</Btn>
            <Btn variant="muted" onClick={bridge.clearLastSamples}>Descartar</Btn>
          </div>
        </div>
      )}

      {/* Log */}
      {showLog && bridge.logs.length > 0 && (
        <div style={{ marginTop: 10, background: 'var(--track)', borderRadius: 8, padding: '10px 12px', maxHeight: 150, overflow: 'auto' }}>
          {bridge.logs.map((l, i) => (
            <p key={i} style={{ fontSize: 10, color: '#a0a0a8', fontFamily: 'monospace', marginBottom: 2 }}>{l}</p>
          ))}
        </div>
      )}

      {bridge.error && (
        <p style={{ fontSize: 11, color: '#ff3b30', marginTop: 8 }}>{bridge.error}</p>
      )}
    </div>
  )
}

// ── Data source metadata ──────────────────────────────────────────────────────

const SOURCE_META = {
  polar:   { label: 'Bluetooth',   icon: '', color: '#34c759', desc: 'Grabado desde sensor por Bluetooth' },
  bridge:  { label: 'Celular',     icon: '', color: '#007aff', desc: 'Grabado desde el celular vía bridge' },
  ios:     { label: 'iOS',         icon: '', color: '#5856d6', desc: 'Importado desde ECG & HRV Insights' },
  offline: { label: 'H10 Memoria', icon: '', color: '#ff9f0a', desc: 'Grabado en la memoria del Polar H10' },
  demo:    { label: 'Demo',        icon: '', color: 'var(--text-faint)', desc: 'Datos de demostración' },
}
function getSourceMeta(source) {
  return SOURCE_META[source] ?? { label: source ?? 'Polar', icon: '', color: '#34c759', desc: '' }
}

// ── HR Zones ──────────────────────────────────────────────────────────────────

const HR_ZONES = [
  { label: 'Reposo',   max: 60,       range: '< 60 bpm',    color: '#007aff', glow: 'rgba(0,122,255,0.35)',   bg: '#001f3f' },
  { label: 'Normal',   max: 90,       range: '60–90 bpm',   color: '#34c759', glow: 'rgba(52,199,89,0.35)',    bg: '#001a0d' },
  { label: 'Activo',   max: 120,      range: '90–120 bpm',  color: '#ff9f0a', glow: 'rgba(255,159,10,0.35)',   bg: '#1a0e00' },
  { label: 'Cardio',   max: 150,      range: '120–150 bpm', color: '#ff6b00', glow: 'rgba(255,107,0,0.35)',    bg: '#1a0500' },
  { label: 'Máxima',   max: Infinity, range: '> 150 bpm',   color: '#ff3b30', glow: 'rgba(255,59,48,0.35)',    bg: '#1a0000' },
]
function getZone(hr) { return HR_ZONES.find(z => hr < z.max) ?? HR_ZONES[HR_ZONES.length - 1] }

// ── Sparkline (SVG, no deps) ──────────────────────────────────────────────────

function Sparkline({ data, color, width = 300, height = 56 }) {
  if (data.length < 2) return null
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height * 0.8 - height * 0.1
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const last = pts[pts.length - 1].split(',')
  return (
    // viewBox + width 100%: se adapta al ancho disponible (teléfonos angostos)
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} style={{ overflow: 'visible', display: 'block' }}>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.9" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r="4.5" fill={color} />
    </svg>
  )
}

// ── Zone Distribution bar ─────────────────────────────────────────────────────

function ZoneDistribution({ readings, compact = false }) {
  if (readings.length < 5) return null
  const counts = HR_ZONES.map((z, i) => {
    const lo = HR_ZONES[i - 1]?.max ?? 0
    return { ...z, n: readings.filter(r => r.hr >= lo && r.hr < z.max).length }
  }).filter(z => z.n > 0)
  const total = counts.reduce((a, b) => a + b.n, 0)

  if (compact) {
    return (
      <div>
        <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', height: 7, marginBottom: 6 }}>
          {counts.map(z => (
            <div key={z.label} style={{ flex: z.n, background: z.color, transition: 'flex 0.6s ease' }} />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
          {counts.map(z => (
            <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: 2, background: z.color, flexShrink: 0 }} />
              <span style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>
                {z.label} <strong style={{ color: 'var(--text)' }}>{Math.round(z.n / total * 100)}%</strong>
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Zonas de frecuencia cardíaca</p>
      <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 10, marginBottom: 10 }}>
        {counts.map(z => (
          <div key={z.label} style={{ flex: z.n, background: z.color, transition: 'flex 0.6s ease' }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
        {counts.map(z => (
          <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: z.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {z.label} <strong style={{ color: 'var(--text)' }}>{Math.round(z.n / total * 100)}%</strong>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Live API base (used to stop retransmission to the live page when ending a session) ──
const API_LIVE = apiBase

const API_PROFILE_MONITOR = apiBase

// ── Floating "always-on-top" window (like Google Meet) using Document Picture-in-Picture.
// Chromium-only (Chrome/Edge) — the same browsers that support Web Bluetooth, so anyone
// running a live session already has it. Falls back silently elsewhere.
function useDocumentPip(theme = 'dark') {
  const supported = typeof window !== 'undefined' && 'documentPictureInPicture' in window
  const [pipWin, setPipWin] = useState(null)
  const ref = useRef(null)
  const close = useCallback(() => {
    const w = ref.current
    ref.current = null
    setPipWin(null)
    if (w) { try { w.close() } catch (_) {} }
  }, [])
  const open = useCallback(async (width, height) => {
    if (!supported || ref.current) return
    try {
      const win = await window.documentPictureInPicture.requestWindow({ width, height })
      const isLight = theme === 'light'
      const bg = isLight ? '#ffffff' : '#0b0b0c'
      const style = win.document.createElement('style')
      style.textContent = `html,body{margin:0;padding:0;height:100%;background:${bg};overflow:hidden}*{box-sizing:border-box}@keyframes pip-pulse{0%,100%{transform:scale(1)}28%{transform:scale(1.18)}45%{transform:scale(1)}}@keyframes pip-blink{0%,100%{opacity:1}50%{opacity:.35}}`
      win.document.head.appendChild(style)
      win.addEventListener('pagehide', () => { ref.current = null; setPipWin(null) })
      ref.current = win
      setPipWin(win)
    } catch (_) { /* user dismissed the prompt */ }
  }, [supported, theme])

  useEffect(() => {
    if (pipWin) {
      const isLight = theme === 'light'
      pipWin.document.body.style.background = isLight ? '#ffffff' : '#0b0b0c'
      pipWin.document.documentElement.setAttribute('data-theme', theme)
    }
  }, [theme, pipWin])

  useEffect(() => () => { if (ref.current) { try { ref.current.close() } catch (_) {} ref.current = null } }, [])
  return { supported, pipWin, open, close }
}

// Compact live readout rendered inside the floating window: big BPM + zone + mini sparkline.
import { MiniLive } from './components/ui/MiniLive'

function MonitorTab({ polar, demo, alerts, onSync, syncing, offline, onSaveOfflineToHistory, bridge, token, liveViewers = 0, onImportFiles, pip, sessionTime, sessionSec = 0, onNavigate, theme }) {
  const isWin = typeof navigator !== 'undefined' && /win/i.test(navigator.platform)
  const finalErr = isWin && (polar.error?.includes('GATT') || polar.error?.includes('timeout_connect') || polar.error?.includes('NetworkError'))
    ? 'Error de conexión en Windows. Si tu Polar H10 figura como "Vinculado" en la configuración de Bluetooth, quitalo de la lista de dispositivos de Windows y volvé a intentar.'
    : polar.error

  const isDemo          = demo.active
  const isCalibrating   = !isDemo && polar.status === 'calibrating' && !polar.hr
  const isReconnecting  = !isDemo && polar.status === 'reconnecting' && !polar.hr
  const isConnected     = polar.status === 'connected' || polar.status === 'calibrating' || polar.status === 'reconnecting' || Boolean(polar.hr) || isDemo
  const showOffline     = offline.status !== 'idle'
  const readings      = isDemo ? demo.history : polar.hrHistory
  const recentReadings = readings.slice(-6).filter(b => b.hr !== null)
  const instantHr   = recentReadings.length
    ? Math.round(recentReadings.reduce((a, b) => a + b.hr, 0) / recentReadings.length)
    : null
  const isBleConnected = isDemo || (polar.status === 'connected' && Boolean(polar.hr))
  const displayHr      = isDemo ? instantHr : (isBleConnected ? polar.hr : null)
  const sensorMoisture = computeMoistureIndex(polar?.ecgSamples, polar?.skinContact, polar?.hr)
  const displaySteps   = isDemo ? (demo.steps ?? 0) : (polar.steps ?? 0)
  const displayMoisture = isDemo ? (demo.moisture || { pct: 92, label: 'Excelente', color: '#30d158' }) : sensorMoisture

  const [autoPip, setAutoPip] = useState(() => {
    try { const v = localStorage.getItem('vc_auto_pip'); return v !== 'false' } catch { return true }
  })
  const toggleAutoPip = () => {
    const next = !autoPip
    setAutoPip(next)
    try { localStorage.setItem('vc_auto_pip', String(next)) } catch {}
  }

  // Vista compacta sin scroll (dashboard cockpit) vs vista detallada
  const [liveViewMode, setLiveViewMode] = useState('dashboard')

  // Minutos acumulados hoy antes de la sesión actual (corte estricto a las 00:00 hora local)
  const [todayRecordedMin, setTodayRecordedMin] = useState(0)
  useEffect(() => {
    if (!token) return

    const loadTodayRecorded = () => {
      const now = new Date()
      const todayStartMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime()
      const todayEndMs = todayStartMs + 24 * 3600 * 1000

      fetch(`${API_LIVE}/sync?since=${todayStartMs - 24 * 3600 * 1000}&sessionsOnly=1`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => {
          const sessions = d.sessions || []
          // Calcular solapamiento estricto de cada sesión con el día de HOY (00:00 a 24:00 hora local)
          let todayMs = 0
          sessions.forEach(s => {
            const sStart = new Date(s.start_time || s.startTime).getTime()
            const sEnd = new Date(s.end_time || s.endTime).getTime()
            const a = Math.max(sStart, todayStartMs)
            const b = Math.min(sEnd, todayEndMs)
            if (b > a) {
              todayMs += (b - a)
            }
          })
          setTodayRecordedMin(Math.round(todayMs / 60000))
        })
        .catch(() => {})
    }

    loadTodayRecorded()
    // Re-evaluar periódicamente para actualizar si cruza medianoche
    const id = setInterval(loadTodayRecorded, 60000)
    return () => clearInterval(id)
  }, [token])

  const [liveMsgInput, setLiveMsgInput] = useState('')
  const [pinNext, setPinNext] = useState(false)
  const [pinnedMsg, setPinnedMsg] = useState(null)
  const [liveChatHistory, setLiveChatHistory] = useState([])
  const [ephemeralMsg, setEphemeralMsg] = useState(null)
  const ephemeralTimer = useRef(null)

  // Sincronizar mensajes en vivo entre dispositivos (al cargar y cada 2.5s)
  useEffect(() => {
    if (!token) return
    const syncLiveMsgs = () => {
      fetch(`${API_LIVE}/live/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => {
          if (d.ok) {
            if (Array.isArray(d.messages)) setLiveChatHistory(d.messages)
            if (d.pinnedMessage !== undefined) setPinnedMsg(d.pinnedMessage)
          }
        })
        .catch(() => {})
    }
    syncLiveMsgs()
    const id = setInterval(syncLiveMsgs, 2500)
    return () => clearInterval(id)
  }, [token])

  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showAllScenarios, setShowAllScenarios] = useState(false)
  const [showLiveHrvAudit, setShowLiveHrvAudit] = useState(false)
  const [isLiveFullscreen, setIsLiveFullscreen] = useState(false)
  const [fsTheme, setFsTheme] = useState(theme || 'dark')
  useEffect(() => { if (theme) setFsTheme(theme) }, [theme])
  const toggleLiveFullscreen = () => {
    if (!isLiveFullscreen) {
      setIsLiveFullscreen(true)
      try {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(() => {})
        }
      } catch (_) {}
    } else {
      setIsLiveFullscreen(false)
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {})
        }
      } catch (_) {}
    }
  }

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        setIsLiveFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  useEffect(() => {
    if (!liveChatHistory.length) return
    const lastMsg = liveChatHistory[liveChatHistory.length - 1]
    // No mostrar como efímeros los mensajes propios
    if (lastMsg.sender === 'self') return

    setEphemeralMsg(lastMsg)
    clearTimeout(ephemeralTimer.current)
    ephemeralTimer.current = setTimeout(() => {
      setEphemeralMsg(null)
    }, 5000)
  }, [liveChatHistory])

  // Seguimiento de tiempo por postura en tiempo real (Sentado, Acostado, Caminando, Parado/Sentado)
  const [livePostureSecs, setLivePostureSecs] = useState({
    sentado: 0,
    acostado: 0,
    caminando: 0,
    parado_sentado: 0,
  })
  const prevStepsRef = useRef(0)
  const postureTimelineRef = useRef([])
  const [currentPosture, setCurrentPosture] = useState('sentado')
  const [manualPosture, setManualPosture] = useState(null)

  useEffect(() => {
    if (!isConnected) return

    const evaluatePosture = () => {
      const isStepping = isDemo ? (demo.posture === 'en_movimiento') : ((polar?.steps ?? 0) > prevStepsRef.current)
      prevStepsRef.current = isDemo ? (demo.steps ?? 0) : (polar?.steps ?? 0)

      const accX = polar?.accSamples?.current?.x || []
      const accY = polar?.accSamples?.current?.y || []
      const accZ = polar?.accSamples?.current?.z || []

      let meanX = 0, meanY = -950, meanZ = 0, motionRms = 0, sitImpact = false
      if (accX.length >= 10) {
        const win = Math.min(accX.length, 75)
        const sliceX = accX.slice(-win)
        const sliceY = accY.slice(-win)
        const sliceZ = accZ.slice(-win)
        const N = sliceX.length

        let sx = 0, sy = 0, sz = 0, sumM = 0
        const mags = new Float32Array(N)
        for (let i = 0; i < N; i++) {
          const xi = sliceX[i]
          const yi = sliceY[i]
          const zi = sliceZ[i]
          sx += xi
          sy += yi
          sz += zi
          const m = Math.sqrt(xi * xi + yi * yi + zi * zi)
          mags[i] = m
          sumM += m
        }
        meanX = sx / N
        meanY = sy / N
        meanZ = sz / N
        const meanM = sumM / N

        let sumSq = 0
        let maxM = 0
        for (let i = 0; i < N; i++) {
          const diff = mags[i] - meanM
          sumSq += diff * diff
          if (mags[i] > maxM) maxM = mags[i]
        }
        motionRms = Math.sqrt(sumSq / N)

        // Detección de impacto de sentarse ("cuando me siento hay un golpe")
        const impactThresh = meanM > 500 ? 1350 : 330
        const calmThresh = meanM > 500 ? 18 : 4.5
        if (maxM > impactThresh && motionRms < calmThresh) {
          sitImpact = true
        }
      }

      const state = isDemo && demo.posture
        ? demo.posture
        : classifyInstantPosture({
            x: meanX,
            y: meanY,
            z: meanZ,
            motionRms,
            isStepping,
            sitImpact,
            hr: displayHr,
          })

      setCurrentPosture(state)
      const effectiveState = manualPosture || state
      postureTimelineRef.current.push({ time: Date.now(), posture: effectiveState })
      if (postureTimelineRef.current.length > 3600) {
        postureTimelineRef.current.shift()
      }
      if (sessionSec > 0) {
        setLivePostureSecs(prev => ({
          ...prev,
          [effectiveState]: (prev[effectiveState] || 0) + 1,
        }))
      }
    }

    evaluatePosture()
    const timer = setInterval(evaluatePosture, 1000)
    return () => clearInterval(timer)
  }, [isConnected, sessionSec, manualPosture, displayHr])

  const effectivePosture = manualPosture || currentPosture

  const livePostureBreakdown = useMemo(() => computePostureBreakdown(livePostureSecs), [livePostureSecs])
  const currentPostureMeta = useMemo(() => {
    const key = (effectivePosture === 'caminando' || effectivePosture === 'en_movimiento')
      ? 'en_movimiento'
      : (effectivePosture === 'parado' || effectivePosture === 'parado_sentado')
        ? 'parado'
        : (effectivePosture === 'acostado' || effectivePosture === 'otro')
          ? 'acostado'
          : 'sentado'
    return POSTURE_TYPES.find(p => p.key === key) || POSTURE_TYPES[0]
  }, [effectivePosture])

  const [showMsgBox, setShowMsgBox] = useState(false)

  const handleSendLiveMessage = async (text, shouldPin = false) => {
    try {
      const res = await fetch(`${API_LIVE}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, isPinned: shouldPin })
      })
      const d = await res.json()
      if (d.ok) {
        setPinnedMsg(d.pinnedMessage || null)
        setLiveChatHistory(d.messages || [])
        setLiveMsgInput('')
      }
    } catch (_) {}
  }

  const handleUnpin = async () => {
    try {
      const res = await fetch(`${API_LIVE}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'unpin' })
      })
      const d = await res.json()
      if (d.ok) setPinnedMsg(null)
    } catch (_) {}
  }

  const handleDeleteMsg = async (id) => {
    try {
      const res = await fetch(`${API_LIVE}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'delete', messageId: id })
      })
      const d = await res.json()
      if (d.ok) {
        setPinnedMsg(d.pinnedMessage || null)
        setLiveChatHistory(d.messages || [])
      }
    } catch (_) {}
  }

  const handleClearAllMsgs = async () => {
    try {
      const res = await fetch(`${API_LIVE}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'clear_all' })
      })
      const d = await res.json()
      if (d.ok) {
        setPinnedMsg(null)
        setLiveChatHistory([])
      }
    } catch (_) {}
  }

  const demoEcgRef = useRef([])
  const [demoEcgTick, setDemoEcgTick] = useState(0)
  const demoEcgPhaseRef = useRef(0)
  const demoHrRef = useRef(demo?.hr || 70)

  useEffect(() => {
    if (demo?.hr) demoHrRef.current = demo.hr
  }, [demo?.hr])

  useEffect(() => {
    if (!isDemo) return
    
    // Generar muestras a 130 Hz de forma fluida y continua sin reinicios
    const intervalMs = 100
    const samplesPerTick = Math.round(ECG_FS * (intervalMs / 1000))
    
    const timer = setInterval(() => {
      const hr = demoHrRef.current || 70
      const samplesPerBeat = (ECG_FS * 60) / hr
      const arr = demoEcgRef.current
      
      for (let i = 0; i < samplesPerTick; i++) {
        const phase = demoEcgPhaseRef.current
        let val = 0
        
        const pStart = Math.round(samplesPerBeat * 0.2)
        const qStart = Math.round(samplesPerBeat * 0.35)
        const rStart = Math.round(samplesPerBeat * 0.4)
        const sStart = Math.round(samplesPerBeat * 0.45)
        const tStart = Math.round(samplesPerBeat * 0.52)
        const tEnd   = Math.round(samplesPerBeat * 0.72)
        
        if (phase >= pStart && phase < qStart) {
          const angle = ((phase - pStart) / (qStart - pStart)) * Math.PI
          val = Math.sin(angle) * 60
        } else if (phase >= qStart && phase < rStart) {
          const angle = ((phase - qStart) / (rStart - qStart)) * Math.PI
          val = -Math.sin(angle) * 50
        } else if (phase >= rStart && phase < sStart) {
          const mid = (rStart + sStart) / 2
          if (phase < mid) {
            val = ((phase - rStart) / (mid - rStart)) * 950
          } else {
            val = (1 - (phase - mid) / (sStart - mid)) * 950
          }
        } else if (phase >= sStart && phase < tStart) {
          const angle = ((phase - sStart) / (tStart - sStart)) * Math.PI
          val = -Math.sin(angle) * 120
        } else if (phase >= tStart && phase < tEnd) {
          const angle = ((phase - tStart) / (tEnd - tStart)) * Math.PI
          val = Math.sin(angle) * 160
        }
        
        const noise = (Math.random() - 0.5) * 10
        arr.push(Math.round(val + noise))
        demoEcgPhaseRef.current = (phase + 1) % Math.round(samplesPerBeat)
      }
      
      const maxSamples = 20 * ECG_FS
      if (arr.length > maxSamples) {
        arr.splice(0, arr.length - maxSamples)
      }
      
      setDemoEcgTick(t => t + 1)
    }, intervalMs)
    
    return () => {
      clearInterval(timer)
      demoEcgRef.current = []
    }
  }, [isDemo])

  // Compartición del live (independiente del sensor): cortar el live NO desconecta el Polar
  const [sharing, setSharing] = useState(true)
  const toggleSharing = async () => {
    const next = !sharing
    setSharing(next)
    try {
      await fetch(`${API_LIVE}/live/${next ? 'resume' : 'pause'}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
    } catch { setSharing(!next) }   // revertir si falló
  }

  // Fetch user profile for training zones
  const [profileMaxHr, setProfileMaxHr] = useState(null)
  useEffect(() => {
    if (!token) return
    fetch(`${API_PROFILE_MONITOR}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(p => {
        if (p?.age) setProfileMaxHr(Math.round(211 - 0.64 * p.age))
      })
      .catch(() => {})
  }, [token])

  const trainingZones   = useMemo(() => buildTrainingZones(profileMaxHr), [profileMaxHr])
  const beats       = expandToBeats(readings)
  const liveZoneData = useMemo(() => {
    if (!trainingZones || !beats.length) return null
    const validBeats = beats.filter(b => b.hr)
    if (!validBeats.length) return null
    const totalCount = validBeats.length

    return trainingZones.map(z => {
      const inZoneCount = validBeats.filter(b => b.hr >= z.lo && b.hr < z.hi).length
      return {
        ...z,
        pct: Math.round((inZoneCount / totalCount) * 100),
        min: Math.round(inZoneCount / 60),
      }
    })
  }, [trainingZones, beats])
  const beatsWithPosture = useMemo(() => {
    const timeline = postureTimelineRef.current
    return beats.map(b => {
      let p = effectivePosture
      if (timeline && timeline.length) {
        for (let i = timeline.length - 1; i >= 0; i--) {
          if (timeline[i].time <= b.time + 1500) {
            p = timeline[i].posture
            break
          }
        }
      }
      return { ...b, posture: p }
    })
  }, [beats, effectivePosture, readings.length])
  const beatsWithGaps = injectLiveGaps(beatsWithPosture, 4000)     // hueco visible en corte (>4s sin latidos)
  const stats          = computeStats(beats)
  const hrv            = computeHRV(readings.slice(-180))
  const zone           = displayHr ? getZone(displayHr) : null
  const trainingZone   = useMemo(() => getTrainingZone(displayHr, trainingZones), [displayHr, trainingZones])
  const readiness      = useMemo(() => {
    return computeReadinessAndWorkoutAdvice(hrv?.rmssd, displayHr, stats?.min, stats?.avg)
  }, [hrv?.rmssd, displayHr, stats?.min, stats?.avg])
  const beatDur     = instantHr ? `${(60 / instantHr).toFixed(2)}s` : '1s'

  // Estado del modo "grabar en el sensor": persiste (si la grabación sigue,
  // al reabrir la app se vuelve a mostrar). phase 'menu' no se persiste.
  const [recordMode, setRecordModeRaw] = useState(() => {
    try { const s = localStorage.getItem('h10_record_mode'); return s ? JSON.parse(s) : null } catch { return null }
  })
  const setRecordMode = (m) => {
    setRecordModeRaw(m)
    try {
      if (m && m.phase === 'recording') localStorage.setItem('h10_record_mode', JSON.stringify(m))
      else localStorage.removeItem('h10_record_mode')
    } catch { /* sin storage */ }
  }

  useEffect(() => {
    const handleReset = () => {
      setRecordMode(prev => prev ? (prev.phase === 'recording' ? { ...prev, minimized: true } : null) : null)
    }
    window.addEventListener('vc-reset-session-view', handleReset)
    return () => window.removeEventListener('vc-reset-session-view', handleReset)
  }, [])

  if (!isConnected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 170px)', width: '100%', boxSizing: 'border-box' }}>
        {recordMode && !recordMode.minimized && (
          <RecordModeScreen mode={recordMode} setMode={setRecordMode} offline={offline} liveBusy={!isDemo && ['connected', 'calibrating', 'reconnecting'].includes(polar.status)} />
        )}
        {(!recordMode || recordMode.minimized) && (
          <div className="vc-center-desk" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {recordMode?.minimized && (
              <button onClick={() => setRecordMode({ ...recordMode, minimized: false })} style={{
                display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420, width: '100%',
                background: 'var(--chip-bg)', border: '1.5px solid var(--border)', borderRadius: 12,
                padding: '10px 14px', marginBottom: 10, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#34c759', animation: 'blink 1.4s ease-in-out infinite', flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>El sensor está grabando solo</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#34c759' }}>Abrir ›</span>
              </button>
            )}
            <DisconnectedScreen
              onConnect={polar.connect}
              onDemo={demo.start}
              onOffline={() => setRecordMode(recordMode ? { ...recordMode, minimized: false } : { phase: 'menu' })}
              onImportFiles={onImportFiles}
              status={polar.status}
              error={finalErr}
            />
          </div>
        )}
        {showOffline && (
          <OfflineSessionPanel offline={offline} onSaveToHistory={onSaveOfflineToHistory} />
        )}
        <BridgeRecordingPanel bridge={bridge} onSaveToHistory={onSaveOfflineToHistory} />
      </div>
    )
  }

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>

      {/* ── Barra de Control Superior Ejecutiva y Minimalista ── */}
      <div style={{
        position: 'relative',
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14,
        padding: '7px 12px', marginBottom: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        {/* Identidad del dispositivo e información de sesión (Izquierda) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: isCalibrating || isReconnecting ? '#ff9f0a' : isDemo ? '#ff9f0a' : '#34c759',
            boxShadow: isCalibrating || isReconnecting ? '0 0 6px #ff9f0a' : isDemo ? '0 0 6px #ff9f0a' : '0 0 6px #34c759',
            animation: isCalibrating || isReconnecting ? 'blink 0.8s ease-in-out infinite' : 'none',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flexWrap: 'nowrap' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', whiteSpace: 'nowrap' }} title={polar.deviceId ? `ID: ${polar.deviceId}` : undefined}>
              {isDemo ? 'Modo demo' : 'Polar H10'}
            </span>
            {!isDemo && polar.battery !== null && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 10.5, fontWeight: 700,
                color: polar.battery <= 20 ? '#ff453a' : 'var(--text-faint)',
                fontVariantNumeric: 'tabular-nums'
              }} title={`Batería: ${polar.battery}%`}>
                {polar.battery <= 20 ? <IconBatteryLow size={12} color="#ff453a" /> : <IconBattery size={12} color="var(--text-faint)" />}
                <span>{polar.battery}%</span>
              </span>
            )}
            <span style={{ fontSize: 11, color: 'var(--border)' }}>·</span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {isCalibrating ? 'Calibrando…' : isReconnecting ? 'Reconectando…' : sessionTime}
            </span>
          </div>
        </div>

        {/* Agrupación limpia de controles a la derecha */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {liveViewers > 0 && (
            <span title={`${liveViewers} personas viendo tu transmisión`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(52,199,89,0.12)', color: '#34c759', borderRadius: 8, padding: '4px 7px', fontSize: 11, fontWeight: 700, height: 28, boxSizing: 'border-box' }}>
              <IconEye size={12} color="#34c759" />
              <span>{liveViewers}</span>
            </span>
          )}

          {/* 1. Selector de vista compacto */}
          <div style={{ display: 'inline-flex', background: 'var(--field-bg)', borderRadius: 8, padding: 2, border: '1px solid var(--border-soft)', height: 28, boxSizing: 'border-box' }}>
            <button
              onClick={() => setLiveViewMode('dashboard')}
              title="Dashboard compacto sin scroll"
              style={{
                background: liveViewMode === 'dashboard' ? 'var(--card-bg)' : 'transparent',
                color: liveViewMode === 'dashboard' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '0 8px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: liveViewMode === 'dashboard' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              Dashboard
            </button>
            <button
              onClick={() => setLiveViewMode('deep')}
              title="Vista extendida con análisis detallado"
              style={{
                background: liveViewMode === 'deep' ? 'var(--card-bg)' : 'transparent',
                color: liveViewMode === 'deep' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '0 8px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: liveViewMode === 'deep' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              Detalle
            </button>
          </div>

          {/* 2. Botón Pantalla Completa destacado */}
          <button
            onClick={toggleLiveFullscreen}
            title="Ver el monitor a pantalla completa"
            style={{
              background: 'var(--chip-bg)', color: 'var(--text)',
              border: '1px solid var(--border-soft)', borderRadius: 8, padding: '0 9px', fontSize: 11.5, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, height: 28, boxSizing: 'border-box',
              transition: 'background 0.15s ease'
            }}
          >
            <span style={{ fontSize: 12 }}>⛶</span>
            <span>Completa</span>
          </button>

          {/* Botón directo Ventana Flotante Always-on-Top */}
          {pip.supported && (
            <button
              onClick={() => pip.pipWin ? pip.close() : pip.open(240, 150)}
              title={pip.pipWin ? 'Cerrar ventana flotante' : 'Abrir ventana flotante fija (Always on Top)'}
              style={{
                background: pip.pipWin ? 'rgba(0,122,255,0.18)' : 'var(--chip-bg)',
                color: pip.pipWin ? '#007aff' : 'var(--text)',
                border: '1px solid ' + (pip.pipWin ? '#007aff' : 'var(--border-soft)'),
                borderRadius: 8, padding: '0 9px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, height: 28, boxSizing: 'border-box',
                transition: 'all 0.15s ease'
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <rect x="11" y="9" width="9" height="6" rx="1" ry="1" fill="currentColor" fillOpacity="0.3"/>
              </svg>
              <span>{pip.pipWin ? 'Cerrar flotante' : 'Ventana flotante'}</span>
            </button>
          )}

          {/* 3. Botón Chat */}
          {!isDemo && (
            <button
              onClick={() => setShowMsgBox(o => !o)}
              title="Mensajes y chat en vivo"
              style={{
                background: (pinnedMsg || liveChatHistory.length > 0) ? 'rgba(0,122,255,0.15)' : (showMsgBox ? 'var(--track)' : 'var(--chip-bg)'),
                color: (pinnedMsg || liveChatHistory.length > 0) ? '#007aff' : 'var(--text)',
                border: '1px solid ' + ((pinnedMsg || liveChatHistory.length > 0) ? '#007aff44' : 'var(--border-soft)'),
                borderRadius: 8, padding: '0 8px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, height: 28, boxSizing: 'border-box'
              }}
            >
              <IconChat size={12} color="currentColor" />
              <span>{pinnedMsg ? 'Aviso' : 'Chat'}</span>
            </button>
          )}

          {/* 4. Menú desplegable '··· Más opciones' (Flotante, Compartir, Pausar) */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMoreMenu(o => !o)}
              title="Más opciones de transmisión y ventana"
              style={{
                background: showMoreMenu ? 'var(--track)' : 'var(--chip-bg)',
                color: 'var(--text)', border: '1px solid var(--border-soft)',
                borderRadius: 8, width: 28, height: 28, fontSize: 13, fontWeight: 900,
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxSizing: 'border-box'
              }}
            >
              ···
            </button>

            {showMoreMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 100 }} onClick={() => setShowMoreMenu(false)} />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 101,
                  background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
                  padding: '5px', minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                  display: 'flex', flexDirection: 'column', gap: 3, animation: 'fadeIn 0.15s ease'
                }}>
                  {/* Ventana Flotante Externa */}
                  {pip.supported && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 9px', borderRadius: 8, background: 'var(--field-bg)',
                    }}>
                      <button
                        onClick={() => { pip.pipWin ? pip.close() : pip.open(220, 140); setShowMoreMenu(false); }}
                        style={{
                          background: 'none', border: 'none', color: 'var(--text)',
                          fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                          display: 'flex', alignItems: 'center', gap: 6, padding: 0
                        }}
                      >
                        <span style={{ fontSize: 12 }}>⧉</span>
                        <span>{pip.pipWin ? 'Cerrar flotante' : 'Ventana flotante'}</span>
                      </button>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={autoPip} onChange={toggleAutoPip} style={{ cursor: 'pointer', width: 11, height: 11 }} />
                        Auto
                      </label>
                    </div>
                  )}

                  {/* Compartir transmisión */}
                  {!isDemo && (
                    <button
                      onClick={() => { onNavigate('compartir'); setShowMoreMenu(false); }}
                      style={{
                        background: 'none', border: 'none', color: 'var(--text)',
                        fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 8,
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--chip-bg)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                    >
                      <SidebarIconShare size={13} />
                      <span>Compartir enlace</span>
                    </button>
                  )}

                  {/* Pausar o reanudar transmisión */}
                  {!isDemo && (
                    <button
                      onClick={() => { toggleSharing(); setShowMoreMenu(false); }}
                      style={{
                        background: 'none', border: 'none',
                        color: sharing ? '#ff3b30' : '#34c759',
                        fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 8,
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--chip-bg)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                    >
                      <span style={{ fontSize: 10 }}>{sharing ? '■' : '●'}</span>
                      <span>{sharing ? 'Pausar transmisión' : 'Reanudar transmisión'}</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* 5. Botón Desconectar / Salir */}
          {isDemo ? (
            <button
              onClick={demo.stop}
              style={{
                background: 'var(--field-bg)', color: 'var(--text-muted)', border: '1px solid var(--border-soft)',
                borderRadius: 8, padding: '0 9px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', height: 28, boxSizing: 'border-box'
              }}
            >
              ✕ Salir
            </button>
          ) : (
            <button
              onClick={polar.disconnect}
              title="Terminar la sesión (se guarda automáticamente)"
              style={{
                background: 'rgba(255,59,48,0.08)', color: '#ff3b30', border: '1px solid rgba(255,59,48,0.20)',
                borderRadius: 8, padding: '0 9px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', height: 28, boxSizing: 'border-box'
              }}
            >
              ✕ Desconectar
            </button>
          )}
        </div>
      </div>

      {/* Caja desplegable para redactar mensaje / chat a los espectadores */}
      {showMsgBox && !isDemo && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14,
          padding: '12px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10,
          animation: 'fadeIn 0.2s ease', boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Chat y avisos en vivo
            </span>
            {(pinnedMsg || liveChatHistory.length > 0) && (
              <button
                type="button"
                onClick={handleClearAllMsgs}
                style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}
              >
                ✕ Limpiar todo
              </button>
            )}
          </div>

          {/* Mensaje fijado activo si existe */}
          {pinnedMsg && (
            <div style={{
              background: 'rgba(255,159,10,0.12)', border: '1px solid rgba(255,159,10,0.35)',
              borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#ff9f0a', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                  Mensaje fijado en el visor
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>
                  “{pinnedMsg.text}”
                </span>
              </div>
              <button
                type="button"
                onClick={handleUnpin}
                style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
              >
                Desfijar
              </button>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (liveMsgInput.trim()) handleSendLiveMessage(liveMsgInput.trim(), pinNext)
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={liveMsgInput}
                onChange={e => setLiveMsgInput(e.target.value)}
                placeholder="Escribe un mensaje para el chat en vivo…"
                maxLength={200}
                style={{
                  flex: 1, minWidth: 0, background: 'var(--inset-bg)', border: '1px solid var(--border-soft)',
                  borderRadius: 10, padding: '8px 12px', fontSize: 13, color: 'var(--text)',
                  outline: 'none', fontFamily: 'inherit'
                }}
              />
              <button
                type="submit"
                disabled={!liveMsgInput.trim()}
                style={{
                  background: liveMsgInput.trim() ? 'var(--btn-strong)' : 'var(--chip-bg)',
                  color: liveMsgInput.trim() ? '#fff' : 'var(--text-faint)',
                  border: 'none', borderRadius: 10, padding: '0 16px', fontSize: 12.5, fontWeight: 700,
                  cursor: liveMsgInput.trim() ? 'pointer' : 'default', fontFamily: 'inherit', flexShrink: 0
                }}
              >
                Enviar
              </button>
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={pinNext}
                onChange={e => setPinNext(e.target.checked)}
                style={{ width: 13, height: 13, cursor: 'pointer' }}
              />
              Fijar este mensaje en la cabecera del visor
            </label>
          </form>

          {/* Opciones rápidas de un toque */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-faint)', fontWeight: 600 }}>Atajos:</span>
            {['Arrancando!', 'Descanso 2 min', 'Pasadas fuertes', 'Zona 2 regenerativo', 'Último esfuerzo!', 'Finalizando'].map(chip => (
              <button
                key={chip}
                type="button"
                onClick={() => {
                  setLiveMsgInput(chip)
                  handleSendLiveMessage(chip, pinNext)
                }}
                style={{
                  background: 'var(--chip-bg)', color: 'var(--text)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 14, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                {chip}
              </button>
            ))}
          </div>

          {/* Mensaje temporal en vivo: se desvanece solo sin dejar listado */}
          {ephemeralMsg && (
            <div style={{
              marginTop: 6, padding: '8px 12px', borderRadius: 8,
              background: 'var(--field-bg)', border: '1px solid var(--border-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              animation: 'fadeIn 0.2s ease'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <IconChat size={13} color="currentColor" />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ephemeralMsg.text}
                </span>
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-faint)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {fmtHMS(ephemeralMsg.ts)} (temporal)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Aviso de sensor desprendido / sin contacto */}
      {!polar.skinContact && !isDemo && isConnected && (
        <div style={{
          background: 'rgba(255, 159, 10, 0.12)', border: '1px solid rgba(255, 159, 10, 0.35)',
          borderRadius: 12, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
          animation: 'fadeIn 0.2s ease'
        }}>
          <IconAlert size={16} color="#ff9f0a" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12.5, fontWeight: 700, color: '#ff9f0a', margin: '0 0 2px' }}>
              Sensor despegado del pecho
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>
              El Polar H10 no detecta contacto con la piel. Se pausó el registro para no guardar lecturas erróneas.
            </p>
          </div>
        </div>
      )}

      {liveViewMode === 'dashboard' ? (
        <div className="vc-dashboard-cockpit" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 340px) 1fr',
          gap: 12,
          marginTop: 8,
          boxSizing: 'border-box',
        }}>
          {/* Columna Izquierda: Pulso gigante + Métricas 2x2 + Zonas */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Hero Pulse Card */}
            <div style={{
              background: zone ? `linear-gradient(145deg, var(--card-bg) 60%, ${zone.color}15)` : 'var(--card-bg)',
              borderRadius: 16, padding: '16px 20px',
              border: zone ? `1.5px solid ${zone.color}45` : '1.5px solid var(--border)',
              boxShadow: zone ? `0 6px 24px ${zone.glow}` : '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-around',
              transition: 'all 0.5s ease',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{
                    fontSize: 68, fontWeight: 900, lineHeight: 1, letterSpacing: -2,
                    color: zone?.color ?? '#ff3b30', fontVariantNumeric: 'tabular-nums',
                    animation: instantHr ? `pulse ${beatDur} ease-in-out infinite` : 'none',
                    display: 'inline-block'
                  }}>
                    {displayHr ?? '–'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>BPM</span>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '4px 0 0' }}>
                  {sessionTime ? `Sesión: ${sessionTime}` : 'En vivo'}
                </p>
              </div>

              <div style={{ textAlign: 'right' }}>
                <span style={{
                  fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 12,
                  background: zone ? `${zone.color}22` : 'var(--track)',
                  color: zone?.color ?? 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5,
                  display: 'inline-block'
                }}>
                  {trainingZone?.short || zone?.label || 'EN VIVO'}
                </span>
                {trainingZone?.lo != null && (
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, margin: '4px 0 0' }}>
                    {trainingZone.lo}–{trainingZone.hi} bpm
                  </p>
                )}
                {hrv?.rmssd && (
                  <button
                    type="button"
                    onClick={() => setShowLiveHrvAudit(s => !s)}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      fontSize: 11, color: '#64d2ff', fontWeight: 700, margin: '4px 0 0',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 3,
                      opacity: 0.9, transition: 'opacity 0.15s ease'
                    }}
                    title="Ver fórmula y validación matemática de HRV en vivo"
                  >
                    <span>HRV {hrv.rmssd} ms</span>
                    <span style={{ fontSize: 9, opacity: 0.6, fontWeight: 900 }}>ℹ</span>
                  </button>
                )}
              </div>
            </div>

            {/* Auditoría ultra minimalista del cálculo de pulso y HRV en vivo */}
            {showLiveHrvAudit && (
              <div style={{
                background: 'var(--field-bg)', border: '1px solid var(--border)',
                borderRadius: 14, padding: '12px 14px', marginTop: -4, animation: 'fadeIn 0.2s ease',
                display: 'flex', flexDirection: 'column', gap: 8, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Auditoría del Cálculo de HRV en Vivo
                  </span>
                  <button
                    onClick={() => setShowLiveHrvAudit(false)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1 }}
                    title="Cerrar validación"
                  >
                    ×
                  </button>
                </div>

                {/* Fórmula de RMSSD */}
                <div>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Fórmula de HRV (RMSSD Vagal):</span>
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 7, padding: '5px 8px', marginTop: 3, fontFamily: 'var(--mono, "JetBrains Mono", monospace)', fontSize: 11, color: '#007aff', fontWeight: 600 }}>
                    RMSSD = √ [ (1 / (N - 1)) * Σ (RR_{'{i+1}'} - RR_{'{i}'})² ]
                  </div>
                </div>

                {/* Variables en vivo */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 7, padding: '5px 8px' }}>
                    <div style={{ fontSize: 9.5, color: 'var(--text-faint)' }}>Latidos (N)</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)' }}>{beats.length} latidos</div>
                  </div>
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 7, padding: '5px 8px' }}>
                    <div style={{ fontSize: 9.5, color: 'var(--text-faint)' }}>RR Promedio</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {displayHr ? `${Math.round(60000 / displayHr)} ms` : '—'}
                    </div>
                  </div>
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 7, padding: '5px 8px' }}>
                    <div style={{ fontSize: 9.5, color: 'var(--text-faint)' }}>RMSSD Actual</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#64d2ff', fontVariantNumeric: 'tabular-nums' }}>{hrv?.rmssd} ms</div>
                  </div>
                </div>

                <p style={{ fontSize: 10.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
                  Cada intervalo R-R es medido al milisegundo por el sensor Polar H10. Se calcula la raíz cuadrática media de las diferencias entre latidos consecutivos en tiempo real.
                </p>
                <div style={{ fontSize: 9.5, color: 'var(--text-faint)', borderTop: '1px solid var(--border-soft)', paddingTop: 4 }}>
                  <strong>Norma Oficial:</strong> Task Force of the European Society of Cardiology & NASPE (1996)
                </div>
              </div>
            )}

            {/* ── Tarjeta Ejecutiva: Recomendación Personalizada de Entrenamiento ── */}
            <div style={{
              background: 'var(--card-bg)', border: `1.5px solid ${readiness.recoveryColor}40`,
              borderRadius: 14, padding: '12px 14px',
              display: 'flex', flexDirection: 'column', gap: 7,
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
            }}>
              {/* Fila superior: Chip de categoría + Botón de acción */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: readiness.recoveryColor, boxShadow: `0 0 8px ${readiness.recoveryColor}`, flexShrink: 0
                  }} />
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Recomendación de Entrenamiento de Hoy
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAllScenarios(true)}
                  style={{
                    background: 'none', border: 'none', color: '#007aff', fontSize: 10.5, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 2
                  }}
                >
                  Ver escenarios y guía →
                </button>
              </div>

              {/* Qué te recomendamos hoy */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--text)' }}>
                  Te recomendamos: <span style={{ color: readiness.recoveryColor }}>{readiness.modalityTitle}</span>
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
                  background: `${readiness.recoveryColor}18`, color: readiness.recoveryColor
                }}>
                  {readiness.scenario?.stateBadge}
                </span>
              </div>

              {/* Justificación fisiológica clara */}
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>
                {readiness.scenario?.why || 'En base a tu variabilidad cardíaca y nivel de recuperación actual.'}
              </p>
            </div>

            {/* Modal emergente limpio para la guía de entrenamiento (solo al hacer clic) */}
            {showAllScenarios && (
              <div
                onClick={() => setShowAllScenarios(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 99999,
                  background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                }}
              >
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: 18, maxWidth: 540, width: '100%', maxHeight: '85vh',
                    overflowY: 'auto', padding: '20px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    animation: 'fadeIn 0.2s ease', display: 'flex', flexDirection: 'column', gap: 12
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 900, color: 'var(--text)', margin: 0 }}>
                      Guía Médica: Escenarios y Prescripción
                    </h3>
                    <button
                      onClick={() => setShowAllScenarios(false)}
                      style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}
                    >
                      ✕ Cerrar
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {TRAINING_SCENARIOS.map(sc => {
                      const isCur = readiness.scenario?.id === sc.id
                      return (
                        <div
                          key={sc.id}
                          style={{
                            background: isCur ? `${sc.stateColor}10` : 'var(--field-bg)',
                            border: `1.5px solid ${isCur ? sc.stateColor : 'var(--border-soft)'}`,
                            borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11.5, fontWeight: 800, color: isCur ? sc.stateColor : 'var(--text)' }}>
                              {isCur ? `● [ACTUAL] ${sc.name}` : sc.name}
                            </span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: sc.stateColor }}>
                              {sc.stateBadge}
                            </span>
                          </div>
                          <p style={{ fontSize: 10.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
                            {sc.why}
                          </p>
                          <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--text-faint)', marginTop: 2 }}>
                            <span><strong>Cardio:</strong> {sc.cardio.summary}</span>
                            <span>·</span>
                            <span><strong>Fuerza:</strong> {sc.fuerza.summary}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Grid 2x2 de Métricas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <MetricCard defKey="avg" value={stats.avg} />
              <MetricCard defKey="max" value={stats.max} color="#ff3b30" />
              <MetricCard defKey="min" value={stats.min} color="#007aff" />
              <MetricCard defKey="moisture" value={displayMoisture.pct} color={displayMoisture.color} />
            </div>

            {/* ── Zonas de Entrenamiento · segundo a segundo (Unificado con 05, Adaptable) ── */}
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Zonas de Entrenamiento <span style={{ fontWeight: 400, color: 'var(--text-soft)', textTransform: 'none' }}>· segundo a segundo</span>
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: trainingZone?.color ?? '#ff3b30' }}>
                  {trainingZone?.name || 'Zonas 1-5'}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {liveZoneData && liveZoneData.length > 0 ? (
                  liveZoneData.map(z => (
                    <div key={z.key} style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                      {/* Fila 1: Badge + Nombre + BPM a la izquierda, Porcentaje y Minutos a la derecha */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0, gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                          {z.num ? (
                            <div style={{
                              width: 15, height: 15, borderRadius: '50%', background: z.color,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                              <span style={{ fontSize: 8.5, fontWeight: 900, color: '#fff' }}>{z.num}</span>
                            </div>
                          ) : (
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: z.color, display: 'inline-block', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {z.name}
                          </span>
                          <span style={{ fontSize: 9.5, color: 'var(--text-faint)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                            ({z.lo}–{z.hi} BPM)
                          </span>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', flexShrink: 0, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                          {z.pct}% <span style={{ fontSize: 9.5, fontWeight: 400, color: 'var(--text-faint)' }}>({z.min} min)</span>
                        </span>
                      </div>

                      {/* Fila 2: Barra horizontal de ancho completo */}
                      <div style={{ width: '100%', height: 5, background: 'var(--chip-bg)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${z.pct}%`, background: z.color, borderRadius: 3, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '4px 0' }}>Acumulando lecturas de la sesión…</p>
                )}
              </div>
            </div>

            {/* Cobertura Diaria del Objetivo */}
            {(() => {
              const now = Date.now()
              const d = new Date(now)
              const todayStartMs = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime()

              // Minutos de la sesión activa que ocurrieron estrictamente HOY (desde las 00:00 hora local)
              const sessionStartMs = now - (sessionSec || 0) * 1000
              const activeTodayStartMs = Math.max(sessionStartMs, todayStartMs)
              const activeTodaySec = Math.max(0, Math.floor((now - activeTodayStartMs) / 1000))
              const currentSessionMinToday = Math.floor(activeTodaySec / 60)

              const totalTodayMin = todayRecordedMin + currentSessionMinToday
              const targetMin = STREAK_MIN_MIN // 6 horas de objetivo diario para racha
              const pct = Math.min(100, Math.round((totalTodayMin / targetMin) * 100))
              const remaining = Math.max(0, targetMin - totalTodayMin)
              return (
                <div style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border)',
                  borderRadius: 14, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Cobertura Diaria (Objetivo 6 h)
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: pct >= 100 ? '#34c759' : '#007aff' }}>
                      {pct >= 100 ? '✓ Cumplido' : `${pct}%`}
                    </span>
                  </div>
                  <div style={{ height: 7, background: 'var(--track)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: pct >= 100 ? '#34c759' : 'linear-gradient(90deg, #007aff, #34c759)',
                      borderRadius: 4, transition: 'width 0.4s ease'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-muted)' }}>
                    <span>Llevás {fmtH(totalTodayMin)} hoy</span>
                    <span style={{ fontWeight: 600, color: pct >= 100 ? '#34c759' : 'var(--text)' }}>
                      {pct >= 100 ? '¡Racha asegurada!' : `Faltan ${fmtH(remaining)}`}
                    </span>
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Columna Derecha: Gráfico de Pulso en Vivo (arriba) + Postura + Pasos + ECG en Vivo (abajo) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {/* Gráfico de Pulso Continuo (Evolución del Pulso) */}
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '10px 12px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Evolución del Pulso
                </span>
                <span style={{ fontSize: 9.5, color: 'var(--text-faint)' }}>
                  Últimos {readings.length} latidos
                </span>
              </div>
              <LiveChart readings={beatsWithGaps} compact posture={effectivePosture} />
            </div>

            {/* Distribución de Postura y Actividad DIRECTAMENTE DEBAJO DEL GRÁFICO */}
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '10px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Distribución de Postura y Actividad
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {manualPosture && (
                    <button
                      onClick={() => setManualPosture(null)}
                      style={{ background: 'var(--chip-bg)', border: 'none', color: 'var(--text-faint)', fontSize: 9.5, fontWeight: 600, cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
                      title="Volver a detección automática del sensor"
                    >
                      Auto
                    </button>
                  )}
                  <span
                    onClick={() => {
                      const cycle = ['sentado', 'parado', 'en_movimiento', 'acostado']
                      const cur = effectivePosture.includes('parado') ? 'parado' : effectivePosture
                      const idx = cycle.indexOf(cur)
                      const next = cycle[(idx + 1) % cycle.length]
                      setManualPosture(next)
                    }}
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: currentPostureMeta?.color ?? 'var(--text)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                      background: `${currentPostureMeta?.color || '#5856d6'}18`,
                      padding: '2px 8px',
                      borderRadius: 6,
                      userSelect: 'none'
                    }}
                    title="Clic para cambiar manualmente: Sentado / Parado / En movimiento / Acostado"
                  >
                    {renderPostureIcon(currentPostureMeta?.key, currentPostureMeta?.color, 12)}
                    <span>{currentPostureMeta?.label}</span>
                  </span>
                </div>
              </div>
              <PostureDistribution breakdown={livePostureBreakdown} compact />
            </div>

            {/* Línea de Pasos de la sesión en vivo directamente debajo del gráfico de pulso */}
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconSteps size={16} color="#ff9500" />
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                    Pasos en la sesión
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 900, color: '#ff9500', lineHeight: 1 }}>
                    {fmtLatidos(displaySteps)}
                  </span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 9.5, color: 'var(--text-faint)', display: 'block' }}>Cadencia estimada</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text)' }}>
                  {sessionSec > 60 ? `${Math.round(((displaySteps || 0) / (sessionSec / 60)))} pasos/min` : '–'}
                </span>
              </div>
            </div>

            {/* Electrocardiograma · 130 Hz (debajo de Evolución del Pulso) */}
            {(polar.ecgAvailable || isDemo) && (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '10px 12px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Electrocardiograma · 130 Hz
                  </span>
                  <span style={{ fontSize: 9.5, color: '#10b981', fontWeight: 700, background: 'rgba(16,185,129,0.12)', padding: '2px 7px', borderRadius: 6 }}>
                    EN VIVO
                  </span>
                </div>
                <ECGChart
                  ecgRef={isDemo ? demoEcgRef : polar.ecgSamples}
                  ecgTick={isDemo ? demoEcgTick : polar.ecgTick}
                  compact
                  theme={theme}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ── Sección 1: Frecuencia Cardíaca ── */}
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>1</span>
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Frecuencia Cardíaca</p>
          <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Pulso instantáneo y evolución de la sesión</p>
        </div>
      </div>

      {/* HR hero + stats */}
      <div className="hr-hero-grid" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, marginBottom: 12, alignItems: 'stretch' }}>
        {/* Big HR number */}
        <div style={{
          background: zone ? `linear-gradient(145deg, var(--card-bg) 60%, ${zone.color}12)` : '#ffffff',
          borderRadius: 14, padding: '18px 22px',
          boxShadow: zone ? `0 4px 24px ${zone.glow}` : 'none',
          border: zone ? `1.5px solid ${zone.color}40` : '1.5px solid var(--border)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 130,
          transition: 'box-shadow 0.8s ease, border-color 0.8s ease',
        }}>
          <span className="hr-number" style={{
            fontSize: 76, fontWeight: 800, lineHeight: 1,
            color: zone?.color ?? '#ff3b30',
            fontVariantNumeric: 'tabular-nums',
            textShadow: zone ? `0 0 30px ${zone.glow}` : 'none',
            animation: instantHr ? `pulse ${beatDur} ease-in-out infinite` : 'none',
            display: 'inline-block',
          }}>
            {displayHr ?? '–'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>bpm</span>
          <div style={{
            marginTop: 8,
            background: zone ? `${zone.color}18` : 'var(--track)',
            borderRadius: 20, padding: '3px 10px',
            border: zone ? `1px solid ${zone.color}40` : '1px solid var(--field-border)',
          }}>
            <span style={{ fontSize: 10, color: zone?.color ?? 'var(--text-faint)', fontWeight: 700, letterSpacing: 0.5 }}>
              {zone ? zone.label.toUpperCase() : 'EN VIVO'}
            </span>
          </div>
        </div>
        {/* Stats cards (diseño unificado y pulido) */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${3 + (!isDemo ? 2 : 1)}, 1fr)`, gap: 10, flex: 1, minWidth: 280 }}>
          <MetricCard defKey="avg"   value={stats.avg} />
          <MetricCard defKey="min"   value={stats.min} color="#007aff" />
          <MetricCard defKey="max"   value={stats.max} color="#ff3b30" />
          
          {/* Tarjeta de Zona Actual en lugar de RMSSD */}
          <div style={{
            position: 'relative', background: 'var(--card-bg)', border: `1px solid ${trainingZone?.color ? trainingZone.color + '45' : 'var(--border)'}`,
            borderRadius: 14, padding: '10px 12px', flex: 1, minWidth: 0, boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'border-color 0.4s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: 9.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, margin: 0 }}>
                Zona FC
              </p>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: trainingZone?.color ?? zone?.color ?? '#34c759' }} />
            </div>
            <div style={{ margin: '3px 0 1px' }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: trainingZone?.color ?? zone?.color ?? 'var(--text)', lineHeight: 1.15, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {trainingZone?.short || zone?.label || 'Reposo'}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
                {trainingZone?.lo != null ? `${trainingZone.lo}–${trainingZone.hi} bpm` : (zone ? `< ${zone.max} bpm` : '—')}
              </span>
            </div>
            <span style={{ fontSize: 9.5, color: 'var(--text-faint)', fontWeight: 600 }}>
              {trainingZone?.num ? `Zona ${trainingZone.num}` : (trainingZone?.name || 'En vivo')}
            </span>
          </div>

          <MetricCard defKey="moisture" value={displayMoisture.pct} color={displayMoisture.color} />
        </div>
      </div>

      {/* HR chart en vivo */}
      <LiveChart readings={beatsWithGaps} posture={effectivePosture} />

      {/* ── Sección 2: Distribución de FC y Zonas de Entrenamiento ── */}
      <div style={{ marginTop: 20, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>2</span>
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Distribución de FC y zonas de entrenamiento</p>
          <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Cuánto tiempo pasaste en cada rango de latidos de la sesión</p>
        </div>
      </div>

      {/* Histograma de distribución cada 5 bpm */}
      {readings.length > 2 && (
        <HRDistributionChart readings={readings} />
      )}

      {/* Zona de entrenamiento en vivo */}
      {trainingZone && (
        <div style={{
          background: trainingZone.bg,
          border: `1.5px solid ${trainingZone.color}40`,
          borderRadius: 14, padding: '12px 16px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 14,
          transition: 'background 0.6s ease, border-color 0.6s ease',
        }}>
          {/* Zone badge */}
          <div style={{
            minWidth: 48, height: 48, borderRadius: 12,
            background: trainingZone.color, flexShrink: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            {trainingZone.num
              ? <>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', fontWeight: 700, letterSpacing: 1 }}>ZONA</span>
                  <span style={{ fontSize: 22, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{trainingZone.num}</span>
                </>
              : <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.2, padding: '0 4px' }}>{trainingZone.short.toUpperCase()}</span>
            }
          </div>
          {/* Zone info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{trainingZone.name}</p>
              <span style={{ fontSize: 11, fontWeight: 600, color: trainingZone.color, background: `${trainingZone.color}20`, borderRadius: 20, padding: '2px 10px', flexShrink: 0 }}>
                {trainingZone.lo}–{trainingZone.hi} BPM
              </span>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '3px 0 0', lineHeight: 1.4 }}>{trainingZone.desc}</p>
          </div>
          {/* Progress within zone */}
          {displayHr && trainingZone.hi - trainingZone.lo > 0 && (() => {
            const pct = Math.min(100, Math.max(0, Math.round((displayHr - trainingZone.lo) / (trainingZone.hi - trainingZone.lo) * 100)))
            return (
              <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 40 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--card-bg)', border: `3px solid ${trainingZone.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: trainingZone.color }}>{pct}%</span>
                </div>
                <p style={{ fontSize: 9, color: 'var(--text-faint)', margin: '3px 0 0' }}>en zona</p>
              </div>
            )
          })()}
        </div>
      )}

      {/* Desglose de zonas Z1 a Z5 */}
      <ZoneDistribution readings={beats.filter(b => b.hr)} />

      {/* Desglose de postura y actividad (Sentado, Acostado, Caminando, Parado/Sentado) */}
      <PostureDistribution breakdown={livePostureBreakdown} />

      {/* ── Sección 3: Electrocardiograma (ECG) ── */}
      {(polar.ecgAvailable || isDemo) && (
        <>
          <div style={{ marginTop: 20, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>3</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Electrocardiograma (ECG en vivo)</p>
              <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Trazado continuo a 130 Hz en tiempo real</p>
            </div>
          </div>
          <ECGChart
            ecgRef={isDemo ? demoEcgRef : polar.ecgSamples}
            ecgTick={isDemo ? demoEcgTick : polar.ecgTick}
            theme={theme}
          />
        </>
      )}

      {/* ── Sección 4: Variabilidad Cardíaca (HRV) ── */}
      <div style={{ marginTop: 20, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>4</span>
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Variabilidad de la Frecuencia Cardíaca (HRV)</p>
          <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Recuperación autonómica y espectro de potencia</p>
        </div>
      </div>

      <HRVPanel hrv={hrv} />
      <SpectralHRVPanel readings={beats} />

      <BridgeRecordingPanel bridge={bridge} onSaveToHistory={onSaveOfflineToHistory} />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn variant="muted" onClick={() => downloadCSV(readings, `polar-sesion-${new Date().toISOString().slice(0,10)}.csv`)} disabled={!readings.length}>
          ↓ Exportar CSV
        </Btn>
      </div>
        </>
      )}

      {/* ── MODO PANTALLA COMPLETA EN VIVO ── */}
      {isLiveFullscreen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999999,
            background: fsTheme === 'dark' ? '#07090e' : '#f5f5f7',
            color: fsTheme === 'dark' ? '#ffffff' : '#000000',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 'max(16px, env(safe-area-inset-top)) 24px max(16px, env(safe-area-inset-bottom))',
            boxSizing: 'border-box',
            animation: 'fadeIn 0.25s ease-out',
            overflow: 'hidden',
          }}
        >
          {/* Fondo bioluminiscente ambiental reactivo a la zona cardíaca */}
          <div
            style={{
              position: 'absolute',
              top: '15%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60vw',
              height: '60vw',
              maxWidth: 700,
              maxHeight: 700,
              borderRadius: '50%',
              background: zone?.color || '#ff3b30',
              filter: 'blur(140px)',
              opacity: fsTheme === 'dark' ? 0.22 : 0.12,
              pointerEvents: 'none',
              transition: 'background 0.8s ease',
              zIndex: 0,
            }}
          />

          {/* 1. Barra Superior del Fullscreen */}
          <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: isBleConnected ? '#34c759' : '#ff9f0a', boxShadow: `0 0 8px ${isBleConnected ? '#34c759' : '#ff9f0a'}` }} />
              <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.5, color: fsTheme === 'dark' ? '#ffffff' : '#000000', textTransform: 'uppercase' }}>
                {isDemo ? 'Modo Demo' : 'Polar H10 · En Vivo'}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)', fontVariantNumeric: 'tabular-nums' }}>
                ⏱ {sessionTime}
              </span>
            </div>

            {/* Postura actual en el centro */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: fsTheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              border: `1px solid ${fsTheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
              borderRadius: 20, padding: '4px 12px'
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: currentPostureMeta?.color || '#30d158', boxShadow: `0 0 6px ${currentPostureMeta?.color || '#30d158'}` }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: fsTheme === 'dark' ? '#ffffff' : '#000000' }}>
                {currentPostureMeta?.label || 'Postura'}
              </span>
            </div>

            {/* Botones de Acción: Modo Día/Noche y Salir */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setFsTheme(t => t === 'dark' ? 'light' : 'dark')}
                style={{
                  background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                  border: `1px solid ${fsTheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
                  borderRadius: 10,
                  color: fsTheme === 'dark' ? '#ffffff' : '#000000',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.15s ease'
                }}
              >
                {fsTheme === 'dark' ? (
                  <>
                    <IconSun size={13} color="#ffffff" />
                    <span>Modo Día</span>
                  </>
                ) : (
                  <>
                    <IconMoon size={13} color="#000000" />
                    <span>Modo Noche</span>
                  </>
                )}
              </button>

              <button
                onClick={toggleLiveFullscreen}
                style={{
                  background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                  border: `1px solid ${fsTheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
                  borderRadius: 10,
                  color: fsTheme === 'dark' ? '#ffffff' : '#000000',
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.15s ease'
                }}
              >
                <span>✕</span> Salir (Esc)
              </button>
            </div>
          </div>

          {/* 2. Centro: Gran Indicador Cardíaco + Zonas */}
          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', margin: 'auto 0' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
              <span style={{
                display: 'inline-block',
                animation: instantHr ? `pulse ${beatDur} ease-in-out infinite` : 'none',
              }}>
                <IconHeart size={44} color={zone?.color ?? '#ff3b30'} fill={zone?.color ?? '#ff3b30'} />
              </span>
              <span style={{
                fontSize: 'clamp(72px, 16vw, 140px)',
                fontWeight: 900,
                color: zone?.color ?? (fsTheme === 'dark' ? '#ffffff' : '#000000'),
                lineHeight: 1,
                letterSpacing: -2,
                fontVariantNumeric: 'tabular-nums',
                textShadow: fsTheme === 'dark' ? `0 0 40px ${zone?.color || '#ff3b30'}66` : 'none'
              }}>
                {displayHr ?? '–'}
              </span>
              <span style={{ fontSize: 'clamp(18px, 3.5vw, 28px)', fontWeight: 800, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)', alignSelf: 'flex-end', marginBottom: 12 }}>
                BPM
              </span>
            </div>

            {/* Zona de Entrenamiento Activa */}
            <div style={{ marginTop: 6 }}>
              <span style={{
                fontSize: 'clamp(13px, 2vw, 16px)',
                fontWeight: 800,
                color: zone?.color ?? '#34c759',
                background: fsTheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${zone?.color || '#34c759'}60`,
                padding: '4px 14px',
                borderRadius: 20
              }}>
                {zone?.name ? `Zona ${trainingZone?.num || ''} · ${zone.name}` : 'En Vivo'}
              </span>
            </div>

            {/* Asesor Prescriptivo de Entrenamiento */}
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{
                fontSize: 11, fontWeight: 800, color: fsTheme === 'dark' ? '#ffffff' : '#000000',
                background: fsTheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${readiness.recoveryColor}60`,
                padding: '3px 10px', borderRadius: 16,
                display: 'inline-flex', alignItems: 'center', gap: 5
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: readiness.recoveryColor, boxShadow: `0 0 5px ${readiness.recoveryColor}` }} />
                <span>Recuperación {readiness.recoveryScore}% · {readiness.modalityTitle}</span>
              </span>
              <span style={{
                fontSize: 10.5, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                background: fsTheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                padding: '3px 8px', borderRadius: 16
              }}>
                Cansancio {readiness.fatigueScore}% · Estrés {readiness.stressScore}%
              </span>
            </div>

            {/* Fila de Métricas Clave en Vivo */}
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'clamp(12px, 4vw, 36px)',
              marginTop: 18, flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>Promedio</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: fsTheme === 'dark' ? '#ffffff' : '#000000', fontVariantNumeric: 'tabular-nums' }}>{stats.avg || '–'} bpm</span>
              </div>
              <div style={{ width: 1, height: 26, background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }} />
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>Rango</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: fsTheme === 'dark' ? '#ffffff' : '#000000', fontVariantNumeric: 'tabular-nums' }}>{stats.min || '–'} – {stats.max || '–'} bpm</span>
              </div>
              <div style={{ width: 1, height: 26, background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }} />
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>HRV (RMSSD)</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#64d2ff', fontVariantNumeric: 'tabular-nums' }}>{hrv?.rmssd ? `${hrv.rmssd} ms` : '–'}</span>
              </div>
              <div style={{ width: 1, height: 26, background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }} />
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: fsTheme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>Pasos</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: '#ff9f0a', fontVariantNumeric: 'tabular-nums' }}>{displaySteps}</span>
              </div>
            </div>
          </div>

          {/* 3. Gráfico en Vivo a Pantalla Completa en la parte inferior */}
          <div style={{ position: 'relative', zIndex: 2, height: 'clamp(140px, 30vh, 220px)', width: '100%', maxWidth: 1100, margin: '0 auto' }}>
            <LiveChart readings={beatsWithGaps} compact={true} posture={effectivePosture} isDark={fsTheme === 'dark'} />
          </div>
        </div>
      )}

    </div>
  )
}

// ── iOS CSV import (multi-file batch) ────────────────────────────────────────

// Extract a comparable date key from a filename to match HR / RR pairs
// Handles: "Heart Rate_2024-01-15 12.30.00.csv", "RR Intervals_2024-01-15 12.30.00.csv"
// and numeric epoch-based names like "hr_1705312200.csv"
function extractFileDateKey(filename) {
  const name = filename.replace(/\.csv$/i, '')
  // Polar Beat / Polar Sensor Logger format: "HR_CsvData", "RR_CsvData", "ECG_CsvData"
  // optionally followed by a space and a session number (e.g. "HR_CsvData 2")
  const polarMatch = name.match(/^(?:HR|RR|ECG|ACC)_CsvData\s*(\d*)$/i)
  if (polarMatch) return `polar_${polarMatch[1] || '0'}`
  // Try ISO-like date: 2024-01-15 or 20240115
  const iso = name.match(/(\d{4})[-_]?(\d{2})[-_]?(\d{2})(?:[T \-_](\d{2})[.:](\d{2}))?/)
  if (iso) return `${iso[1]}${iso[2]}${iso[3]}${iso[4]??'00'}${iso[5]??'00'}`
  // Try epoch seconds
  const epoch = name.match(/(\d{9,10})/)
  if (epoch) {
    const d = new Date(parseInt(epoch[1]) * 1000)
    return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}`
  }
  return name.replace(/\W/g, '').toLowerCase()
}

function isRRFile(filename) {
  // Explicit RR keywords
  if (/rr[\s_\-]|[\s_\-]rr|rr\.csv|intervals?|ibi/i.test(filename)) return true
  // ECG raw data files are neither HR nor RR — skip them
  if (/ecg|electrocard/i.test(filename)) return false
  // Remaining: check negatively for HR keywords
  return false
}

function isECGRawFile(filename) {
  return /ecg|electrocard/i.test(filename)
}

// Parse raw ECG CSV from ECG & HRV Insights / Polar Sensor Logger
// Returns { samples: Float32Array, fs: number, startTime: ms, endTime: ms }
// or null if unrecognizable
function parseECGFile(text) {
  const firstNewline = text.indexOf('\n')
  if (firstNewline === -1) return null
  
  const rawHeader = text.slice(0, firstNewline).replace(/^﻿/, '').trim()
  const headers   = rawHeader.split(',').map(h => h.trim().toLowerCase())

  // Find time col
  const tIdx = headers.findIndex(h => /epoch|^time$|timestamp/i.test(h))
  const vIdx = headers.findIndex(h => /ecg|voltage|[\[\(]mv[\]\)]|[\[\(]uv[\]\)]|^value$|^x\b/i.test(h))

  if (tIdx === -1 || vIdx === -1) return null

  // Fast-count newlines
  let totalLines = 0
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') totalLines++
  }
  if (totalLines < 50) return null

  // Parse first and last lines to estimate duration
  const secondNewline = text.indexOf('\n', firstNewline + 1)
  if (secondNewline === -1) return null
  const firstLine = text.slice(firstNewline + 1, secondNewline)
  const firstParts = firstLine.split(',')
  const tStart = parseFloat(firstParts[tIdx])

  let lastLineIdx = text.lastIndexOf('\n')
  if (lastLineIdx === text.length - 1) {
    lastLineIdx = text.lastIndexOf('\n', lastLineIdx - 1)
  }
  const secondToLastNewline = text.lastIndexOf('\n', lastLineIdx - 1)
  const lastLine = text.slice(secondToLastNewline + 1, lastLineIdx)
  const lastParts = lastLine.split(',')
  const tEnd = parseFloat(lastParts[tIdx])

  const isMs = tStart > 1e12
  const durationMs = isMs ? (tEnd - tStart) : (tEnd - tStart) * 1000
  const durationSec = durationMs / 1000
  const startMs = isMs ? tStart : tStart * 1000

  // We want to fit within the server's 500,000 samples limit.
  // We use 25 Hz as high-quality default, or scale down if needed for very long recordings.
  const sourceFs = Math.round((totalLines - 1) / durationSec)
  const targetFs = Math.min(25, Math.floor(500000 / durationSec))
  const step = Math.max(1, Math.round(sourceFs / targetFs))

  const lines = text.trim().split(/\r?\n/)
  const samples = []
  
  // Detect unit on the first few valid rows
  let testCount = 0
  let absMax = 0
  for (let i = 1; i < Math.min(lines.length, 500); i++) {
    const parts = lines[i].split(',')
    const v = Math.abs(parseFloat(parts[vIdx]))
    if (!isNaN(v)) {
      if (v > absMax) absMax = v
      testCount++
    }
  }
  const inMv = absMax < 10

  // Parse every 'step'th line directly!
  for (let i = 1; i < lines.length; i += step) {
    const parts = lines[i].split(',')
    const v = parseFloat(parts[vIdx])
    if (isNaN(v)) continue
    samples.push(Math.round(inMv ? v * 1000 : v))
  }

  const finalFs = Math.round(sourceFs / step)

  return { samples, fs: finalFs, startTime: Math.round(startMs), endTime: Math.round(startMs + durationMs) }
}

// Downsample ECG to targetFs (default 25 Hz) for storage
function downsampleECG(samples, sourceFs, targetFs = 25) {
  const step = Math.max(1, Math.round(sourceFs / targetFs))
  const out = []
  for (let i = 0; i < samples.length; i += step) out.push(Math.round(samples[i]))
  return out
}

// Derive RR intervals and synthetic HR readings from parsed ECG data
function ecgDataToReadings(ecgData) {
  const { samples, fs, startTime } = ecgData
  const filtered     = notchFilter50(hpFilter(samples))
  const peakIndices  = detectRPeaks(filtered, fs)

  // Build one reading per second, each carrying RR intervals within that window
  const durationMs  = (samples.length / fs) * 1000
  const readings    = []
  const msPerSample = 1000 / fs

  for (let secIdx = 0; secIdx * 1000 < durationMs; secIdx++) {
    const winStart = secIdx * 1000
    const winEnd   = winStart + 1000
    const rrs = []
    for (let pi = 1; pi < peakIndices.length; pi++) {
      const peakMs = peakIndices[pi] * msPerSample
      if (peakMs >= winStart && peakMs < winEnd) {
        const rrMs = Math.round((peakIndices[pi] - peakIndices[pi - 1]) * msPerSample)
        if (rrMs > 250 && rrMs < 2000) rrs.push(rrMs)
      }
    }
    const hr = rrs.length > 0 ? Math.round(60000 / rrs[rrs.length - 1]) : null
    readings.push({ time: startTime + winStart, hr, rr: rrs, source: 'ecg' })
  }
  return readings.filter(r => r.hr !== null || r.rr.length > 0)
}

function parseCSVGeneric(text, maxRows = 500_000) {
  const lines = text.trim().split(/\r?\n/)
  if (!lines.length) return []
  const headers = lines[0].replace(/^﻿/, '').split(',').map(h => h.trim())
  // Detect raw ECG files: >10k rows + no HR/RR-like column headers
  const hasHRCol = headers.some(h => /hr|bpm|value|rr|interval|ibi/i.test(h))
  const rowCount = lines.length - 1
  if (!hasHRCol || rowCount > maxRows) return []   // not a usable HR/RR file
  return lines.slice(1).map(line => {
    const vals = line.split(',')
    const row = {}
    headers.forEach((h, i) => { row[h] = (vals[i] ?? '').trim() })
    return row
  }).filter(r => Object.values(r).some(v => v))
}

function parseHRFile(text) {
  const rows = parseCSVGeneric(text)
  return rows.map(row => {
    const epoch = parseFloat(row.epoch ?? row.HREpoch ?? row.time ?? row.timestamp)
    const bpm   = parseInt(row.value ?? row.HRValue ?? row.hr ?? row.bpm, 10)
    if (isNaN(epoch) || isNaN(bpm) || bpm < 20 || bpm > 300) return null
    return { t: Math.round(epoch * 1000), hr: bpm }
  }).filter(Boolean).sort((a, b) => a.t - b.t)
}

function parseRRFile(text) {
  const rows = parseCSVGeneric(text)
  return rows.map(row => {
    const epoch = parseFloat(row.epoch ?? row.RREpoch ?? row.time ?? row.timestamp)
    const val   = parseFloat(row.value ?? row.RRValue ?? row.rr ?? row.ibi)
    if (isNaN(epoch) || isNaN(val)) return null
    // ECG & HRV Insights exports RR in seconds; convert to ms if < 10
    const rrMs = val < 10 ? Math.round(val * 1000) : Math.round(val)
    if (rrMs < 250 || rrMs > 2500) return null
    return { t: Math.round(epoch * 1000), rr: rrMs }
  }).filter(Boolean).sort((a, b) => a.t - b.t)
}

// Match RR list to HR list using midpoint windows
function matchRRtoHR(hrList, rrList) {
  let rrIdx = 0
  return hrList.map((h, i) => {
    const lo = i === 0            ? h.t - 2000 : Math.round((hrList[i-1].t + h.t) / 2)
    const hi = i === hrList.length-1 ? h.t + 2000 : Math.round((h.t + hrList[i+1].t) / 2)
    while (rrIdx < rrList.length && rrList[rrIdx].t < lo) rrIdx++
    const rrs = []; let j = rrIdx
    while (j < rrList.length && rrList[j].t < hi) { rrs.push(rrList[j].rr); j++ }
    return { time: h.t, hr: h.hr, rr: rrs, source: 'ios' }
  })
}

// Score how well two date keys match (lower = better match)
// Allows for slight drift: same day different time → score by minute diff
function dateKeyScore(k1, k2) {
  if (k1 === k2) return 0
  // Same date prefix (YYYYMMDD)?
  if (k1.length >= 8 && k2.length >= 8 && k1.slice(0,8) === k2.slice(0,8)) {
    const m1 = parseInt(k1.slice(8,12) || '0')
    const m2 = parseInt(k2.slice(8,12) || '0')
    return Math.abs(m1 - m2)
  }
  return Infinity
}

// Recursively collect all File objects from a DataTransferItem directory entry
async function collectFilesFromEntry(entry) {
  if (!entry) return []
  if (entry.isFile) {
    return [await new Promise(res => entry.file(res, () => res(null)))]
  }
  if (entry.isDirectory) {
    const reader = entry.createReader()
    const allEntries = await new Promise(res => {
      const acc = []
      const read = () => reader.readEntries(batch => {
        if (!batch.length) { res(acc); return }
        acc.push(...batch); read()
      }, () => res(acc))
      read()
    })
    const nested = await Promise.all(allEntries.map(collectFilesFromEntry))
    return nested.flat().filter(Boolean)
  }
  return []
}

const API_IMPORT = apiBase

function IosImportPanel({ onImported, token, existingSessions = [], inline = false }) {
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  // Initial state comes from importMem so a tab switch mid-import doesn't wipe the view
  const [sessions,  setSessions]  = useState(() => importMem.sessions)
  const [importing, setImporting] = useState(importMem.importing)
  const [batchDone, setBatchDone] = useState(importMem.batchDone)
  const [globalErr, setGlobalErr] = useState(null)
  const [dragOver,  setDragOver]  = useState(false)
  const [isOpen,    setIsOpen]    = useState(importMem.isOpen)
  // 'drop' = drag target open (no modal yet), 'classify' = modal showing classification
  const [step,      setStep]      = useState(importMem.step)

  // Keep the module-scope mirror in sync while mounted…
  useEffect(() => {
    importMem.sessions = sessions; importMem.importing = importing
    importMem.batchDone = batchDone; importMem.step = step; importMem.isOpen = isOpen
  })
  // …and pull background upload progress back into the panel (the upload loop
  // may belong to a previous mount — it publishes via importBus + importMem).
  useEffect(() => importBus.subscribe(() => {
    if (!importBus.state) return
    if (isMounted.current) {
      setSessions([...importMem.sessions])
      setImporting(!!importBus.state.importing)
      if (!importBus.state.importing) setBatchDone(true)
    }
  }), [])

  // ── Ready sessions ref ──────────────────────────────────────────────────────
  const readyCountRef = useRef(0)
  useEffect(() => {
    const r = sessions.filter(s => s.status === 'ready' || (s.status === 'dupe' && s.dupeAction === 'replace')).length
    readyCountRef.current = r
  }, [sessions])

  const processAllFiles = useCallback(async (allFiles) => {
    setGlobalErr(null); setBatchDone(false)
    const ecgFiles  = allFiles.filter(f => isECGRawFile(f.name))
    const usable    = allFiles.filter(f => !isECGRawFile(f.name))
    const hrFiles   = usable.filter(f => !isRRFile(f.name))
    const rrFiles   = usable.filter(f =>  isRRFile(f.name))

    if (!hrFiles.length && !ecgFiles.length) {
      setGlobalErr('No se encontraron archivos HR ni ECG.')
      return
    }

    // Sort files numerically by polar session number (polar_0 < polar_2 < … < polar_10)
    const polarNum = f => { const m = f.name.match(/^(?:HR|RR|ECG|ACC)_CsvData\s*(\d*)/i); return m ? parseInt(m[1] || '0') : Infinity }
    hrFiles.sort((a, b) => polarNum(a) - polarNum(b))

    // ── HR+RR+ECG pairs ───────────────────────────────────────────────────────
    const hrPairs = hrFiles.map(hr => {
      const hrKey = extractFileDateKey(hr.name)
      let bestRR = null, bestRRScore = Infinity
      let bestECG = null, bestECGScore = Infinity
      for (const rr of rrFiles) {
        const s = dateKeyScore(hrKey, extractFileDateKey(rr.name))
        if (s < bestRRScore) { bestRRScore = s; bestRR = rr }
      }
      for (const ecg of ecgFiles) {
        const s = dateKeyScore(hrKey, extractFileDateKey(ecg.name))
        if (s < bestECGScore) { bestECGScore = s; bestECG = ecg }
      }
      return {
        hrFile: hr,
        rrFile: bestRRScore < 120 ? bestRR : null,
        ecgFile: bestECGScore < 120 ? bestECG : null,
        key: hrKey, status: 'pending', preview: null, error: null,
      }
    })

    // ── ECG-only pairs (no matching HR file) ─────────────────────────────────
    const matchedEcgFiles = new Set(hrPairs.map(p => p.ecgFile).filter(Boolean))
    const ecgPairs = ecgFiles
      .filter(ecg => !matchedEcgFiles.has(ecg))
      .map(ecg => ({
        hrFile: null, rrFile: null, ecgFile: ecg, key: extractFileDateKey(ecg.name),
        status: 'pending', preview: null, error: null,
      }))

    // Sort by date key (oldest first) so the list processes and fills top-to-bottom
    const pairs = [...hrPairs, ...ecgPairs].sort((a, b) => String(a.key ?? '').localeCompare(String(b.key ?? '')))
    importMem.sessions = pairs
    if (isMounted.current) {
      setSessions(pairs)
    }

    const processed = [...pairs]
    for (let i = 0; i < processed.length; i++) {
      const { hrFile, rrFile, ecgFile } = processed[i]
      try {
        if (ecgFile && !hrFile) {
          // ECG-only: derive readings from R-peaks
          const ecgText = await ecgFile.text()
          const ecgData = parseECGFile(ecgText)
          if (!ecgData) throw new Error('No se pudo parsear el ECG')
          const readings = ecgDataToReadings(ecgData)
          if (!readings.length) throw new Error('Sin latidos detectados en el ECG')
          const stats = computeStats(readings)
          const hrv   = computeHRV(readings)
          processed[i] = { ...processed[i], status: 'ready', preview: { readings, stats, hrv, ecgData } }
        } else {
          const hrText  = await hrFile.text()
          const hrList  = parseHRFile(hrText)
          if (!hrList.length) throw new Error('Sin datos HR válidos')

          let rrList = []
          if (rrFile) {
            const rrText = await rrFile.text()
            rrList = parseRRFile(rrText)
          }

          // Parse paired ECG waveform if present (non-critical)
          let ecgData = null
          if (ecgFile) {
            try {
              const ecgText = await ecgFile.text()
              ecgData = parseECGFile(ecgText) ?? null
            } catch (_) { /* ECG is non-critical */ }
          }

          const readings = matchRRtoHR(hrList, rrList)
          const stats    = computeStats(readings)
          const hrv      = computeHRV(readings)
          processed[i]   = { ...processed[i], status: 'ready', preview: { readings, stats, hrv, ecgData } }
        }
        // Mark duplicates immediately after parsing
        if (processed[i].status === 'ready') {
          const startMs = processed[i].preview.readings[0]?.time
          const dupe    = startMs != null ? existingSessions.find(s =>
            Math.abs((s.startTime ?? new Date(s.start_time ?? 0).getTime()) - startMs) < 5 * 60 * 1000
          ) : null
          if (dupe) processed[i] = { ...processed[i], status: 'dupe', dupeId: dupe.id, dupeAction: 'skip' }
        }
      } catch(e) {
        processed[i] = { ...processed[i], status: 'error', error: e.message }
      }
      importMem.sessions = [...processed]   // survives a tab switch mid-parse
      if (isMounted.current) {
        setSessions([...processed])
      }
    }
  }, [existingSessions])

  const handleFiles = useCallback((files) => {
    const csvFiles = Array.from(files).filter(f => f && /\.csv$/i.test(f.name))
    if (!csvFiles.length) {
      if (isMounted.current) setGlobalErr('No se encontraron archivos .csv en lo que soltaste.')
      return
    }
    if (isMounted.current) {
      setGlobalErr(null)
      setStep('classify')
      setIsOpen(true)
    }
    processAllFiles(csvFiles)
  }, [processAllFiles])

  const handleDrop = useCallback(async (e) => {
    e.preventDefault()
    setDragOver(false)
    const items = e.dataTransfer?.items
    if (items && items.length > 0) {
      // Use FileSystemEntry API to support folders
      const entryPromises = Array.from(items).map(item => {
        const entry = item.webkitGetAsEntry?.()
        return entry ? collectFilesFromEntry(entry) : Promise.resolve([item.getAsFile()].filter(Boolean))
      })
      const groups = await Promise.all(entryPromises)
      handleFiles(groups.flat())
    } else {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles])

  // ── Duplicate detection ───────────────────────────────────────────────────
  // Returns existing session if one overlaps within 5 min of startTime
  const findDuplicate = (startTimeMs) => {
    const TOLERANCE = 5 * 60 * 1000 // 5 minutes
    return existingSessions.find(s =>
      Math.abs((s.startTime ?? s.start_time_ms ?? new Date(s.start_time).getTime()) - startTimeMs) < TOLERANCE
    ) ?? null
  }

  const importAll = async () => {
    if (!token) return
    if (isMounted.current) setImporting(true)
    importMem.importing = true
    const updated = [...sessions]
    const total = updated.filter(s => s.status === 'ready' || (s.status === 'dupe' && s.dupeAction === 'replace')).length
    let processed = 0
    importBus.set({ importing: true, done: 0, total })   // background-safe progress (nav badge)
    for (let i = 0; i < updated.length; i++) {
      const s = updated[i]
      // skip sessions the user chose to not import
      if (s.status === 'dupe' && s.dupeAction === 'skip') {
        updated[i] = { ...s, status: 'skipped' }
        importMem.sessions = [...updated]
        if (isMounted.current) setSessions([...updated])
        continue
      }
      if (s.status !== 'ready' && !(s.status === 'dupe' && s.dupeAction === 'replace')) continue

      updated[i] = { ...updated[i], status: 'uploading' }
      importMem.sessions = [...updated]
      if (isMounted.current) setSessions([...updated])
      try {
        // If replacing, delete the old session first
        if (s.status === 'dupe' && s.dupeAction === 'replace' && s.dupeId) {
          await authFetch(token, `${API_IMPORT}/sessions/${s.dupeId}`, { method: 'DELETE' })
        }

        const { readings, stats, hrv, ecgData } = updated[i].preview
        const startTimeMs = readings[0].time
        const session = {
          startTime: startTimeMs, endTime: readings[readings.length-1].time,
          durationSec: stats.duration,
          deviceName: ecgData ? 'Polar H10 (ECG)' : 'ECG & HRV Insights',
          source: 'ios',
          avgHr: stats.avg, minHr: stats.min, maxHr: stats.max,
          rmssd: hrv.rmssd, sdnn: hrv.sdnn, pnn50: hrv.pnn50, respRate: hrv.respRate,
          readingCount: stats.count,
        }
        const result    = await uploadSession(token, session, readings)
        const sessionId = result?.sessionId

        let ecgUploaded = null   // null = sin ECG · true = guardado · false = falló
        if (ecgData && sessionId) {
          try {
            // Se sube el ECG completo (que fue adaptado dinámicamente bajo el límite de 500k muestras de BigQuery)
            const ecgRes = await authFetch(token, `${API_IMPORT}/sessions/${sessionId}/ecg`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fs: ecgData.fs, startTime: ecgData.startTime, samples: ecgData.samples }),
            })
            if (!ecgRes.ok) throw new Error(`ECG HTTP ${ecgRes.status}`)
            ecgUploaded = true
          } catch (ecgErr) {
            console.warn('ECG upload failed (non-critical):', ecgErr)
            ecgUploaded = false
          }
        }

        updated[i] = { ...updated[i], status: 'done', sessionId, ecgUploaded }
      } catch(e) {
        updated[i] = { ...updated[i], status: 'error', error: e.message }
      }
      processed++
      importMem.sessions = [...updated]
      importBus.set({ importing: true, done: processed, total })
      if (isMounted.current) setSessions([...updated])
    }
    importMem.importing = false
    importMem.batchDone = true
    if (isMounted.current) {
      setImporting(false)
      setBatchDone(true)
    }
    importBus.set({ importing: false, done: processed, total })
    setTimeout(() => { if (!importBus.state?.importing) importBus.set(null) }, 6000)
    onImported?.()
  }

  const reset = () => {
    Object.assign(importMem, { sessions: [], importing: false, batchDone: false, step: 'idle', isOpen: false })
    if (isMounted.current) {
      setSessions([]); setGlobalErr(null); setBatchDone(false); setIsOpen(false); setStep('idle')
    }
  }

  const readyCount   = sessions.filter(s => s.status === 'ready' || (s.status === 'dupe' && s.dupeAction === 'replace')).length
  const dupeCount    = sessions.filter(s => s.status === 'dupe').length
  const doneCount    = sessions.filter(s => s.status === 'done').length
  const errorCount   = sessions.filter(s => s.status === 'error').length
  const skippedCount = sessions.filter(s => s.status === 'skipped').length

  const fmtFull = ms => {
    const d = new Date(ms), pad = n => String(n).padStart(2,'0')
    const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic']
    return `${d.getDate()} ${months[d.getMonth()]} · ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const STATUS_CFG = {
    pending:   { label:'Procesando…', color:'var(--text-fainter)' },
    ready:     { label:'Listo',        color:'#34c759' },
    dupe:      { label:'Ya existe',    color:'#ff9f0a' },
    uploading: { label:'Subiendo…',    color:'#007aff' },
    done:      { label:'Subida ✓',     color:'#34c759' },
    error:     { label:'Error',        color:'#ff3b30' },
    skipped:   { label:'No importada', color:'var(--text-fainter)' },
  }

  const setDupeAction = (i, action) => {
    setSessions(prev => prev.map((s, idx) => idx === i ? { ...s, dupeAction: action } : s))
  }
  const setAllDupes = (action) => {
    setSessions(prev => prev.map(s => s.status === 'dupe' ? { ...s, dupeAction: action } : s))
  }

  const sessionTypeLabel = s => {
    if (!s.preview) return null
    const h = new Date(s.preview.readings[0].time).getHours()
    const avg = s.preview.stats.avg ?? 0
    const max = s.preview.stats.max ?? 0
    if ((h >= 22 || h < 8) && avg < 82) return { label: 'Sueño', color: '#5856d6' }
    if (avg > 95 || max > 140)           return { label: 'Ejercicio', color: '#ff9f0a' }
    return                                      { label: 'Salud', color: '#34c759' }
  }

  const isClassifying = step === 'classify' && sessions.some(s => s.status === 'pending')
  const allParsed     = sessions.length > 0 && sessions.every(s => s.status !== 'pending')

  return (
    <>
      {/* ── Minimal floating button — hidden in the inline tab view ── */}
      {!inline && (
      <div style={{ position: 'fixed', bottom: 80, right: 16, zIndex: 500 }}>
        {batchDone ? (
          <button onClick={reset}
            style={{ background: '#34c759', color: '#fff', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(52,199,89,0.4)' }}>
            ✓ {doneCount} importada{doneCount !== 1 ? 's' : ''} · cerrar
          </button>
        ) : (
          <button
            onClick={() => setIsOpen(true)}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              background: dragOver ? '#007aff' : '#1c1c1edd',
              color: '#fff', border: 'none', borderRadius: 20,
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              backdropFilter: 'blur(8px)',
              boxShadow: dragOver ? '0 2px 12px rgba(0,122,255,0.5)' : '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {dragOver ? 'Soltá aquí' : '↑ Importar'}
          </button>
        )}
      </div>
      )}

      {/* ── Import panel — inline inside the tab, or floating modal elsewhere ── */}
      {(inline || isOpen) && (
        <div
          style={inline
            ? { display: 'flex', justifyContent: 'center' }
            : { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (!inline && e.target === e.currentTarget && !readyCountRef.current) reset() }}
        >
          <div style={{ background: 'var(--card-bg)', borderRadius: inline ? 16 : 20, width: '100%', maxWidth: inline ? '100%' : 540, maxHeight: inline ? 'none' : '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: inline ? '1px solid var(--border)' : 'none', boxShadow: inline ? 'none' : '0 8px 40px rgba(0,0,0,0.22)' }}>

            {/* Modal header */}
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                  {sessions.length === 0 ? 'Importar sesiones' : isClassifying ? 'Analizando archivos…' : `${sessions.length} sesi${sessions.length !== 1 ? 'ones encontradas' : 'ón encontrada'}`}
                </p>
                {sessions.length > 0 && allParsed && (
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0' }}>
                    Revisá y confirmá lo que querés importar
                  </p>
                )}
              </div>
              {!inline && <button onClick={reset} style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: '50%', width: 28, height: 28, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexShrink: 0 }}>×</button>}
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

              {/* Error */}
              {globalErr && (
                <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                  <p style={{ fontSize: 12, color: '#ff3b30', margin: 0 }}>[AVISO] {globalErr}</p>
                </div>
              )}

              {/* Drop zone — shown when no sessions loaded yet */}
              {sessions.length === 0 && (
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false) }}
                  onDrop={handleDrop}
                  onClick={(e) => {
                    if (!e.target.closest('[data-folder-btn]')) {
                      fileInputRef.current?.click()
                    }
                  }}
                  style={{
                    borderRadius: 14, textAlign: 'center', cursor: 'pointer',
                    border: `2px dashed ${dragOver ? '#007aff' : 'var(--border)'}`,
                    background: dragOver ? 'var(--tint-blue)' : 'var(--field-bg)',
                    padding: '36px 20px', transition: 'all 0.15s',
                  }}>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <p style={{ fontSize: 15, fontWeight: 700, color: dragOver ? '#007aff' : 'var(--text)', margin: '0 0 6px' }}>
                    {dragOver ? 'Soltá la carpeta o archivos' : 'Arrastrá una carpeta o archivos CSV'}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 18px', lineHeight: 1.5 }}>
                    Soporta carpetas completas · HR y RR se emparejan solos por fecha
                  </p>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--btn-strong)', color: '#fff', borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Elegir archivos
                    <input ref={fileInputRef} type="file" accept=".csv,text/csv" multiple style={{ display: 'none' }}
                      onChange={e => handleFiles(e.target.files)} />
                  </div>
                  <br />
                  <button
                    type="button"
                    data-folder-btn="true"
                    onClick={(e) => {
                      e.stopPropagation()
                      folderInputRef.current?.click()
                    }}
                    style={{ background: 'none', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 10, color: '#007aff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    O elegir una carpeta
                    <input ref={folderInputRef} type="file" webkitdirectory="" style={{ display: 'none' }}
                      onChange={e => handleFiles(e.target.files)} />
                  </button>
                </div>
              )}

              {/* Loading skeleton while classifying */}
              {isClassifying && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Keep the original order: parsed cards replace their own skeleton in place,
                      so the list visibly fills from top to bottom */}
                  {sessions.map((s, i) => {
                    if (s.status === 'pending') {
                      return <div key={i} style={{ height: 64, background: 'var(--chip-bg)', borderRadius: 12, animation: 'pulse 1.2s ease-in-out infinite' }} />
                    }
                    const p   = s.preview
                    const cfg = STATUS_CFG[s.status] ?? STATUS_CFG.pending
                    const typ = sessionTypeLabel(s)
                    return (
                      <div key={i} style={{ padding: '12px 14px', background: 'var(--field-bg)', borderRadius: 12, borderLeft: `3px solid ${cfg.color}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{p ? fmtFull(p.readings[0].time) : (s.hrFile ?? s.ecgFile)?.name}</p>
                          {typ && <span style={{ fontSize: 11, color: typ.color, background: typ.color+'15', borderRadius: 10, padding: '2px 8px', fontWeight: 600 }}>{typ.label}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Classification cards — all parsed */}
              {allParsed && sessions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sessions.map((s, i) => {
                    const p   = s.preview
                    const cfg = STATUS_CFG[s.status] ?? STATUS_CFG.pending
                    const typ = sessionTypeLabel(s)
                    return (
                      <div key={i} style={{ background: 'var(--field-bg)', borderRadius: 14, padding: '14px 16px', borderLeft: `3px solid ${cfg.color}` }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            {p ? (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{fmtFull(p.readings[0].time)}</p>
                                  {typ && <span style={{ fontSize: 11, color: typ.color, background: typ.color+'18', borderRadius: 10, padding: '2px 8px', fontWeight: 600, flexShrink: 0 }}>{typ.label}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDuration(p.stats.duration)}</span>
                                  {p.stats.avg  && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.stats.avg} bpm</span>}
                                  {p.hrv.rmssd != null && <span style={{ fontSize: 12, color: p.hrv.toneColor, fontWeight: 600 }}>HRV {p.hrv.rmssd} ms</span>}
                                  {p.stats.count && <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>{p.stats.count} lecturas</span>}
                                  {(() => { const rrCount = p.readings.filter(r => r.rr?.length).length; return rrCount > 0 ? <span style={{ fontSize: 11, color: '#34c759' }}>RR ✓</span> : <span style={{ fontSize: 11, color: '#ff9f0a' }}>sin RR</span> })()}
                                  {p.ecgData && (
                                    s.ecgUploaded === true  ? <span style={{ fontSize: 11, color: '#34c759', fontWeight: 600 }}>ECG guardado ✓</span> :
                                    s.ecgUploaded === false ? <span style={{ fontSize: 11, color: '#ff3b30', fontWeight: 600 }}>ECG falló ✗</span> :
                                                              <span style={{ fontSize: 11, color: '#007aff' }}>ECG ✓</span>
                                  )}
                                </div>
                                <p style={{ fontSize: 10, color: 'var(--text-fainter)', margin: 0, fontFamily: 'monospace' }}>
                                  {s.hrFile?.name}{s.rrFile ? ` + ${s.rrFile.name}` : ''}{s.ecgFile ? ` + ${s.ecgFile.name}` : ''}
                                </p>
                              </>
                            ) : (
                              <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>{s.hrFile.name}</p>
                            )}
                            {s.status === 'error' && s.error && (
                              <p style={{ fontSize: 11, color: cfg.color, margin: '4px 0 0' }}>{s.error}</p>
                            )}
                            {/* Dupe resolution UI */}
                            {s.status === 'dupe' && (
                              <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: 'var(--text-faint)', marginRight: 4 }}>Ya existe en el historial:</span>
                                <button
                                  onClick={() => setDupeAction(i, 'skip')}
                                  style={{ fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontFamily: 'inherit', background: s.dupeAction === 'skip' ? 'var(--btn-strong)' : 'var(--chip-bg)', color: s.dupeAction === 'skip' ? 'var(--seg-active-text-inv)' : 'var(--text)' }}>
                                  No importar
                                </button>
                                <button
                                  onClick={() => setDupeAction(i, 'replace')}
                                  style={{ fontSize: 11, fontWeight: 500, border: '1px solid var(--field-border)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', background: s.dupeAction === 'replace' ? '#ff3b3015' : 'transparent', color: s.dupeAction === 'replace' ? '#ff3b30' : 'var(--text-soft)' }}>
                                  Reemplazar
                                </button>
                              </div>
                            )}
                          </div>
                          {s.status !== 'dupe' && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.color+'18', borderRadius: 8, padding: '3px 8px', flexShrink: 0 }}>{cfg.label}</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

            </div>

            {/* Modal footer */}
            <div style={{ padding: '12px 20px 20px', borderTop: '1px solid var(--border-soft)', flexShrink: 0 }}>
              {allParsed && (importing || readyCount > 0 || dupeCount > 0) && !batchDone && (
                <>
                  {!importing && dupeCount > 0 && (
                    <p style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center', margin: '0 0 10px' }}>
                      {dupeCount} sesi{dupeCount !== 1 ? 'ones ya existen' : 'ón ya existe'} — elegí qué hacer con cada una
                    </p>
                  )}
                  {!importing && dupeCount > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button onClick={() => setAllDupes('replace')} style={{ flex: 1, fontSize: 12, fontWeight: 600, border: '1px solid #ff3b3040', borderRadius: 8, padding: '7px 0', cursor: 'pointer', fontFamily: 'inherit', background: '#ff3b3010', color: '#ff3b30' }}>Reemplazar todas</button>
                      <button onClick={() => setAllDupes('skip')} style={{ flex: 1, fontSize: 12, fontWeight: 600, border: '1px solid var(--field-border)', borderRadius: 8, padding: '7px 0', cursor: 'pointer', fontFamily: 'inherit', background: 'var(--chip-bg)', color: 'var(--text)' }}>No importar ninguna</button>
                    </div>
                  )}
                  {importing ? (() => {
                    // Stable totals come from importBus (readyCount shrinks as items finish)
                    const total = importBus.state?.total || (doneCount + readyCount) || 1
                    const done  = importBus.state?.done  ?? doneCount
                    const pct   = Math.min(100, Math.round(done / total * 100))
                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Subiendo sesiones…</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#007aff', fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: 'var(--chip-bg)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: 'linear-gradient(90deg, #007aff, #5ac8fa)', transition: 'width 0.4s ease' }} />
                        </div>
                        <p style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center', margin: '10px 0 0' }}>
                          Podés cambiar de pestaña — la subida sigue en segundo plano
                        </p>
                      </div>
                    )
                  })() : (
                  <button onClick={importAll} disabled={readyCount === 0}
                    style={{ width: '100%', padding: '14px 0', background: readyCount === 0 ? 'var(--track)' : 'var(--btn-strong)', color: readyCount === 0 ? 'var(--text-faint)' : '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: readyCount === 0 ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    {readyCount === 0 ? 'Nada para importar'
                      : `Importar ${readyCount} sesi${readyCount !== 1 ? 'ones' : 'ón'}`}
                  </button>
                  )}
                </>
              )}
              {allParsed && !importing && readyCount === 0 && dupeCount === 0 && !batchDone && (
                <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-faint)', margin: 0 }}>
                  Sin sesiones nuevas para importar.
                </p>
              )}
              {batchDone && (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#34c759', marginBottom: 4 }}>✓ {doneCount} sesi{doneCount !== 1 ? 'ones' : 'ón'} importada{doneCount !== 1 ? 's' : ''}</p>
                  {(skippedCount > 0 || errorCount > 0) && <p style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 10 }}>{skippedCount > 0 && `${skippedCount} duplicada${skippedCount !== 1 ? 's' : ''}`}{skippedCount > 0 && errorCount > 0 && ' · '}{errorCount > 0 && `${errorCount} con error`}</p>}
                  <button onClick={reset} style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 10, padding: '8px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)' }}>Cerrar</button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  )
}

// ── Session classification ────────────────────────────────────────────────────

function detectSessionType(seg) {
  const hour       = new Date(seg.startTime).getHours()
  const durMin     = (seg.endTime - seg.startTime) / 60000
  const avgHr      = seg.avg_hr ?? 0
  const maxHr      = seg.max_hr ?? 0

  if ((hour >= 22 || hour < 8) && avgHr > 0 && avgHr < 82 && durMin > 20) return 'sueño'
  if (avgHr > 95 || maxHr > 140) return 'entrenamiento'
  return 'salud'
}

const SESSION_TYPES = {
  all:           { label: 'Todos' },
  sueño:         { label: 'Sueño' },
  entrenamiento: { label: 'Entrenamiento' },
  salud:         { label: 'Salud general' },
}

// ── Clinical alerts ───────────────────────────────────────────────────────────


// ── Personas tab ──────────────────────────────────────────────────────────────

// ── Personas tab ──────────────────────────────────────────────────────────────

const API_PERSONAS = apiBase

const DURATIONS = [
  { label: '4 horas',     value: 4 },
  { label: '8 horas',     value: 8 },
  { label: '24 horas',    value: 24 },
  { label: '1 semana',    value: 168 },
  { label: 'Indefinido',  value: 0 },
]

function PersonasTab({ token, user, inline }) {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  const [followers, setFollowers] = useState([])
  const [doctorLinks, setDoctorLinks] = useState([])
  const [name,     setName]     = useState('')
  const [email,    setEmail]    = useState('')
  const [hours,    setHours]    = useState(0)      // 0 = indefinido
  const [loading,  setLoading]  = useState(false)
  const [copied,   setCopied]   = useState(null)
  const [error,    setError]    = useState(null)
  const [liveNow,  setLiveNow]  = useState(null)   // { total, byLink } — quién mira AHORA
  const [shareType, setShareType] = useState('live')   // 'live' | 'doctor'
  const [editingId, setEditingId] = useState(null)     // ID del acceso que se está editando
  const [editingText, setEditingText] = useState('')   // Texto temporal de edición

  const plan      = user?.plan ?? 'free'
  const canInvite = plan !== 'free'

  const load = async () => {
    try {
      const resLive = await fetch(`${API_PERSONAS}/followers`, { headers: { Authorization: `Bearer ${token}` } })
      if (resLive.ok) setFollowers(await resLive.json())
      const resDoc = await fetch(`${API_PERSONAS}/doctor-links`, { headers: { Authorization: `Bearer ${token}` } })
      if (resDoc.ok) setDoctorLinks(await resDoc.json())
    } catch (e) {
      console.error('Error loading accesses:', e)
    }
  }

  const handleSaveEdit = async (item, newText) => {
    const txt = newText?.trim() || ''
    if (!txt) {
      setEditingId(null)
      return
    }
    try {
      const endpoint = item.type === 'live' ? `/followers/${item.id}` : `/doctor-links/${item.id}`
      const body = item.type === 'live' ? { name: txt } : { label: txt }
      const res = await fetch(`${API_PERSONAS}${endpoint}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      })
      if (res.ok) {
        setEditingId(null)
        setEditingText('')
        await load()
      }
    } catch (e) {
      console.error('Error saving access name edit:', e)
    }
  }

  useEffect(() => { load() }, [])

  // Espectadores en vivo - poll cada 5 s
  useEffect(() => {
    let alive = true
    const poll = () => fetch(`${API_PERSONAS}/live/viewers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive && d) setLiveNow(d) })
      .catch(() => {})
    poll()
    const iv = setInterval(poll, 5000)
    return () => { alive = false; clearInterval(iv) }
  }, [token])

  const nowFor = (f) => liveNow?.byLink?.find(b => String(b.followerId) === String(f.id))?.viewers ?? 0

  const handleCreate = async () => {
    setLoading(true); setError(null)
    try {
      if (shareType === 'live') {
        if (!email.trim()) throw new Error('El email es obligatorio para transmisiones en vivo.')
        const res = await fetch(`${API_PERSONAS}/followers/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ email: email.trim(), name: name.trim() || email.trim(), expiresHours: hours || null }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || 'Error al crear acceso en vivo.')
      } else {
        // shareType === 'doctor'
        // Map hours to days: 4h->1d, 8h->1d, 24h->1d, 168h->7d, 0->3650d (indefinido)
        const days = hours === 168 ? 7 : hours === 24 ? 1 : hours === 8 ? 1 : hours === 4 ? 1 : hours === 0 ? 3650 : Math.max(1, Math.round(hours / 24))
        const res = await fetch(`${API_PERSONAS}/doctor-links`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ label: name.trim() || email.trim() || 'Acceso médico', days }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || 'Error al crear acceso médico.')
      }
      setName(''); setEmail(''); setHours(0)
      await load()
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const handleRemove = async (item) => {
    const label = item.label || item.email || 'este acceso'
    const ok = window.confirm(`¿Estás seguro de que querés eliminar el acceso para "${label}"?`)
    if (!ok) return
    try {
      const endpoint = item.type === 'live' ? `/followers/${item.id}` : `/doctor-links/${item.id}`
      await fetch(`${API_PERSONAS}${endpoint}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      await load()
    } catch (e) {
      console.error('Error removing access:', e)
    }
  }

  const copy = (link, id) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(link)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = link
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setCopied(id); setTimeout(() => setCopied(null), 2000)
    } catch (e) {
      console.error('Copy fallback failed:', e)
    }
  }

  const fmtExpiryLabel = (item) => {
    if (!item.expires_at) return 'Acceso indefinido'
    const now = Math.floor(Date.now() / 1000)
    if (item.expires_at < now) return 'Expirado'
    const h = Math.floor((item.expires_at - now) / 3600)
    if (item.type === 'doctor') {
      const d = Math.round(h / 24)
      return d > 365 ? 'Acceso indefinido' : d < 1 ? 'Expira hoy' : `Expira en ${d} día${d !== 1 ? 's' : ''}`
    }
    return h < 1 ? 'Expira en menos de 1h' : h < 24 ? `Expira en ${h}h` : `Expira en ${Math.floor(h/24)}d`
  }

  const getLink = (item) => {
    if (item.type === 'live') {
      return `${window.location.origin}/live?token=${item.token}&owner=${user?.id ?? ''}`
    }
    return `${window.location.origin}/doctor/${item.token}`
  }

  const allLinks = [
    ...followers.map(f => ({ ...f, type: 'live' })),
    ...doctorLinks.map(l => ({ ...l, type: 'doctor', name: l.label, email: 'Médico' }))
  ].sort((a, b) => {
    const aTime = a.expires_at ? a.expires_at : Infinity
    const bTime = b.expires_at ? b.expires_at : Infinity
    return bTime - aTime // Indefinites first, then soonest expiry
  })

  const totalOpens = allLinks.reduce((sum, item) => sum + (item.open_count ?? 0), 0)

  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 18, padding: '16px 20px',
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, width: '100%',
      boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
    }}>
      {/* Columna Izquierda: Formulario de Compartir */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, margin: 0 }}>Generar nuevo acceso</p>
        
        {/* Selector de tipo como dropdown al principio del formulario */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>Tipo de acceso</p>
          <select value={shareType} onChange={e => setShareType(e.target.value)}
            style={{ width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', color: 'var(--text)', fontWeight: 600 }}>
            <option value="live">Transmisión en vivo</option>
            <option value="doctor">Historial médico (1 año)</option>
          </select>
        </div>

        {/* Explicación dinámica de la diferencia directamente abajo del campo */}
        <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '0 0 2px 0', lineHeight: 1.4 }}>
          {shareType === 'doctor' ? (
            <span><strong>Historial médico:</strong> Tu médico verá todo tu historial del último año (sesiones, pulso y HRV) en modo solo lectura, sin contraseñas ni cuentas.</span>
          ) : (
            <span><strong>Transmisión en vivo:</strong> Permite a otros ver tu transmisión de pulso y ECG en tiempo real en un monitor flotante interactivo.</span>
          )}
        </p>

        {!canInvite ? (
          <div style={{ background: 'var(--inset-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
            <p style={{ fontSize: 12.5, color: '#ff453a', margin: 0, lineHeight: 1.45 }}>[AVISO] El plan <strong>Free</strong> no permite compartir acceso. Actualizá a Premium.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder={shareType === 'live' ? "Nombre del receptor (opcional)" : "Nombre (ej: Dr. García)"}
                style={{ width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', color: 'var(--text)' }} />
              
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder={shareType === 'live' ? "Email del receptor *" : "Email del receptor (opcional)"} type="email"
                style={{ width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', color: 'var(--text)' }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>Duración del acceso</p>
                <select value={hours} onChange={e => setHours(Number(e.target.value))}
                  style={{ width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', color: 'var(--text)' }}>
                  {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            </div>

            {error && <p style={{ fontSize: 12, color: '#ff453a', margin: '4px 0 0' }}>{error}</p>}

            <button onClick={handleCreate} disabled={loading || (shareType === 'live' && !email.trim())} style={{
              background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10, marginTop: 4,
              padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', opacity: loading || (shareType === 'live' && !email.trim()) ? 0.6 : 1, transition: 'all 0.15s'
            }}>
              {loading ? 'Generando…' : 'Generar link de acceso'}
            </button>
          </div>
        )}
      </div>

      {/* Columna Derecha: Lista de Accesos Activos Deslizable */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, margin: 0 }}>
            Accesos activos ({allLinks.length})
          </p>
          {(liveNow?.total ?? 0) > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 800, borderRadius: 20, padding: '1px 8px',
              color: '#1d9b4e', background: '#e8f8ee', border: '1px solid #b5e8c8',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#30d158', animation: 'blink 2s ease-in-out infinite' }} />
              {liveNow.total} en vivo
            </span>
          )}
        </div>

        {allLinks.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '10px 0' }}>Todavía no has generado ningún acceso.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '290px', overflowY: 'auto', paddingRight: '4px' }}>
            {allLinks.map(item => {
              const expired = item.expires_at && item.expires_at < Math.floor(Date.now() / 1000)
              const link = getLink(item)
              const views = item.open_count ?? 0
              const now = item.type === 'live' ? nowFor(item) : 0
              return (
                <div key={item.id} style={{ background: 'var(--inset-bg)', border: '1px solid var(--border-soft)', borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: now > 0 ? '#e8f8ee' : 'var(--track)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: now > 0 ? '1px solid #b5e8c8' : 'none' }}>
                    {expired ? <IconLock size={15} color="var(--text-faint)" /> : item.type === 'live' ? <IconRadio size={15} color="#007aff" /> : <IconStethoscope size={15} color="#34c759" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingId === item.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, width: '100%' }}>
                        <input
                          value={editingText}
                          onChange={e => setEditingText(e.target.value)}
                          onBlur={e => handleSaveEdit(item, e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(item, e.target.value)
                            } else if (e.key === 'Escape') {
                              setEditingId(null)
                              setEditingText('')
                            }
                          }}
                          autoFocus
                          style={{ background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: 6, padding: '3px 8px', fontSize: 12, outline: 'none', color: 'var(--text)', width: '100%', maxWidth: '160px', fontWeight: 600 }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <p style={{ fontSize: 12.5, fontWeight: 700, color: expired ? 'var(--text-muted)' : 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name || item.email}
                        </p>
                        <button onClick={() => { setEditingId(item.id); setEditingText(item.name || '') }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-fainter)', fontSize: 10, padding: '2px 4px', transition: 'color 0.15s', fontWeight: 600 }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-faint)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-fainter)'}
                          title="Editar nombre"
                        >Editar</button>
                      </div>
                    )}
                    <p style={{ fontSize: 10.5, color: 'var(--text-faint)', margin: '1px 0 0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{fmtExpiryLabel(item)}</span>
                      <span>•</span>
                      <span style={{ color: now > 0 ? '#1d9b4e' : 'var(--text-faint)', fontWeight: now > 0 ? 800 : 'inherit' }}>
                        {now > 0 ? `● MIRANDO EN VIVO (${now} ${now === 1 ? 'espectador' : 'espectadores'})` : `${views} ${views === 1 ? 'apertura' : 'aperturas'}`}
                      </span>
                    </p>
                  </div>
                  {!expired && (
                    <button onClick={() => copy(link, item.id)} style={{
                      background: copied === item.id ? (isDark ? 'rgba(48,209,88,0.15)' : '#e8f8ee') : 'var(--card-bg)',
                      color: copied === item.id ? '#30d158' : 'var(--text-faint)',
                      border: `1px solid ${copied === item.id ? '#30d158' : 'var(--border)'}`,
                      borderRadius: 8, padding: '6px 10px', fontSize: 11.5,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, transition: 'all 0.15s',
                    }}>
                      {copied === item.id ? '✓ Copiado' : 'Copiar'}
                    </button>
                  )}
                  <button onClick={() => handleRemove(item)} style={{
                    background: 'none', border: 'none', color: '#ff453a',
                    fontSize: 16, cursor: 'pointer', padding: '2px 4px', flexShrink: 0,
                  }} title="Eliminar">✕</button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ── History tab ───────────────────────────────────────────────────────────────

function SourceBadge({ source, active }) {
  const meta = getSourceMeta(source)
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      color: active ? '#a0a0a8' : meta.color,
      background: active ? 'rgba(255,255,255,0.08)' : `${meta.color}18`,
      borderRadius: 5, padding: '2px 6px',
      whiteSpace: 'nowrap',
    }}>
      {meta.icon} {meta.label}
    </span>
  )
}

function SessionCard({ seg, onClick, active, onDelete }) {
  const dur     = fmtDurationMs(seg.endTime - seg.startTime)
  const zone    = seg.avg_hr ? getZone(seg.avg_hr) : null
  const dateStr = new Date(seg.startTime).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })
  const timeStr = fmtTime(seg.startTime)

  return (
    <div onClick={onClick} style={{
      background: active ? 'var(--track)' : 'var(--card-bg)',
      borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
      border: `1.5px solid ${active ? (zone?.color ?? 'var(--text-soft)') : 'var(--border)'}`,
      transition: 'all 0.15s',
    }}>
      <div style={{ height: 3, background: zone?.color ?? '#d1d1d6' }} />
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{dateStr}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeStr} · {dur}</span>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (window.confirm('¿Seguro que querés eliminar esta sesión de la base de datos?')) {
                    onDelete(seg.id)
                  }
                }}
                style={{ background: 'none', border: 'none', color: '#ff453a', fontSize: 13, cursor: 'pointer', padding: '0 4px', opacity: 0.6 }}
                title="Eliminar sesión"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
          <div style={{ lineHeight: 1 }}>
            <span style={{ fontSize: 44, fontWeight: 800, color: zone?.color ?? 'var(--text)', letterSpacing: -2 }}>
              {seg.avg_hr ?? '—'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>bpm</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingBottom: 3 }}>
            {zone && (
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: zone.color, background: `${zone.color}18`, borderRadius: 5, padding: '2px 7px', alignSelf: 'flex-start' }}>
                {zone.label}
              </span>
            )}
            {seg.rmssd != null && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>HRV {Math.round(seg.rmssd)} ms</span>
            )}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, paddingBottom: 3 }}>
            {seg.max_hr && <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>máx {seg.max_hr}</span>}
            {seg.min_hr && <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>mín {seg.min_hr}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Calendar picker ───────────────────────────────────────────────────────────

function DeltaBadge({ cur, prev, betterWhen = 'neutral', fmt = v => `${v}` }) {
  if (cur == null || prev == null) return <span style={{ fontSize: 10, color: 'var(--text-fainter)' }}>sin comparación</span>
  const d = cur - prev
  if (d === 0) return <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>= sem. ant.</span>
  const up   = d > 0
  const good = betterWhen === 'neutral' ? null : (betterWhen === 'up' ? up : !up)
  const color = good == null ? 'var(--text-faint)' : good ? '#34c759' : '#ff3b30'
  return <span style={{ fontSize: 10, fontWeight: 700, color }}>{up ? '▲' : '▼'} {fmt(Math.abs(d))}</span>
}

function PeriodReport({ mode, periodKey, label, sessions, prevSessions, histZones, histMaxHr }) {
  const [hoveredKey, setHoveredKey] = useState(null)
  const [showAnalysis, setShowAnalysis] = useState(false)
  const cur  = periodAgg(sessions)
  const prev = periodAgg(prevSessions)
  const hasPrev = prevSessions.length > 0
  const prevWord = mode === 'week' ? 'semana anterior' : 'mes anterior'

  // Sub-period buckets: per-day for a week, per-day (all days of month) for a month.
  const buckets = (() => {
    if (mode === 'week') {
      const out = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(periodKey + 'T12:00:00'); d.setDate(d.getDate() + i)
        const key = localDateKey(d.getTime())
        const ds  = sessions.filter(s => localDateKey(s.startTime) === key)
        out.push({ key, label: d.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', ''), sub: String(d.getDate()), ...periodAgg(ds) })
      }
      return out
    }
    // mode === 'month' -> year, month from YYYY-MM
    const [yrStr, moStr] = periodKey.split('-')
    const year = Number(yrStr), month = Number(moStr)
    const daysInMonth = new Date(year, month, 0).getDate()
    const out = []
    for (let i = 0; i < daysInMonth; i++) {
      const d = new Date(year, month - 1, i + 1, 12)
      const key = localDateKey(d.getTime())
      const ds  = sessions.filter(s => localDateKey(s.startTime) === key)
      out.push({
        key,
        ms: d.getTime(),
        label: (i === 0 || (i + 1) % 5 === 0) ? String(i + 1) : '',
        sub: '', // no sub label to save vertical space
        future: key > localDateKey(Date.now()),
        ...periodAgg(ds)
      })
    }
    return out
  })()
  const maxBucketMin = Math.max(1, ...buckets.map(b => b.totalMin))

  const rmssdTone = !cur.avgRmssd ? null
    : cur.avgRmssd > 50 ? { t: 'Sistema nervioso tranquilo — buena recuperación', c: '#34c759' }
    : cur.avgRmssd > 30 ? { t: 'Balance equilibrado', c: '#007aff' }
    : cur.avgRmssd > 15 ? { t: 'Algo de estrés acumulado — considerá descansar más', c: '#ff9f0a' }
    : { t: 'Alta activación sostenida', c: '#ff3b30' }

  // Comparison cards — value now + delta vs previous period.
  const cards = [
    { label: 'Horas registradas', value: fmtHmShort(cur.totalMin), cur: cur.totalMin,   prev: prev.totalMin,   betterWhen: 'neutral', fmt: fmtHmShort, color: 'var(--text)' },
    { label: 'Días activos',      value: cur.activeDays,           cur: cur.activeDays,  prev: prev.activeDays,  betterWhen: 'up',      fmt: v => `${v}`, color: 'var(--text)' },
    { label: 'FC prom',           value: cur.avgHr ?? '—',         cur: cur.avgHr,       prev: prev.avgHr,       betterWhen: 'neutral', fmt: v => `${v}`, color: 'var(--text)' },
    { label: 'FC reposo',         value: cur.restHr ?? '—',        cur: cur.restHr,      prev: prev.restHr,      betterWhen: 'down',    fmt: v => `${v}`, color: '#34c759' },
    { label: 'FC máx',            value: cur.maxHr ?? '—',         cur: cur.maxHr,       prev: prev.maxHr,       betterWhen: 'neutral', fmt: v => `${v}`, color: '#ff3b30' },
    { label: 'HRV prom',          value: cur.avgRmssd ?? '—',      cur: cur.avgRmssd,    prev: prev.avgRmssd,    betterWhen: 'up',      fmt: v => `${v}`, color: '#007aff' },
  ]

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark'
  const scale = Math.max(STREAK_MIN_MIN * 1.6, ...buckets.map(b => b.totalMin))
  const goalY = Math.round(88 * STREAK_MIN_MIN / scale)

  const syntheticReadings = useMemo(() => {
    const out = []
    sessions.forEach(s => {
      const avg = s.avg_hr || s.avgHr
      const min = s.min_hr || s.minHr
      const max = s.max_hr || s.maxHr
      const durationMin = Math.round((s.endTime - s.startTime) / 60000)
      if (!avg || durationMin <= 0) return
      
      if (min) out.push({ hr: min })
      if (max) out.push({ hr: max })
      const rem = Math.max(0, durationMin - (min ? 1 : 0) - (max ? 1 : 0))
      for (let i = 0; i < rem; i++) {
        out.push({ hr: avg })
      }
    })
    return out
  }, [sessions])

  return (
    <div>
      {/* ── Sección 1: FC ── */}
      {(cur.avgHr || cur.maxHr || cur.restHr) && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, marginTop: 4 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>1</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Frecuencia Cardíaca</p>
              <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Resumen del período</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 16 }}>
            {cur.avgHr && <MetricCard defKey="avg" value={cur.avgHr} />}
            {cur.restHr && <MetricCard defKey="min" value={cur.restHr} color="#007aff" />}
            {cur.maxHr && <MetricCard defKey="max" value={cur.maxHr} color="#ff3b30" />}
          </div>
        </>
      )}

      {/* ── Sección 2: Distribución de FC y zonas de entrenamiento ── */}
      {cur.avgHr && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, marginTop: 4 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>2</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Distribución de FC y zonas de entrenamiento</p>
              <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Rango y acumulación en zonas del período</p>
            </div>
          </div>
          
          <HRDistributionChart readings={syntheticReadings} />

          {cur.maxHr && histMaxHr && histZones && sessions.some(s => s.avg_hr) && (() => {
            // Calculate minutes spent in each zone based on second-to-second synthetic readings
            const zoneData = histZones.map(z => {
              const zoneMin = syntheticReadings.filter(r => r.hr >= z.lo && r.hr < z.hi).length
              return { ...z, min: zoneMin }
            })

            const totalMin = zoneData.reduce((a, z) => a + z.min, 0)
            
            return (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', margin: '0 0 10px' }}>
                  Zonas de entrenamiento <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>· segundo a segundo</span>
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {zoneData.map(z => {
                    const pct = totalMin ? Math.round(z.min / totalMin * 100) : 0
                    if (window.innerWidth <= 640) {
                      return (
                        <div key={z.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                          {/* Fila 1: Nombre y BPM a la izquierda, Minutos y % a la derecha */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: z.color, display: 'inline-block' }} />
                              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>{z.num ? `${z.num}. ` : ''}{z.name}</span>
                              <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>({z.lo}–{z.hi} BPM)</span>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>
                              {pct}% <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-faint)' }}>({z.min} min)</span>
                            </span>
                          </div>
                          {/* Fila 2: Barra horizontal de ancho completo */}
                          <div style={{ width: '100%', height: 6, background: 'var(--chip-bg)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: z.color, borderRadius: 3 }} />
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div key={z.key} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
                        {/* Columna 1: Badge + Nombre */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '150px', flexShrink: 0, minWidth: 120 }}>
                          {z.num ? (
                            <div style={{ width: 18, height: 18, borderRadius: '50%', background: z.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 9, fontWeight: 900, color: '#fff' }}>{z.num}</span>
                            </div>
                          ) : (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, display: 'inline-block', flexShrink: 0, margin: '0 5px' }} />
                          )}
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.name}</span>
                        </div>

                        {/* Columna 2: Rangos de pulso */}
                        <div style={{ width: '85px', flexShrink: 0, fontSize: 10.5, color: 'var(--text-faint)' }}>
                          {z.lo}–{z.hi} BPM
                        </div>

                        {/* Columna 3: Barra de progreso horizontal flexible */}
                        <div style={{ flex: 1, minWidth: 60, height: 7, background: 'var(--chip-bg)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: z.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
                        </div>

                        {/* Columna 4: Porcentajes y minutos */}
                        <div style={{ width: '95px', flexShrink: 0, textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                          {pct}% <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-faint)' }}>({z.min} min)</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </>
      )}

      {/* Toggle Estadísticas Avanzadas */}
      <button
        onClick={() => setShowAnalysis(v => !v)}
        style={{ width: '100%', background: showAnalysis ? 'var(--btn-strong)' : 'var(--chip-bg)', color: showAnalysis ? 'var(--seg-active-text-inv)' : 'var(--text)', border: 'none', borderRadius: 12, padding: '11px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.15s', marginTop: 12 }}>
        <span>Estadísticas Avanzadas</span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>{showAnalysis ? '▲ ocultar' : '▼ ver HRV · Sistema Nervioso'}</span>
      </button>

      {/* ── Sección 3: HRV ── */}
      {showAnalysis && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, marginTop: 12 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>3</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Sistema Nervioso (HRV)</p>
              <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>Variabilidad cardíaca del período</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
            <MetricCard defKey="rmssd" value={cur.avgRmssd} color={cur.avgRmssd ? (cur.avgRmssd > 50 ? '#34c759' : cur.avgRmssd > 30 ? '#007aff' : cur.avgRmssd > 15 ? '#ff9f0a' : '#ff453a') : undefined} />
            <MetricCard defKey="sdnn" value={cur.avgSdnn} />
            <MetricCard defKey="pnn50" value={cur.avgPnn50} />
            <MetricCard defKey="resp" value={cur.avgResp} />
          </div>
        </>
      )}
    </div>
  )
}


// Interactive label drawn INSIDE the chart, over a detected period: asks "¿Estabas X acá?"
// with clickable Sí/No (SVG). Recharts calls this with the band's pixel viewBox.
function renderPendingBandLabel(props, ev, onYes, onNo) {
  const vb = props?.viewBox
  if (!vb || vb.width == null) return null
  const cx = vb.x + vb.width / 2
  const topY = vb.y + 13
  const icon = (ACT_OPTS.find(o => o[0] === ev.activity)?.[1]) || '•'
  const verb = ACT_GERUND[ev.activity] || 'haciendo algo'
  return (
    <g>
      <text x={cx} y={topY} textAnchor="middle" fontSize={11} fontWeight={700} fill="#1c1c1e">{icon} ¿Estabas {verb} acá?</text>
      <g onClick={onYes} style={{ cursor: 'pointer' }}>
        <rect x={cx - 50} y={topY + 7} width={46} height={20} rx={6} fill="#34c759" />
        <text x={cx - 27} y={topY + 21} textAnchor="middle" fontSize={11} fontWeight={700} fill="#ffffff">Sí</text>
      </g>
      <g onClick={onNo} style={{ cursor: 'pointer' }}>
        <rect x={cx + 4} y={topY + 7} width={46} height={20} rx={6} fill="#ff3b30" />
        <text x={cx + 27} y={topY + 21} textAnchor="middle" fontSize={11} fontWeight={700} fill="#ffffff">No</text>
      </g>
    </g>
  )
}

// Heuristic event detector (v2, high-accuracy): finds time ranges that match
// Sleep, Walk (active/Caminar) or Exercise (Entrenar) with near-100% accuracy using steps + local hour.
function detectActivityEvents(readings, segments) {
  const pts = (readings || []).filter(r => r.hr > 0).map(r => ({ t: r.time, hr: r.hr })).sort((a, b) => a.t - b.t)
  if (pts.length < 20) return []
  // Personal baseline (~resting) = 20th percentile
  const hrSorted = pts.map(p => p.hr).slice().sort((a, b) => a - b)
  const baseline = hrSorted[Math.floor(hrSorted.length * 0.2)] || hrSorted[0]

  const WIN = 90000
  const feat = pts.map((p, i) => {
    let lo = p.hr, hi = p.hr, sum = 0, cnt = 0
    for (let j = i; j >= 0 && p.t - pts[j].t <= WIN; j--) { const v = pts[j].hr; if (v < lo) lo = v; if (v > hi) hi = v; sum += v; cnt++ }
    for (let j = i + 1; j < pts.length && pts[j].t - p.t <= WIN; j++) { const v = pts[j].hr; if (v < lo) lo = v; if (v > hi) hi = v; sum += v; cnt++ }
    
    // Interpolate steps/minute for this reading point from segments
    let stepsPerMin = 0
    const activeSeg = (segments || []).find(s => p.t >= s.startTime && p.t <= s.endTime)
    if (activeSeg && activeSeg.steps > 0) {
      const durMin = (activeSeg.endTime - activeSeg.startTime) / 60000
      stepsPerMin = durMin > 0 ? activeSeg.steps / durMin : 0
    }

    return { t: p.t, hr: p.hr, range: hi - lo, mean: sum / cnt, stepsRate: stepsPerMin }
  })

  const events = []
  const GAP = 600000, MINEX = 600000, MINWALK = 480000, MINSL = 1800000 

  // 1. Detect Exercise (Elevated volatile HR OR sustained very high, with low steps p.ej. musculación)
  const isEx = f => {
    const hrHigh = f.mean >= Math.max(115, baseline + 35)
    const hrVolatile = f.mean >= baseline + 20 && f.range >= 15
    return (hrHigh || hrVolatile) && f.stepsRate < 35
  }
  let s = null, last = null
  const flushEx = () => { if (s != null && last != null && last - s >= MINEX) events.push({ activity: 'exercise', startMs: s, endMs: last }); s = null }
  for (const f of feat) {
    if (isEx(f)) { if (s == null) s = f.t }
    else if (s != null) flushEx()
    if (s != null && last != null && f.t - last > GAP) flushEx()
    last = f.t
  }
  flushEx()

  // 2. Detect Walk (Caminar): Moderate stable HR + high continuous steps
  const isWalk = f => f.mean >= baseline + 12 && f.range < 12 && f.stepsRate >= 35
  let sw = null, lw = null
  const flushWalk = () => { if (sw != null && lw != null && lw - sw >= MINWALK) events.push({ activity: 'active', startMs: sw, endMs: lw }); sw = null }
  for (const f of feat) {
    if (isWalk(f)) { if (sw == null) sw = f.t }
    else if (sw != null) flushWalk()
    if (sw != null && lw != null && f.t - lw > GAP) flushWalk()
    lw = f.t
  }
  flushWalk()

  // 3. Detect Sleep (Dormir): Night hours + low stable HR + steps = 0
  let ss = null, sl = null, prev = null
  const flushSleep = () => { if (ss != null && sl != null && sl - ss >= MINSL) events.push({ activity: 'sleep', startMs: ss, endMs: sl }); ss = null }
  for (const f of feat) {
    const h = new Date(f.t).getHours()
    const night = h >= 22 || h < 9
    if (night && f.hr <= baseline + 8 && f.range <= 10 && f.stepsRate === 0) { if (ss == null) ss = f.t }
    else if (ss != null) flushSleep()
    if (ss != null && prev != null && f.t - prev > 300000) flushSleep()
    prev = f.t; sl = f.t
  }
  flushSleep()

  return events.sort((a, b) => a.startMs - b.startMs)
}

function SessionEventsCard({ pending, annotations, visibleRange, onSave, onDelete }) {
  const [note, setNote] = useState('')
  const [editId, setEditId] = useState(null)
  const am = a => ACT_OPTS.find(o => o[0] === a)
  const confirmed = (annotations || []).filter(a => a.status === 'confirmed')
  const pend = pending || []
  const btn = (bg, fg) => ({ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: bg, color: fg })
  // "Change state" = delete + recreate with the new activity (backend has no UPDATE)
  const changeActivity = (a, k) => { onDelete(a.id); onSave({ start_ms: a.start_ms, end_ms: a.end_ms, activity: k, note: a.note || null, status: 'confirmed' }); setEditId(null) }
  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 6 }}>
        <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Actividad y comentarios</p>
        {pend.length > 1 && (
          <button
            onClick={() => {
              if (window.confirm(`¿Querés descartar las ${pend.length} preguntas de actividad automáticas?`)) {
                pend.forEach(e => onSave({ start_ms: e.startMs, end_ms: e.endMs, activity: e.activity, status: 'rejected' }))
              }
            }}
            style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', outline: 'none' }}
          >
            Descartar todas las preguntas
          </button>
        )}
      </div>

      {/* Preguntas (debajo del gráfico) */}
      {pend.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff8e8', border: '1px solid #ffe1a6', borderRadius: 10, padding: '10px 12px', marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 150 }}>
            {am(e.activity)?.[1]} ¿Estabas <strong>{ACT_GERUND[e.activity] || 'haciendo algo'}</strong> de {fmtTime(e.startMs)} a {fmtTime(e.endMs)}?
          </span>
          <button onClick={() => onSave({ start_ms: e.startMs, end_ms: e.endMs, activity: e.activity, status: 'confirmed' })} style={btn('#34c759', '#fff')}>Sí</button>
          <button onClick={() => onSave({ start_ms: e.startMs, end_ms: e.endMs, activity: e.activity, status: 'rejected' })} style={btn('var(--chip-bg)', 'var(--text-muted)')}>No</button>
        </div>
      ))}

      {/* Períodos confirmados — tocá un recuadro para cambiar o eliminar */}
      {confirmed.map(a => {
        const col = ACT_COLOR[a.activity] || 'var(--text-faint)'
        const open = editId === a.id
        return (
          <div key={a.id} style={{ border: `1.5px solid ${col}55`, background: `${col}0d`, borderRadius: 10, padding: '9px 12px', marginBottom: 8 }}>
            <div onClick={() => setEditId(open ? null : a.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: col, flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1 }}>
                <strong>{a.activity ? am(a.activity)?.[2] : 'Nota'}</strong>
                {a.start_ms != null && <span style={{ color: 'var(--text-faint)' }}> · {fmtTime(a.start_ms)}–{fmtTime(a.end_ms)}</span>}
                {a.note && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}> — “{a.note}”</span>}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#007aff' }}>{open ? 'cerrar' : 'editar'}</span>
            </div>
            {open && (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '0 0 6px' }}>Cambiar a:</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {ACT_OPTS.filter(o => o[0] !== 'other').map(([k, ic, name]) => (
                    <button key={k} onClick={() => changeActivity(a, k)} style={btn(a.activity === k ? (ACT_COLOR[k] || 'var(--btn-strong)') : 'var(--chip-bg)', a.activity === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)')}>{ic} {name}</button>
                  ))}
                </div>
                <button onClick={() => { onDelete(a.id); setEditId(null) }} style={btn('#ffecec', '#ff3b30')}>Eliminar este período</button>
              </div>
            )}
          </div>
        )
      })}

      {/* Marcar un tramo a mano */}
      <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '14px 0 7px' }}>Marcá vos un tramo: movés el selector debajo del gráfico para elegir el rango y tocás la actividad.</p>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
        {ACT_OPTS.filter(o => o[0] !== 'other').map(([k, ic, name]) => (
          <button key={k} disabled={!visibleRange}
            onClick={() => onSave({ start_ms: visibleRange.startMs, end_ms: visibleRange.endMs, activity: k, note: note.trim() || null, status: 'confirmed' })}
            style={{ ...btn(visibleRange ? (ACT_COLOR[k] || 'var(--btn-strong)') : 'var(--track)', visibleRange ? '#fff' : 'var(--text-soft)'), opacity: visibleRange ? 1 : 0.7 }}>{ic} {name}</button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Comentario (opcional)…"
          style={{ flex: 1, minWidth: 170, background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit' }} />
        <button disabled={!visibleRange || !note.trim()}
          onClick={() => { onSave({ start_ms: visibleRange.startMs, end_ms: visibleRange.endMs, note: note.trim(), status: 'confirmed' }); setNote('') }}
          style={btn(visibleRange && note.trim() ? 'var(--btn-strong)' : 'var(--track)', visibleRange && note.trim() ? '#fff' : 'var(--text-soft)')}>
          Solo comentario
        </button>
      </div>
      <p style={{ fontSize: 10.5, color: 'var(--text-soft)', margin: '8px 0 0', lineHeight: 1.5 }}>
        El tramo se marca con color en el gráfico. Tus respuestas y marcas entrenan al sistema.
      </p>
    </div>
  )
}

// ── Carga progresiva: las secciones aparecen de arriba hacia abajo ───────────
// Las tarjetas pesadas (recharts/canvas) se montan escalonadas: el gráfico 1
// aparece primero y el resto va "bajando" — nunca se pinta uno de abajo antes
// que uno de arriba. Mientras espera, muestra un esqueleto del mismo alto.
function Reveal({ order = 1, minHeight = 260, children }) {
  const [show, setShow] = useState(order === 0)
  useEffect(() => {
    if (show) return undefined
    const t = setTimeout(() => setShow(true), 240 * order)
    return () => clearTimeout(t)
  }, [show, order])
  if (show) return children
  return (
    <div aria-hidden style={{
      minHeight, borderRadius: 14, border: '1px solid var(--border-soft)', marginBottom: 18,
      background: 'linear-gradient(100deg, #fafafa 40%, #f1f1f4 50%, #fafafa 60%)',
      backgroundSize: '200% 100%', animation: 'vcShimmer 1.2s linear infinite',
    }} />
  )
}

function HistoryTab({
  token, user, refreshKey,
  periodMode, setPeriodMode,
  selectedDate, setSelectedDate,
  selectedWeek, setSelectedWeek,
  selectedMonth, setSelectedMonth,
  theme
}) {
  // Reacción instantánea al cambio de tema del DOM ([data-theme="dark"])
  const [domTheme, setDomTheme] = useState(() => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark')
  useEffect(() => {
    if (typeof document === 'undefined') return
    const observer = new MutationObserver(() => {
      setDomTheme(document.documentElement.getAttribute('data-theme') || 'dark')
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  const isDark = domTheme === 'dark' || theme === 'dark'
  const [view, setView] = useState('second') // modo del gráfico por latido: 1 s / 1 min / 1 h
  // ── apiBase MUST be first — referenced in deps arrays below ──────────────────
  const userId = user?.id ?? 'anon'

  const [allReadings,    setAllReadings]    = useState([])
  const [segments,       setSegments]       = useState([])

  // Deduplicate segments — same start time ±5 min. When a re-import leaves an old
  // duplicate behind, keep the RICHEST version (one with ECG, then HRV, then longest)
  // so a stale no-ECG copy never hides the good one ("no tiene ECG guardado").
  const deduplicatedSegments = useMemo(() => {
    const richness = s => (s.ecg ? 1e12 : 0) + (s.rmssd ? 1e9 : 0) + (s.endTime - s.startTime)
    const result = []
    const sorted = [...segments].sort((a, b) => a.startTime - b.startTime)
    sorted.forEach(seg => {
      const dupIdx = result.findIndex(s => Math.abs(s.startTime - seg.startTime) < 5 * 60 * 1000)
      if (dupIdx === -1) result.push(seg)
      else if (richness(seg) > richness(result[dupIdx])) result[dupIdx] = seg
    })

    // Virtual split of midnight-crossing sessions
    const splitResult = []
    result.forEach(s => {
      const startLocal = localDateKey(s.startTime)
      const endLocal   = localDateKey(s.endTime)
      if (startLocal !== endLocal && s.endTime > s.startTime) {
        // Split session across midnight
        const sStart = s.startTime
        const sEnd   = s.endTime
        
        // Midnight of the first day in local timezone
        const nextDay = new Date(startLocal + 'T12:00:00')
        nextDay.setDate(nextDay.getDate() + 1)
        const midnight = new Date(localDateKey(nextDay.getTime()) + 'T00:00:00').getTime()
        
        // First part (day 1)
        splitResult.push({
          ...s,
          id: `${s.id}_part1`,
          startTime: sStart,
          endTime: midnight - 1000,
          originalSession: s,
          part: 1
        })
        
        // Second part (day 2)
        splitResult.push({
          ...s,
          id: `${s.id}_part2`,
          startTime: midnight,
          endTime: sEnd,
          originalSession: s,
          part: 2
        })
      } else {
        splitResult.push(s)
      }
    })
    return splitResult
  }, [segments])

  const daySessions = useMemo(() => {
    if (!selectedDate) return []
    const dayStart = new Date(selectedDate + 'T00:00:00').getTime()
    const dayEnd   = new Date(selectedDate + 'T23:59:59').getTime()
    return deduplicatedSegments.filter(s => s.startTime < dayEnd && s.endTime > dayStart)
  }, [selectedDate, deduplicatedSegments])

  const [loading,        setLoading]        = useState(true)
  const [loadErr,        setLoadErr]        = useState(null)
  const [loadingSession, setLoadingSession] = useState(false)
  const [sessionLoadErr, setSessionLoadErr] = useState(null)
  const [histView,       setHistView]       = useState('unified')
  const [activeSegment,  setActiveSegment]  = useState(null)
  const [historyEcgInverted, setHistoryEcgInverted] = useState(false)
  const [ecgLeadsCount, setEcgLeadsCount] = useState(1)
  const [analysisProfile,    setAnalysisProfile]    = useState('biohacker')
  const [annotations, setAnnotations] = useState([])        // time-ranged activity/comment annotations for the selected day
  const [showAnnotations, setShowAnnotations] = useState(true)
  const [sel, setSel] = useState(null)       // on-chart selection / band being edited
  const [weekWindowStart, setWeekWindowStart] = useState(null)
  const [dayWindowStart, setDayWindowStart]   = useState(null)
  const [selNote, setSelNote] = useState('')
  // La tarjeta-resumen del día ("Actividad moderada", "Día tranquilo"…) se
  // muestra unos segundos y se pliega a una línea — no queda eterna en pantalla.
  const [verdictOpen, setVerdictOpen] = useState(true)
  const [typeFilter,     setTypeFilter]     = useState('all')
  const [sinceDays,      setSinceDays]      = useState(9999)
  const chartRef      = useRef(null)
  const dateScrollRef = useRef(null)
  const dayFcChartRef = useRef(null)   // day HR chart (for copy/download)
  const poincareRef   = useRef(null)   // day Poincaré chart (for copy/download)
  const fullViewRef   = useRef(null)   // whole day/week/month report (for copy/download)
  const [fullView, setFullView] = useState('')   // '' | 'copying' | 'saving' | 'copied'
  const [showDeepClinical, setShowDeepClinical] = useState(false)

  // Sincronizar Semana y Mes basándose en el día seleccionado al cambiar de periodMode
  useEffect(() => {
    if (selectedDate) {
      const wk = weekKeyOf(selectedDate)
      const mo = selectedDate.slice(0, 7) // YYYY-MM
      
      // Sincronizar semana
      if (periodMode === 'week') {
        setSelectedWeek(wk)
      }
      // Sincronizar mes
      if (periodMode === 'month') {
        setSelectedMonth(mo)
      }
    }
  }, [periodMode, selectedDate, setSelectedWeek, setSelectedMonth])
  // Annotations are keyed by the selected DAY (date string) so they work at the day level
  useEffect(() => {
    if (!token || !selectedDate) { setAnnotations([]); return }
    fetch(`${apiBase}/sessions/${selectedDate}/annotations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(rows => setAnnotations(rows || []))
      .catch(async () => {
        // Sin señal: anotaciones de la copia local del día (si viajó al teléfono)
        const off = await loadDayOffline(userId, selectedDate).catch(() => null)
        setAnnotations(Array.isArray(off?.annotations) ? off.annotations : [])
      })
  }, [token, apiBase, selectedDate])
  // La tarjeta-resumen del día se muestra 8 s al abrir cada día y se pliega sola
  useEffect(() => {
    setVerdictOpen(true)
    setShowDeepClinical(false)
    const t = setTimeout(() => setVerdictOpen(false), 8000)
    return () => clearTimeout(t)
  }, [selectedDate])
  // Per-day analysis summary (which days I manually labeled) for the calendar.
  const [annotatedDays, setAnnotatedDays] = useState({})   // { 'YYYY-MM-DD': { n, activities:[] } }
  const [summaryTick,   setSummaryTick]   = useState(0)    // bump to refetch after save/delete
  useEffect(() => {
    if (!token) { setAnnotatedDays({}); return }
    fetch(`${apiBase}/annotations/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(rows => { const m = {}; (rows || []).forEach(x => { m[x.session_id] = { n: x.n, activities: x.activities || [] } }); setAnnotatedDays(m) })
      .catch(() => setAnnotatedDays({}))
  }, [token, apiBase, summaryTick])
  const saveAnnotation = useCallback((payload) => {
    if (!selectedDate) return
    fetch(`${apiBase}/sessions/${selectedDate}/annotations`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }).then(r => r.ok ? r.json() : null).then(res => { if (res?.id) { setAnnotations(a => [...a, { id: res.id, ...payload }]); setSummaryTick(t => t + 1) } }).catch(() => {})
  }, [token, apiBase, selectedDate])
  const deleteAnnotation = useCallback((annId) => {
    setAnnotations(a => a.filter(x => x.id !== annId))
    setSummaryTick(t => t + 1)
    fetch(`${apiBase}/annotations/${annId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
  }, [token, apiBase])

  // Full-view export (copy/save the whole day/week/month). Declared AFTER the
  // period state it reads, so the useCallback dep arrays don't hit a TDZ.
  const fullViewName = useCallback(() => {
    if (periodMode === 'week')  return `manabi-semana-${selectedWeek  || ''}`
    if (periodMode === 'month') return `manabi-mes-${selectedMonth || ''}`
    return `manabi-dia-${selectedDate || ''}`
  }, [periodMode, selectedDate, selectedWeek, selectedMonth])
  const downloadFullView = useCallback(async () => {
    if (!fullViewRef.current) return
    setFullView('saving')
    try {
      const dataUrl = await domToPng(fullViewRef.current, { backgroundColor: 'var(--field-bg)', pixelRatio: 2, cacheBust: true })
      const a = document.createElement('a'); a.download = `${fullViewName()}.png`; a.href = dataUrl; a.click()
    } catch (e) { console.error('export failed', e) }
    setFullView('')
   
  }, [fullViewName])
  const copyFullView = useCallback(async () => {
    if (!fullViewRef.current || !navigator.clipboard || !window.ClipboardItem) return downloadFullView()
    setFullView('copying')
    try {
      const blobPromise = domToBlob(fullViewRef.current, { backgroundColor: 'var(--field-bg)', pixelRatio: 2, cacheBust: true }).then(b => { if (!b) throw new Error('no blob'); return b })
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blobPromise })])
      setFullView('copied'); setTimeout(() => setFullView(''), 1400)
    } catch { setFullView(''); await downloadFullView() }
   
  }, [downloadFullView])
  const [calendarMonth,  setCalendarMonth]  = useState(null)    // 'YYYY-MM' — calendar nav
  const [calendarOpen,   setCalendarOpen]   = useState(false)   // date picker popover
  const [weekPickerOpen, setWeekPickerOpen] = useState(false)   // week picker popover
  const [dayReadings,    setDayReadings]    = useState([])
  const [dayLoading,     setDayLoading]     = useState(false)
  const [dayErr,         setDayErr]         = useState(null)
  const [todFilter,      setTodFilter]      = useState('all')
  const [histMaxHr,      setHistMaxHr]      = useState(null)

  // ── ECG waveform for selected session ────────────────────────────────────────
  const [ecgWaveform,    setEcgWaveform]    = useState(null)   // { samples, fs }
  const [ecgLoading,     setEcgLoading]     = useState(false)
  const [ecgWinSec,      setEcgWinSec]      = useState(10)     // ECG strip window length: 5 | 10 | 30 s
  const [ecgOffsetSec,   setEcgOffsetSec]   = useState(0)      // scroll offset into the recording (s)
  const [ecgGain,        setEcgGain]        = useState(1)      // ECG amplitude zoom: 0.5 | 1 | 2
  const ecgPanRef = useRef(null)                               // arrastre del trazado (pointer events)
  const ecgCanvasRef = useRef(null)

  // ── Toggle análisis técnico ───────────────────────────────────────────────────
  const [showAnalysis,   setShowAnalysis]   = useState(false)

  // ── Controlled Brush range (animated) ───────────────────────────────────────
  const [dayBrushRange, setDayBrushRange] = useState({ start: 0, end: undefined })
  const animFrameRef = useRef(null)

  // ── Chart granularity ────────────────────────────────────────────────────────
  // 'auto' = the original 5-minute buckets; the user can switch to finer/coarser.
  // Seconds uses 10 s buckets: 1 s would mean 86 400 points/day and freeze the chart.
  const [dayGran, setDayGran] = useState('auto')
  const dayBucketMs = { auto: 5 * 60 * 1000, seg: 10 * 1000, min: 60 * 1000, hora: 60 * 60 * 1000 }[dayGran]

  // ── Memoized day analysis — avoids recomputing on every render ─────────────
  const visibleReadings = useMemo(() => {
    if (!dayReadings.length) return []
    // Ocultar y descartar momentos donde el Polar estuvo desconectado o sin contacto fisiológico
    const valid = dayReadings.filter(r => {
      const hr = Number(r.hr)
      if (isNaN(hr) || hr < 32 || hr > 235) return false
      // Si tiene intervalos RR, validar que existan latidos biológicos reales (250 ms a 2000 ms)
      if (Array.isArray(r.rr) && r.rr.length > 0) {
        return r.rr.some(v => v >= 250 && v <= 2000)
      }
      return true
    })

    if (todFilter === 'all') return valid
    return valid.filter(r => {
      const h = new Date(r.time).getHours()
      if (todFilter === 'sleep') return h >= 22 || h < 8
      if (todFilter === 'work')  return h >= 9 && h < 18
      return true
    })
  }, [dayReadings, todFilter])

  // ── Full-day buckets (chart always shows complete day) ────────────────────
  const allDayBuckets = useMemo(() => {
    if (!selectedDate || !dayReadings.length) return []
    const BUCKET_MS = dayBucketMs
    const dayStart  = new Date(selectedDate + 'T00:00:00').getTime()   // local midnight
    const dayEnd    = new Date(selectedDate + 'T23:59:59').getTime()   // local end of day
    
    // Filtramos las sesiones de este día para distribuir los pasos proporcionalmente en cada bucket
    const daySessions = deduplicatedSegments.filter(s => {
      return s.startTime < dayEnd && s.endTime > dayStart
    })

    const buckets   = {}
    visibleReadings.forEach(r => {
      const slot = Math.floor((r.time - dayStart) / BUCKET_MS)
      if (!buckets[slot]) buckets[slot] = []
      if (r.hr) buckets[slot].push(r.hr)
    })
    const total = Math.ceil((dayEnd - dayStart) / BUCKET_MS)
    return Array.from({ length: total }, (_, i) => {
      const t   = dayStart + i * BUCKET_MS
      const hrs = buckets[i] ?? []
      const avg = hrs.length ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length) : null
      const pad = n => String(n).padStart(2, '0')
      const d   = new Date(t)
      // Seconds granularity needs HH:MM:SS labels; the rest keep HH:MM
      const label = dayGran === 'seg'
        ? `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        : `${pad(d.getHours())}:${pad(d.getMinutes())}`

      // Calcular pasos estimados para este bucket
      let stepsInBucket = 0
      const bStart = t
      const bEnd = t + BUCKET_MS
      daySessions.forEach(s => {
        const overlapStart = Math.max(s.startTime, bStart)
        const overlapEnd = Math.min(s.endTime, bEnd)
        if (overlapEnd > overlapStart && s.steps > 0) {
          const sessionDurationMin = (s.endTime - s.startTime) / 60000
          const stepsPerMin = s.steps / sessionDurationMin
          const overlapMin = (overlapEnd - overlapStart) / 60000
          stepsInBucket += stepsPerMin * overlapMin
        }
      })

      const ACC_START_DATE = new Date('2026-09-29T23:40:00-03:00').getTime()
      const hasAccData = t >= ACC_START_DATE

      const isStepping = stepsInBucket >= 12
      let post = hasAccData ? 'sentado' : null
      if (hasAccData) {
        if (isStepping) {
          post = 'en_movimiento'
        } else if (avg != null) {
          if (avg >= 74) post = 'parado'
          else post = 'sentado'
        }
      }

      return { 
        label, 
        hr: avg,
        steps: stepsInBucket > 0 ? Math.round(stepsInBucket) : null,
        posture: post,
        postureColor: post ? getPostureColor(post) : '#ff3b30',
        postureLabel: post ? getPostureLabel(post) : null,
      }
    })
  }, [dayReadings, selectedDate, todFilter, dayBucketMs, dayGran, deduplicatedSegments])

  // ── Target brush range for animated filter zoom ───────────────────────────
  const targetBrushRange = useMemo(() => {
    if (!allDayBuckets.length) return { start: 0, end: 0 }
    const n = allDayBuckets.length - 1
    // 'all' no longer forces the full 24 h — it falls through to the data-detection
    // below (inFilter returns true for every hour), so the chart opens cropped to
    // where the readings actually start/end. The Brush still lets you drag out to
    // the whole day ("des-acotar").

    const inFilter = (label) => {
      const hh = parseInt(label, 10)
      if (todFilter === 'sleep') return hh >= 22 || hh < 8
      if (todFilter === 'work')  return hh >= 9  && hh < 18
      return true
    }

    // Autofiltro al rango disponible con registros (p. ej. de 12:00 a 16:00):
    let first = -1, last = -1
    allDayBuckets.forEach((b, i) => {
      if (inFilter(b.label) && b.hr !== null) {
        if (first === -1) first = i
        last = i
      }
    })

    if (first !== -1 && last !== -1) {
      // 1 bucket de margen en los extremos para visualización nítida y enfocada
      const PAD = 1
      return { start: Math.max(0, first - PAD), end: Math.min(n, last + PAD) }
    }

    // Fallback: si no hay datos, mostrar rango completo
    return { start: 0, end: n }
  }, [todFilter, allDayBuckets, dayBucketMs])

  const lastDateRef = useRef(selectedDate)
  useEffect(() => {
    if (selectedDate !== lastDateRef.current) {
      lastDateRef.current = selectedDate
      setDayBrushRange({ start: 0, end: undefined })
    }
  }, [selectedDate])

  const granChangeRef = useRef(false)
  useEffect(() => {
    cancelAnimationFrame(animFrameRef.current)
    if (granChangeRef.current) { granChangeRef.current = false; return }
    if (targetBrushRange.end !== undefined) {
      setDayBrushRange(targetBrushRange)
    }
  }, [targetBrushRange])



  // dayBuckets kept for backward-compat (stats still use visibleReadings)
  const daySegStats = useMemo(() => computeStats(visibleReadings), [visibleReadings])
  const daySegHrv   = useMemo(() => computeHRV(visibleReadings),   [visibleReadings])

  const dayStepsTotal = useMemo(() => {
    return deduplicatedSegments
      .filter(s => localDateKey(s.startTime) === selectedDate)
      .reduce((sum, s) => sum + (s.steps || 0), 0)
  }, [deduplicatedSegments, selectedDate])

  const { recoveryScore, recLabel, recColor, recSecondaryColor, recTip } = useMemo(() => {
    const rmssd = daySegHrv?.rmssd
    const minHr = daySegStats?.restingHr || daySegStats?.min
    if (!rmssd) {
      return {
        recoveryScore: null,
        recLabel: 'Sin datos suficientes',
        recColor: 'var(--text-faint)',
        recSecondaryColor: 'var(--text-soft)',
        recTip: 'Registrá una sesión con tu Polar H10 para evaluar tu recuperación.'
      }
    }
    const hrvPart = Math.min(60, Math.max(10, Math.round((rmssd / 55) * 50)))
    const hrPart = minHr ? Math.min(40, Math.max(10, Math.round((75 - minHr) * 1.5))) : 25
    const score = Math.min(99, Math.max(15, hrvPart + hrPart))

    if (score >= 75) {
      return {
        recoveryScore: score,
        recLabel: 'Óptimo para entrenar',
        recColor: '#30d158',
        recSecondaryColor: '#10b981',
        recTip: 'Tu sistema parasimpático está alto y recuperado. Día ideal para intensidad o volumen.'
      }
    }
    if (score >= 50) {
      return {
        recoveryScore: score,
        recLabel: 'Recuperación estable',
        recColor: '#007aff',
        recSecondaryColor: '#5e5ce6',
        recTip: 'Equilibrio autonómico normal. Podés entrenar a intensidad moderada o continuar tu rutina.'
      }
    }
    if (score >= 35) {
      return {
        recoveryScore: score,
        recLabel: 'Fatiga leve',
        recColor: '#ff9f0a',
        recSecondaryColor: '#ffb340',
        recTip: 'Tono vagal moderadamente bajo. Conviene priorizar descanso activo o bajar cargas.'
      }
    }
    return {
      recoveryScore: score,
      recLabel: 'Fatiga acumulada — descansá',
      recColor: '#ff453a',
      recSecondaryColor: '#ff2d55',
      recTip: 'Tu cuerpo pide regeneración. Evitá esfuerzos intensos y priorizá el sueño nocturno.'
    }
  }, [daySegHrv, daySegStats])
  const dayArrhythmia = useMemo(() => computeArrhythmiaRisk(visibleReadings), [visibleReadings])
  const detectedEvents = useMemo(() => detectActivityEvents(visibleReadings, deduplicatedSegments), [visibleReadings, deduplicatedSegments])
  const pendingEvents = useMemo(() => detectedEvents.filter(e => !annotations.some(a => a.start_ms != null && e.startMs < a.end_ms && e.endMs > a.start_ms)), [detectedEvents, annotations])
  const dayStartMs = useMemo(() => selectedDate ? new Date(selectedDate + 'T00:00:00').getTime() : 0, [selectedDate])
  const visibleRange = useMemo(() => {
    if (!allDayBuckets.length) return null
    const BUCKET_MS = dayBucketMs
    const s = Math.max(0, dayBrushRange.start ?? 0)
    const e = Math.min(allDayBuckets.length - 1, dayBrushRange.end ?? (allDayBuckets.length - 1))
    return { startMs: dayStartMs + s * BUCKET_MS, endMs: dayStartMs + (e + 1) * BUCKET_MS }
  }, [dayBrushRange, allDayBuckets, dayStartMs, dayBucketMs])
  // The chart shows only the selected window (zoom = slice the day's buckets)
  const visibleBuckets = useMemo(() => {
    if (!allDayBuckets.length) return []
    const s = Math.max(0, dayBrushRange.start ?? 0)
    const e = Math.min(allDayBuckets.length - 1, dayBrushRange.end ?? (allDayBuckets.length - 1))
    return e > s ? allDayBuckets.slice(s, e + 1) : allDayBuckets
  }, [allDayBuckets, dayBrushRange])

  // ── Range filter shared by ALL analysis sections ────────────────────────────
  // The chart-1 brush drives every chart below (distribution, HRV, Poincaré,
  // spectral, rhythm check). Debounced so dragging the selector doesn't rerun
  // heavy HRV math (DFA, SampEn) on every frame.
  const [settledRange, setSettledRange] = useState(null)
  useEffect(() => {
    const t = setTimeout(() => setSettledRange(visibleRange), 250)
    return () => clearTimeout(t)
  }, [visibleRange])
  const rangeReadings = useMemo(() => {
    if (!settledRange) return visibleReadings
    const filtered = visibleReadings.filter(r => r.time >= settledRange.startMs && r.time < settledRange.endMs)
    return filtered.length ? filtered : visibleReadings   // empty window → fall back to the whole day
  }, [visibleReadings, settledRange])

  const sleepArchitecture = useMemo(() => {
    if (!dayReadings || dayReadings.length < 25) return null
    return analyzeSleepArchitecture(dayReadings)
  }, [dayReadings])

  // ── Drag-to-select directly on the HR chart (mark / validate / edit) ─────────
  const labelToMs = useCallback((label) => {
    if (label == null) return null
    const [hh, mm, ss] = String(label).split(':').map(Number)   // HH:MM or HH:MM:SS (seconds granularity)
    return dayStartMs + (hh * 3600 + (mm || 0) * 60 + (ss || 0)) * 1000
  }, [dayStartMs])
  const selRangeMs = useMemo(() => {
    if (!sel || sel.aLabel == null) return null
    const a = labelToMs(sel.aLabel), b = labelToMs(sel.bLabel)
    return { startMs: Math.min(a, b), endMs: Math.max(a, b) + dayBucketMs }
  }, [sel, labelToMs, dayBucketMs])

  const mouseSelectedReadings = useMemo(() => {
    if (!selRangeMs) return null
    const filtered = visibleReadings.filter(r => r.time >= selRangeMs.startMs && r.time < selRangeMs.endMs)
    return filtered.length ? filtered : null
  }, [visibleReadings, selRangeMs])

  const liveReadings = useMemo(() => {
    if (!visibleRange) return visibleReadings
    const filtered = visibleReadings.filter(r => r.time >= visibleRange.startMs && r.time < visibleRange.endMs)
    return filtered.length ? filtered : visibleReadings
  }, [visibleReadings, visibleRange])

  const liveStats = useMemo(() => {
    const sourceReadings = mouseSelectedReadings || liveReadings
    const active = sourceReadings.length < visibleReadings.length
    return active ? computeStats(sourceReadings) : daySegStats
  }, [mouseSelectedReadings, liveReadings, visibleReadings.length, daySegStats])

  const liveSteps = useMemo(() => {
    const dayStart = new Date(selectedDate + 'T00:00:00').getTime()
    const dayEnd = new Date(selectedDate + 'T23:59:59').getTime()
    const daySessions = deduplicatedSegments.filter(s => s.startTime < dayEnd && s.endTime > dayStart)
    const totalDailySteps = daySessions.reduce((sum, s) => sum + (s.steps || 0), 0)
    if (!totalDailySteps) return null
    if (!dayReadings.length) return totalDailySteps
    const sourceReadings = mouseSelectedReadings || liveReadings
    const ratio = Math.min(1, sourceReadings.length / dayReadings.length)
    return Math.round(totalDailySteps * ratio)
  }, [selectedDate, deduplicatedSegments, dayReadings, mouseSelectedReadings, liveReadings])

  const activeStatsReadings = mouseSelectedReadings || rangeReadings
  const rangeActive     = activeStatsReadings.length < visibleReadings.length
  const rangeStats      = useMemo(() => rangeActive ? computeStats(activeStatsReadings) : daySegStats, [rangeActive, activeStatsReadings, daySegStats])
  const rangeHrv        = useMemo(() => rangeActive ? computeHRV(activeStatsReadings) : daySegHrv, [rangeActive, activeStatsReadings, daySegHrv])
  const rangeArrhythmia = useMemo(() => rangeActive ? computeArrhythmiaRisk(activeStatsReadings) : dayArrhythmia, [rangeActive, activeStatsReadings, dayArrhythmia])

  // Chip shown under each section header while a sub-range is selected
  const fmtRangeHM = ms => { const d = new Date(ms), p = n => String(n).padStart(2, '0'); return `${p(d.getHours())}:${p(d.getMinutes())}` }
  const rangeChip = (() => {
    if (selRangeMs && mouseSelectedReadings) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '-4px 0 12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#30d158', background: 'rgba(48,209,88,0.1)', border: '1px solid rgba(48,209,88,0.2)', borderRadius: 8, padding: '3px 10px' }}>
            Selección activa: {fmtRangeHM(selRangeMs.startMs)}–{fmtRangeHM(selRangeMs.endMs)}
          </span>
          <button onClick={() => { setSel(null); setSelNote('') }}
            style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            ✕ limpiar selección
          </button>
        </div>
      )
    }
    if (rangeActive && settledRange) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '-4px 0 12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#007aff', background: '#007aff12', border: '1px solid #007aff2e', borderRadius: 8, padding: '3px 10px' }}>
            Analizando zoom {fmtRangeHM(settledRange.startMs)}–{fmtRangeHM(settledRange.endMs)}
          </span>
          <button onClick={() => { cancelAnimationFrame(animFrameRef.current); setDayBrushRange({ start: 0, end: allDayBuckets.length - 1 }) }}
            style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            ✕ usar todo el día
          </button>
        </div>
      )
    }
    return null
  })()
  const onChartDown = useCallback((e) => { if (e && e.activeLabel) setSel({ aLabel: e.activeLabel, bLabel: e.activeLabel, dragging: true, moved: false }) }, [])
  const onChartMove = useCallback((e) => { setSel(s => (s && s.dragging && e && e.activeLabel) ? { ...s, bLabel: e.activeLabel, moved: s.moved || e.activeLabel !== s.aLabel } : s) }, [])
  const onChartUp = useCallback(() => {
    setSel(s => {
      if (!s || !s.dragging) return s
      if (s.moved) return { ...s, dragging: false, mode: 'new' }
      // a tap (no drag): if it lands on a band, edit/validate it; otherwise clear
      const tapMs = labelToMs(s.aLabel)
      const conf = annotations.find(a => a.status === 'confirmed' && a.start_ms != null && tapMs >= a.start_ms && tapMs < a.end_ms)
      if (conf) return { mode: 'edit', annId: conf.id }
      const pIdx = pendingEvents.findIndex(ev => tapMs >= ev.startMs && tapMs < ev.endMs)
      if (pIdx >= 0) return { mode: 'validate', idx: pIdx }
      return null
    })
  }, [labelToMs, annotations, pendingEvents])

  // ── Teclado (desktop): con un período/banda seleccionado en el gráfico,
  //    Supr/Backspace elimina (o rechaza la sugerencia) y Esc cierra ──────────
  const selKbRef = useRef(null)
  useEffect(() => { selKbRef.current = sel }, [sel])
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace' && e.key !== 'Escape') return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const s = selKbRef.current
      if (!s) return
      e.preventDefault()
      if (e.key === 'Escape') { setSel(null); setSelNote(''); return }
      if (s.mode === 'edit' && s.annId != null) deleteAnnotation(s.annId)
      else if (s.mode === 'validate') {
        const ev = pendingEvents[s.idx]
        if (ev) saveAnnotation({ start_ms: ev.startMs, end_ms: ev.endMs, activity: ev.activity, status: 'rejected' })
      }
      setSel(null); setSelNote('')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pendingEvents])

  // Beat analysis of the ECG segment currently visible on the strip (R-peaks → HR/RR/regularity)
  const ecgWindowStats = useMemo(() => {
    if (!ecgWaveform) return null
    const { samples, fs } = ecgWaveform
    const off = Math.min(Math.round(fs * ecgOffsetSec), Math.max(0, samples.length - 1))
    const win = Math.min(Math.round(fs * ecgWinSec), samples.length - off)
    if (win < fs * 2) return null
    const peaks = detectRPeaks(hpFilter(samples.slice(off, off + win)), fs)
    if (peaks.length < 2) return { beats: peaks.length, hr: null, rr: null, reg: null }
    const rrs    = peaks.slice(1).map((p, i) => (p - peaks[i]) / fs * 1000)
    const rrMean = rrs.reduce((a, b) => a + b, 0) / rrs.length
    const sd     = Math.sqrt(rrs.reduce((a, b) => a + (b - rrMean) ** 2, 0) / rrs.length)
    return {
      beats: peaks.length,
      hr:    Math.round(60000 / rrMean),
      rr:    Math.round(rrMean),
      reg:   Math.round((1 - Math.min(1, sd / rrMean)) * 100),   // 100% = perfectamente regular
    }
  }, [ecgWaveform, ecgWinSec, ecgOffsetSec])
  const dayRmssdPersonalAvg = useMemo(() => {
    const allRR = visibleReadings.flatMap(r => r.rr ?? []).filter(v => v > 250 && v < 2000)
    if (allRR.length < 5) return null
    const diffs = allRR.slice(1).map((v, i) => Math.abs(v - allRR[i]))
    return Math.round(Math.sqrt(diffs.reduce((a, b) => a + b * b, 0) / diffs.length))
  }, [visibleReadings])

  // Fetch profile for zone calculations (apiBase already declared above)
  useEffect(() => {
    if (!token) return
    fetch(`${apiBase}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(p => { if (p?.age) setHistMaxHr(Math.round(211 - 0.64 * p.age)) })
      .catch(() => {})
  }, [token, apiBase])

  const historicalMinHr = useMemo(() => {
    const valid = deduplicatedSegments.map(s => s.min_hr || s.minHr || s.min_hr_raw).filter(h => h > 30 && h < 90)
    if (!valid.length) return 55 // default
    return Math.min(...valid)
  }, [deduplicatedSegments])

  const historicalMaxHr = useMemo(() => {
    const valid = deduplicatedSegments.map(s => s.max_hr || s.maxHr || s.max_hr_raw).filter(h => h > 110 && h < 220)
    if (!valid.length) return histMaxHr || 190
    return Math.max(...valid)
  }, [deduplicatedSegments, histMaxHr])

  const histZones = useMemo(() => buildTrainingZones(historicalMaxHr, historicalMinHr), [historicalMaxHr, historicalMinHr])

  // Cache helpers — RAM-first (no quota), with sessionStorage as a reload-survival
  // layer for small payloads only. 30 min TTL: re-opening the same day/week/month
  // is instant and never re-hits BigQuery within the window.
  const CACHE_TTL_SEGS = 30 * 60 * 1000
  const CACHE_TTL_DAY  = 30 * 60 * 1000
  const cacheKey   = (type, key) => `vc_${type}_${userId}_${key}`
  const cacheRead  = (k, ttl) => {
    const m = _vcMem.get(k)
    if (m && Date.now() - m.ts < ttl) return m.data
    try {                                   // cold RAM → try sessionStorage (small lists)
      const v = sessionStorage.getItem(k)
      if (v) { const p = JSON.parse(v); if (Date.now() - p.ts < ttl) { _vcMem.set(k, p); return p.data } }
    } catch {}
    return null
  }
  const cacheWrite = (k, data) => {
    const entry = { ts: Date.now(), data }
    _vcMem.set(k, entry)                     // always cache in RAM (no quota)
    try {                                    // mirror to sessionStorage only if small (<1.5 MB)
      const s = JSON.stringify(entry)
      if (s.length < 1_500_000) sessionStorage.setItem(k, s)
    } catch {}
  }
  const cacheClear = (prefix) => {
    for (const k of _vcMem.keys()) if (k.startsWith(`vc_${prefix}_${userId}`)) _vcMem.delete(k)
    try { Object.keys(sessionStorage).filter(k => k.startsWith(`vc_${prefix}_${userId}`)).forEach(k => sessionStorage.removeItem(k)) } catch {}
  }

  const [downloadingAcc, setDownloadingAcc] = useState(false)

  const handleDownloadAccCsv = async (sessionId) => {
    if (!sessionId || downloadingAcc) return
    setDownloadingAcc(true)
    try {
      let accData = await getAccRecording(sessionId)
      if (!accData) {
        const res = await fetchAccRecording(token, sessionId)
        if (res?.dataBase64) {
          accData = await unpackAndDecompressAcc(res.dataBase64)
          saveAccRecording(sessionId, accData)
        }
      }
      if (!accData) {
        alert('No se encontraron datos crudos de acelerómetro para esta sesión')
        return
      }
      const csv = exportAccToCsv(accData)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `acelerometro_${sessionId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      alert('Error descargando acelerómetro: ' + e.message)
    } finally {
      setDownloadingAcc(false)
    }
  }

  // Phase 1: load session list only (fast, cached)
  const load = useCallback(async (days, { bustCache = false } = {}) => {
    const ck = cacheKey('segs', days)
    if (!bustCache) {
      const cached = cacheRead(ck, CACHE_TTL_SEGS)
      if (cached) { setSegments(cached); setLoading(false); return }
    }

    setLoading(true)
    setLoadErr(null)
    setSegments([])
    setAllReadings([])
    setActiveSegment(null)
    try {

      const since = Date.now() - days * 24 * 3600 * 1000
      const hdrs  = { Authorization: `Bearer ${token}` }

      const mapServerSessions = (sessions) => sessions.map(s => ({
        id:         s.session_id,
        startTime:  new Date(s.start_time).getTime(),
        endTime:    new Date(s.end_time).getTime(),
        source:     s.source ?? 'polar',
        deviceName: s.device_name ?? 'Sensor',
        avg_hr:     s.avg_hr,
        max_hr:     s.max_hr,
        min_hr:     s.min_hr,
        rmssd:      s.rmssd,
        pnn50:      s.pnn50,
        ecg:        !!s.has_ecg,
        has_acc:    !!s.has_acc,
        steps:      s.steps ?? null,
      }))
      try {
        const r1 = await fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1&_t=${Date.now()}`, { headers: hdrs })
        if (!r1.ok) {
          const body = await r1.json().catch(() => ({}))
          throw new Error(body.error || `Error del servidor (HTTP ${r1.status})`)
        }
        const { sessions = [] } = await r1.json()
        const mapped = mapServerSessions(sessions)
        cacheWrite(ck, mapped)
        setSegments(mapped)
      } catch (e) {
        // Sin señal: la lista guardada en el teléfono reemplaza a la del servidor
        const off = await loadSessionsOffline(userId)
        if (off?.length) setSegments(mapServerSessions(off))
        else throw e
      }
    } catch (e) {
      setLoadErr(friendlyErr(e))
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, apiBase, userId])

  // Phase 2 (lazy): load readings for a specific session on demand
  const loadSessionReadings = useCallback(async (seg) => {
    if (!seg?.id) return
    const targetId = seg.id.replace('_part1', '').replace('_part2', '')
    const startTime = seg.originalSession?.startTime || seg.startTime
    setLoadingSession(true)
    setSessionLoadErr(null)
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 40_000)   // 40s hard timeout
    try {
      const hdrs = { Authorization: `Bearer ${token}` }
      // Fetch readings for this specific session
      const url = `${apiBase}/sync?sessionId=${encodeURIComponent(targetId)}&since=${startTime - 60_000}`
      const r = await fetch(url, { headers: hdrs, signal: ctrl.signal })
      if (!r.ok) throw new Error(`Error del servidor (HTTP ${r.status})`)
      const body = await r.json()
      const readings = body.readings ?? []
      if (import.meta.env.DEV) console.log(`[session ${targetId}] lecturas:`, readings.length)
      if (!readings.length) throw new Error('Esta sesión no tiene lecturas guardadas')
      const mapped = readings.map(rd => ({
        time:      new Date(rd.time).getTime(),
        hr:        rd.hr,
        rr:        Array.isArray(rd.rr) ? rd.rr : [],
        source:    rd.source ?? 'polar',
        sessionId: rd.session_id,
      }))
      setAllReadings(prev => {
        const seen = new Set(prev.map(x => x.sessionId + '_' + x.time))
        const novel = mapped.filter(x => !seen.has(x.sessionId + '_' + x.time))
        return [...prev, ...novel]
      })
    } catch (e) {
      const msg = e.name === 'AbortError' ? 'Tiempo de espera agotado — BigQuery tardó demasiado' : e.message
      if (import.meta.env.DEV) console.error('loadSessionReadings', msg)
      setSessionLoadErr(msg)
    } finally {
      clearTimeout(timer)
      setLoadingSession(false)
    }
  }, [token, apiBase])   // ← sin allReadings en deps para evitar stale closure

  useEffect(() => { if (token) load(sinceDays) }, [load, sinceDays, token, refreshKey])

  // When a session is activated, lazy-load its readings
  useEffect(() => {
    if (!activeSegment) return
    // Check if already loaded (by sessionId or time range)
    const hasData = allReadings.some(r =>
      r.sessionId === activeSegment.id ||
      r.sessionId === activeSegment.originalSession?.id ||
      (r.time >= activeSegment.startTime && r.time <= activeSegment.endTime)
    )
    if (!hasData) loadSessionReadings(activeSegment)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSegment?.id, loadSessionReadings])

  // Carga y unifica automáticamente todo el ECG registrado en el día seleccionado en un solo gráfico continuo
  useEffect(() => {
    setEcgOffsetSec(0)
    if (!selectedDate || !token) { setEcgWaveform(null); return }
    
    // Buscar todas las sesiones que pertenecen a este día
    const targetSessions = deduplicatedSegments.filter(s => {
      const sDay = localDateKey(s.startTime)
      const eDay = localDateKey(s.endTime)
      return sDay === selectedDate || eDay === selectedDate
    })

    if (!targetSessions.length) {
      setEcgWaveform(null)
      return
    }

    const ecgCandidateSessions = targetSessions.filter(s => s.ecg)
    const sessionsToFetch = ecgCandidateSessions.length > 0 ? ecgCandidateSessions : targetSessions
    const uniqueIds = [...new Set(sessionsToFetch.map(s => s.id.replace('_part1', '').replace('_part2', '')))]

    let alive = true
    setEcgLoading(true)

    Promise.all(uniqueIds.map(id =>
      fetch(`${apiBase}/ecg/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    ))
    .then(results => {
      if (!alive) return
      const validEcgList = results.filter(r => r && Array.isArray(r.samples) && r.samples.length > 0)
      if (!validEcgList.length) {
        setEcgWaveform(null)
        return
      }

      // Ordenar cronológicamente
      validEcgList.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

      const primaryFs = validEcgList[0].fs ?? 130
      const unifiedSamples = []
      const firstStartTime = new Date(validEcgList[0].startTime).getTime()

      validEcgList.forEach((ecgItem, idx) => {
        if (idx > 0) {
          const prevItem = validEcgList[idx - 1]
          const prevEndTime = new Date(prevItem.startTime).getTime() + (prevItem.samples.length / (prevItem.fs || primaryFs)) * 1000
          const curStartTime = new Date(ecgItem.startTime).getTime()
          if (curStartTime > prevEndTime) {
            const padCount = Math.min(130, Math.round(((curStartTime - prevEndTime) / 1000) * primaryFs))
            for (let p = 0; p < padCount; p++) unifiedSamples.push(0)
          }
        }
        // Inserción segura por bloques para evitar RangeError: Maximum call stack size exceeded
        const samps = ecgItem.samples
        const CHUNK = 10000
        for (let i = 0; i < samps.length; i += CHUNK) {
          unifiedSamples.push(...samps.slice(i, i + CHUNK))
        }
      })

      setEcgWaveform({
        samples: unifiedSamples,
        fs: primaryFs,
        startTime: firstStartTime,
        unifiedCount: validEcgList.length
      })
    })
    .catch(err => {
      console.warn('Error cargando ECG unificado del día:', err)
      if (alive) setEcgWaveform(null)
    })
    .finally(() => {
      if (alive) setEcgLoading(false)
    })

    return () => { alive = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, deduplicatedSegments, token, apiBase])

  // Draw ECG strip on canvas — clinical paper standard
  useEffect(() => {
    let raf
    const draw = () => {
    if (!ecgWaveform) return
    const canvas = ecgCanvasRef.current
    // Canvas may mount a frame later than the waveform state — retry until it exists
    if (!canvas) { raf = requestAnimationFrame(draw); return }
    const { samples, fs } = ecgWaveform
    const dpr  = window.devicePixelRatio || 1
    const cssW = canvas.offsetWidth
    // First paint can happen before layout (offsetWidth = 0), which left the strip
    // blank until the user touched the scrollbar — retry on the next frame instead.
    if (!cssW) { raf = requestAnimationFrame(draw); return }
    const cssH = ecgLeadsCount === 2 ? 320 : 200

    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width  = Math.round(cssW * dpr)
      canvas.height = Math.round(cssH * dpr)
    }

    const ctx = canvas.getContext('2d')
    ctx.save()
    ctx.scale(dpr, dpr)

    // Window into the stored ECG: ecgWinSec seconds starting at ecgOffsetSec
    const offsetSamp = Math.min(Math.round(fs * ecgOffsetSec), Math.max(0, samples.length - 1))
    const winSamp    = Math.round(fs * ecgWinSec)
    const maxSamp    = Math.min(winSamp, samples.length - offsetSamp)
    const disp       = samples.slice(offsetSamp, offsetSamp + maxSamp)

    // ECG clinical grid constants — adapted for stored fs (usually 25 Hz)
    const sampPerLarge = fs * 0.2          // 200 ms × fs samples = large square
    const nLargeH      = maxSamp / sampPerLarge   // number of large squares horizontally
    const largeW       = cssW / nLargeH
    const smallW       = largeW / 5
    const nVertSquares = ecgLeadsCount === 2 ? 6.4 : 4
    const largeH       = cssH / nVertSquares
    const smallH       = largeH / 5
    const largeUV      = 500               // µV per large square (standard)

    // Calibration pulse geometry
    const calW = largeW            // 200 ms wide (1 large square)
    const calX = 4
    const traceX = calX + calW + 4

    const bgCol = isDark ? '#0d1117' : '#fff5f5' // Papel clínico rosado-durazno cálido estándar internacional
    const thinGridCol = isDark ? 'rgba(140,165,190,0.10)' : 'rgba(224, 49, 49, 0.16)' // Grilla milimétrica (1mm) rosada clara estándar
    const thickGridCol = isDark ? 'rgba(140,165,190,0.24)' : 'rgba(224, 49, 49, 0.36)' // Grilla mayor (5mm) rosada oscura estándar
    const calCol = isDark ? '#64d2ff' : '#000000' // Pulso en tinta negra para máxima claridad
    const waveCol = isDark ? '#35e08c' : '#000000' // Trazado ECG en tinta negra sólida de alta resolución

    // Fondo estilo monitor (pantalla oscura en dark mode, papel clínico claro en light mode)
    ctx.fillStyle = bgCol
    ctx.fillRect(0, 0, cssW, cssH)

    // Small grid (1 mm equivalent)
    ctx.strokeStyle = thinGridCol
    ctx.lineWidth = 0.5
    for (let x = 0; x <= cssW + 0.5; x += smallW) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cssH); ctx.stroke() }
    for (let y = 0; y <= cssH + 0.5; y += smallH) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cssW, y); ctx.stroke() }

    // Large grid (5 mm equivalent)
    ctx.strokeStyle = thickGridCol
    ctx.lineWidth = 1
    for (let x = 0; x <= cssW + 0.5; x += largeW) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cssH); ctx.stroke() }
    for (let y = 0; y <= cssH + 0.5; y += largeH) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cssW, y); ctx.stroke() }

    // Horizontal split for 2 leads
    if (ecgLeadsCount === 2) {
      ctx.strokeStyle = isDark ? 'rgba(140,165,190,0.3)' : 'rgba(0,0,0,0.15)'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(0, cssH / 2)
      ctx.lineTo(cssW, cssH / 2)
      ctx.stroke()
    }

    const pxPerUV = (largeH / largeUV) * ecgGain   // pixels per µV (× user amplitude zoom)
    const filtered = notchFilter50(hpFilter(disp))

    const drawLead = (midY, signalMultiplier, leadName, labelY1, labelY2) => {
      // 1 mV calibration pulse
      const calPxH = 1000 * pxPerUV  // 1000 µV = 1 mV
      const calY   = midY + calPxH / 2
      ctx.strokeStyle = calCol
      ctx.lineWidth   = 1.5
      ctx.lineJoin    = 'round'
      ctx.beginPath()
      ctx.moveTo(calX,        calY)
      ctx.lineTo(calX,        calY - calPxH)
      ctx.lineTo(calX + calW, calY - calPxH)
      ctx.lineTo(calX + calW, calY)
      ctx.stroke()
      ctx.fillStyle = calCol
      ctx.font = '400 7px -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('1 mV', calX + calW / 2, calY - calPxH - 2)

      // ECG trace
      const traceW = cssW - traceX
      ctx.strokeStyle = waveCol
      ctx.lineWidth   = 1.6
      ctx.lineJoin    = 'round'
      ctx.lineCap     = 'round'
      ctx.beginPath()
      for (let i = 0; i < filtered.length; i++) {
        const x = traceX + (i / maxSamp) * traceW
        const y = midY - filtered[i] * pxPerUV * signalMultiplier
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      }
      ctx.stroke()

      // R-peak markers using Pan-Tompkins adapted for stored fs
      const peaks = detectRPeaks(filtered, fs)
      ctx.fillStyle = '#ff453a'
      peaks.forEach(pi => {
        const x = traceX + (pi / maxSamp) * traceW
        const y = midY - filtered[pi] * pxPerUV * signalMultiplier
        ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2); ctx.fill()
      })

      // Labels: top-left (lead, speed, gain)
      ctx.fillStyle = isDark ? 'rgba(225,240,255,0.5)' : '#000000'
      ctx.font = '500 8px -apple-system, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText(leadName, 6, labelY1)
      ctx.fillText('25 mm/s · 10 mm/mV', 6, labelY2)
    }

    if (ecgLeadsCount === 2) {
      // Draw DI (Normal) on top half, DII (Inverted) on bottom half
      drawLead(cssH / 4, 1, 'Derivación I (DI)', 12, 22)
      drawLead((cssH * 3) / 4, -1, 'Derivación II (DII)', cssH / 2 + 12, cssH / 2 + 22)
    } else {
      // Draw single lead with potential inversion
      const mult = historyEcgInverted ? -1 : 1
      const label = historyEcgInverted ? 'Derivación II (DII Invertido)' : 'Derivación I (DI)'
      drawLead(cssH / 2, mult, label, 12, 22)
    }

    // Top-right: window duration and sample rate (moved up to free the bottom edge)
    ctx.fillStyle = isDark ? 'rgba(225,240,255,0.4)' : 'rgba(0,0,0,0.4)'
    ctx.font = '400 8px -apple-system, sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText(`${(maxSamp / fs).toFixed(0)} s · ${fs} Hz`, cssW - 6, 12)

    // Time axis — actual clock time (HH:MM:SS), spaced so labels don't overlap.
    const baseMs    = ecgWaveform.startTime
    const pxPerSec  = largeW * 5
    const labelStep = Math.max(1, Math.ceil(52 / pxPerSec))   // ~52 px per HH:MM:SS label
    ctx.fillStyle = isDark ? 'rgba(225,240,255,0.45)' : 'rgba(0,0,0,0.45)'
    ctx.font = '400 8px -apple-system, sans-serif'
    for (let t = 0; traceX + t * pxPerSec <= cssW + 1; t += labelStep) {
      const x     = traceX + t * pxPerSec
      const label = baseMs ? fmtHMS(baseMs + (ecgOffsetSec + t) * 1000) : `${ecgOffsetSec + t}s`
      ctx.textAlign = t === 0 ? 'left' : 'center'
      ctx.fillText(label, t === 0 ? traceX : x, cssH - 3)
    }

    ctx.restore()
    }
    draw()
    // Redraw whenever the canvas changes size (tab becomes visible, sidebar
    // collapse, window resize) — this is what used to require moving the bar.
    const ro = new ResizeObserver(() => draw())
    if (ecgCanvasRef.current) ro.observe(ecgCanvasRef.current)
    // Redraw after a small delay to handle layout expansion / mounting animations (prevents blank canvas on first load)
    const t = setTimeout(draw, 100)
    return () => { cancelAnimationFrame(raf); ro.disconnect(); clearTimeout(t) }
  }, [ecgWaveform, ecgWinSec, ecgOffsetSec, ecgGain, historyEcgInverted, ecgLeadsCount, isDark, theme])

  // Per-day summary for the calendar hover panel: recorded time, sessions, HR, ECG, sleep.
  const daySummary = useMemo(() => {
    const m = {}
    deduplicatedSegments.forEach(s => {
      if (!(s.endTime > s.startTime)) return

      let t = s.startTime
      let guard = 0
      while (t < s.endTime && guard++ < 400) {
        const d = new Date(t)
        d.setHours(24, 0, 0, 0)
        const cut = Math.min(s.endTime, d.getTime())
        const k = localDateKey(t)

        const e = m[k] ?? (m[k] = { sessions: 0, totalMs: 0, maxHr: 0, minHr: 0, hrSum: 0, hrCount: 0, hasEcg: false, sleepMin: 0, firstMs: t, lastMs: cut, steps: 0 })
        e.sessions++
        e.totalMs += (cut - t)
        e.firstMs = Math.min(e.firstMs, t)
        e.lastMs  = Math.max(e.lastMs, cut)
        if (s.max_hr) e.maxHr = Math.max(e.maxHr, s.max_hr)
        if (s.min_hr) e.minHr = e.minHr ? Math.min(e.minHr, s.min_hr) : s.min_hr
        if (s.avg_hr) { e.hrSum += s.avg_hr; e.hrCount++ }
        if (s.ecg) e.hasEcg = true
        e.sleepMin += nightOverlapMin(t, cut)

        const sessionDurationMin = (s.endTime - s.startTime) / 60000
        const stepsPerMin = s.steps > 0 && sessionDurationMin > 0 ? s.steps / sessionDurationMin : 0
        const overlapMin = (cut - t) / 60000
        e.steps += stepsPerMin * overlapMin

        t = cut
      }
    })
    Object.values(m).forEach(e => {
      e.totalMin = Math.round(e.totalMs / 60000)
      e.avgHr    = e.hrCount ? Math.round(e.hrSum / e.hrCount) : null
      e.hasSleep = e.sleepMin >= 120   // ≥2 h within 22:00–08:00 → counts as a sleep period
      e.steps    = e.steps ? Math.round(e.steps) : null
    })
    return m
  }, [deduplicatedSegments])

  const constanciaSessions = useMemo(
    () => deduplicatedSegments.map(s => ({ start: s.startTime, end: s.endTime })),
    [deduplicatedSegments]
  )

  const availableDates = useMemo(() => {
    const s = new Set()
    Object.entries(daySummary).forEach(([k, e]) => {
      if (e.totalMin > 0) {
        s.add(k)
      }
    })
    return s
  }, [daySummary])

  const availableDatesSorted = useMemo(() => [...availableDates].sort(), [availableDates])

  // Navegación restringida estrictamente a días que tienen registros reales + hoy
  const navigableDates = useMemo(() => {
    const today = localDateKey(Date.now())
    const set = new Set(availableDatesSorted)
    set.add(today)
    return [...set].sort()
  }, [availableDatesSorted])

  const prevDate = useMemo(() => {
    if (!selectedDate || navigableDates.length === 0) return null
    const earlier = navigableDates.filter(d => d < selectedDate)
    return earlier.length > 0 ? earlier[earlier.length - 1] : null
  }, [selectedDate, navigableDates])

  const nextDate = useMemo(() => {
    if (!selectedDate || navigableDates.length === 0) return null
    const later = navigableDates.filter(d => d > selectedDate)
    return later.length > 0 ? later[0] : null
  }, [selectedDate, navigableDates])

  const navigateDay = useCallback((iso) => {
    setSelectedDate(iso)
    setDayReadings([])
    setCalendarMonth(iso.slice(0, 7))
    setCalendarOpen(false)
  }, [])

  const daySwipeRef = useRef(null)

  // Auto-select latest recorded date with data + sync calendar to this month
  useEffect(() => {
    if (availableDatesSorted.length > 0) {
      const latest = availableDatesSorted[availableDatesSorted.length - 1]
      setSelectedDate(current => {
        if (!current || !availableDates.has(current)) {
          return latest
        }
        return current
      })
      setCalendarMonth(prev => prev ?? latest.slice(0, 7))
    }
  }, [availableDatesSorted, availableDates])

  // Scroll date chips so selected date is always visible (most recent = far right)
  useEffect(() => {
    if (!dateScrollRef.current) return
    const sel = dateScrollRef.current.querySelector('[data-selected="true"]')
    if (sel) sel.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'nearest' })
    else dateScrollRef.current.scrollLeft = dateScrollRef.current.scrollWidth
  }, [selectedDate])

  // Load readings for the selected day
  const loadDayReadings = useCallback(async (dateIso, { bustCache = false } = {}) => {
    if (!dateIso) return
    const ck = cacheKey('day', dateIso)
    if (!bustCache) {
      const cached = cacheRead(ck, CACHE_TTL_DAY)
      if (cached) { setDayReadings(cached); setDayLoading(false); return }
    }

    setDayLoading(true)
    setDayErr(null)
    setDayReadings([])
    const dayStart = new Date(dateIso + 'T00:00:00').getTime()   // local midnight
    const dayEnd   = new Date(dateIso + 'T23:59:59').getTime()   // local end of day
    try {

      const mapReadings = (readings) => readings.map(rd => ({
        time:      new Date(rd.time).getTime(),
        hr:        rd.hr,
        rr:        Array.isArray(rd.rr) ? rd.rr : [],
        source:    rd.source ?? 'polar',
        sessionId: rd.session_id,
      }))
      try {
        const hdrs = { Authorization: `Bearer ${token}` }
        const r = await fetch(`${apiBase}/sync?since=${dayStart}&until=${dayEnd}&_t=${Date.now()}`, { headers: hdrs })
        if (!r.ok) {
          const b = await r.json().catch(() => ({}))
          throw new Error(b.error || `HTTP ${r.status}`)
        }
        const { readings = [] } = await r.json()
        // Copia local del día para verlo sin señal (conserva las anotaciones que hubiera)
        loadDayOffline(userId, dateIso).then(prev => saveDayOffline(userId, dateIso, {
          readings, annotations: prev?.annotations ?? null, complete: dateIso < localDateKey(Date.now()),
        })).catch(() => {})
        const mapped = mapReadings(readings)
        cacheWrite(ck, mapped)
        setDayReadings(mapped)
      } catch (e) {
        // Sin señal: si el día viajó al teléfono, se muestra la copia local
        const off = await loadDayOffline(userId, dateIso)
        if (off?.readings?.length) setDayReadings(mapReadings(off.readings))
        else if (off) setDayReadings([])
        else throw new Error(navigator.onLine ? friendlyErr(e) : 'Este día no está guardado en el teléfono — abrí la app con señal para bajarlo')
      }
    } catch (e) {
      setDayErr(friendlyErr(e))
    } finally {
      setDayLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, apiBase, userId])

  useEffect(() => {
    if (selectedDate) loadDayReadings(selectedDate)
  }, [selectedDate, loadDayReadings])

  // Handle cross-tab navigation and focus range from Resumen Tab
  useEffect(() => {
    if (!dayLoading && dayReadings.length > 0 && window.__pendingFocusRange) {
      const { dateIso, startHour, endHour } = window.__pendingFocusRange
      if (dateIso === selectedDate && allDayBuckets.length > 0) {
        const BK = 5 * 60000
        const nn = allDayBuckets.length - 1
        const startIdx = Math.max(0, Math.floor((startHour * 3600000) / BK))
        const endIdx = Math.min(nn, Math.ceil((endHour * 3600000) / BK) - 1)
        cancelAnimationFrame(animFrameRef.current)
        setDayBrushRange({ start: startIdx, end: endIdx })
        window.__pendingFocusRange = null
        
        setTimeout(() => {
          const el = document.getElementById('section-fc')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 150)
      }
    }
  }, [dayLoading, dayReadings, selectedDate, allDayBuckets])

  // Auto-select the most recent day / week / month with data so it shows data immediately
  useEffect(() => {
    if (periodMode === 'day') {
      const days = availableDatesSorted
      if (days.length > 0) {
        setSelectedDate(current => {
          if (!current || !availableDates.has(current)) {
            const last = days[days.length - 1]
            setCalendarMonth(m => m ?? last.slice(0, 7))
            return last
          }
          return current
        })
      }
    } else if (periodMode === 'week') {
      const weeks = [...new Set([...availableDates].map(weekKeyOf))].sort()
      if (weeks.length > 0) {
        setSelectedWeek(current => {
          if (!current || !weeks.includes(current)) {
            return weeks[weeks.length - 1]
          }
          return current
        })
      }
    } else if (periodMode === 'month') {
      const months = [...new Set([...availableDates].map(d => d.slice(0, 7)))].sort()
      if (months.length > 0) {
        setSelectedMonth(current => {
          if (!current || !months.includes(current)) {
            return months[months.length - 1]
          }
          return current
        })
      }
    }
  }, [periodMode, availableDates, availableDatesSorted])

  // Auto-activate the most recent session of the selected period (day, week, or month) so ECG loads automatically.
  // Use local midnight boundaries (no Z suffix) to match Argentina timezone — same as allDayBuckets.
  useEffect(() => {
    if (!deduplicatedSegments.length) return
    let filteredSess = []
    if (periodMode === 'day' && selectedDate) {
      const dayStart = new Date(selectedDate + 'T00:00:00').getTime()
      const dayEnd   = new Date(selectedDate + 'T23:59:59').getTime()
      filteredSess = deduplicatedSegments.filter(s => s.startTime < dayEnd && s.endTime > dayStart)
    } else if (periodMode === 'week' && selectedWeek) {
      const inWeek = (key, s) => weekKeyOf(localDateKey(s.startTime)) === key
      filteredSess = deduplicatedSegments.filter(s => inWeek(selectedWeek, s))
    } else if (periodMode === 'month' && selectedMonth) {
      filteredSess = deduplicatedSegments.filter(s => localMonthKey(s.startTime) === selectedMonth)
    }

    // Sort by startTime desc to get the most recent one
    const mostRecent = [...filteredSess].sort((a, b) => b.startTime - a.startTime)[0]
    setActiveSegment(prev => {
      // Keep previous active segment if it's still in the current period's sessions
      if (prev && filteredSess.some(s => s.id === prev.id)) return prev
      return mostRecent ?? null
    })
  }, [selectedDate, selectedWeek, selectedMonth, periodMode, deduplicatedSegments])

  // Keep filtered for the Importaciones (sessions) tab — uses allReadings from session lazy-load
  const filtered = useMemo(() => allReadings.filter(r => {
    if (activeSegment) return r.time >= activeSegment.startTime && r.time <= activeSegment.endTime
    return true
  }), [allReadings, activeSegment])

  const typedSegments = useMemo(() =>
    typeFilter === 'all' ? deduplicatedSegments : deduplicatedSegments.filter(s => detectSessionType(s) === typeFilter)
  , [deduplicatedSegments, typeFilter])

  const totalHours = useMemo(() => {
    const ms = deduplicatedSegments.reduce((a, s) => a + (s.endTime - s.startTime), 0)
    const h  = Math.floor(ms / 3600000)
    const m  = Math.floor((ms % 3600000) / 60000)
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }, [deduplicatedSegments])

  const SectionHeader = ({ n, title, sub, info }) => {
    const [open, setOpen] = useState(false)
    return (
      <div style={{ marginBottom: 14, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginTop: 4, width: '100%', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>{n}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</p>
              {sub && <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</p>}
            </div>
          </div>
          {info && (
            <button
              onClick={() => setOpen(o => !o)}
              aria-label="Más información"
              style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', padding: 4, background: 'none', border: 'none', color: open ? 'var(--btn-strong)' : 'var(--text-faint)', transition: 'color 0.2s', outline: 'none' }}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: open ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          )}
        </div>
        {open && info && (
          <div style={{ padding: '8px 12px', background: 'var(--chip-bg)', borderRadius: 10, border: '1px dashed var(--border)', fontSize: 11.5, color: 'var(--text-soft)', lineHeight: 1.4, marginTop: 6, textAlign: 'left' }}>
            {info}
          </div>
        )}
      </div>
    )
  }

  const SH = ({ n, title, sub }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, marginTop: 4 }}>
      <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>{n}</span>
      </div>
      <div>
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{title}</p>
        {sub && <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0 }}>{sub}</p>}
      </div>
    </div>
  )

  const renderEcgPanel = (sectionNum) => {
    if (!ecgWaveform && !ecgLoading) return null
    return (
      <Reveal order={2} minHeight={300}>
        <SectionHeader n={sectionNum} title="Electrocardiograma" sub="1 derivación (bipolar, tórax ≈ DI) · 25 mm/s · 10 mm/mV · arrastrá para recorrer" info="Ver la forma real de cada latido (onda P-QRS-T) en alta resolución, para reconocer latidos irregulares o anómalos que el pulso por sí solo no muestra." />
        {(ecgLoading || ecgWaveform) ? (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 18 }}>
            {ecgLoading && !ecgWaveform && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Cargando ECG…</div>
            )}
            {ecgWaveform && (() => {
              const totalSec  = Math.floor(ecgWaveform.samples.length / ecgWaveform.fs)
              const maxOffset = Math.max(0, totalSec - ecgWinSec)
              const winEnd    = Math.min(ecgOffsetSec + ecgWinSec, totalSec)
              const clampOff  = (v) => Math.max(0, Math.min(maxOffset, Math.round(v)))
              const chipS = (on) => ({ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: on ? (isDark ? '#35e08c' : '#007aff') : 'var(--chip-bg)',
                color: on ? (isDark ? '#06281a' : '#fff') : 'var(--text-muted)' })
              const pageBtnS = { fontSize: 15, fontWeight: 700, width: 30, height: 26, borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--chip-bg)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }
              return (
                <>
                  {/* Arrastrá el trazado para moverte por la grabación (como en el vivo) */}
                  <div
                    style={{ cursor: 'ew-resize', touchAction: 'pan-y', background: isDark ? '#0d1117' : '#fff5f5', borderRadius: 10, overflow: 'hidden' }}
                    onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); ecgPanRef.current = { x: e.clientX, off: ecgOffsetSec, w: e.currentTarget.getBoundingClientRect().width } }}
                    onPointerMove={e => {
                      const p = ecgPanRef.current
                      if (!p) return
                      setEcgOffsetSec(clampOff(p.off + (p.x - e.clientX) * (ecgWinSec / p.w)))
                    }}
                    onPointerUp={() => { ecgPanRef.current = null }}
                    onPointerCancel={() => { ecgPanRef.current = null }}
                  >
                    <canvas ref={ecgCanvasRef} style={{ display: 'block', width: '100%', height: ecgLeadsCount === 2 ? 320 : 200 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', background: 'var(--inset-bg)' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setEcgOffsetSec(o => clampOff(o - ecgWinSec))} disabled={ecgOffsetSec <= 0} title="Ventana anterior"
                        style={{ ...pageBtnS, opacity: ecgOffsetSec <= 0 ? 0.35 : 1 }}>‹</button>
                      <button onClick={() => setEcgOffsetSec(o => clampOff(o + ecgWinSec))} disabled={ecgOffsetSec >= maxOffset} title="Ventana siguiente"
                        style={{ ...pageBtnS, opacity: ecgOffsetSec >= maxOffset ? 0.35 : 1 }}>›</button>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }} title="Ventana de tiempo">
                      {[5, 10, 30].map(w => (
                        <button key={w} onClick={() => { setEcgWinSec(w); setEcgOffsetSec(o => Math.min(o, Math.max(0, totalSec - w))) }} style={chipS(ecgWinSec === w)}>
                          {w}s
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }} title="Amplitud (zoom vertical)">
                      {[[0.5, '½×'], [1, '1×'], [2, '2×']].map(([g, l]) => (
                        <button key={g} onClick={() => setEcgGain(g)} style={chipS(ecgGain === g)}>
                          {l}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }} title="Invertir onda / Derivación">
                      <button onClick={() => setHistoryEcgInverted(v => !v)} style={chipS(historyEcgInverted)}>
                        {historyEcgInverted ? 'DII (Inv)' : 'DI (Norm)'}
                      </button>
                    </div>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 96, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {ecgWaveform.startTime ? `${fmtHMS(ecgWaveform.startTime + ecgOffsetSec * 1000)} · ` : ''}{ecgOffsetSec}–{winEnd}s / {totalSec}s
                    </span>
                  </div>

                  {/* Línea de tiempo del ECG estilizada */}
                  {maxOffset > 0 && (() => {
                    const percentage = (ecgOffsetSec / maxOffset) * 100
                    return (
                      <div style={{ padding: '12px 16px 14px', background: 'var(--inset-bg)', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, fontWeight: 600, color: 'var(--text-soft)', alignItems: 'center' }}>
                          <span>{ecgWaveform.startTime ? fmtHMS(ecgWaveform.startTime) : '0s'}</span>
                          <span style={{ color: isDark ? '#35e08c' : '#007aff', fontWeight: 700, background: isDark ? 'rgba(53,224,140,0.1)' : 'rgba(0,122,255,0.1)', padding: '2px 8px', borderRadius: 6 }}>
                            Posición actual: {ecgWaveform.startTime ? `${ecgOffsetSec}s (${fmtHMS(ecgWaveform.startTime + ecgOffsetSec * 1000)})` : `${ecgOffsetSec}s`}
                          </span>
                          <span>{ecgWaveform.startTime ? fmtHMS(ecgWaveform.startTime + totalSec * 1000) : `${totalSec}s`}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 10.5, fontFamily: 'monospace', color: 'var(--text-faint)', minWidth: 20 }}>0s</span>
                          <input
                            type="range"
                            min={0}
                            max={maxOffset}
                            step={1}
                            value={Math.min(ecgOffsetSec, maxOffset)}
                            onChange={e => setEcgOffsetSec(Number(e.target.value))}
                            style={{
                              flex: 1,
                              height: 8,
                              borderRadius: 4,
                              cursor: 'pointer',
                              accentColor: isDark ? '#35e08c' : '#007aff',
                              background: `linear-gradient(to right, ${isDark ? '#35e08c' : '#007aff'} ${percentage}%, var(--chip-bg) ${percentage}%)`,
                              WebkitAppearance: 'none',
                              appearance: 'none',
                              outline: 'none',
                              margin: 0,
                            }}
                          />
                          <span style={{ fontSize: 10.5, fontFamily: 'monospace', color: 'var(--text-faint)', minWidth: 28, textAlign: 'right' }}>{totalSec}s</span>
                        </div>
                      </div>
                    )
                  })()}
                  {ecgWindowStats && (
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', padding: '8px 12px', borderTop: '1px solid var(--border)', background: 'var(--chip-bg)' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>En esta vista:</span>
                      <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600 }}>{ecgWindowStats.beats} latidos</span>
                      {ecgWindowStats.hr  != null && <span style={{ fontSize: 12, color: isDark ? '#35e08c' : '#007aff', fontWeight: 700 }}>{ecgWindowStats.hr} bpm</span>}
                      {ecgWindowStats.rr  != null && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>RR medio {ecgWindowStats.rr} ms</span>}
                      {ecgWindowStats.reg != null && <span style={{ fontSize: 11, fontWeight: 600, color: ecgWindowStats.reg >= 92 ? (isDark ? '#35e08c' : '#34c759') : ecgWindowStats.reg >= 80 ? '#ffd60a' : '#ff453a' }}>Regularidad {ecgWindowStats.reg}%</span>}
                    </div>
                  )}
                  <div style={{ padding: '7px 12px', borderTop: '1px solid var(--border)', background: 'var(--inset-bg)' }}>
                    <p style={{ fontSize: 10, color: 'var(--text-soft)', margin: 0, lineHeight: 1.5 }}>
                      El ECG es una <strong style={{ color: 'var(--text)' }}>muestra de alta resolución de {Math.round(totalSec / 60)} min</strong>{ecgWaveform.startTime ? ` (${fmtHMS(ecgWaveform.startTime)}–${fmtHMS(ecgWaveform.startTime + totalSec * 1000)})` : ''}, no el día entero — guardar el electro de 130 Hz de todo el día sería enorme. La FC y la HRV de arriba sí cubren todo el día.
                    </p>
                  </div>
                </>
              )
            })()}
          </div>
        ) : (
          <div style={{ background: 'var(--inset-bg)', border: '1px dashed var(--border)', borderRadius: 14, padding: '30px 20px', textAlign: 'center', marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <IconStethoscope size={24} color="var(--text-faint)" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>Esta sesión no tiene trazado de ECG guardado.</p>
          </div>
        )}
        <MetricCalculationAudit
          title="Trazado y Filtros de ECG"
          formula="V(t) = Raw_μV   |   Calibración: 10 mm/mV (1 mm = 0.1 mV)   |   Velocidad: 25 mm/s"
          variables={[
            { label: 'Tasa de Muestreo (Fs)', value: `${ecgWaveform?.fs || 130} Hz` },
            { label: 'Filtro Notch de Línea', value: '50 Hz (Red Eléctrica)' },
            { label: 'Filtro Pasa-Altos', value: '0.5 Hz (Deriva de Base)' },
            { label: 'Derivación', value: 'Derivación I (DI / Bipolar)' },
          ]}
          stepByStep="La señal de 130 Hz transmitida por el Polar H10 se pasa por un filtro pasa-altos digital de 0.5 Hz para eliminar la respiración y un filtro notch de 50 Hz para suprimir la interferencia eléctrica, mapeando 10 mm de altura por cada 1 mV de amplitud."
          reference="AHA/ACC/HRS Standardization and Interpretation of the Electrocardiogram"
        />
      </Reveal>
    )
  }

  // Demo users have no real history — show a clear CTA instead
  if (user?.demo) {
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 18, padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <IconActivity size={40} color="#007aff" />
        </div>
        <p style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Estás en modo demo</p>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 24, maxWidth: 320, margin: '0 auto 24px' }}>
          El historial está disponible sólo para cuentas reales. Creá tu cuenta para guardar y ver tus sesiones.
        </p>
        <a href="/register" style={{
          display: 'inline-block', background: 'var(--btn-strong)', color: '#fff',
          borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 700,
          textDecoration: 'none',
        }}>Crear cuenta gratuita →</a>
      </div>
    )
  }

  const triggerStats = useMemo(() => {
    if (!selectedDate || dayLoading || dayErr) return null
    const dayStart    = new Date(selectedDate + 'T00:00:00').getTime()
    const dayEnd      = new Date(selectedDate + 'T23:59:59').getTime()
    const daySessions = deduplicatedSegments.filter(s => s.startTime < dayEnd && s.endTime > dayStart)
    if (daySessions.length === 0) return null

    if (dayReadings && dayReadings.length > 0) {
      const durMin = Math.round(visibleReadings.length / 60)
      return {
        lecturas: visibleReadings.length,
        minutos: durMin,
        avgHr: daySegStats.avg,
        maxHr: daySegStats.max,
        rmssd: daySegHrv.rmssd,
      }
    } else {
      const totalMs = daySessions.reduce((a, s) => a + (s.endTime - s.startTime), 0)
      const totalMin = Math.round(totalMs / 60000)
      const withHr = daySessions.filter(s => s.avg_hr)
      const withHrv = daySessions.filter(s => s.rmssd)
      const avgHr = withHr.length ? Math.round(withHr.reduce((a, s) => a + s.avg_hr, 0) / withHr.length) : null
      const maxHr = daySessions.reduce((m, s) => Math.max(m, s.max_hr ?? 0), 0) || null
      const avgRmssd = withHrv.length ? Math.round(withHrv.reduce((a, s) => a + s.rmssd, 0) / withHrv.length) : null
      return {
        lecturas: 0,
        minutos: totalMin,
        avgHr,
        maxHr,
        rmssd: avgRmssd,
        isImported: true,
      }
    }
  }, [selectedDate, dayLoading, dayErr, dayReadings, visibleReadings, daySegStats, daySegHrv, deduplicatedSegments])

  return (
    <div>
      {/* Importar se movió a la pestaña "Nueva sesión" */}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: 14 }}>
          <span className="vc-spinner" />
          <p style={{ color: 'var(--text-muted)', fontSize: 14, fontWeight: 600, margin: 0 }}>Cargando tu historial…</p>
          <p style={{ color: 'var(--text-soft)', fontSize: 12, margin: 0 }}>Reuniendo tus sesiones de frecuencia, HRV y ECG</p>
        </div>
      )}

      {!loading && loadErr && (
        <div style={{ background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 13, color: '#c0392b' }}>[ERROR] No se pudo cargar el historial: {loadErr}</p>
          <button onClick={() => load(sinceDays)} style={{ background: 'none', border: '1px solid #fbb', borderRadius: 8, padding: '5px 12px', fontSize: 12, color: '#c0392b', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, marginLeft: 10 }}>Reintentar</button>
        </div>
      )}

      {!loading && !loadErr && deduplicatedSegments.length === 0 && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 6 }}>No hay sesiones guardadas</p>
          <p style={{ color: 'var(--text-soft)', fontSize: 13 }}>Grabá desde En vivo o importá desde iOS usando el botón de abajo</p>
        </div>
      )}

      {false && (
        <div>
          {/* sessions view removed — import is now a floating modal button */}

          {/* Tipo de sesión — scroll horizontal en mobile */}
          <div style={{ overflowX: 'auto', marginBottom: 12, paddingBottom: 2 }}>
            <div style={{ display: 'flex', gap: 6, minWidth: 'max-content' }}>
              {Object.entries(SESSION_TYPES).map(([key, t]) => {
                const count = key === 'all' ? deduplicatedSegments.length : deduplicatedSegments.filter(s => detectSessionType(s) === key).length
                const active = typeFilter === key
                return (
                  <button key={key} onClick={() => { setTypeFilter(key); setActiveSegment(null) }} style={{
                    background: active ? 'var(--btn-strong)' : 'var(--chip-bg)',
                    color:      active ? '#fff'    : 'var(--text-muted)',
                    border: 'none', borderRadius: 20, padding: '7px 14px', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}>
                    <span>{t.label}</span>
                    <span style={{
                      background: active ? 'rgba(255,255,255,0.2)' : 'var(--track)',
                      color: active ? '#fff' : 'var(--text-faint)',
                      borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                    }}>{count}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {typedSegments.length === 0 && (
            <p style={{ color: 'var(--text-faint)', textAlign: 'center', padding: 24, fontSize: 14 }}>No hay sesiones de este tipo.</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[...typedSegments].reverse().map(seg => (
              <SessionCard
                key={seg.id}
                seg={seg}
                active={activeSegment?.id === seg.id}
                onClick={() => setActiveSegment(activeSegment?.id === seg.id ? null : seg)}
              />
            ))}
          </div>

          {activeSegment && (() => {
            // Prefer sessionId match (more reliable), fallback to time range
            const segReadings = allReadings.filter(r =>
              r.sessionId === activeSegment.id ||
              (r.time >= activeSegment.startTime && r.time <= activeSegment.endTime)
            )
            if (loadingSession && segReadings.length === 0) return (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px', textAlign: 'center', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <div style={{ width: 16, height: 16, border: '2px solid var(--field-border)', borderTopColor: 'var(--text-muted)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Cargando lecturas…</p>
              </div>
            )
            if (sessionLoadErr && segReadings.length === 0) return (
              <div style={{ background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 12, padding: '16px', textAlign: 'center', marginBottom: 14 }}>
                <p style={{ fontSize: 13, color: '#c0392b' }}>[ERROR] {sessionLoadErr}</p>
                <button onClick={() => loadSessionReadings(activeSegment)} style={{ marginTop: 8, background: 'none', border: '1px solid var(--field-border)', borderRadius: 8, padding: '6px 14px', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}>Reintentar</button>
              </div>
            )
            const segStats   = computeStats(segReadings)
            const segHrv     = computeHRV(segReadings)
            const segBeats   = expandToBeats(segReadings)
            const segPosture = analyzeSessionPosture(null, segReadings, activeSegment?.steps ?? 0)
            return (
              <div style={{ animation: 'fadeIn 0.2s ease' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
                  <MetricCard defKey="avg"   value={segStats.avg} />
                  <MetricCard defKey="min"   value={segStats.min} color="#007aff" />
                  <MetricCard defKey="max"   value={segStats.max} color="#ff3b30" />
                  {segHrv.rmssd    !== null && <MetricCard defKey="rmssd"  value={segHrv.rmssd}    color={segHrv.toneColor} />}
                  {segHrv.sdnn     !== null && <MetricCard defKey="sdnn"   value={segHrv.sdnn} />}
                  {segHrv.pnn50    !== null && <MetricCard defKey="pnn50"  value={segHrv.pnn50} />}
                  {segHrv.respRate !== null && <MetricCard defKey="resp"   value={segHrv.respRate} />}
                </div>

                {/* Desglose de postura y actividad en la sesión */}
                <PostureDistribution breakdown={segPosture} />
                {activeSegment.has_acc && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'var(--chip-bg)', border: '1px solid var(--border-soft)',
                    borderRadius: 12, padding: '8px 14px', marginBottom: 14
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <IconBarChart size={14} color="#007aff" />
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                        Acelerómetro crudo almacenado (50 Hz, 3 ejes X/Y/Z)
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownloadAccCsv(activeSegment.id)}
                      disabled={downloadingAcc}
                      style={{
                        background: 'var(--btn-strong)', color: '#fff', border: 'none',
                        borderRadius: 7, padding: '5px 11px', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4
                      }}
                    >
                      {downloadingAcc ? 'Generando CSV…' : '↓ Exportar CSV'}
                    </button>
                  </div>
                )}
                {segHrv.tone && (
                  <div style={{ background: 'var(--track)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: segHrv.toneColor, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: segHrv.toneColor, fontWeight: 600 }}>{segHrv.tone}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {segHrv.rrCount} intervalos RR{segHrv.rrCount < 30 ? ' (pocas lecturas)' : ''}</span>
                  </div>
                )}
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 18px 12px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Frecuencia cardíaca</p>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Un punto por latido · línea azul = promedio</p>
                    </div>
                    <SegControl value={view} onChange={setView}
                      options={[{ key: 'second', label: '1 s' }, { key: 'minute', label: '1 min' }, { key: 'hour', label: '1 h' }]} />
                  </div>
                  <div ref={chartRef}>
                    {(() => {
                      const segChartData = injectGaps(aggregateReadings(segBeats, view), view === 'second' ? 8000 : view === 'minute' ? 300_000 : 7_200_000)
                      if (!segChartData.some(d => d.hr != null)) {
                        return (
                          <div style={{
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            background: 'var(--field-bg)',
                            borderRadius: 10,
                            border: '1px dashed var(--border)',
                            margin: '8px 0',
                            padding: 16
                          }}>
                            <IconActivity size={24} color="var(--text-faint)" />
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)' }}>
                              Sin registros
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-faint)', textAlign: 'center' }}>
                              No hay lecturas de frecuencia cardíaca disponibles para esta sesión.
                            </span>
                          </div>
                        )
                      }
                      const segGradientStops = (() => {
                        if (!segChartData.length) return [{ offset: '0%', color: '#ff3b30' }, { offset: '100%', color: '#ff3b30' }]
                        const N = segChartData.length
                        if (N === 1) {
                          const c = segChartData[0].postureColor || '#ff3b30'
                          return [{ offset: '0%', color: c }, { offset: '100%', color: c }]
                        }
                        const stops = []
                        let currentC = null
                        let segStart = 0
                        for (let i = 0; i < N; i++) {
                          const c = segChartData[i].postureColor || '#ff3b30'
                          if (c !== currentC) {
                            if (currentC !== null) {
                              const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
                              const p2 = Math.min(100, Math.round((i / (N - 1)) * 100))
                              stops.push({ offset: `${p1}%`, color: currentC })
                              stops.push({ offset: `${p2}%`, color: currentC })
                            }
                            currentC = c
                            segStart = i
                          }
                        }
                        if (currentC !== null) {
                          const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
                          stops.push({ offset: `${p1}%`, color: currentC })
                          stops.push({ offset: '100%', color: currentC })
                        }
                        return stops
                      })()

                      return (
                        <>
                          <ResponsiveContainer width="100%" height={270}>
                            <AreaChart data={segChartData} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                              <defs>
                                <linearGradient id="gradSegStroke" x1="0" y1="0" x2="1" y2="0">
                                  {segGradientStops.map((s, idx) => (
                                    <stop key={idx} offset={s.offset} stopColor={s.color} />
                                  ))}
                                </linearGradient>
                                <linearGradient id="gradSegFill" x1="0" y1="0" x2="1" y2="0">
                                  {segGradientStops.map((s, idx) => (
                                    <stop key={idx} offset={s.offset} stopColor={s.color} stopOpacity={0.28} />
                                  ))}
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
                              <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-faint)' }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                              <YAxis domain={[40, 170]} tick={{ fontSize: 9, fill: 'var(--text-faint)' }} axisLine={false} tickLine={false} width={28} />
                              {segStats.avg && <ReferenceLine y={segStats.avg} stroke="#007aff" strokeDasharray="4 4" strokeWidth={1.5} />}
                              <Tooltip content={<ChartTooltip />} />
                              <Area type="linear" dataKey="hr" stroke="url(#gradSegStroke)" strokeWidth={2} fill="url(#gradSegFill)" dot={false} isAnimationActive={false} connectNulls={false} />
                              <Brush dataKey="label" height={24} stroke={isDark ? "rgba(255,255,255,0.15)" : "#e5e5ea"} fill={isDark ? "#161b22" : "#f9f9fb"} travellerWidth={8} tickFormatter={() => ''} startIndex={undefined} endIndex={undefined}>
                                <AreaChart>
                                  <Area type="linear" dataKey="hr" stroke="#ff3b30" strokeWidth={1} fill="#ff3b3018" dot={false} isAnimationActive={false} connectNulls={false} />
                                </AreaChart>
                              </Brush>
                            </AreaChart>
                          </ResponsiveContainer>
                        </>
                      )
                    })()}
                  </div>
                </div>
                {segHrv.rmssd !== null && (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px', marginBottom: 14 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>HRV — Variabilidad (RMSSD)</p>
                    <HRVTrendChart readings={segReadings} />
                  </div>
                )}

                {/* ── Análisis profundo — distribución por zonas ── */}
                {histZones && segReadings.length > 0 && (() => {
                  const beats = expandToBeats(segReadings)
                  const total = beats.length
                  if (!total) return null
                  const dist = histZones.map(z => {
                    const n = beats.filter(b => b.hr >= z.lo && b.hr < z.hi).length
                    return { ...z, n, pct: Math.round(n / total * 100) }
                  })
                  const durMin = Math.round((segReadings[segReadings.length-1]?.time - segReadings[0]?.time) / 60000)
                  return (
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px', marginBottom: 14 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>Distribución por zonas</p>
                      <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 14 }}>Tiempo real por zona · {durMin} min de sesión · FCmáx teórica {histMaxHr} BPM</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {dist.map(z => (
                          <div key={z.key}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {z.num
                                  ? <div style={{ width: 20, height: 20, borderRadius: 5, background: z.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                      <span style={{ fontSize: 9, fontWeight: 900, color: '#fff' }}>{z.num}</span>
                                    </div>
                                  : <span style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, display: 'inline-block', flexShrink: 0 }} />
                                }
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{z.name}</span>
                                <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>{z.lo}–{z.hi} BPM</span>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                                {z.pct}% <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-faint)' }}>({Math.round(z.n / 60)} min)</span>
                              </span>
                            </div>
                            <div style={{ height: 6, background: 'var(--chip-bg)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${z.pct}%`, background: z.color, borderRadius: 3, transition: 'width 0.5s ease' }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )
          })()}

        </div>
      )}

      {!loading && (
        <div style={{ paddingTop: 14, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 'calc(100vh - 84px)' }}>
          {/* ── Selector de período (compartido con Resumen) ── */}
          <div style={{ marginBottom: 10 }}>
            <PeriodToggle value={periodMode} onChange={setPeriodMode} />
          </div>

          {/* ── Selector de día: en celular la tira abierta y táctil, en computadora el selector clásico con dropdown ── */}
          {periodMode === 'day' && (() => {
            const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640

            if (isMobile) {
              const datesWithData = availableDatesSorted
              const curIndex = datesWithData.indexOf(selectedDate)

              // Mantener ventana de 5 fechas con datos fija en pantalla para no saltar bajo el dedo
              const defaultStart = curIndex >= 0 ? Math.max(0, curIndex - 2) : Math.max(0, datesWithData.length - 5)
              const startIdx = (dayWindowStart !== null && dayWindowStart >= 0 && dayWindowStart <= Math.max(0, datesWithData.length - 1))
                ? dayWindowStart
                : defaultStart
              const visibleDates = datesWithData.slice(startIdx, startIdx + 5)

              const weekDays = visibleDates.map(iso => {
                const d = new Date(iso + 'T12:00:00')
                const summary = daySummary[iso]
                return {
                  iso,
                  dateObj: d,
                  dayNum: d.getDate(),
                  dayName: d.toLocaleDateString('es-AR', { weekday: 'short' }).slice(0, 3).toUpperCase(),
                  hasData: true,
                  minutes: summary?.totalMin || 0,
                  avgHr: summary?.avgHr || null,
                  maxHr: summary?.maxHr || null,
                  sessions: summary?.sessions || 0,
                  isToday: iso === localDateKey(Date.now()),
                  isSelected: iso === selectedDate
                }
              })

              const canPrev = startIdx > 0
              const canNext = startIdx + 5 < datesWithData.length
              const lastDayIso = datesWithData.length > 0 ? datesWithData[datesWithData.length - 1] : null

              const handleStripTouch = (e) => {
                if (!e.touches || !e.touches[0]) return
                const touch = e.touches[0]
                const el = document.elementFromPoint(touch.clientX, touch.clientY)
                const btn = el?.closest('[data-scrub-day]')
                if (btn) {
                  const targetIso = btn.getAttribute('data-scrub-day')
                  if (targetIso && targetIso !== selectedDate) {
                    navigateDay(targetIso)
                    try { navigator.vibrate?.(10) } catch (_) {}
                  }
                }
              }

              const shiftDaysWindow = (delta) => {
                const maxStart = Math.max(0, datesWithData.length - 5)
                const nextStart = Math.max(0, Math.min(maxStart, startIdx + delta))
                setDayWindowStart(nextStart)
                if (delta < 0 && datesWithData[nextStart]) navigateDay(datesWithData[nextStart])
                else if (delta > 0 && datesWithData[Math.min(datesWithData.length - 1, nextStart + 4)]) navigateDay(datesWithData[Math.min(datesWithData.length - 1, nextStart + 4)])
              }

              const curDate = selectedDate ? new Date(selectedDate + 'T12:00:00') : new Date()

              return (
                <div className="vc-touch-scrubber" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 14, padding: '8px 10px', marginBottom: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  {/* Fila superior con fecha y botón Último día */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, padding: '0 2px' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Días grabados ({datesWithData.length})
                    </span>
                    {lastDayIso && selectedDate !== lastDayIso && (
                      <button
                        onClick={() => {
                          const maxStart = Math.max(0, datesWithData.length - 5)
                          setDayWindowStart(maxStart)
                          navigateDay(lastDayIso)
                        }}
                        style={{
                          background: 'var(--chip-bg)',
                          border: 'none',
                          borderRadius: 6,
                          color: '#ff3b30',
                          padding: '2px 7px',
                          fontSize: 10.5,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3
                        }}
                      >
                        Último día →
                      </button>
                    )}
                  </div>

                  {/* Carrusel interactivo fino con solo días con datos */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      onClick={() => shiftDaysWindow(-1)}
                      disabled={!canPrev}
                      style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 44, cursor: canPrev ? 'pointer' : 'default', fontSize: 16, color: canPrev ? 'var(--text-soft)' : 'var(--text-fainter)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Días anteriores"
                    >
                      ‹
                    </button>

                    <div
                      onTouchStart={handleStripTouch}
                      onTouchMove={handleStripTouch}
                      style={{ flex: 1, display: 'flex', gap: 3, justifyContent: 'space-between', touchAction: 'none' }}
                    >
                      {weekDays.map(wd => {
                        const active = wd.isSelected
                        return (
                          <button
                            key={wd.iso}
                            data-scrub-day={wd.iso}
                            onClick={() => {
                              navigateDay(wd.iso)
                              try { navigator.vibrate?.(10) } catch (_) {}
                            }}
                            style={{
                              flex: 1, minWidth: 0, height: 44, borderRadius: 9, border: 'none',
                              background: active ? 'var(--btn-strong)' : 'transparent',
                              color: active ? '#fff' : (wd.hasData ? 'var(--text)' : 'var(--text-faint)'),
                              cursor: 'pointer', fontFamily: 'inherit',
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              padding: '2px 0', gap: 0,
                              transform: active ? 'scale(1.03)' : 'scale(1)',
                              boxShadow: active ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5, opacity: active ? 0.9 : 0.65 }}>
                              {wd.dayName}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: active ? 800 : 700, lineHeight: 1.1 }}>
                              {wd.dayNum}
                            </span>
                            {wd.hasData ? (
                              <span style={{ fontSize: 7.5, fontWeight: 800, color: active ? '#fff' : '#34c759', lineHeight: 1 }}>
                                {wd.minutes >= 60 ? `${(wd.minutes / 60).toFixed(1)}h` : `${wd.minutes}m`}
                              </span>
                            ) : (
                              <span style={{ fontSize: 8, color: 'var(--text-fainter)' }}>—</span>
                            )}
                          </button>
                        )
                      })}
                    </div>

                    <button
                      onClick={() => shiftDaysWindow(1)}
                      disabled={!canNext}
                      style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 44, cursor: canNext ? 'pointer' : 'default', fontSize: 16, color: canNext ? 'var(--text-soft)' : 'var(--text-fainter)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Días siguientes"
                    >
                      ›
                    </button>
                  </div>

                  {/* Subtítulo informativo del día elegido con estadísticas completas */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-soft)' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
                      {curDate.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: triggerStats ? '#34c759' : 'var(--text-faint)' }}>
                      {triggerStats ? (
                        `● ${triggerStats.minutos} min${triggerStats.avgHr ? ` · ${triggerStats.avgHr} bpm` : ''}${triggerStats.rmssd ? ` · ${triggerStats.rmssd} ms` : ''}`
                      ) : (
                        dayLoading ? 'Cargando…' : '○ Sin registros'
                      )}
                    </span>
                  </div>
                </div>
              )
            }

            // ── COMPUTADORA / DESKTOP: Selector clásico compacto y elegante ──
            return (
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button onClick={() => prevDate && navigateDay(prevDate)} disabled={!prevDate}
                    style={{ background: prevDate ? 'var(--chip-bg)' : 'transparent', border: 'none', borderRadius: 9, width: 30, height: 36, cursor: prevDate ? 'pointer' : 'default', fontSize: 18, color: prevDate ? 'var(--text)' : '#d1d1d6', flexShrink: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                    ‹
                  </button>
                  <button
                    onClick={() => setCalendarOpen(o => !o)}
                    style={{
                      flex: 1, background: 'var(--btn-strong)', border: 'none', borderRadius: 10,
                      padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      textAlign: 'left', minWidth: 0, transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                      <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedDate ? (() => {
                          const d = new Date(selectedDate + 'T12:00:00')
                          return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                        })() : 'Elegir día'}
                      </p>
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {triggerStats ? (
                          triggerStats.isImported
                            ? `${triggerStats.minutos} min (importado)`
                            : `${triggerStats.lecturas?.toLocaleString() || 0} lecturas · ${triggerStats.minutos} min`
                        ) : (
                          dayLoading ? 'Cargando…' : 'Sin registros'
                        )}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: 10, textAlign: 'center', flexShrink: 0, alignItems: 'center' }}>
                      {triggerStats ? (
                        <>
                          {triggerStats.avgHr && <div><p style={{ fontSize: 13.5, fontWeight: 800, color: '#fff', margin: 0 }}>{triggerStats.avgHr}</p><p style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', margin: 0 }}>prom</p></div>}
                          {triggerStats.maxHr && <div><p style={{ fontSize: 13.5, fontWeight: 800, color: '#ff6b6b', margin: 0 }}>{triggerStats.maxHr}</p><p style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', margin: 0 }}>máx</p></div>}
                          {triggerStats.rmssd !== null && <div><p style={{ fontSize: 13.5, fontWeight: 800, color: '#64d2ff', margin: 0 }}>{triggerStats.rmssd}</p><p style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', margin: 0 }}>rmssd</p></div>}
                        </>
                      ) : (
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{calendarOpen ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </button>
                  <button onClick={() => nextDate && navigateDay(nextDate)} disabled={!nextDate}
                    style={{ background: nextDate ? 'var(--chip-bg)' : 'transparent', border: 'none', borderRadius: 9, width: 30, height: 36, cursor: nextDate ? 'pointer' : 'default', fontSize: 18, color: nextDate ? 'var(--text)' : '#d1d1d6', flexShrink: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s' }}>
                    ›
                  </button>
                  {availableDatesSorted.length > 0 && selectedDate !== availableDatesSorted[availableDatesSorted.length - 1] && (
                    <button onClick={() => navigateDay(availableDatesSorted[availableDatesSorted.length - 1])} style={{ background: 'var(--chip-bg)', color: '#ff3b30', border: 'none', borderRadius: 9, padding: '0 10px', height: 36, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, transition: 'all 0.1s' }}>Última</button>
                  )}
                </div>

                {calendarOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setCalendarOpen(false)} />
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)',
                      width: 'min(440px, 92vw)', maxWidth: '92vw', zIndex: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.13)', borderRadius: 14
                    }}>
                      <CalendarPicker
                        selectedDate={selectedDate}
                        availableDates={availableDates}
                        daySummary={daySummary}
                        annotatedDays={annotatedDays}
                        viewMonth={calendarMonth ?? new Date().toISOString().slice(0, 7)}
                        onViewMonthChange={setCalendarMonth}
                        onSelect={(iso) => { setSelectedDate(iso); setDayReadings([]); setCalendarMonth(iso.slice(0, 7)); setCalendarOpen(false) }}
                      />
                    </div>
                  </>
                )}
              </div>
            )
          })()}

          {/* ── Semana: selector en tira horizontal abierta y táctil (Scrubber) ── */}
          {periodMode === 'week' && (() => {
            const weeks = [...new Set([...availableDates].map(weekKeyOf))].sort()
            const curIndex = weeks.indexOf(selectedWeek)

            // Mantener ventana de 5 semanas fija en pantalla para que no salte al tocar una píldora
            const defaultStart = curIndex >= 0 ? Math.max(0, curIndex - 2) : Math.max(0, weeks.length - 5)
            const startIdx = (weekWindowStart !== null && weekWindowStart >= 0 && weekWindowStart <= Math.max(0, weeks.length - 1))
              ? weekWindowStart
              : defaultStart
            const visibleWeeks = weeks.slice(startIdx, startIdx + 5)

            const curWeekSessions = selectedWeek ? deduplicatedSegments.filter(s => weekKeyOf(localDateKey(s.startTime)) === selectedWeek) : []
            const totalMs = curWeekSessions.reduce((a, s) => a + (s.endTime - s.startTime), 0)
            const totalMin = Math.round(totalMs / 60000)
            const cur = selectedWeek ? weekFullLabel(selectedWeek) : null

            const canPrev = startIdx > 0
            const canNext = startIdx + 5 < weeks.length
            const lastWk = weeks.length > 0 ? weeks[weeks.length - 1] : null

            const handleWeekTouch = (e) => {
              if (!e.touches || !e.touches[0]) return
              const touch = e.touches[0]
              const el = document.elementFromPoint(touch.clientX, touch.clientY)
              const btn = el?.closest('[data-scrub-week]')
              if (btn) {
                const wIso = btn.getAttribute('data-scrub-week')
                if (wIso && wIso !== selectedWeek) {
                  setSelectedWeek(wIso)
                  try { navigator.vibrate?.(10) } catch (_) {}
                }
              }
            }

            const shiftWeeksWindow = (delta) => {
              const maxStart = Math.max(0, weeks.length - 5)
              const nextStart = Math.max(0, Math.min(maxStart, startIdx + delta))
              setWeekWindowStart(nextStart)
              if (delta < 0 && weeks[nextStart]) setSelectedWeek(weeks[nextStart])
              else if (delta > 0 && weeks[Math.min(weeks.length - 1, nextStart + 4)]) setSelectedWeek(weeks[Math.min(weeks.length - 1, nextStart + 4)])
            }

            return (
              <div className="vc-touch-scrubber" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 14, padding: '8px 10px', marginBottom: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                {/* Fila superior con botón Última semana */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, padding: '0 2px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Semanas grabadas
                  </span>
                  {lastWk && selectedWeek !== lastWk && (
                    <button
                      onClick={() => {
                        const maxStart = Math.max(0, weeks.length - 5)
                        setWeekWindowStart(maxStart)
                        setSelectedWeek(lastWk)
                      }}
                      style={{
                        background: 'var(--chip-bg)',
                        border: 'none',
                        borderRadius: 6,
                        color: '#ff3b30',
                        padding: '2px 7px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                      }}
                    >
                      Última semana →
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => shiftWeeksWindow(-1)}
                    disabled={!canPrev}
                    style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 44, cursor: canPrev ? 'pointer' : 'default', fontSize: 16, color: canPrev ? 'var(--text-soft)' : 'var(--text-fainter)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Semana anterior"
                  >
                    ‹
                  </button>

                  <div
                    onTouchStart={handleWeekTouch}
                    onTouchMove={handleWeekTouch}
                    style={{ flex: 1, display: 'flex', gap: 3, justifyContent: 'space-between', touchAction: 'none' }}
                  >
                    {visibleWeeks.map(wIso => {
                      const active = wIso === selectedWeek
                      const info = weekFullLabel(wIso)
                      const sess = deduplicatedSegments.filter(s => weekKeyOf(localDateKey(s.startTime)) === wIso)
                      const wMs = sess.reduce((a, s) => a + (s.endTime - s.startTime), 0)
                      const wMin = Math.round(wMs / 60000)
                      return (
                        <button
                          key={wIso}
                          data-scrub-week={wIso}
                          onClick={() => {
                            setSelectedWeek(wIso)
                            try { navigator.vibrate?.(10) } catch (_) {}
                          }}
                          style={{
                            flex: 1, minWidth: 0, height: 44, borderRadius: 9, border: 'none',
                            background: active ? 'var(--btn-strong)' : 'transparent',
                            color: active ? '#fff' : (sess.length > 0 ? 'var(--text)' : 'var(--text-faint)'),
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            padding: '2px 0', gap: 0,
                            transform: active ? 'scale(1.03)' : 'scale(1)',
                            boxShadow: active ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5, opacity: active ? 0.9 : 0.65 }}>
                            SEM {info.wn}
                          </span>
                          <span style={{ fontSize: 12.5, fontWeight: active ? 800 : 700, lineHeight: 1.1 }}>
                            {info.range.split(' ')[0]}
                          </span>
                          {wMin > 0 ? (
                            <span style={{ fontSize: 7.5, fontWeight: 800, color: active ? '#fff' : '#34c759', marginTop: 1 }}>
                              {wMin >= 60 ? `${(wMin/60).toFixed(1)}h` : `${wMin}m`}
                            </span>
                          ) : (
                            <span style={{ fontSize: 8, color: 'var(--text-fainter)' }}>—</span>
                          )}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    onClick={() => shiftWeeksWindow(1)}
                    disabled={!canNext}
                    style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 44, cursor: canNext ? 'pointer' : 'default', fontSize: 16, color: canNext ? 'var(--text-soft)' : 'var(--text-fainter)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Semana siguiente"
                  >
                    ›
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-soft)' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>
                    {cur ? `Semana ${cur.wn} · del ${cur.range}` : 'Semana seleccionada'}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: curWeekSessions.length > 0 ? '#34c759' : 'var(--text-faint)' }}>
                    {curWeekSessions.length > 0 ? `● ${curWeekSessions.length} ses. · ${totalMin >= 60 ? `${Math.floor(totalMin/60)}h ${totalMin%60}m` : `${totalMin}m`}` : '○ Sin registros'}
                  </span>
                </div>
              </div>
            )
          })()}

          {/* ── Mes: selector en tira horizontal abierta y táctil (Scrubber) ── */}
          {periodMode === 'month' && (() => {
            const curYm = selectedMonth || new Date().toISOString().slice(0, 7)
            const [curYear, curMonthNum] = curYm.split('-').map(Number)

            const yearMonths = []
            const MONTH_NAMES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC']
            for (let m = 1; m <= 12; m++) {
              const ym = `${curYear}-${String(m).padStart(2, '0')}`
              const sess = deduplicatedSegments.filter(s => localMonthKey(s.startTime) === ym)
              const mMs = sess.reduce((a, s) => a + (s.endTime - s.startTime), 0)
              const mMin = Math.round(mMs / 60000)
              yearMonths.push({
                ym,
                name: MONTH_NAMES[m - 1],
                hasData: sess.length > 0,
                sessCount: sess.length,
                totalMin: mMin,
                isSelected: ym === selectedMonth
              })
            }

            const curMonthSessions = selectedMonth ? deduplicatedSegments.filter(s => localMonthKey(s.startTime) === selectedMonth) : []
            const totalMs = curMonthSessions.reduce((a, s) => a + (s.endTime - s.startTime), 0)
            const totalMin = Math.round(totalMs / 60000)
            const curLabel = selectedMonth ? new Date(selectedMonth + '-15T12:00:00').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }) : 'Elegir mes'
            const lastDataYm = yearMonths.filter(m => m.hasData).pop()?.ym

            const handleMonthTouch = (e) => {
              if (!e.touches || !e.touches[0]) return
              const touch = e.touches[0]
              const el = document.elementFromPoint(touch.clientX, touch.clientY)
              const btn = el?.closest('[data-scrub-month]')
              if (btn) {
                const ym = btn.getAttribute('data-scrub-month')
                if (ym && ym !== selectedMonth) {
                  setSelectedMonth(ym)
                  try { navigator.vibrate?.(10) } catch (_) {}
                }
              }
            }

            const shiftYear = (delta) => {
              const nextYear = curYear + delta
              const nextYm = `${nextYear}-${String(curMonthNum).padStart(2, '0')}`
              setSelectedMonth(nextYm)
            }

            return (
              <div className="vc-touch-scrubber" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 14, padding: '8px 10px', marginBottom: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                {/* Fila superior con año y botón Último mes */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, padding: '0 2px' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Año {curYear}
                  </span>
                  {lastDataYm && selectedMonth !== lastDataYm && (
                    <button
                      onClick={() => setSelectedMonth(lastDataYm)}
                      style={{
                        background: 'var(--chip-bg)',
                        border: 'none',
                        borderRadius: 6,
                        color: '#ff3b30',
                        padding: '2px 7px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3
                      }}
                    >
                      Último mes →
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => shiftYear(-1)}
                    style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 38, cursor: 'pointer', fontSize: 16, color: 'var(--text-soft)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Año anterior"
                  >
                    ‹
                  </button>

                  <div
                    onTouchStart={handleMonthTouch}
                    onTouchMove={handleMonthTouch}
                    style={{ flex: 1, display: 'flex', gap: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'none' }}
                  >
                    {yearMonths.map(mItem => {
                      const active = mItem.isSelected
                      return (
                        <button
                          key={mItem.ym}
                          data-scrub-month={mItem.ym}
                          onClick={() => {
                            setSelectedMonth(mItem.ym)
                            try { navigator.vibrate?.(10) } catch (_) {}
                          }}
                          style={{
                            flex: 1, minWidth: 22, height: 38, borderRadius: 8, border: 'none',
                            background: active ? 'var(--btn-strong)' : 'transparent',
                            color: active ? '#fff' : (mItem.hasData ? 'var(--text)' : 'var(--text-faint)'),
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            padding: '1px 0', gap: 0,
                            transform: active ? 'scale(1.03)' : 'scale(1)',
                            boxShadow: active ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span style={{ fontSize: 9, fontWeight: active ? 800 : 700, letterSpacing: 0.2 }}>
                            {mItem.name}
                          </span>
                          {mItem.totalMin > 0 ? (
                            <span style={{ fontSize: 7, fontWeight: 800, color: active ? '#fff' : '#34c759' }}>
                              {mItem.totalMin >= 60 ? `${Math.round(mItem.totalMin/60)}h` : `${mItem.totalMin}m`}
                            </span>
                          ) : (
                            <span style={{ fontSize: 7.5, color: 'var(--text-fainter)' }}>—</span>
                          )}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    onClick={() => shiftYear(1)}
                    style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, width: 26, height: 38, cursor: 'pointer', fontSize: 16, color: 'var(--text-soft)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Año siguiente"
                  >
                    ›
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-soft)' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
                    {curLabel}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: curMonthSessions.length > 0 ? '#34c759' : 'var(--text-faint)' }}>
                    {curMonthSessions.length > 0 ? `● ${curMonthSessions.length} ses. · ${totalMin >= 60 ? `${Math.floor(totalMin/60)}h ${totalMin%60}m` : `${totalMin}m`}` : '○ Sin registros'}
                  </span>
                </div>
              </div>
            )
          })()}

          <div
            ref={fullViewRef}
            onTouchStart={e => {
              if (periodMode === 'day') {
                daySwipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() }
              }
            }}
            onTouchEnd={e => {
              if (!daySwipeRef.current || periodMode !== 'day') return
              const dx = e.changedTouches[0].clientX - daySwipeRef.current.x
              const dy = e.changedTouches[0].clientY - daySwipeRef.current.y
              const dt = Date.now() - daySwipeRef.current.t
              daySwipeRef.current = null
              if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx) * 0.8 || dt > 500) return
              if (dx < 0 && nextDate) {
                try { if ('vibrate' in navigator) navigator.vibrate([25]) } catch(e) {}
                navigateDay(nextDate)
              } else if (dx > 0 && prevDate) {
                try { if ('vibrate' in navigator) navigator.vibrate([25]) } catch(e) {}
                navigateDay(prevDate)
              }
            }}
          >
            {/* ── COCKPIT EJECUTIVO DE SALUD & RECUPERACIÓN (Día seleccionado) ── */}
            {periodMode === 'day' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 12, marginBottom: 14 }}>
                {/* 1. Semáforo y Batería de Recuperación (Diseño Whoop / Apple Health) */}
                <div style={{
                  background: `radial-gradient(130% 120% at 0% 0%, ${recColor}16 0%, var(--card-bg) 60%)`,
                  border: `1.5px solid ${recColor}33`,
                  borderRadius: 20,
                  padding: '18px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  boxShadow: `0 8px 24px -8px ${recColor}20, var(--shadow-sm)`,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Fila superior: Tag de categoría + Badge pill de estado */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 6, background: `${recColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconZap size={13} color={recColor} />
                      </div>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Recuperación & Batería Corporal
                      </span>
                    </div>

                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '3px 10px',
                      borderRadius: 20,
                      background: `${recColor}16`,
                      color: recColor,
                      border: `1px solid ${recColor}30`,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0.2
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: recColor, boxShadow: `0 0 6px ${recColor}` }} />
                      {recLabel}
                    </span>
                  </div>

                  {/* Fila central: Anillo Gauge HD + Diagnóstico Clínico */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    {/* Ring circular estilizado con gradiente */}
                    <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0 }}>
                      <svg width="84" height="84" viewBox="0 0 100 100">
                        <defs>
                          <linearGradient id="recScoreGrad" x1="0" y1="1" x2="1" y2="0">
                            <stop offset="0%" stopColor={recColor || '#30d158'} />
                            <stop offset="100%" stopColor={recSecondaryColor || recColor || '#10b981'} />
                          </linearGradient>
                        </defs>
                        <circle cx="50" cy="50" r="41" fill="none" stroke="var(--track)" strokeWidth="8" opacity="0.6" />
                        <circle
                          cx="50" cy="50" r="41" fill="none" stroke="url(#recScoreGrad)" strokeWidth="8"
                          strokeLinecap="round" strokeDasharray={`${((recoveryScore || 0) / 100) * 257.6} 257.6`}
                          transform="rotate(-90 50 50)" style={{ transition: 'stroke-dasharray 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 28, fontWeight: 900, color: recColor, lineHeight: 1, letterSpacing: -1 }}>
                          {recoveryScore ?? '–'}
                        </span>
                        <span style={{ fontSize: 8, color: 'var(--text-faint)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 1 }}>
                          / 100
                        </span>
                      </div>
                    </div>

                    {/* Texto de diagnóstico y recomendación clínica */}
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px', letterSpacing: -0.3 }}>
                        {recLabel}
                      </h4>
                      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>
                        {recTip}
                      </p>
                    </div>
                  </div>

                  {/* Fila inferior: Barra de Batería Corporal & Capacidad */}
                  <div style={{ borderTop: '1px solid var(--border-soft)', paddingTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 11 }}>
                      <span style={{ color: 'var(--text-faint)', fontWeight: 700 }}>Capacidad Energética</span>
                      <span style={{ color: recColor, fontWeight: 800 }}>{recoveryScore ? `${recoveryScore}% disponible` : 'Sin datos'}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--track)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${recoveryScore ? Math.min(100, recoveryScore) : 0}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${recColor || '#30d158'}, ${recSecondaryColor || recColor || '#10b981'})`,
                        borderRadius: 3,
                        transition: 'width 0.8s ease'
                      }} />
                    </div>
                  </div>
                </div>

                {/* 2. Cuadrícula de 4 Métricas Clave (Estilo Laboratorio Clínico) */}
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 18,
                  padding: '16px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginBottom: 26,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                  <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                    <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                      01
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Métricas Cardíacas Basales
                    </span>
                  </div>
                  {/* FC Reposo */}
                  <div style={{
                    background: 'var(--field-bg)',
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        FC Reposo
                      </span>
                      <IconHeart size={14} color="#ff3b30" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)', fontFamily: 'monospace' }}>
                          {daySegStats?.restingHr || daySegStats?.min || '–'}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)' }}>bpm</span>
                      </div>
                      <span style={{ fontSize: 9.5, color: 'var(--text-soft)', fontWeight: 600 }}>Basal mínimo</span>
                    </div>
                  </div>

                  {/* HRV (RMSSD) */}
                  <div style={{
                    background: 'var(--field-bg)',
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        HRV (RMSSD)
                      </span>
                      <IconZap size={14} color="#30d158" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: '#30d158', fontFamily: 'monospace' }}>
                          {daySegHrv?.rmssd || '–'}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)' }}>ms</span>
                      </div>
                      <span style={{ fontSize: 9.5, color: 'var(--text-soft)', fontWeight: 600 }}>Tono parasimpático</span>
                    </div>
                  </div>

                  {/* FC Promedio */}
                  <div style={{
                    background: 'var(--field-bg)',
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        FC Promedio
                      </span>
                      <IconActivity size={14} color="#007aff" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)', fontFamily: 'monospace' }}>
                          {daySegStats?.avg || '–'}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)' }}>bpm</span>
                      </div>
                      <span style={{ fontSize: 9.5, color: 'var(--text-soft)', fontWeight: 600 }}>Carga del día</span>
                    </div>
                  </div>

                  {/* Pasos */}
                  <div style={{
                    background: 'var(--field-bg)',
                    border: 'none',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Pasos Totales
                      </span>
                      <IconSteps size={14} color="#ff9500" />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: '#ff9500', fontFamily: 'monospace' }}>
                          {fmtLatidos(dayStepsTotal || 0)}
                        </span>
                      </div>
                      <span style={{ fontSize: 9.5, color: 'var(--text-soft)', fontWeight: 600 }}>Actividad motriz</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Constancia (la misma card del Resumen) siempre arriba en Análisis ── */}
            {constanciaSessions.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <ConstanciaCard
                  sessions={constanciaSessions}
                  initialView={periodMode}
                  activeDate={
                    periodMode === 'day' ? selectedDate :
                    periodMode === 'week' ? selectedWeek :
                    selectedMonth ? `${selectedMonth}-01` : null
                  }
                  onSelectTimeRange={(dateIso, startHour, endHour) => {
                    const BK = 5 * 60000
                    const nn = allDayBuckets.length - 1
                    if (nn > 0) {
                      const startIdx = Math.max(0, Math.floor((startHour * 3600000) / BK))
                      const endIdx = Math.min(nn, Math.ceil((endHour * 3600000) / BK) - 1)
                      cancelAnimationFrame(animFrameRef.current)
                      setDayBrushRange({ start: startIdx, end: endIdx })
                      
                      setTimeout(() => {
                        const el = document.getElementById('section-fc')
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }, 100)
                    }
                  }}
                />
              </div>
            )}

            {/* ── Estado: cargando / error / sin datos / charts ── */}
          {periodMode === 'day' && !selectedDate && (
            <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: '32px', textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'var(--text-faint)' }}>Seleccioná un día</p>
            </div>
          )}

          {periodMode === 'day' && selectedDate && dayLoading && (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '32px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <span style={{ width: 16, height: 16, border: '2px solid var(--field-border)', borderTopColor: 'var(--text)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block', flexShrink: 0 }} />
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Cargando datos del {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}…</p>
            </div>
          )}

          {periodMode === 'day' && selectedDate && !dayLoading && dayErr && (
            <div style={{ background: 'rgba(255, 59, 48, 0.1)', border: '1px solid rgba(255, 59, 48, 0.25)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 13, color: '#ff453a', margin: 0 }}>[ERROR] {dayErr}</p>
              <button onClick={() => loadDayReadings(selectedDate)} style={{ background: 'none', border: '1px solid rgba(255, 59, 48, 0.4)', borderRadius: 8, padding: '5px 12px', fontSize: 12, color: '#ff453a', cursor: 'pointer', fontFamily: 'inherit' }}>Reintentar</button>
            </div>
          )}

          {periodMode === 'day' && selectedDate && !dayLoading && !dayErr && dayReadings.length === 0 && (() => {
            const dateLabel   = new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-AR', { weekday:'long', day:'numeric', month:'long' })
            const fmtHM  = ms => { const d = new Date(ms); const p = n=>String(n).padStart(2,'0'); return `${p(d.getHours())}:${p(d.getMinutes())}` }
            const fmtMin = (a,b) => { const m = Math.round((b-a)/60000); return m>=60?`${Math.floor(m/60)}h ${m%60}m`:`${m}m` }

            if (daySessions.length === 0) return (
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '24px', textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--text-faint)', margin: 0 }}>Sin lecturas registradas para este día.</p>
              </div>
            )

            // ── Aggregate metrics from all sessions ──────────────────────────
            const totalMs    = daySessions.reduce((a,s) => a + s.endTime - s.startTime, 0)
            const totalMin   = Math.round(totalMs / 60000)
            const withHr     = daySessions.filter(s => s.avg_hr)
            const withHrv    = daySessions.filter(s => s.rmssd)
            const avgHr      = withHr.length  ? Math.round(withHr.reduce((a,s) => a+s.avg_hr, 0) / withHr.length)   : null
            const maxHr      = daySessions.reduce((m,s) => Math.max(m, s.max_hr ?? 0), 0) || null
            const minHr      = daySessions.reduce((m,s) => Math.min(m, s.min_hr ?? 999), 999) < 999 ? daySessions.reduce((m,s) => Math.min(m, s.min_hr ?? 999), 999) : null
            const avgRmssd   = withHrv.length ? Math.round(withHrv.reduce((a,s) => a+s.rmssd,  0) / withHrv.length)  : null
            const avgPnn50   = withHrv.length && daySessions.some(s=>s.pnn50!=null)
              ? Math.round(daySessions.filter(s=>s.pnn50!=null).reduce((a,s)=>a+(s.pnn50??0),0) / daySessions.filter(s=>s.pnn50!=null).length)
              : null

            const rmssdTone  = !avgRmssd ? null
              : avgRmssd > 50 ? {t:'Parasimpático alto', c:'#34c759'}
              : avgRmssd > 30 ? {t:'Equilibrado',         c:'#007aff'}
              : avgRmssd > 15 ? {t:'Simpático activo',    c:'#ff9f0a'}
              :                 {t:'Alta activación',     c:'#ff3b30'}

            const hrZone     = avgHr && histZones ? histZones.find(z => avgHr >= z.lo && avgHr < z.hi) : null

            const SH = ({n,title,sub}) => (
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, marginTop:4 }}>
                <div style={{ width:22, height:22, borderRadius:6, background:'var(--btn-strong)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:10, fontWeight:900, color:'#fff' }}>{n}</span>
                </div>
                <div>
                  <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>{title}</p>
                  {sub && <p style={{ fontSize:11, color:'var(--text-faint)', margin:0 }}>{sub}</p>}
                </div>
              </div>
            )

            return (
              <div>
                {/* ── Info banner: Sesión importada desde iOS adaptada a modo noche ── */}
                <div style={{
                  background: 'var(--tint-blue)',
                  border: '1px solid var(--tint-blue-border)',
                  borderRadius: 12,
                  padding: '12px 14px',
                  marginBottom: 16,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center'
                }}>
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <IconRadio size={18} color="#007aff" />
                  </div>
                  <div>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)', margin: '0 0 2px' }}>
                      Sesión importada desde Apple Salud / iOS
                    </p>
                    <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: 0, lineHeight: 1.45 }}>
                      Esta medición contiene el resumen de la sesión (sin lecturas segundo a segundo). Los gráficos de alta frecuencia y análisis espectral requieren grabación directa con la app.
                    </p>
                  </div>
                </div>

                {/* ── Sección 1: FC ── */}
                {(avgHr || maxHr || minHr) && (
                  <>
                    <SH n="1" title="Frecuencia Cardíaca" sub="Resumen de la sesión" />
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
                      {avgHr && <MetricCard defKey="avg" value={avgHr} />}
                      {minHr && <MetricCard defKey="min" value={minHr} color="#007aff" />}
                      {maxHr && <MetricCard defKey="max" value={maxHr} color="#ff3b30" />}
                    </div>
                  </>
                )}

                {/* ── Zona estimada ── */}
                {hrZone && (
                  <div style={{ background:hrZone.bg, border:`1.5px solid ${hrZone.color}40`, borderRadius:14, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ minWidth:48, height:48, borderRadius:12, background:hrZone.color, flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                      {hrZone.num
                        ? <><span style={{ fontSize:9, color:'rgba(255,255,255,0.8)', fontWeight:700, letterSpacing:1 }}>ZONA</span><span style={{ fontSize:22, fontWeight:900, color:'#fff', lineHeight:1 }}>{hrZone.num}</span></>
                        : <span style={{ fontSize:11, fontWeight:800, color:'#fff', textAlign:'center', lineHeight:1.2, padding:'0 4px' }}>{hrZone.short?.toUpperCase()}</span>
                      }
                    </div>
                    <div>
                      <p style={{ fontSize:11, color:'var(--text-faint)', fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, margin:'0 0 2px' }}>Zona estimada (FC promedio)</p>
                      <p style={{ fontSize:15, fontWeight:700, color:hrZone.color, margin:'0 0 3px' }}>{hrZone.name}</p>
                      <p style={{ fontSize:12, color:'var(--text-muted)', margin:0 }}>{hrZone.desc}</p>
                    </div>
                  </div>
                )}

                {/* ── Sección 2: HRV ── */}
                {avgRmssd && (
                  <>
                    <SH n="2" title="Sistema Nervioso (HRV)" sub="Variabilidad cardíaca del resumen" />
                    <div style={{ display:'grid', gridTemplateColumns:`repeat(${avgPnn50!=null?2:1},1fr)`, gap:10, marginBottom:12 }}>
                      <MetricCard defKey="rmssd" value={avgRmssd} color={rmssdTone?.c} />
                      {avgPnn50 != null && <MetricCard defKey="pnn50" value={avgPnn50} />}
                    </div>
                    {rmssdTone && (
                      <div style={{ background:`${rmssdTone.c}12`, border:`1px solid ${rmssdTone.c}30`, borderRadius:10, padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:rmssdTone.c, flexShrink:0 }}/>
                        <span style={{ fontSize:13, fontWeight:700, color:rmssdTone.c }}>{rmssdTone.t}</span>
                        <span style={{ fontSize:11, color:'var(--text-muted)' }}>· RMSSD {avgRmssd} ms</span>
                      </div>
                    )}
                  </>
                )}

                {/* ── Sección 3: Sesiones del día ── */}
                <SH n={avgRmssd ? '3' : '2'} title="Sesiones del día" sub={`${daySessions.length} sesi${daySessions.length>1?'ones':'ón'}`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  {daySessions.map(seg => (
                    <SessionCard
                      key={seg.id}
                      seg={seg}
                      active={activeSegment?.id === seg.id}
                      onClick={() => setActiveSegment(activeSegment?.id === seg.id ? null : seg)}
                      onDelete={async (sessId) => {
                        const cleanId = String(sessId).replace('_part1', '').replace('_part2', '')
                        try {
                          const res = await fetch(`${apiBase}/sessions/${cleanId}`, {
                            method: 'DELETE',
                            headers: { Authorization: `Bearer ${token}` }
                          })
                          if (res.ok) {
                            setSegments(prev => prev.filter(s => s.id !== cleanId && s.id !== sessId))
                            setDayReadings([])
                          } else {
                            const d = await res.json().catch(() => ({}))
                            alert(d.error || 'Error al eliminar sesión')
                          }
                        } catch (e) {
                          alert('Error de conexión')
                        }
                      }}
                    />
                  ))}
                </div>
                {renderEcgPanel(avgRmssd ? "4" : "3")}
              </div>
            )
          })()}

          {/* ── Día con datos — usa valores memoizados del componente ── */}
          {periodMode === 'day' && selectedDate && !dayLoading && !dayErr && dayReadings.length > 0 && (() => {
            // All heavy computation (visibleReadings, dayBuckets, daySegStats, daySegHrv,
            // dayArrhythmia, dayRmssdPersonalAvg) is memoized at component level above.
            const dayLabel  = new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            const todLabel  = todFilter === 'sleep' ? ' · Sueño (22–8h)' : todFilter === 'work' ? ' · Trabajo (9–18h)' : ''

            const zoneDistDay = histZones && rangeReadings.length > 0 ? (() => {
              const beats = expandToBeats(rangeReadings)
              if (!beats.length) return null
              return histZones.map(z => ({
                ...z,
                pct: Math.round(beats.filter(b => b.hr >= z.lo && b.hr < z.hi).length / beats.length * 100),
                min: Math.round(beats.filter(b => b.hr >= z.lo && b.hr < z.hi).length / 60),
              }))
            })() : null

            if (visibleReadings.length === 0) return (
              <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: '24px', textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: 'var(--text-faint)' }}>Sin datos en el horario seleccionado{todLabel}</p>
              </div>
            )

            const SectionHeader = ({ n, title, sub, info }) => {
              const [open, setOpen] = useState(false)
              return (
                <div style={{ marginBottom: 14, width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginTop: 4, width: '100%', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--btn-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>{n}</span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</p>
                        {sub && <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</p>}
                      </div>
                    </div>
                    {info && (
                      <button
                        onClick={() => setOpen(o => !o)}
                        aria-label="Más información"
                        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', padding: 4, background: 'none', border: 'none', color: open ? 'var(--btn-strong)' : 'var(--text-faint)', transition: 'color 0.2s', outline: 'none' }}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: open ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {open && info && (
                    <div style={{ padding: '8px 12px', background: 'var(--chip-bg)', borderRadius: 10, border: '1px dashed var(--border)', fontSize: 11.5, color: 'var(--text-soft)', lineHeight: 1.4, marginTop: 6, textAlign: 'left' }}>
                      [INFO] {info}
                    </div>
                  )}
                </div>
              )
            }

            const ChartPurpose = () => null

            // ── Plain-language summary ────────────────────────────────────────
            const durationMin    = Math.round(visibleReadings.length / 60)
            const isNight        = todFilter === 'sleep' || new Date(visibleReadings[0].time).getHours() >= 22 || new Date(visibleReadings[0].time).getHours() < 6
            const avgHrNum       = daySegStats.avg ?? 0
            const maxHrNum       = daySegStats.max ?? 0
            const rmssdNum       = daySegHrv.rmssd ?? 0
            const hrVerdict = isNight
              ? (avgHrNum > 0 && avgHrNum < 65 ? { icon: '[SUEÑO]', text: 'Sueño tranquilo — corazón en calma', color: '#5856d6' }
                                                : { icon: '[NOCHE]', text: 'Registro nocturno', color: 'var(--text-faint)' })
              : maxHrNum > 140 ? { icon: '[EJERCICIO]', text: 'Sesión de ejercicio intenso', color: '#ff9f0a' }
              : maxHrNum > 110 ? { icon: '[ACTIVIDAD]', text: 'Actividad moderada', color: '#ff9f0a' }
              : avgHrNum > 0 && avgHrNum < 75 ? { icon: '[REPOSO]', text: 'Día tranquilo — frecuencia normal en reposo', color: '#34c759' }
              : { icon: '[NORMAL]', text: 'Actividad normal', color: '#007aff' }

            const hrDesc = avgHrNum > 0
              ? (avgHrNum < 60 ? `${avgHrNum} bpm — bradicardia leve, normal en deportistas`
              : avgHrNum < 80 ? `${avgHrNum} bpm — dentro del rango normal en reposo`
              : avgHrNum < 100 ? `${avgHrNum} bpm — ligeramente elevado`
              : `${avgHrNum} bpm — elevado, posible actividad intensa o estrés`) : null

            const hrvDesc = rmssdNum > 0
              ? (rmssdNum > 50 ? `HRV de ${rmssdNum} ms — sistema nervioso recuperado, listo para rendir`
              : rmssdNum > 30 ? `HRV de ${rmssdNum} ms — buen balance entre actividad y descanso`
              : rmssdNum > 15 ? `HRV de ${rmssdNum} ms — algo de estrés o fatiga acumulada`
              : `HRV de ${rmssdNum} ms — cuerpo bajo carga, priorizá el descanso`) : null

            const hasTechnical = daySegHrv.rmssd !== null || dayArrhythmia
            // Pasos del día: suma de los pasos (acelerómetro del sensor) de las sesiones de esa fecha
            const daySteps = deduplicatedSegments.reduce((a, s) =>
              a + ((localDateKey(s.startTime) === selectedDate && s.steps > 0) ? s.steps : 0), 0)

            return (
             <div>
                {/* ── Sección 1: Frecuencia Cardíaca ── */}
                <div id="section-fc">
                  <SectionHeader n="1" title="Frecuencia Cardíaca" sub={
                    todFilter === 'sleep' ? 'Sueño 22:00–08:00 · el selector de abajo filtra todas las secciones' :
                    todFilter === 'work'  ? 'Trabajo 09:00–18:00 · el selector de abajo filtra todas las secciones' :
                    'Arrastrá el selector de abajo para elegir el rango — filtra todas las secciones'
                  } info="Ver cómo cambió tu pulso durante el día (esfuerzo, estrés o descanso). Arrastrá el selector inferior para elegir el rango de tiempo, y arrastrá sobre el gráfico para marcar qué hacías." />
                </div>
                {rangeChip}
                
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${3 + (rangeHrv.respRate !== null ? 1 : 0) + (liveSteps !== null ? 1 : 0)}, 1fr)`, gap: 10, marginBottom: 12 }}>
                  <MetricCard defKey="avg"  value={liveStats.avg} />
                  <MetricCard defKey="min"  value={liveStats.min} color="#007aff" />
                  <MetricCard defKey="max"  value={liveStats.max} color="#ff3b30" />
                  {rangeHrv.respRate !== null && <MetricCard defKey="resp" value={rangeHrv.respRate} />}
                  {liveSteps !== null && <MetricCard defKey="steps" value={liveSteps} />}
                </div>

                {/* Desglose de postura y actividad del período visible */}
                <PostureDistribution breakdown={analyzeSessionPosture(null, visibleReadings, daySteps)} />
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px 14px 10px', marginBottom: 26, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                        02
                      </span>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        Curva Continua de Pulso (BPM)
                      </span>
                    </div>
                    {allDayBuckets.length > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          onClick={() => { cancelAnimationFrame(animFrameRef.current); setDayBrushRange({ start: 0, end: allDayBuckets.length - 1 }) }}
                          style={{
                            fontSize: 10, fontWeight: 700, color: 'var(--text-soft)',
                            background: 'var(--chip-bg)', border: '1px solid var(--border-soft)',
                            borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit',
                            transition: 'all .15s'
                          }}
                        >
                          Ver todo
                        </button>
                        <button
                          onClick={() => { cancelAnimationFrame(animFrameRef.current); setDayBrushRange(targetBrushRange) }}
                          style={{
                            fontSize: 10, fontWeight: 700, color: '#007aff',
                            background: 'rgba(0,122,255,0.08)', border: '1px solid rgba(0,122,255,0.22)',
                            borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit',
                            transition: 'all .15s'
                          }}
                          title="Volver a acotar al rango registrado"
                        >
                          Acotar a datos
                        </button>
                        {/* Granularidad del gráfico: Auto = 5 min (como siempre) */}
                        <div style={{ display: 'flex', background: 'var(--chip-bg)', borderRadius: 7, padding: 2 }} title="Granularidad del gráfico">
                          {[['auto', 'Auto'], ['seg', 'Seg'], ['min', 'Min'], ['hora', 'Hora']].map(([k, l]) => (
                            <button key={k} onClick={() => {
                              if (k === dayGran) return
                              const cur = visibleRange
                              granChangeRef.current = true
                              setDayGran(k)
                              if (cur) {
                                const newMs = { auto: 5 * 60 * 1000, seg: 10 * 1000, min: 60 * 1000, hora: 60 * 60 * 1000 }[k]
                                const s = Math.max(0, Math.floor((cur.startMs - dayStartMs) / newMs))
                                const e = Math.max(s, Math.ceil((cur.endMs - dayStartMs) / newMs) - 1)
                                cancelAnimationFrame(animFrameRef.current)
                                setDayBrushRange({ start: s, end: e })
                              }
                            }}
                              style={{ fontSize: 9.5, fontWeight: 700, border: 'none', borderRadius: 5, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit',
                                background: dayGran === k ? 'var(--seg-active)' : 'transparent', color: dayGran === k ? 'var(--text)' : 'var(--text-faint)',
                                boxShadow: dayGran === k ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', transition: 'all .15s' }}>
                              {l}
                            </button>
                          ))}
                        </div>
                        <ChartActions chartRef={dayFcChartRef} filename={`frecuencia-cardiaca-${selectedDate}.png`} />
                      </div>
                    )}
                  </div>
                  <div ref={dayFcChartRef}>
                  {(() => {
                    if (!allDayBuckets.some(d => d.hr != null)) {
                      return (
                        <div style={{
                          height: 240,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          background: 'var(--field-bg)',
                          borderRadius: 12,
                          border: '1px dashed var(--border)',
                          margin: '10px 0',
                          padding: 20
                        }}>
                          <IconActivity size={28} color="var(--text-faint)" />
                          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-muted)' }}>
                            Sin registros
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', maxWidth: 360 }}>
                            No hay mediciones de frecuencia cardíaca registradas para este día o rango seleccionado.
                          </span>
                        </div>
                      )
                    }

                    const dayGradientStops = (() => {
                      if (!allDayBuckets.length) return [{ offset: '0%', color: '#ff3b30' }, { offset: '100%', color: '#ff3b30' }]
                      const vs = Math.max(0, Math.min(dayBrushRange.start ?? 0, allDayBuckets.length - 1))
                      const ve = Math.max(vs, Math.min(dayBrushRange.end ?? (allDayBuckets.length - 1), allDayBuckets.length - 1))
                      const visibleSlice = allDayBuckets.slice(vs, ve + 1)
                      const N = visibleSlice.length
                      if (N <= 1) {
                        const c = visibleSlice[0]?.postureColor || '#ff3b30'
                        return [{ offset: '0%', color: c }, { offset: '100%', color: c }]
                      }
                      const stops = []
                      let currentC = null
                      let segStart = 0
                      for (let i = 0; i < N; i++) {
                        const c = visibleSlice[i].postureColor || '#ff3b30'
                        if (c !== currentC) {
                          if (currentC !== null) {
                            const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
                            const p2 = Math.min(100, Math.round((i / (N - 1)) * 100))
                            stops.push({ offset: `${p1}%`, color: currentC })
                            stops.push({ offset: `${p2}%`, color: currentC })
                          }
                          currentC = c
                          segStart = i
                        }
                      }
                      if (currentC !== null) {
                        const p1 = Math.max(0, Math.round((segStart / (N - 1)) * 100))
                        stops.push({ offset: `${p1}%`, color: currentC })
                        stops.push({ offset: '100%', color: currentC })
                      }
                      return stops
                    })()

                    return (
                      <>
                        <ResponsiveContainer key={`${selectedDate}_${dayLoading}_${dayReadings.length}`} width="100%" height={320}>
                          <AreaChart data={allDayBuckets} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} onMouseDown={onChartDown} onMouseMove={onChartMove} onMouseUp={onChartUp} onMouseLeave={onChartUp} style={{ cursor: 'crosshair' }}>
                            <defs>
                              <linearGradient id="gradDayStroke" x1="0" y1="0" x2="1" y2="0">
                                {dayGradientStops.map((s, idx) => (
                                  <stop key={idx} offset={s.offset} stopColor={s.color} />
                                ))}
                              </linearGradient>
                              <linearGradient id="gradDayFill" x1="0" y1="0" x2="1" y2="0">
                                {dayGradientStops.map((s, idx) => (
                                  <stop key={idx} offset={s.offset} stopColor={s.color} stopOpacity={0.28} />
                                ))}
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 9, fill: 'var(--text-faint)' }} interval="preserveStartEnd" minTickGap={40} axisLine={false} tickLine={false} />
                            <YAxis domain={[40, 'auto']} tick={{ fontSize: 9, fill: 'var(--text-faint)' }} axisLine={false} tickLine={false} width={28} />
                            {rangeStats.avg && <ReferenceLine y={rangeStats.avg} stroke="#007aff" strokeDasharray="4 4" strokeWidth={1.5} />}
                            <Tooltip content={<ChartTooltip />} />
                            <Area type="monotone" dataKey="hr" stroke="url(#gradDayStroke)" strokeWidth={2} fill="url(#gradDayFill)" dot={false} isAnimationActive={false} connectNulls={false} />
                            {showAnnotations && annotations.filter(a => a.status === 'confirmed' && a.start_ms != null).map(a => {
                              const BUCKET_MS = dayBucketMs
                              const vs = dayBrushRange.start ?? 0, ve = dayBrushRange.end ?? (allDayBuckets.length - 1)
                              let li = Math.floor((a.start_ms - dayStartMs) / BUCKET_MS), ri = Math.floor((a.end_ms - dayStartMs) / BUCKET_MS)
                              if (ri < vs || li > ve) return null
                              li = Math.max(li, vs); ri = Math.min(ri, ve)
                              const x1 = allDayBuckets[li]?.label, x2 = allDayBuckets[ri]?.label
                              if (!x1 || !x2) return null
                              const col = ACT_COLOR[a.activity] || 'var(--text-faint)'
                              return <ReferenceArea key={a.id} x1={x1} x2={x2} fill={col} fillOpacity={0.13} stroke={col} strokeOpacity={0.4}
                                label={{ value: a.activity ? (ACT_OPTS.find(o => o[0] === a.activity)?.[2] || '') : 'Nota', position: 'insideTop', fontSize: 9, fill: col }} />
                            })}
                            {showAnnotations && pendingEvents.map((e, i) => {
                              const BUCKET_MS = dayBucketMs
                              const vs = dayBrushRange.start ?? 0, ve = dayBrushRange.end ?? (allDayBuckets.length - 1)
                              let li = Math.floor((e.startMs - dayStartMs) / BUCKET_MS), ri = Math.floor((e.endMs - dayStartMs) / BUCKET_MS)
                              if (ri < vs || li > ve) return null
                              li = Math.max(li, vs); ri = Math.min(ri, ve)
                              const x1 = allDayBuckets[li]?.label, x2 = allDayBuckets[ri]?.label
                              if (!x1 || !x2) return null
                              const col = ACT_COLOR[e.activity] || 'var(--text-faint)'
                              return <ReferenceArea key={`p${i}`} x1={x1} x2={x2} fill={col} fillOpacity={0.2} stroke={col} strokeOpacity={0.6} strokeDasharray="4 3"
                                label={{ value: `${ACT_OPTS.find(o => o[0] === e.activity)?.[2] || ''} ?`, position: 'insideTop', fontSize: 9, fill: col }} />
                            })}
                            {sel && (sel.dragging || sel.mode === 'new') && sel.aLabel != null && (
                              <ReferenceArea x1={sel.aLabel < sel.bLabel ? sel.aLabel : sel.bLabel} x2={sel.aLabel < sel.bLabel ? sel.bLabel : sel.aLabel} fill="#007aff" fillOpacity={0.15} stroke="#007aff" strokeOpacity={0.5} />
                            )}
                            <Brush dataKey="label" height={20} stroke={isDark ? "rgba(255,255,255,0.15)" : "#d8d8dd"} fill={isDark ? "#161b22" : "#f9f9fb"} travellerWidth={9} tickFormatter={() => ''}
                              startIndex={Math.max(0, Math.min(dayBrushRange.start ?? 0, allDayBuckets.length - 1))}
                              endIndex={Math.max(0, Math.min(dayBrushRange.end ?? (allDayBuckets.length - 1), allDayBuckets.length - 1))}
                              onChange={r => { if (r && r.startIndex != null && r.endIndex != null) { cancelAnimationFrame(animFrameRef.current); setDayBrushRange({ start: r.startIndex, end: r.endIndex }) } }}>
                              <AreaChart>
                                <Area type="monotone" dataKey="hr" stroke="url(#gradDayStroke)" strokeWidth={1} fill="url(#gradDayFill)" dot={false} isAnimationActive={false} connectNulls={false} />
                              </AreaChart>
                            </Brush>
                          </AreaChart>
                        </ResponsiveContainer>
                      </>
                    )
                  })()}
                  </div>

                  {/* Gráfico de Pasos dedicado agrupado por hora para barras grandes y legibles */}
                  {allDayBuckets.some(b => (b.steps || 0) > 0) && (() => {
                    const hourlySteps = (() => {
                      const map = {}
                      for (let h = 0; h < 24; h++) {
                        const lbl = `${String(h).padStart(2, '0')}:00`
                        map[lbl] = { label: lbl, steps: 0, hour: h }
                      }
                      allDayBuckets.forEach(b => {
                        const s = b.steps || 0
                        if (!s) return
                        const hourNum = b.time ? new Date(b.time).getHours() : (parseInt(b.label?.slice(0, 2), 10) || 0)
                        const lbl = `${String(hourNum).padStart(2, '0')}:00`
                        if (map[lbl]) map[lbl].steps += s
                      })
                      return Object.values(map)
                    })()
                    const totalDaySteps = allDayBuckets.reduce((acc, b) => acc + (b.steps || 0), 0)
                    return (
                      <div style={{ marginTop: 14, marginBottom: 26, background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '14px 16px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                              04
                            </span>
                            <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                              Pasos y Cadencia por Hora
                            </span>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 800, color: '#ff9500' }}>
                            Total: {fmtLatidos(totalDaySteps)} pasos
                          </span>
                        </div>
                        <ResponsiveContainer width="100%" height={105}>
                          <BarChart data={hourlySteps} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 8.5, fill: 'var(--text-faint)' }} interval={2} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 8.5, fill: 'var(--text-faint)' }} axisLine={false} tickLine={false} width={28} />
                            <Tooltip content={<ChartTooltip />} />
                            <Bar dataKey="steps" fill="#ff9500" radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )
                  })()}

                  {/* ── Marcar/validar actividad — todo sobre el gráfico ── */}
                  {sel?.mode === 'new' && selRangeMs ? (
                    <div style={{ marginTop: 8, background: 'var(--tint-blue)', border: '1px solid var(--tint-blue-border)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>¿Qué hacías de {fmtTime(selRangeMs.startMs)} a {fmtTime(selRangeMs.endMs)}?</span>
                        <button onClick={() => { setSel(null); setSelNote('') }} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
                      </div>
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
                        {ACT_OPTS.filter(o => o[0] !== 'other').map(([k, ic, name]) => (
                          <button key={k} onClick={() => { saveAnnotation({ start_ms: selRangeMs.startMs, end_ms: selRangeMs.endMs, activity: k, note: selNote.trim() || null, status: 'confirmed' }); setSel(null); setSelNote('') }}
                            style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: ACT_COLOR[k] || '#1c1c1e', color: '#fff' }}>{ic} {name}</button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={selNote} onChange={e => setSelNote(e.target.value)} placeholder="…o escribí un comentario"
                          style={{ flex: 1, background: 'var(--card-bg)', border: '1px solid var(--field-border)', borderRadius: 8, padding: '8px 11px', fontSize: 13, fontFamily: 'inherit' }} />
                        <button disabled={!selNote.trim()} onClick={() => { saveAnnotation({ start_ms: selRangeMs.startMs, end_ms: selRangeMs.endMs, note: selNote.trim(), status: 'confirmed' }); setSel(null); setSelNote('') }}
                          style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '8px 12px', cursor: selNote.trim() ? 'pointer' : 'default', fontFamily: 'inherit', background: selNote.trim() ? 'var(--text)' : 'var(--track)', color: selNote.trim() ? '#fff' : 'var(--text-soft)' }}>Guardar</button>
                      </div>
                      <button onClick={() => { const BK = 5 * 60000, nn = allDayBuckets.length - 1; const si = Math.max(0, Math.min(nn, Math.floor((selRangeMs.startMs - dayStartMs) / BK))); const ei = Math.max(0, Math.min(nn, Math.floor((selRangeMs.endMs - dayStartMs) / BK))); cancelAnimationFrame(animFrameRef.current); setDayBrushRange({ start: Math.min(si, ei), end: Math.max(si, ei) }); setSel(null); setSelNote('') }}
                        style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: 'var(--text-soft)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Acercar a este rango (acotar el estudio)</button>
                    </div>
                  ) : sel?.mode === 'edit' ? (() => {
                    const a = annotations.find(x => x.id === sel.annId)
                    if (!a) return null
                    const col = ACT_COLOR[a.activity] || 'var(--text-faint)'
                    return (
                      <div style={{ marginTop: 8, background: `${col}0d`, border: `1px solid ${col}55`, borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{a.activity ? ACT_OPTS.find(o => o[0] === a.activity)?.[2] : 'Nota'} · {fmtTime(a.start_ms)}–{fmtTime(a.end_ms)}{a.note ? ` · “${a.note}”` : ''}</span>
                          <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                        <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '0 0 6px' }}>Cambiar a:</p>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                          {ACT_OPTS.filter(o => o[0] !== 'other').map(([k, ic, name]) => (
                            <button key={k} onClick={() => { deleteAnnotation(a.id); saveAnnotation({ start_ms: a.start_ms, end_ms: a.end_ms, activity: k, note: a.note || null, status: 'confirmed' }); setSel(null) }}
                              style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: a.activity === k ? (ACT_COLOR[k] || 'var(--btn-strong)') : 'var(--chip-bg)', color: a.activity === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{ic} {name}</button>
                          ))}
                        </div>
                        <button onClick={() => { deleteAnnotation(a.id); setSel(null) }} style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: '#ffecec', color: '#ff3b30' }}>Eliminar</button>
                      </div>
                    )
                  })() : sel?.mode === 'validate' ? (() => {
                    const ev = pendingEvents[sel.idx]
                    if (!ev) return null
                    return (
                      <div style={{ marginTop: 8, background: isDark ? 'rgba(255, 159, 10, 0.08)' : '#fff8e8', border: `1px solid ${isDark ? 'rgba(255, 159, 10, 0.25)' : '#ffe1a6'}`, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1, minWidth: 150 }}>{ACT_OPTS.find(o => o[0] === ev.activity)?.[1]} ¿Estabas <strong>{ACT_GERUND[ev.activity] || 'haciendo algo'}</strong> de {fmtTime(ev.startMs)} a {fmtTime(ev.endMs)}?</span>
                        <button onClick={() => { saveAnnotation({ start_ms: ev.startMs, end_ms: ev.endMs, activity: ev.activity, status: 'confirmed' }); setSel(null) }} style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: '#34c759', color: '#fff' }}>Sí</button>
                        <button onClick={() => { saveAnnotation({ start_ms: ev.startMs, end_ms: ev.endMs, activity: ev.activity, status: 'rejected' }); setSel(null) }} style={{ fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontFamily: 'inherit', background: 'var(--chip-bg)', color: 'var(--text-muted)' }}>No</button>
                      </div>
                    )
                  })() : (
                    <p style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 8, lineHeight: 1.5 }}>
                      Arrastrá sobre el gráfico para marcar qué hacías en ese rango. Tocá una banda de color para cambiarla o borrarla. Esto entrena al sistema para reconocer tus actividades.
                    </p>
                  )}
                </div>

                <ChartPurpose>ver cómo cambió tu pulso durante el día —esfuerzo, estrés o descanso—. Arrastrá el selector inferior para elegir el rango de tiempo, y arrastrá sobre el gráfico para marcar qué hacías en cada tramo (eso entrena al sistema).</ChartPurpose>

                <MetricCalculationAudit
                  title="Frecuencia Cardíaca y Métricas"
                  formula="μ_HR = (1 / N) * Σ HR_i   |   HR_min = min(HR_i)   |   HR_max = max(HR_i)"
                  variables={[
                    { label: 'Muestras analizadas (N)', value: `${rangeReadings.length} s` },
                    { label: 'FC Mínima', value: `${rangeStats.min ?? '—'} bpm` },
                    { label: 'FC Máxima', value: `${rangeStats.max ?? '—'} bpm` },
                    { label: 'FC Promedio', value: `${rangeStats.avg ?? '—'} bpm` },
                  ]}
                  stepByStep="Se promedian las lecturas de frecuencia cardíaca de la ventana seleccionada excluyendo artefactos fuera del rango fisiológico (30–240 bpm)."
                  reference="AHA / ACC Guidelines for Ambulatory Electrocardiography"
                />

                {/* ── Sección 2: Distribución de FC y zonas (unificado) ── */}
                {/* Reveal: se monta después de la Sección 1 (carga de arriba hacia abajo) */}
                <Reveal order={1} minHeight={320}>
                <SectionHeader n="2" title="Distribución de FC y zonas de entrenamiento" sub={histMaxHr ? `Cuánto tiempo pasaste en cada intensidad · FCmáx teórica ${histMaxHr} BPM` : 'Cuánto tiempo pasaste en cada intensidad de pulso'} info="Saber cuánto tiempo pasaste en cada intensidad de pulso, de reposo a esfuerzo máximo. La distribución muestra el detalle fino (cada 5 bpm) y las zonas lo resumen en niveles de entrenamiento." />
                {rangeChip}
                <HRDistributionChart readings={rangeReadings} />
                {!histMaxHr && (
                  <div style={{ background: '#fff8f0', border: '1px solid #ffe4b3', borderRadius: 12, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IconSteps size={14} color="#b45309" />
                    <p style={{ fontSize: 12, color: '#b45309', margin: 0 }}>
                      Agregá tu edad en el perfil para ver las zonas de entrenamiento (FCmáx personalizada).
                    </p>
                  </div>
                )}
                {zoneDistDay && zoneDistDay.length > 0 && (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px 18px', marginBottom: 26, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                        05
                      </span>
                      <p style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, margin: 0 }}>
                        Zonas de Entrenamiento <span style={{ fontWeight: 500, color: 'var(--text-soft)', textTransform: 'none' }}>· segundo a segundo</span>
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {zoneDistDay.map(z => {
                        if (window.innerWidth <= 640) {
                          return (
                            <div key={z.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                              {/* Fila 1: Nombre y BPM a la izquierda, Minutos y % a la derecha */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: z.color, display: 'inline-block' }} />
                                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>{z.num ? `${z.num}. ` : ''}{z.name}</span>
                                  <span style={{ fontSize: 9, color: 'var(--text-faint)' }}>({z.lo}–{z.hi} BPM)</span>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>
                                  {z.pct}% <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-faint)' }}>({z.min} min)</span>
                                </span>
                              </div>
                              {/* Fila 2: Barra horizontal de ancho completo */}
                              <div style={{ width: '100%', height: 6, background: 'var(--chip-bg)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${z.pct}%`, background: z.color, borderRadius: 3 }} />
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div key={z.key} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
                            {/* Columna 1: Badge + Nombre */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '150px', flexShrink: 0, minWidth: 120 }}>
                              {z.num ? (
                                <div style={{ width: 18, height: 18, borderRadius: '50%', background: z.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <span style={{ fontSize: 9, fontWeight: 900, color: '#fff' }}>{z.num}</span>
                                </div>
                              ) : (
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, display: 'inline-block', flexShrink: 0, margin: '0 5px' }} />
                              )}
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.name}</span>
                            </div>

                            {/* Columna 2: Rangos de pulso */}
                            <div style={{ width: '85px', flexShrink: 0, fontSize: 10.5, color: 'var(--text-faint)' }}>
                              {z.lo}–{z.hi} BPM
                            </div>

                            {/* Columna 3: Barra de progreso horizontal flexible */}
                            <div style={{ flex: 1, minWidth: 60, height: 7, background: 'var(--chip-bg)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${z.pct}%`, background: z.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
                            </div>

                            {/* Columna 4: Porcentajes y minutos */}
                            <div style={{ width: '95px', flexShrink: 0, textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                              {z.pct}% <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-faint)' }}>({z.min} min)</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                <ChartPurpose>saber cuánto tiempo pasaste en cada intensidad de pulso, de reposo a esfuerzo máximo. La <strong>distribución</strong> muestra el detalle fino (cada 5 bpm) y las <strong>zonas</strong> lo resumen en niveles de entrenamiento — son dos vistas del mismo dato.</ChartPurpose>

                <MetricCalculationAudit
                  title="Distribución y Zonas de Entrenamiento"
                  formula="% FCmáx = (HR / FCmáx) * 100   |   FCmáx = 220 - edad (o histórico)"
                  variables={[
                    { label: 'FC Máx de Referencia', value: `${histMaxHr ?? 196} bpm` },
                    { label: 'Z1 Recuperación (50-60%)', value: `${Math.round((histMaxHr ?? 196) * 0.5)}–${Math.round((histMaxHr ?? 196) * 0.6)} bpm` },
                    { label: 'Z2 Aeróbica (60-70%)', value: `${Math.round((histMaxHr ?? 196) * 0.6)}–${Math.round((histMaxHr ?? 196) * 0.7)} bpm` },
                    { label: 'Z5 Máxima (90-100%)', value: `${Math.round((histMaxHr ?? 196) * 0.9)}–${histMaxHr ?? 196} bpm` },
                  ]}
                  stepByStep="Cada segundo de grabación se clasifica dentro de su rango de intensidad. El porcentaje de cada zona es (segundos en zona / segundos totales) * 100."
                  reference="Modelo de 5 Zonas de Intensidad de Fisiología del Ejercicio (Seiler & Tønnessen)"
                />
                </Reveal>

                {/* ── Arquitectura y Fases del Sueño (Hipnograma estilo Amazon Halo) ── */}
                {(todFilter === 'sleep' || sleepArchitecture) && (
                  <div style={{ marginTop: 14, marginBottom: 26 }}>
                    <SleepHypnogram sleepData={sleepArchitecture} />
                    <MetricCalculationAudit
                      title="Arquitectura de Sueño (Fases)"
                      formula="Épocas de 30s: Caída de FC relativa al basal nocturno + RMSSD autonómico"
                      variables={[
                        { label: 'Duración de Época', value: '30 segundos' },
                        { label: 'Profundo (N3/SWS)', value: 'FC mínima sostenida + tono vagal alto' },
                        { label: 'REM', value: 'FC variable con atonía motora' },
                      ]}
                      stepByStep="El algoritmo analiza ventanas de 30 segundos evaluando la atenuación del pulso respecto a la media de vigilia combinada con el perfil de acelerometría para estimar Sueño Ligero, Profundo y REM."
                      reference="Criterios de Polisomnografía de la Academia Americana de Medicina del Sueño (AASM)"
                    />
                  </div>
                )}

                {/* ── Sección 3: Electrocardiograma (ECG) — tira clínica — unificada ── */}
                {renderEcgPanel("3")}

                {/* ── Toggle Estadísticas Avanzadas ── */}
                {hasTechnical && (
                  <button
                    onClick={() => setShowAnalysis(v => !v)}
                    style={{ width: '100%', background: showAnalysis ? 'var(--btn-strong)' : 'var(--chip-bg)', color: showAnalysis ? 'var(--seg-active-text-inv)' : 'var(--text)', border: 'none', borderRadius: 12, padding: '11px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.15s' }}>
                    <span>Estadísticas Avanzadas</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>{showAnalysis ? '▲ ocultar' : '▼ ver HRV · Poincaré · espectro · arritmias'}</span>
                  </button>
                )}

                {showAnalysis && (
                  <div>
                {/* ── Sección 4: Sistema Nervioso (HRV) ── */}
                {daySegHrv.rmssd !== null && (
                  <>
                    <SectionHeader n="4" title="Sistema Nervioso (HRV)" sub="Qué tan recuperado (en calma) o activado (estrés/esfuerzo) está tu cuerpo" info="Mide la pequeña variación en el tiempo entre un latido y el siguiente. Más variación = más recuperado y en calma (el “freno” del sistema nervioso está activo). Menos variación = más estrés, esfuerzo o cansancio." />
                    {rangeChip}

                    {/* 2a — Dominio temporal */}
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Dominio temporal</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                      <MetricCard defKey="rmssd" value={rangeHrv.rmssd}  color={rangeHrv.toneColor} />
                      <MetricCard defKey="sdnn"  value={rangeHrv.sdnn  ?? null} />
                      <MetricCard defKey="pnn50" value={rangeHrv.pnn50 ?? null} />
                    </div>
                    {rangeHrv.tone && (
                      <div style={{ background: `${rangeHrv.toneColor}12`, border: `1px solid ${rangeHrv.toneColor}30`, borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: rangeHrv.toneColor, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: rangeHrv.toneColor }}>{rangeHrv.tone}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {rangeHrv.rrCount} intervalos RR · {dayRmssdPersonalAvg ? `promedio personal ${dayRmssdPersonalAvg} ms` : 'sin baseline'}</span>
                      </div>
                    )}
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px', marginBottom: 26, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                          07
                        </span>
                        <p style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, margin: 0 }}>
                          Tendencia Temporal de RMSSD (Variabilidad)
                        </p>
                      </div>
                      <HRVTrendChart readings={rangeReadings} personalAvg={dayRmssdPersonalAvg} embedded />
                    </div>
                    <ChartPurpose>seguir tu recuperación a lo largo del tiempo: cuando el RMSSD sube estás más en calma; si cae sostenido, hubo esfuerzo o estrés. Arrastrá el selector para enfocar un tramo.</ChartPurpose>
                    <MetricCalculationAudit
                      title="RMSSD (Variabilidad Cardíaca)"
                      formula="RMSSD = √ [ (1 / (N - 1)) * Σ (RR_{i+1} - RR_i)² ]"
                      variables={[
                        { label: 'Intervalos RR (N)', value: `${rangeHrv.rrCount ?? rangeReadings.length}` },
                        { label: 'RMSSD Obtenido', value: rangeHrv.rmssd != null ? `${rangeHrv.rmssd} ms` : '—' },
                        { label: 'SDNN Obtenido', value: rangeHrv.sdnn != null ? `${rangeHrv.sdnn} ms` : '—' },
                        { label: 'pNN50', value: rangeHrv.pnn50 != null ? `${rangeHrv.pnn50}%` : '—' },
                      ]}
                      stepByStep="1) Se calculan las diferencias sucesivas ΔRR = RR_{i+1} - RR_i. 2) Se eleva cada diferencia al cuadrado. 3) Se promedian las diferencias cuadráticas. 4) Se calcula la raíz cuadrada. Refleja la actividad del nervio vago (parasimpático)."
                      reference="Task Force of the European Society of Cardiology & NASPE (1996)"
                    />

                    {/* 2b — Poincaré */}
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Poincaré (no-lineal)</p>
                    {rangeHrv.sd1 === null && rangeHrv.rmssd !== null && (
                      <div style={{ background: 'var(--inset-bg)', border: '1px solid var(--border-soft)', borderRadius: 10, padding: '9px 13px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#ff9f0a' }}>[AVISO]</span>
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                          Esta sesión no tiene datos de intervalos RR — las métricas avanzadas (SD1/SD2, DFA, SampEn, LF/HF) requieren reimportar incluyendo el archivo de RR junto al de HR.
                        </p>
                      </div>
                    )}
                    {rangeHrv.sd1 !== null && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4 }}>SD1</p>
                          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{rangeHrv.sd1}</p>
                          <p style={{ fontSize: 9, color: 'var(--text-faint)' }}>ms · corto plazo</p>
                        </div>
                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4 }}>SD2</p>
                          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{rangeHrv.sd2}</p>
                          <p style={{ fontSize: 9, color: 'var(--text-faint)' }}>ms · largo plazo</p>
                        </div>
                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                          <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4 }}>SD1/SD2</p>
                          <p style={{ fontSize: 20, fontWeight: 800, color: rangeHrv.sd1sd2 > 0.75 ? '#ff9f0a' : rangeHrv.sd1sd2 < 0.3 ? '#ff3b30' : '#34c759' }}>{rangeHrv.sd1sd2}</p>
                          <p style={{ fontSize: 9, color: 'var(--text-faint)' }}>balance SNA</p>
                        </div>
                      </div>
                    )}
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px', marginBottom: 26, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ fontSize: 9.5, fontWeight: 900, background: 'var(--chip-bg)', color: 'var(--text)', padding: '2px 7px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                            08
                          </span>
                          <p style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, margin: 0 }}>
                            Diagrama de Poincaré (RR n vs RR n+1)
                          </p>
                        </div>
                        <ChartActions chartRef={poincareRef} filename={`poincare-${selectedDate}.png`} />
                      </div>
                      <div ref={poincareRef}><PoincaréChart readings={rangeReadings} /></div>
                    </div>
                    <ChartPurpose>ver de un vistazo la forma de tu variabilidad latido a latido: una nube compacta y ovalada indica un ritmo sano; muy dispersa o sin forma puede señalar irregularidad.</ChartPurpose>
                    <MetricCalculationAudit
                      title="Poincaré (SD1, SD2 y Balance)"
                      formula="SD1 = RMSSD / √2   |   SD2 = √ (2 * SDNN² - 0.5 * RMSSD²)   |   Ratio = SD1 / SD2"
                      variables={[
                        { label: 'SD1 (Corto Plazo)', value: rangeHrv.sd1 != null ? `${rangeHrv.sd1} ms` : '—' },
                        { label: 'SD2 (Largo Plazo)', value: rangeHrv.sd2 != null ? `${rangeHrv.sd2} ms` : '—' },
                        { label: 'Ratio SD1/SD2', value: rangeHrv.sd1sd2 != null ? `${rangeHrv.sd1sd2}` : '—' },
                      ]}
                      stepByStep="Transformación de dispersión sobre la nube RR_n vs RR_{n+1}. SD1 mide la dispersión perpendicular a la línea de identidad (parasimpático rápido); SD2 mide la dispersión a lo largo del eje mayor (inervación lenta/simpática)."
                      reference="Brennan et al., IEEE Transactions on Biomedical Engineering (2001)"
                    />

                    {/* 2c — DFA α1 + SampEn */}
                    {(rangeHrv.dfa1 !== null || rangeHrv.sampen !== null) && (
                      <>
                        <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Fractal y complejidad</p>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${[rangeHrv.dfa1, rangeHrv.sampen].filter(v => v !== null).length}, 1fr)`, gap: 10, marginBottom: 12 }}>
                          {rangeHrv.dfa1 !== null && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                              <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4 }}>DFA α1</p>
                              <p style={{ fontSize: 20, fontWeight: 800, color: rangeHrv.dfa1 < 0.75 ? '#ff3b30' : rangeHrv.dfa1 > 1.0 ? '#ff9f0a' : '#34c759' }}>{rangeHrv.dfa1}</p>
                              <p style={{ fontSize: 9, color: 'var(--text-faint)' }}>correlación fractal · 0.75–1.0 óptimo</p>
                            </div>
                          )}
                          {rangeHrv.sampen !== null && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
                              <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4 }}>SampEn</p>
                              <p style={{ fontSize: 20, fontWeight: 800, color: rangeHrv.sampen < 0.5 ? '#ff3b30' : rangeHrv.sampen > 2.0 ? '#ff9f0a' : '#34c759' }}>{rangeHrv.sampen}</p>
                              <p style={{ fontSize: 9, color: 'var(--text-faint)' }}>entropía muestral · típico 1–2.5</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {(rangeHrv.dfa1 !== null || rangeHrv.sampen !== null) && (
                      <ChartPurpose>medir qué tan “ordenada” y adaptable es tu variabilidad. <strong>DFA α1</strong> cerca de 1.0 = sistema sano y equilibrado; muy por debajo de 0.75 = fatiga o sobrecarga. <strong>SampEn</strong> mide la complejidad de la señal: más alto = más sana (menos rígida).</ChartPurpose>
                    )}
                    {(rangeHrv.dfa1 !== null || rangeHrv.sampen !== null) && (
                      <MetricCalculationAudit
                        title="DFA α1 y Complejidad (SampEn)"
                        formula="DFA α1: F(n) ~ n^α1 (ventana 4≤n≤16)   |   SampEn = -ln(A / B) [m=2, r=0.2*SDNN]"
                        variables={[
                          { label: 'DFA α1 Obtenido', value: rangeHrv.dfa1 != null ? `${rangeHrv.dfa1}` : '—' },
                          { label: 'SampEn Obtenido', value: rangeHrv.sampen != null ? `${rangeHrv.sampen}` : '—' },
                          { label: 'Rango Óptimo α1', value: '0.75 – 1.00 (Fractal)' },
                        ]}
                        stepByStep="DFA remueve tendencias lineales locales para medir correlaciones autosimilares de largo alcance en el ritmo cardíaco. SampEn cuantifica la probabilidad de que secuencias de 2 latidos similares permanezcan similares en el siguiente latido."
                        reference="Peng CK et al., Chaos (1995); Richman & Moorman, Am J Physiol (2000)"
                      />
                    )}

                    {/* 2d — Espectro LF/HF */}
                    <SpectralHRVPanel readings={rangeReadings} />
                    <ChartPurpose>separar tu variabilidad por frecuencias para ver el balance del sistema nervioso: <strong>HF</strong> = recuperación (parasimpático, el “freno”), <strong>LF</strong> = activación. Un <strong>LF/HF</strong> alto indica más estrés o esfuerzo; bajo, más recuperación.</ChartPurpose>
                    <MetricCalculationAudit
                      title="Espectro de Potencia (LF / HF)"
                      formula="LF = ∫ (0.04 a 0.15 Hz) P(f) df   |   HF = ∫ (0.15 a 0.40 Hz) P(f) df   |   Ratio = LF / HF"
                      variables={[
                        { label: 'Banda LF (Simpático)', value: '0.04 – 0.15 Hz' },
                        { label: 'Banda HF (Parasimpático)', value: '0.15 – 0.40 Hz' },
                        { label: 'Frecuencia de Remuestreo', value: '4.0 Hz (Cúbica/Lineal)' },
                      ]}
                      stepByStep="La serie de intervalos RR no equidistante se remuestrea a 4 Hz mediante interpolación, se elimina la media, se aplica ventana de Hanning y se integra la densidad espectral de potencia mediante FFT."
                      reference="Heart Rate Variability: Standards of Measurement (Circulation, 1996)"
                    />
                  </>
                )}

                {/* Zonas de entrenamiento unificadas con Distribución de FC en la Sección 2 (arriba) */}

                {/* ── Sección 5: Chequeo de ritmo cardíaco (Arritmias - visible incondicionalmente al expandir) ── */}
                {rangeArrhythmia && (
                  <>
                    <SectionHeader n="5" title="Chequeo de ritmo cardíaco (Arritmias)" sub="¿Tu corazón late con un ritmo parejo? Es un chequeo orientativo, no un diagnóstico" info="¿Tu corazón late con un ritmo parejo? Es un chequeo orientativo basado en tus intervalos de pulso, no reemplaza a un cardiólogo ni sirve como diagnóstico formal." />
                    {rangeChip}
                    <ArrhythmiaPanel readings={rangeReadings} precomputed={rangeArrhythmia} />
                    <ChartPurpose>estimar si tu ritmo es parejo o irregular. Es orientativo (no reemplaza a un médico), pero te avisa si conviene una consulta.</ChartPurpose>
                    <MetricCalculationAudit
                      title="Detección de Arritmias y Ectopias"
                      formula="ΔRR = |RR_i - RR_{prom5}| / RR_{prom5} > 0.20 + Pausa Compensatoria"
                      variables={[
                        { label: 'Latidos Analizados', value: `${rangeReadings.length}` },
                        { label: 'Umbral de Desvío', value: '> 20% vs media móvil' },
                        { label: 'Criterio Clínico', value: 'Filtro de artefacto + compensación' },
                      ]}
                      stepByStep="Cada latido se compara con el promedio móvil de los 5 latidos precedentes. Si difiere en más de 20% en un tramo limpio sin ruido de electrodo y le sigue un intervalo alargado, se clasifica como latido atípico o extrasístole."
                      reference="Criterios Clínicos de Monitoreo Holter / ACC/AHA Guidelines"
                    />
                  </>
                )}

                  </div>
                )}

              </div>
            )
          })()}

          {/* ── SEMANA view ─────────────────────────────────────────────────── */}
          {periodMode === 'week' && selectedWeek && (() => {
            const inWeek = (key, s) => weekKeyOf(localDateKey(s.startTime)) === key
            const weekSessions = deduplicatedSegments.filter(s => inWeek(selectedWeek, s))
            if (!weekSessions.length) return <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: 24, textAlign: 'center' }}><p style={{ color: 'var(--text-faint)', fontSize: 14 }}>Sin sesiones esta semana</p></div>
            const pm = new Date(selectedWeek + 'T12:00:00'); pm.setDate(pm.getDate() - 7)
            const prevWeekSessions = deduplicatedSegments.filter(s => inWeek(localDateKey(pm.getTime()), s))
            const mon = new Date(selectedWeek + 'T12:00:00'); const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
            const label = `Semana del ${mon.getDate()} al ${sun.getDate()} de ${sun.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}`
            return <PeriodReport mode="week" periodKey={selectedWeek} label={label} sessions={weekSessions} prevSessions={prevWeekSessions} histZones={histZones} histMaxHr={histMaxHr} />
          })()}

          {periodMode === 'week' && !selectedWeek && (
            <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: 32, textAlign: 'center' }}><p style={{ color: 'var(--text-faint)', fontSize: 14 }}>Seleccioná una semana</p></div>
          )}

          {/* ── MES view ────────────────────────────────────────────────────── */}
          {periodMode === 'month' && selectedMonth && (() => {
            const monthSessions = deduplicatedSegments.filter(s => localMonthKey(s.startTime) === selectedMonth)
            if (!monthSessions.length) return <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: 24, textAlign: 'center' }}><p style={{ color: 'var(--text-faint)', fontSize: 14 }}>Sin sesiones este mes</p></div>
            const pd = new Date(selectedMonth + '-15T12:00:00'); pd.setMonth(pd.getMonth() - 1)
            const prevKey = localMonthKey(pd.getTime())
            const prevMonthSessions = deduplicatedSegments.filter(s => localMonthKey(s.startTime) === prevKey)
            const label = new Date(selectedMonth + '-15T12:00:00').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
            return <PeriodReport mode="month" periodKey={selectedMonth} label={label} sessions={monthSessions} prevSessions={prevMonthSessions} histZones={histZones} histMaxHr={histMaxHr} />
          })()}

          {periodMode === 'month' && !selectedMonth && (
            <div style={{ background: 'var(--field-bg)', borderRadius: 14, padding: 32, textAlign: 'center' }}><p style={{ color: 'var(--text-faint)', fontSize: 14 }}>Seleccioná un mes</p></div>
          )}

          {/* ── Exportar toda la vista (día / semana / mes) al final de la página (no fijado) ── */}
          {((periodMode === 'day' && selectedDate) || (periodMode === 'week' && selectedWeek) || (periodMode === 'month' && selectedMonth)) && (
            <div style={{
              marginTop: 16,
              marginBottom: 18,
              paddingTop: 8,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              flexWrap: 'wrap',
              opacity: 0.55,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.55'}
            >
              <button
                onClick={downloadFullView}
                disabled={!!fullView}
                title="Descargar captura como PNG"
                style={{
                  background: 'transparent', border: '1px solid transparent', borderRadius: 6,
                  color: 'var(--text-faint)', fontSize: 10.5, fontWeight: 600,
                  padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                  boxShadow: 'none', opacity: fullView ? 0.4 : 0.8, transition: 'all 0.15s ease'
                }}
              >
                <IconFolder size={11} color="var(--text-faint)" />
                {fullView === 'saving' ? '✓ Generando…' : 'Descargar como PNG'}
              </button>
              <button
                onClick={() => window.print()}
                title="Imprimir o guardar como PDF"
                style={{
                  background: 'transparent', border: '1px solid transparent', borderRadius: 6,
                  color: 'var(--text-faint)', fontSize: 10.5, fontWeight: 600,
                  padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                  boxShadow: 'none', opacity: 0.8, transition: 'all 0.15s ease'
                }}
              >
                <IconBarChart size={11} color="var(--text-faint)" />
                Descargar como PDF
              </button>
            </div>
          )}

          </div>{/* /fullViewRef */}

        </div>
      )}
    </div>
  )
}

// ── Draft recovery banner ────────────────────────────────────────────────────

function DraftRecoveryBanner({ draft, onRecover, onDiscard }) {
  const start  = draft.readings[0].time
  const end    = draft.readings[draft.readings.length - 1].time
  const device = draft.deviceName ?? 'Polar H10'
  return (
    <div style={{
      background: '#1f1a00', border: '1.5px solid #ffd60a', borderRadius: 14,
      padding: '16px 20px', marginBottom: 18, animation: 'fadeIn 0.3s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, background: '#ffd60a',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16, color: '#000', fontWeight: 900,
        }}>!</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#ffd60a', marginBottom: 4 }}>
            Sesión anterior no guardada — {device}
          </p>
          <p style={{ fontSize: 13, color: '#8e7a00', marginBottom: 14, lineHeight: 1.65 }}>
            Hay datos de una sesión que no llegó a guardarse.{' '}
            <strong style={{ color: 'var(--text)' }}>{fmtDate(start)}</strong> · de <strong style={{ color: 'var(--text)' }}>{fmtTime(start)}</strong> a <strong style={{ color: 'var(--text)' }}>{fmtTime(end)}</strong> · {fmtDurationMs(end - start)} · {draft.readings.length} lecturas
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="sync" onClick={onRecover}>Guardar en historial</Btn>
            <Btn variant="muted" onClick={onDiscard}>Descartar</Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

function SavedSessionNotice({ notice, onDismiss }) {
  const isCheckpoint = notice.isCheckpoint
  return (
    <div style={{
      background: isCheckpoint ? 'rgba(0,122,255,0.12)' : '#0a1f12',
      border: `1.5px solid ${isCheckpoint ? '#007aff' : '#30d158'}`,
      borderRadius: 14,
      padding: '12px 16px', marginBottom: 18, animation: 'fadeIn 0.3s ease',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: isCheckpoint ? '#007aff' : '#30d158', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <p style={{ fontSize: 13.5, fontWeight: 700, color: isCheckpoint ? '#64d2ff' : '#4ee07a', margin: '0 0 2px' }}>
            {isCheckpoint ? 'Checkpoint guardado en BigQuery (sesión en curso)' : 'Sesión guardada en historial'}
          </p>
          <p style={{ fontSize: 11.5, color: isCheckpoint ? 'rgba(255,255,255,0.7)' : '#b4e6c0', margin: 0, fontWeight: 500 }}>
            {notice.deviceName} · {notice.count} lecturas acumuladas · {fmtTime(notice.start)}–{fmtTime(notice.end)}
          </p>
        </div>
      </div>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isCheckpoint ? '#64d2ff' : '#88bf99', fontSize: 20, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
    </div>
  )
}

// ── Performance panel ─────────────────────────────────────────────────────────

function PerformancePanel() {
  const [fps,    setFps]    = useState(null)
  const [mem,    setMem]    = useState(null)
  const [open,   setOpen]   = useState(false)
  const frameRef = useRef(0)
  const lastRef  = useRef(performance.now())

  useEffect(() => {
    let raf
    function tick(now) {
      frameRef.current++
      if (now - lastRef.current >= 1000) {
        setFps(frameRef.current)
        frameRef.current = 0
        lastRef.current  = now
        if (performance.memory) {
          setMem({
            used:  Math.round(performance.memory.usedJSHeapSize  / 1048576),
            total: Math.round(performance.memory.totalJSHeapSize / 1048576),
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576),
          })
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  const fpsColor = fps === null ? 'var(--text-fainter)' : fps >= 50 ? '#34c759' : fps >= 30 ? '#ff9f0a' : '#ff3b30'
  const memPct   = mem ? Math.round(mem.used / mem.limit * 100) : null

  return (
    <div style={{ marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '4px 0',
      }}>
        <span style={{ fontSize: 11, color: 'var(--text-fainter)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7 }}>
          Rendimiento
        </span>
        <div style={{ display: 'flex', gap: 10, marginLeft: 4 }}>
          {fps !== null && (
            <span style={{ fontSize: 11, color: fpsColor, fontWeight: 700 }}>{fps} fps</span>
          )}
          {mem && (
            <span style={{ fontSize: 11, color: memPct > 70 ? '#ff9f0a' : 'var(--text-faint)' }}>
              {mem.used} MB / {mem.limit} MB
            </span>
          )}
        </div>
        <span style={{ fontSize: 10, color: 'var(--text-fainter)', marginLeft: 'auto' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6 }}>FPS (frames/s)</p>
            <span style={{ fontSize: 22, fontWeight: 700, color: fpsColor }}>{fps ?? '–'}</span>
            <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 2 }}>
              {fps >= 55 ? 'Fluido' : fps >= 30 ? 'Moderado' : fps !== null ? 'Lento — cerrá otras pestañas' : ''}
            </p>
          </div>
          {mem && (<>
            <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6 }}>Memoria JS usada</p>
              <span style={{ fontSize: 22, fontWeight: 700, color: memPct > 70 ? '#ff9f0a' : 'var(--text)' }}>{mem.used} MB</span>
              <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 2 }}>{memPct}% del límite ({mem.limit} MB)</p>
            </div>
            <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6 }}>Heap JS total</p>
              <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{mem.total} MB</span>
              <p style={{ fontSize: 10, color: 'var(--text-fainter)', marginTop: 2 }}>asignado por el motor V8</p>
            </div>
          </>)}
          {!mem && (
            <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <p style={{ fontSize: 11, color: 'var(--text-faint)' }}>Estadísticas de memoria no disponibles en este navegador (solo Chrome)</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Guide tab ─────────────────────────────────────────────────────────────────

// Static example data for guide charts
const GUIDE_HR_EX = [
  {t:'0:00',hr:64},{t:'4:00',hr:66},{t:'8:00',hr:88},
  {t:'12:00',hr:116},{t:'16:00',hr:124},{t:'18:00',hr:120},
  {t:'22:00',hr:98},{t:'26:00',hr:78},{t:'30:00',hr:67},{t:'32:00',hr:64},
]

const GUIDE_PP_RELAXED = [
  {x:810,y:860},{x:860,y:920},{x:900,y:830},{x:780,y:870},{x:870,y:950},
  {x:930,y:855},{x:845,y:790},{x:790,y:845},{x:865,y:905},{x:960,y:825},
  {x:820,y:775},{x:885,y:945},{x:800,y:830},{x:855,y:800},{x:905,y:875},
  {x:835,y:875},{x:875,y:835},{x:840,y:915},{x:760,y:835},{x:890,y:820},
]
const GUIDE_PP_STRESSED = [
  {x:695,y:702},{x:703,y:697},{x:700,y:706},{x:697,y:700},{x:706,y:703},
  {x:699,y:694},{x:704,y:701},{x:698,y:705},{x:702,y:697},{x:707,y:703},
  {x:695,y:699},{x:701,y:707},{x:704,y:697},{x:699,y:703},{x:706,y:700},
  {x:697,y:704},{x:703,y:697},{x:700,y:702},{x:705,y:699},{x:697,y:704},
]

// Synthetic ECG waveform: 4 clean heartbeats
const GUIDE_ECG_EX = (() => {
  const pts = []
  const BEAT = 110
  for (let b = 0; b < 4; b++) {
    for (let i = 0; i < BEAT; i++) {
      let v = 0
      if (i >= 10 && i < 24)  v = 0.15 * Math.sin((i - 10) / 14 * Math.PI)
      else if (i === 31)       v = -0.12
      else if (i >= 32 && i <= 40) v = Math.sin((i - 32) / 8.5 * Math.PI)
      else if (i === 41)       v = -0.18
      else if (i === 42)       v = -0.08
      else if (i >= 52 && i < 72) v = 0.22 * Math.sin((i - 52) / 20 * Math.PI)
      pts.push({ i: b * BEAT + i, v: parseFloat(v.toFixed(3)) })
    }
  }
  return pts
})()

function GuideHRChart() {
  return (
    <div style={{ marginTop: 12, background: '#fafafa', borderRadius: 10, padding: '10px 12px 6px' }}>
      <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 6 }}>Ejemplo: reposo → esfuerzo → recuperación (minutos)</p>
      <ResponsiveContainer width="100%" height={110}>
        <AreaChart data={GUIDE_HR_EX} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff3b30" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#ff3b30" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
          <XAxis dataKey="t" tick={{ fontSize: 8, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} interval={2} />
          <YAxis domain={[55, 135]} tick={{ fontSize: 8, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={22} />
          <ReferenceLine y={100} stroke="#ff9f0a" strokeDasharray="4 4" strokeWidth={1}
            label={{ value: 'Esfuerzo mod. (>100)', fontSize: 8, fill: '#ff9f0a', position: 'insideTopRight' }} />
          <ReferenceLine y={60} stroke="#007aff" strokeDasharray="4 4" strokeWidth={1}
            label={{ value: 'Reposo (60–100)', fontSize: 8, fill: '#007aff', position: 'insideTopRight' }} />
          <Area type="monotone" dataKey="hr" stroke="#ff3b30" strokeWidth={2} fill="url(#gG)" dot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function GuidePoincareChart() {
  const lo = 640, hi = 980
  return (
    <div style={{ marginTop: 12, background: '#fafafa', borderRadius: 10, padding: '10px 12px 6px' }}>
      <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 6 }}>
        Diagrama de Poincaré: cada punto = RR actual vs. RR siguiente. Nube grande diagonal = alta VFC. Punto apretado = baja VFC.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          { label: 'Relajado — alta VFC (bueno)', data: GUIDE_PP_RELAXED, color: '#34c759' },
          { label: 'Activo / estresado — baja VFC', data: GUIDE_PP_STRESSED, color: '#ff9f0a' },
        ].map(cfg => (
          <div key={cfg.label} style={{ flex: 1 }}>
            <p style={{ fontSize: 9, color: cfg.color, fontWeight: 700, marginBottom: 4 }}>{cfg.label}</p>
            <ResponsiveContainer width="100%" height={140}>
              <ScatterChart margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" />
                <XAxis type="number" dataKey="x" domain={[lo, hi]} tick={{ fontSize: 7, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false}
                  label={{ value: 'RR[n] ms', position: 'insideBottom', offset: -2, fontSize: 7, fill: 'var(--text-fainter)' }} />
                <YAxis type="number" dataKey="y" domain={[lo, hi]} tick={{ fontSize: 7, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={30}
                  label={{ value: 'RR[n+1]', angle: -90, position: 'insideLeft', offset: 10, fontSize: 7, fill: 'var(--text-fainter)' }} />
                <ReferenceLine segment={[{ x: lo, y: lo }, { x: hi, y: hi }]} stroke="var(--border-soft)" strokeWidth={1} />
                <Scatter data={cfg.data} fill={cfg.color} fillOpacity={0.7} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  )
}

function GuideECGChart() {
  return (
    <div style={{ marginTop: 12, background: '#fff9f9', borderRadius: 10, padding: '10px 12px 6px', border: '1px solid #ffe0e0' }}>
      <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 2 }}>Señal ECG sintética — 4 latidos a ~70 bpm</p>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        {[['P','Aurícula','#007aff'],['QRS','Ventrículo','#ff3b30'],['T','Relajación','#34c759']].map(([w,d,c]) => (
          <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
            <span style={{ fontSize: 9, color: 'var(--text-muted)' }}><strong>{w}</strong> = {d}</span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={GUIDE_ECG_EX} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(200,80,80,0.2)" />
          <XAxis dataKey="i" hide />
          <YAxis domain={[-0.3, 1.1]} hide />
          <ReferenceLine y={0} stroke="#f0c0c0" strokeWidth={1} />
          <Line type="monotone" dataKey="v" stroke="#1c1c1e" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function GuideHRVTrendChart() {
  const data = [
    { t: '10min', rmssd: 55 }, { t: '20min', rmssd: 52 }, { t: '30min', rmssd: 48 },
    { t: '40min', rmssd: 32 }, { t: '50min', rmssd: 20 }, { t: '60min', rmssd: 18 },
    { t: '70min', rmssd: 25 }, { t: '80min', rmssd: 38 }, { t: '90min', rmssd: 50 },
  ]
  return (
    <div style={{ marginTop: 12, background: '#fafafa', borderRadius: 10, padding: '10px 12px 6px' }}>
      <p style={{ fontSize: 10, color: 'var(--text-faint)', marginBottom: 6 }}>
        Ejemplo: sesión que empieza relajada, hay actividad/estrés en el medio, y se recupera al final
      </p>
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" vertical={false} />
          <XAxis dataKey="t" tick={{ fontSize: 8, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 70]} tick={{ fontSize: 8, fill: 'var(--text-fainter)' }} axisLine={false} tickLine={false} width={22} />
          <ReferenceLine y={50} stroke="#34c759" strokeDasharray="4 4" strokeWidth={1}
            label={{ value: 'Muy relajado', fontSize: 8, fill: '#34c759', position: 'insideTopRight' }} />
          <ReferenceLine y={20} stroke="#ff9f0a" strokeDasharray="4 4" strokeWidth={1}
            label={{ value: 'Alta activación', fontSize: 8, fill: '#ff9f0a', position: 'insideTopRight' }} />
          <Line type="monotone" dataKey="rmssd" stroke="#007aff" strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

const GUIDE = [
  // ── Sección 1 ───────────────────────────────────────────────────────────────
  {
    key: 'section_general',
    title: '1. Métricas Generales — Salud de Base y Composición',
    body: `## 1. Métricas Generales (Salud de Base y Composición)

### Frecuencia Cardíaca en Reposo (RHR / FC Reposo)

- **Qué es:** El número mínimo de latidos por minuto (BPM) del corazón cuando estás en total reposo físico y mental.
- **Utilidad Clínica:** Es un indicador directo de la eficiencia de tu músculo cardíaco. Un corazón más fuerte bombea más sangre en cada latido, por lo que necesita latir menos veces por minuto.
- **Rango de Referencia:** En adultos sanos oscila entre **60 y 100 BPM**. En atletas de resistencia puede bajar a **40–50 BPM**. Un aumento progresivo en tu línea de base suele ser una señal temprana de fatiga acumulada, sobreentrenamiento o estrés.

### Frecuencia Respiratoria Basal (FR)

- **Qué es:** La cantidad de respiraciones (ciclos de inhalación y exhalación) que realizás por minuto en estado de calma diurna.
- **Utilidad Clínica:** El sistema la calcula aislando la oscilación de la Arritmia Sinusal Respiratoria — el cambio sutil de ritmo cardíaco que ocurre de forma natural al respirar. Sirve para evaluar la estabilidad del sistema respiratorio y el equilibrio autonómico.
- **Cómo se estima:** Al inhalar el corazón acelera levemente; al exhalar frena. Esa oscilación cíclica queda grabada en los intervalos RR y permite estimar la respiración sin sensor adicional.
- **Rango de Referencia:** Lo normal en adultos en reposo es entre **12 y 18 respiraciones por minuto**.

### Índice de Masa Corporal (IMC)

- **Qué es:** Un cálculo matemático estandarizado por la OMS que asocia el peso corporal con la altura del usuario.
- **Fórmula:**
[IMC = Peso (kg) ÷ Altura² (m)]
- **Utilidad Clínica:** Clasificación rápida del estado nutricional. Categoriza el perfil en: Bajo peso (< 18.5), Normopeso (18.5–24.9), Sobrepeso (25–29.9) u Obesidad (> 30). Sirve como parámetro estático para ajustar otras estimaciones de gasto calórico o carga.`,
  },

  // ── Sección 2 ───────────────────────────────────────────────────────────────
  {
    key: 'section_training',
    title: '2. Métricas de Entrenamiento y Rendimiento',
    body: `## 2. Métricas de Entrenamiento y Rendimiento

### Frecuencia Cardíaca Instantánea (BPM)

- **Qué es:** El ritmo exacto al que está latiendo el corazón en el segundo actual.
- **Utilidad Clínica:** No espera a que pase un minuto; calcula el tiempo exacto en milisegundos entre el último latido y el anterior (intervalo RR) y lo traduce a pulsaciones por minuto. Permite detectar picos de esfuerzo inmediatos o anomalías en la respuesta al ejercicio.
- **Fórmula:**
[BPM = 60000 ÷ RR (ms)]

### Frecuencia Cardíaca Máxima (FCmáx)

- **Qué es:** El límite teórico superior de pulsaciones al que tu corazón puede bombear de forma segura bajo un esfuerzo físico extremo.
- **Fórmula (Tanaka — más precisa que 220 − edad):**
[FCmáx = 211 − (0.64 × Edad)]
- **Utilidad Clínica:** Es la piedra angular para programar cualquier entrenamiento basado en pulsaciones. La fórmula de Tanaka está validada clínicamente y evita subestimaciones en adultos mayores y sobreexigencias peligrosas en jóvenes.

### Zonas de Intensidad Cardíaca (Z1 a Z5)

- **Qué es:** Cinco rangos de esfuerzo delimitados por porcentajes específicos de tu FCmáx individual.
- **Utilidad Clínica:** Permiten entrenar con un objetivo fisiológico preciso:
- **Z1 (50–60 %):** Recuperación metabólica y calentamiento.
- **Z2 (60–70 %):** Resistencia aeróbica base y zona óptima de oxidación de grasas (lipólisis).
- **Z3 (70–80 %):** Capacidad cardiovascular general y eficiencia de transporte de oxígeno.
- **Z4 (80–90 %):** Umbral anaeróbico; el cuerpo empieza a acumular lactato en los músculos.
- **Z5 (90–100 %):** Capacidad anaeróbica láctica pura — esfuerzos máximos de muy corta duración.

### Carga Cardiovascular — TRIMP (Training Impulse)

- **Qué es:** Un índice que mide el impacto o estrés acumulado que una sesión de ejercicio le generó a tu corazón.
- **Utilidad Clínica:** No toma solo la duración de la sesión, sino la intensidad real ponderada. Un minuto en Z5 suma exponencialmente más "puntos de carga" que un minuto en Z2. Es la métrica clave para planificar la progresión del entrenamiento y evitar lesiones o sobreentrenamiento crónico.

### Tiempo de Recuperación Cardíaca (HRR)

- **Qué es:** La diferencia en BPM entre tu frecuencia cardíaca al detener el ejercicio y tu frecuencia exactamente un minuto después.
- **Utilidad Clínica:** Mide la velocidad con la que tu sistema nervioso parasimpático (el "freno" del cuerpo) retoma el control del corazón tras el esfuerzo. Un descenso rápido — **mayor a 12–20 BPM en los primeros 60 segundos** — es uno de los mejores indicadores clínicos de un corazón sano y una excelente condición física.`,
  },

  // ── Sección 3 ───────────────────────────────────────────────────────────────
  {
    key: 'section_sleep',
    title: '3. Métricas de Sueño y Descanso Nocturno',
    body: `## 3. Métricas de Sueño y Descanso Nocturno

### RMSSD Nocturno (Variabilidad Parasimpática)

- **Qué es:** La raíz cuadrada de la media de las diferencias al cuadrado entre intervalos RR sucesivos durante las horas de sueño.
- **Utilidad Clínica:** Es el **estándar de oro médico** para evaluar la actividad del sistema nervioso parasimpático — el encargado de la restauración celular, la digestión y el descanso. Un RMSSD nocturno alto o estable indica que tu cuerpo se recuperó profundamente durante la noche. Si cae de forma sostenida, refleja fatiga, estrés psicológico o un proceso inflamatorio/infeccioso en curso.
- **Rango de Referencia:** En adultos de 24 años, esperable entre **45–70 ms** en reposo nocturno.

### SDNN Nocturno (Variabilidad Global)

- **Qué es:** La desviación estándar de todos los intervalos entre latidos registrados durante toda la noche.
- **Utilidad Clínica:** Mide la variabilidad global del corazón — la interacción combinada entre el sistema simpático de alerta y el parasimpático de calma. Un SDNN alto representa resiliencia biológica general. Clínicamente, un valor crónicamente bajo se asocia con un mayor riesgo de eventos cardiovasculares.
- **Rango de Referencia:** Referencia nocturna para adulto joven: **45–85 ms** (promedio ~60 ms).

### Frecuencia Respiratoria Nocturna Promedio

- **Qué es:** El promedio de respiraciones por minuto sostenido mientras dormís.
- **Utilidad Clínica:** En condiciones normales, esta métrica es sumamente estable noche tras noche (varía menos de 1 RPM). Un incremento repentino en la frecuencia respiratoria nocturna es un **predictor clínico muy sensible** de fiebre, incubación de virus respiratorios o apnea del sueño.
- **Rango de Referencia:** **12–18 RPM** en adulto sano durmiendo.

### Estabilidad del Ritmo — Segmentación de Fases del Sueño

- **Qué es:** El análisis de la regularidad y los patrones de la HRV para diferenciar los momentos de descanso profundo de los más ligeros.
- **Utilidad Clínica:** Durante el sueño profundo (fases No-REM), el ritmo cardíaco y respiratorio se vuelven extremadamente estables y lineales — baja variabilidad inmediata. En fases REM o micro-despertares, el ritmo se vuelve más caótico. El software procesa estas firmas estadísticas para dar una estimación de la estructura y calidad real de tu arquitectura del sueño.`,
  },

  // ── Sección 4 ───────────────────────────────────────────────────────────────
  {
    key: 'arrhythmia',
    title: '4. Arritmias — Detección y Clasificación',
    body: `## 4. Arritmias

Para detectar una arritmia, el software busca **caos, pausas o disparos mecánicos anormales** en los intervalos RR, mediante tres análisis matemáticos complementarios.

### A. Gráfico de Poincaré (Geometría del Ritmo)

La herramienta analítica por excelencia en cardiología para HRV. Grafica cada intervalo RR[i] contra el intervalo siguiente RR[i+1].

- **En un corazón sano:** el gráfico genera una elipse limpia en forma de "cometa". Si hay una arritmia, la geometría se rompe por completo.
- **SD1 (Dispersión a corto plazo):** variabilidad latido a latido — asociado al RMSSD.
- **SD2 (Dispersión a largo plazo):** variabilidad continua del ritmo — asociado al SDNN.
- **Relación SD1/SD2:** ratio ideal en reposo ≈ 0.5–0.7. Si se dispara (> 0.75) o la elipse se deforma, hay una disrupción del ritmo. La Fibrilación Auricular genera un patrón de "nube caótica" sin forma alguna.

### B. Índice de Variabilidad de Lorenz (CSI y CVI)

Derivado del gráfico de Poincaré, sirve específicamente para detectar arritmias severas o episodios de estrés autonómico extremo.

- **CSI (Cardiac Sympathetic Index):** mide la longitud de la distribución de los puntos — activación simpática.
- **CVI (Cardiac Vagal Index):** mide el ancho de la distribución — tono vagal/parasimpático.
- Un quiebre matemático drástico en el CSI/CVI activa una alerta de ritmo irregular.

### C. Filtro Estadístico de Prematuridad — Detección de Extrasístoles

Las extrasístoles son latidos adelantados. El algoritmo las identifica analizando la relación del latido actual con el promedio móvil de los 10 latidos anteriores (RRprom):

- **Latido prematuro (extrasístole):** si un intervalo RRi es notablemente más corto que el promedio:
[RRi < 0.80 × RRprom]
- **Pausa compensatoria:** típicamente le sigue un latido más largo para "acomodar" el ciclo:
[RRi+1 > 1.20 × RRprom]
- Si el software detecta este patrón repetido — un latido muy corto seguido de uno muy largo — contabiliza una **alerta de latido ectópico / extrasístole**.

### Clasificación del Origen de la Arritmia

A través del análisis temporal de los intervalos RR, se puede orientar clínicamente el origen de la anomalía:

- **Fibrilación Auricular (FA):** el ritmo se vuelve "irregularmente irregular". No hay patrón alguno — el RMSSD y el SDNN se disparan a niveles anómalos porque los latidos caen en tiempos completamente aleatorios. En el gráfico de Poincaré, la elipse desaparece y se convierte en dispersión total.
- **Extrasístoles Ventriculares (EV) aisladas:** se caracterizan por tener una pausa compensatoria completa. Si el algoritmo detecta la regla del "latido corto + latido largo compensatorio exacto", orienta a un origen ectópico aislado.
- **Pausas Sinusales / Bloqueos AV:** se buscan silencios eléctricos. Si un intervalo RR duplica o triplica el promedio móvil (por ejemplo, un RR de 2200 ms a un ritmo de 70 BPM), el software detecta una **pausa cardíaca** y registra la hora exacta y la duración.
- **Arritmia Sinusal Respiratoria (ASR) — falso positivo normal:** la oscilación cíclica del corazón al respirar. Es fisiológica y saludable. El software la distingue porque es **cíclica y rítmica** — si la variación del RR sube y baja en fases de 3–5 segundos de forma constante, se cataloga como "Ritmo Sinusal con Alta Actividad Vagal".

### Cribado de Ritmo — Indicadores Clave

- **Contador de Eventos Irregulares:** cantidad de latidos que rompieron la regla del ±20 % del promedio móvil en la franja horaria.
- **Densidad de Extrasístoles:** porcentaje de latidos anómalos sobre el total. Clínicamente, si es menor al 1 % de los latidos diarios suele considerarse benigno.
- **Registro de Pausas > 2.0 s:** listado con timestamp exacto de ventanas sin latidos por más de dos segundos.
- **Alerta de Ritmo Caótico:** se activa si el gráfico de Poincaré pierde su geometría elíptica por más de 30 segundos seguidos — sugiere consulta médica preventiva para descartar episodios de fibrilación.`,
  },

  // ── Sección HRV avanzada ─────────────────────────────────────────────────────
  {
    key: 'rmssd',
    title: 'HRV — RMSSD, SDNN y pNN50 en detalle',
    body: `## HRV — Variabilidad de la Frecuencia Cardíaca

La Variabilidad de la Frecuencia Cardíaca (VFC / HRV) mide cuánto varía el tiempo entre latidos. Sorpresa: que varíe es **bueno**.

Un corazón sano en reposo no late como un metrónomo exacto. Varía porque el sistema nervioso lo regula constantemente. Más variación = sistema nervioso más activo y saludable.

### RMSSD

- **Qué es:** Raíz cuadrada de la media de las diferencias al cuadrado entre intervalos RR sucesivos (Root Mean Square of Successive Differences).
- **Utilidad:** Estándar de oro para medir la actividad parasimpática de corto plazo.
- **Cómo se calcula:** Para cada par de latidos seguidos, se mide la diferencia en ms, se eleva al cuadrado, se promedia, y se saca la raíz cuadrada.
- **Rango de Referencia:** > 50 ms: muy relajado, buena recuperación · 30–50 ms: equilibrado · 15–30 ms: estrés leve · < 15 ms: alta activación o fatiga. Hombre 24 años en reposo: referencia 45–70 ms.

### SDNN

- **Qué es:** Desviación estándar de TODOS los intervalos RR de la sesión.
- **Utilidad:** Mide la variabilidad global — la interacción combinada entre el simpático y el parasimpático. El SDNN siempre es ≥ RMSSD.
- **Rango de Referencia:** > 50 ms: buena variabilidad · 20–50 ms: moderada · < 20 ms: baja (estrés crónico, fatiga). Referencia adulto joven: 45–85 ms.

### pNN50

- **Qué es:** Porcentaje de pares de latidos consecutivos con diferencia mayor a 50 ms.
- **Utilidad:** Otra ventana al tono parasimpático. Si cae durante el día puede indicar acumulación de fatiga.
- **Rango de Referencia:** > 20 %: buen tono parasimpático · 5–20 %: moderado · < 5 %: sistema simpático activo.

El diagrama de Poincaré a continuación muestra la diferencia visual: la nube grande y dispersa representa alta VFC (reposo/calma); la nube pequeña y apretada representa baja VFC (estrés/esfuerzo).`,
  },

  // ── Sección de tendencia ─────────────────────────────────────────────────────
  {
    key: 'hrv_trend',
    title: 'Tendencia RMSSD durante la sesión',
    body: `## Tendencia RMSSD a lo Largo del Tiempo

El RMSSD calculado sobre toda la sesión da un número único. Más interesante es ver **cómo cambia** durante la sesión.

Este gráfico divide la sesión en ventanas de 2 minutos y calcula el RMSSD en cada una. Así podés ver:

- En qué momento del entrenamiento hubo más esfuerzo o estrés (RMSSD cae)
- Si la recuperación fue progresiva al final (RMSSD sube gradualmente)
- Si empezaste relajado y la actividad fue escalando

### Interpretación

- **RMSSD cae abruptamente:** pico de esfuerzo o estrés
- **RMSSD sube gradualmente después del esfuerzo:** buena recuperación parasimpática
- **RMSSD oscila sin tendencia:** ritmo variable o arritmia sinusal respiratoria activa
- **RMSSD muy bajo y plano durante toda la sesión:** estrés sostenido, fatiga acumulada o mala noche previa

El gráfico de abajo muestra una sesión típica: la persona empieza relajada, hay actividad en el medio, y recupera el tono parasimpático al final.`,
  },

  // ── ECG ─────────────────────────────────────────────────────────────────────
  {
    key: 'ecg',
    title: 'ECG — Electrocardiograma',
    body: `## ECG — Electrocardiograma

El ECG muestra la señal eléctrica del corazón latido a latido, a **130 muestras por segundo**. Cada ciclo cardíaco tiene tres eventos eléctricos:

### Componentes del Ciclo Cardíaco

- **Onda P (pequeño bump inicial):** las aurículas se contraen y mandan sangre a los ventrículos.
- **Complejo QRS (el pico alto):** los ventrículos se contraen y mandan sangre al cuerpo — este es el "latido" que sentís y el evento que genera el intervalo RR.
- **Onda T (bump ancho final):** los ventrículos se relajan y se repolarizan para el siguiente ciclo.

### Interpretación Visual

En un ritmo normal los picos QRS son **uniformes en altura, forma y espaciado**. Si hay irregularidades en alguno de estos tres aspectos, puede ser relevante clínicamente — aunque la interpretación siempre requiere un médico.

### Escala del Papel

El fondo imita el papel médico estándar de electrocardiografía:

- Cuadro pequeño = 40 ms × 0.1 mV
- Cuadro grande = 200 ms × 0.5 mV

[AVISO] Esta visualización es educativa. No es un diagnóstico médico.`,
  },

  // ── Alertas ──────────────────────────────────────────────────────────────────
  {
    key: 'alerts',
    title: 'Alertas Automáticas del Monitor',
    body: `## Alertas Automáticas

La app detecta automáticamente tres situaciones clínicas de interés:

### Taquicardia

- **Condición:** FC promedio > 100 BPM sostenida durante 30 segundos en estado de reposo.
- **Interpretación:** Puede ser fisiológica (esfuerzo, cafeína, estrés) o patológica si aparece en reposo sin causa aparente.

### Bradicardia

- **Condición:** FC promedio < 50 BPM.
- **Interpretación:** Puede ser normal en atletas de resistencia bien entrenados. En personas sedentarias sin antecedentes, merece evaluación.

### Ritmo Irregular

- **Condición:** Alta variabilidad en los intervalos RR detectada en los últimos 30 segundos (desviación estándar de diferencias > 80 ms).
- **Interpretación:** Puede ser arritmia sinusal respiratoria fisiológica (ver sección Arritmias) o una alteración del ritmo que amerita seguimiento.

Las alertas aparecen como una banda de color sobre el monitor y se pueden descartar. Aparecen como máximo una vez cada 90 segundos para evitar saturación.

[AVISO] Estas alertas son **indicativas, no diagnósticas**. Consultá un profesional ante cualquier síntoma o si las alertas aparecen con frecuencia en reposo.`,
  },

  // ── H10 ─────────────────────────────────────────────────────────────────────
  {
    key: 'memory',
    title: 'Memoria Interna del Polar H10',
    body: `## Memoria Interna del Polar H10

El H10 tiene memoria interna para **una sesión de FC** (hasta 30 horas).

### Cómo Usarla

- Mantené apretado el botón del H10 por 2 segundos antes de salir → el LED parpadea y la grabación inicia.
- Usalo todo el tiempo que quieras sin estar conectado a ningún dispositivo.
- Al volver, conectalo y la app puede descargar la sesión grabada.

### Limitaciones

- **Solo guarda UNA sesión a la vez.** Al iniciar una nueva, borra la anterior.
- **Solo guarda FC (1 Hz)** — no guarda intervalos RR ni ECG. Esos solo están disponibles en streaming en vivo.
- La descarga directa requiere que hayas iniciado la grabación con el botón del dispositivo.

Por ahora, para acceder a la sesión guardada podés sincronizar con la app Polar Beat.`,
  },
  {
    key: 'capabilities',
    title: 'Qué Puede Medir el Polar H10',
    body: `## Capacidades del Polar H10

El H10 es el sensor de pecho de referencia para HRV y monitoreo cardíaco de alta precisión.

### Lo que esta app captura hoy

- **FC en tiempo real** (BLE estándar, perfil 0x180D)
- **Intervalos RR de alta precisión** (exactitud > 1 ms — calidad clínica)
- **ECG completo a 130 Hz** (protocolo propietario Polar PMD)

### En Desarrollo

- Descarga de sesión grabada en memoria interna (protocolo Polar Offline Recordings)
- Acelerómetro (movimiento, postura, detección de pasos)

### Por Qué el H10 es Diferente

La mayoría de los wearables de muñeca (Apple Watch, Garmin, Fitbit) miden la FC por fotopletismografía (PPG) — luz que rebota en la sangre. El H10 mide la señal eléctrica directa del corazón (como un ECG de un cable), lo que le da una precisión de los intervalos RR 10× mayor, suficiente para análisis clínico de HRV.`,
  },
]

const GUIDE_CHART_MAP = {
  hr:         <GuideHRChart />,
  rmssd:      <GuidePoincareChart />,
  hrv_trend:  <GuideHRVTrendChart />,
  ecg:        <GuideECGChart />,
  arrhythmia: <GuidePoincareChart />,
}

// ── Markdown renderer (subset: ##, ###, **bold**, - lists, [formula]) ─────────

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/)
  if (parts.length === 1) return text
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} style={{ color: 'var(--text)', fontWeight: 600 }}>{p.slice(2, -2)}</strong>
      : p
  )
}

function renderMd(text) {
  const lines = text.split('\n')
  const els   = []
  let   key   = 0

  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i]

    if (ln.startsWith('## ')) {
      els.push(
        <h2 key={key++} style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '22px 0 8px', paddingBottom: 7, borderBottom: '2px solid var(--border-soft)' }}>
          {renderInline(ln.slice(3))}
        </h2>
      )
    } else if (ln.startsWith('### ')) {
      els.push(
        <h3 key={key++} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: '16px 0 5px' }}>
          {renderInline(ln.slice(4))}
        </h3>
      )
    } else if (ln.startsWith('- ') || ln.startsWith('* ')) {
      els.push(
        <div key={key++} style={{ display: 'flex', gap: 8, marginBottom: 4, paddingLeft: 6 }}>
          <span style={{ color: 'var(--text-fainter)', flexShrink: 0, lineHeight: 1.65 }}>·</span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.65 }}>{renderInline(ln.slice(2))}</span>
        </div>
      )
    } else if (ln.startsWith('[') && ln.endsWith(']')) {
      els.push(
        <div key={key++} style={{ background: '#f5f5fa', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', margin: '8px 0', textAlign: 'center', fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 13, color: 'var(--text)', letterSpacing: 0.3 }}>
          {ln.slice(1, -1)}
        </div>
      )
    } else if (ln.trim() === '') {
      els.push(<div key={key++} style={{ height: 7 }} />)
    } else {
      els.push(
        <p key={key++} style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, margin: '0 0 3px' }}>
          {renderInline(ln)}
        </p>
      )
    }
  }

  return els
}

function GuideTab() {
  const [searchParams] = useSearchParams()
  const [open, setOpen] = useState(() => {
    const s = searchParams.get('s')
    return s ? GUIDE.findIndex(item => item.key === s) : null
  })
  return (
    <div>
      <div style={{ background: '#f0f6ff', border: '1px solid #c8deff', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: '#005cc5', lineHeight: 1.5 }}>
          <strong>Aviso:</strong> Esta app es una herramienta de monitoreo personal. Las alertas y métricas son indicativas y no reemplazan diagnóstico médico.
        </p>
      </div>
      {GUIDE.map((item, i) => (
        <div key={i} style={{ background: 'var(--card-bg)', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 8, overflow: 'hidden' }}>
          <div onClick={() => setOpen(open === i ? null : i)} style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{item.title}</span>
            <span style={{ color: 'var(--text-fainter)', fontSize: 13, flexShrink: 0 }}>{open === i ? '▲' : '▼'}</span>
          </div>
          {open === i && (
            <div style={{ borderTop: '1px solid var(--border-soft)', padding: '4px 18px 18px', animation: 'fadeIn 0.2s ease' }}>
              {renderMd(item.body)}
              {GUIDE_CHART_MAP[item.key] ?? null}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Live ECG canvas ───────────────────────────────────────────────────────────

function LiveECGCanvas({ ecgBuf, color }) {
  const canvasRef = useRef(null)
  const rafRef    = useRef(null)
  const colorRef  = useRef(color)
  colorRef.current = color

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const draw = () => {
      const W = canvas.width, H = canvas.height
      ctx.clearRect(0, 0, W, H)

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      for (let x = 0; x <= W; x += W / 10) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
      for (let y = 0; y <= H; y += H / 4)  { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }

      const show = ecgBuf.current.slice(-650)
      if (show.length < 10) {
        ctx.fillStyle = 'rgba(255,255,255,0.2)'
        ctx.font = '13px -apple-system, sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('Esperando señal ECG…', W / 2, H / 2)
        rafRef.current = requestAnimationFrame(draw)
        return
      }

      // Safe min/max — avoid spread on large arrays
      let min = show[0], max = show[0]
      for (let i = 1; i < show.length; i++) {
        if (show[i] < min) min = show[i]
        if (show[i] > max) max = show[i]
      }
      const range = max - min || 1
      const pad = 0.08

      ctx.beginPath()
      ctx.strokeStyle = colorRef.current
      ctx.lineWidth = 2
      ctx.lineJoin = 'round'
      ctx.lineCap  = 'round'
      for (let i = 0; i < show.length; i++) {
        const x = (i / (show.length - 1)) * W
        const y = H - ((show[i] - min) / range) * H * (1 - pad * 2) - H * pad
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
      }
      ctx.stroke()

      // Moving scan-line at the leading edge
      const lx = W - 1
      ctx.strokeStyle = `${colorRef.current}60`
      ctx.lineWidth = 1.5
      ctx.beginPath(); ctx.moveTo(lx, 0); ctx.lineTo(lx, H); ctx.stroke()

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])  // runs once; colorRef keeps color current without restarting the RAF loop

  return (
    <canvas ref={canvasRef} width={680} height={220}
      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }} />
  )
}

// ── Live view (phone) ─────────────────────────────────────────────────────────

const API = apiBase

const LIVE_OWNER = 'matias@cagliero.ar'

// ── Vista médica: historial completo de solo lectura via link con token ───────
// El médico abre /doctor/<token> (link generado en Perfil → Compartir acceso,
// plan Biohacker). El server devuelve TODO el historial del último año.
export function DoctorView() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr]   = useState(null)
  const [openDay, setOpenDay] = useState(null)

  useEffect(() => {
    const base = apiBase
    fetch(`${base}/doctor/${token}`)
      .then(async r => {
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        return d
      })
      .then(setData)
      .catch(e => setErr(friendlyErr(e)))
  }, [token])

  const days = useMemo(() => {
    if (!data) return []
    const byDay = {}
    ;(data.sessions || []).forEach(s => {
      const start = new Date(s.start_time).getTime(), end = new Date(s.end_time).getTime()
      if (!(end > start)) return
      const k = localDateKey(start)
      ;(byDay[k] ||= { k, sessions: [], min: 0, hrs: [] }).sessions.push({ ...s, start, end })
      byDay[k].min += (end - start) / 60000
    })
    ;(data.readings || []).forEach(r => {
      const t = new Date(r.time).getTime()
      const d = byDay[localDateKey(t)]
      if (d && r.hr) d.hrs.push({ t, hr: r.hr })
    })
    const out = Object.values(byDay).sort((a, b) => b.k.localeCompare(a.k))
    out.forEach(d => {
      d.hrs.sort((a, b) => a.t - b.t)
      d.sessions.sort((a, b) => a.start - b.start)
      const hs = d.hrs.map(h => h.hr)
      d.fcMin = hs.length ? Math.min(...hs) : null
      d.fcMax = hs.length ? Math.max(...hs) : null
    })
    return out
  }, [data])

  useEffect(() => { if (days.length && !openDay) setOpenDay(days[0].k) }, [days])

  const fmtMin = (min) => min < 60 ? `${Math.round(min)} min` : `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`
  const hhmm = (ms) => new Date(ms).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  const expiresDays = data ? Math.max(0, Math.round((data.expiresAt * 1000 - Date.now()) / 86400000)) : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--chip-bg)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", padding: '24px 14px 60px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.4 }}>Manabi</span>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#5e5ce6', background: '#eeeefe', borderRadius: 6, padding: '2px 8px' }}>ACCESO MÉDICO</span>
        </div>

        {err && (
          <div style={{ background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--border)', padding: '28px 20px', textAlign: 'center', marginTop: 14 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>No se pudo abrir el historial</p>
            <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>{err}</p>
          </div>
        )}
        {!err && !data && (
          <p style={{ fontSize: 13, color: 'var(--text-faint)', marginTop: 20 }}>Cargando historial…</p>
        )}

        {data && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: '10px 0 2px' }}>Historial de {data.owner?.name ?? 'Paciente'}</h1>
            <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '0 0 16px' }}>
              Último año · {days.length} día{days.length !== 1 ? 's' : ''} con registro · solo lectura
              {expiresDays != null && ` · este acceso expira en ${expiresDays} día${expiresDays !== 1 ? 's' : ''}`}
            </p>

            {days.length === 0 && (
              <div style={{ background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--border)', padding: '28px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>Todavía no hay sesiones registradas.</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {days.map(d => {
                const open = openDay === d.k
                const spark = d.hrs.length > 1 ? d.hrs.filter((_, i) => i % Math.max(1, Math.ceil(d.hrs.length / 400)) === 0).map(h => h.hr) : []
                return (
                  <div key={d.k} style={{ background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
                    <button onClick={() => setOpenDay(open ? null : d.k)} style={{ width: '100%', background: 'none', border: 'none', padding: '13px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ textAlign: 'left', minWidth: 0 }}>
                        <p style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)', margin: 0, textTransform: 'capitalize' }}>
                          {new Date(d.k + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: '2px 0 0' }}>
                          {fmtMin(d.min)} · {d.sessions.length} sesi{d.sessions.length !== 1 ? 'ones' : 'ón'}{d.fcMin != null ? ` · FC ${d.fcMin}–${d.fcMax} bpm` : ''}
                        </p>
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-fainter)', flexShrink: 0 }}>{open ? '▴' : '▾'}</span>
                    </button>
                    {open && (
                      <div style={{ padding: '0 16px 14px' }}>
                        {spark.length > 1 && (
                          <div style={{ background: 'var(--inset-bg)', borderRadius: 12, padding: '10px 12px 6px', marginBottom: 10 }}>
                            <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.6 }}>Frecuencia cardíaca del día</p>
                            <div style={{ width: '100%', overflow: 'hidden' }}>
                              <Sparkline data={spark} color="#ff3b30" width={620} height={54} />
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {d.sessions.map(s => (
                            <div key={s.session_id + s.start} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--inset-bg)', borderRadius: 10, padding: '8px 12px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{hhmm(s.start)}–{hhmm(s.end)}</span>
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{fmtMin((s.end - s.start) / 60000)}</span>
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                {s.avg_hr ? `FC ${s.avg_hr} prom (${s.min_hr ?? '—'}–${s.max_hr ?? '—'})` : 'sin FC'}
                              </span>
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{s.rmssd ? `HRV ${Math.round(s.rmssd)} ms` : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <p style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 18, textAlign: 'center' }}>
              Datos registrados con Polar H10 · Manabi — manabi.cagliero.ar
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export function LiveView() {
  const params = new URLSearchParams(window.location.search)
  const followerToken = params.get('token')
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('polar_user')) } catch { return null } })()
  const isOwner = storedUser?.email === LIVE_OWNER

  // Un link de seguidor explícito manda (salvo que seas el propio dueño).
  if (followerToken && !isOwner) {
    return <LiveViewFollower followerToken={followerToken} />
  }
  // El dueño ve SU propia transmisión. Cualquier otra persona —con o sin sesión—
  // ve la transmisión pública. Antes, si la otra computadora estaba logueada con
  // OTRA cuenta (o con una sesión demo vieja), /live intentaba abrir el canal de
  // ESA cuenta (vacío) y quedaba "esperando conexión" para siempre.
  if (isOwner) {
    return <LiveViewInner />
  }
  return <LiveViewPublic />
}

// ── /live sin token: transmisión pública del usuario principal ────────────────
// Resuelve el token del link "Publico" (una persona más de la pestaña Personas,
// con su misma lógica de expiración/revocación) y entra por el flujo de seguidor.
// ── Pantalla moderna de Límite de Capacidad de Espectadores ─────────────────
function CapacityLimitView({ maxAllowed = 5, isPro = false, onRetry }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(circle at 50% 20%, #1c150c 0%, #0a0a0a 70%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      color: '#fff',
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        maxWidth: 440, width: '100%',
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(255, 159, 10, 0.25)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: 24,
        padding: '36px 26px 30px',
        textAlign: 'center',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(255, 159, 10, 0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Glow sutil en la parte superior */}
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 200, height: 100,
          background: 'radial-gradient(ellipse, rgba(255,159,10,0.25) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        {/* Badge de Capacidad */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255, 159, 10, 0.15)',
          border: '1px solid rgba(255, 159, 10, 0.4)',
          borderRadius: 20, padding: '5px 14px', marginBottom: 20
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff9f0a', boxShadow: '0 0 8px #ff9f0a' }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: '#ff9f0a', letterSpacing: 1, textTransform: 'uppercase' }}>
            Transmisión Llena
          </span>
        </div>

        {/* Icono de Espectadores */}
        <div style={{
          width: 68, height: 68, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,159,10,0.2) 0%, rgba(255,59,48,0.1) 100%)',
          border: '1px solid rgba(255,159,10,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
          boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff9f0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>

        {/* Título y Explicación */}
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 8px', letterSpacing: -0.4 }}>
          Límite de espectadores alcanzado
        </h2>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, margin: '0 0 24px' }}>
          Esta transmisión en vivo completó su cupo máximo de <strong style={{ color: '#fff' }}>{maxAllowed} espectadores simultáneos</strong> en el plan Gratis.
        </p>

        {/* Tarjeta comparativa de Planes */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: '16px 14px',
          marginBottom: 24,
          textAlign: 'left',
          display: 'flex', flexDirection: 'column', gap: 10
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }}>Plan Gratis actual</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.8)' }}>Hasta 5 personas</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#ffd60a', display: 'block' }}>Plan Pro (Premium)</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Para equipos, clubes y entrenadores</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 900, color: '#34c759' }}>Hasta 30 personas</span>
          </div>
        </div>

        {/* Acciones */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                width: '100%',
                background: 'var(--btn-strong)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '13px',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                transition: 'opacity 0.2s'
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              Reintentar ingresar
            </button>
          )}

          <a
            href="/register"
            style={{
              display: 'block',
              textDecoration: 'none',
              background: 'linear-gradient(90deg, #b45309 0%, #d97706 100%)',
              color: '#fff',
              borderRadius: 12,
              padding: '12px',
              fontSize: 13.5,
              fontWeight: 700,
              boxShadow: '0 4px 16px rgba(217, 119, 6, 0.25)',
              textAlign: 'center'
            }}
          >
            Mejorar cuenta a Plan Pro →
          </a>

          <a
            href="/"
            style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: 12.5,
              textDecoration: 'none',
              marginTop: 6,
              fontWeight: 600
            }}
          >
            Volver a Manabi
          </a>
        </div>
      </div>
    </div>
  )
}

function LiveViewPublic() {
  const [tok, setTok] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    fetch(`${API}/live/public-token`)
      .then(async r => {
        const d = await r.json()
        if (!r.ok) {
          const e = new Error(d.error || `HTTP ${r.status}`)
          e.limitReached = d.limitReached
          e.maxAllowed = d.maxAllowed
          throw e
        }
        setTok(d.token)
      })
      .catch(e => setErr(e))
  }, [])
  if (err?.limitReached) {
    return <CapacityLimitView maxAllowed={err.maxAllowed || 5} onRetry={() => window.location.reload()} />
  }
  if (tok) return <LiveViewFollower followerToken={tok} />
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 24, fontFamily: '-apple-system,sans-serif' }}>
      {err ? (
        <>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          </div>
          <p style={{ color: '#fff', fontSize: 16, fontWeight: 700, margin: 0 }}>Transmisión no disponible</p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center', margin: 0 }}>{err.message || String(err)}</p>
        </>
      ) : (
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 15, margin: 0 }}>Conectando…</p>
      )}
    </div>
  )
}

// ── Follower gate: exchange UUID token for JWT, then show live view ───────────
function LiveViewFollower({ followerToken }) {
  const [jwt, setJwt]             = useState(null)
  const [errorInfo, setErrorInfo] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    fetch(`${API}/followers/auth/${followerToken}`)
      .then(async r => {
        const d = await r.json()
        if (!r.ok) {
          const err = new Error(d.error || `HTTP ${r.status}`)
          err.limitReached = d.limitReached
          err.maxAllowed   = d.maxAllowed
          err.isPro        = d.isPro
          throw err
        }
        setJwt(d.token)
        setErrorInfo(null)
      })
      .catch(e => setErrorInfo({ message: e.message, limitReached: e.limitReached, maxAllowed: e.maxAllowed, isPro: e.isPro }))
  }, [followerToken, retryCount])

  if (errorInfo?.limitReached) {
    return (
      <CapacityLimitView
        maxAllowed={errorInfo.maxAllowed || 5}
        isPro={errorInfo.isPro}
        onRetry={() => setRetryCount(c => c + 1)}
      />
    )
  }

  if (errorInfo) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24, fontFamily: '-apple-system,sans-serif' }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,59,48,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <p style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Link no disponible</p>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center' }}>{errorInfo.message}</p>
    </div>
  )
  if (!jwt) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(255,59,48,0.2)', borderTopColor: '#ff3b30', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )
  return <LiveViewInner overrideToken={jwt} />
}

// ── Corazón que late al ritmo real ────────────────────────────────────────────
// El "lub-dub" lo dispara JS cada 60/bpm s (un timeout por latido), así cada
// golpe SIEMPRE se completa y el tempo se adapta sin reiniciar animaciones CSS
// (el approach viejo de `animation-duration` variable se cortaba en cada lectura).
function darkenHex(hex, f = 0.55) {
  const n = parseInt(hex.slice(1), 16)
  const ch = s => Math.round(((n >> s) & 255) * f)
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`
}
const HEART_PATH = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'

function BeatingHeart({ hr, color, glow, online }) {
  const heartRef = useRef(null)
  const ringsRef = useRef(null)
  const liveRef  = useRef({ hr, color })
  liveRef.current = { hr, color }
  const uid = useId()
  const gradId = `vcHeartGrad${uid}`, shineId = `vcHeartShine${uid}`

  useEffect(() => {
    if (!online) return
    let alive = true, to
    const beat = () => {
      if (!alive) return
      const { hr: bpmRaw, color: col } = liveRef.current
      const bpm      = Math.min(220, Math.max(30, bpmRaw || 60))
      const interval = 60000 / bpm
      // Accesibilidad: sin thump ni ondas si el sistema pide menos movimiento
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        to = setTimeout(beat, 1000)
        return
      }
      // El lub-dub ocupa como mucho ~85% del espacio entre latidos (a 180 bpm se acorta solo)
      const thump = Math.min(620, interval * 0.85)
      const el = heartRef.current
      if (el) {
        el.style.animation = 'none'
        void el.offsetWidth   // reflow: reinicia la animación desde cero
        el.style.animation = `vcLubDub ${Math.round(thump)}ms cubic-bezier(.25,.65,.4,1)`
      }
      // Onda expansiva sincronizada con el golpe
      const rc = ringsRef.current
      if (rc && rc.childElementCount < 6) {
        const ring = document.createElement('div')
        ring.className = 'vc-beat-ring'
        ring.style.borderColor = col
        ring.style.width = '100%'
        ring.style.height = '100%'
        rc.appendChild(ring)
        setTimeout(() => ring.remove(), 1000)
      }
      to = setTimeout(beat, interval)
    }
    beat()
    return () => { alive = false; clearTimeout(to); if (heartRef.current) heartRef.current.style.animation = 'none' }
  }, [online])

  const size = 'min(240px, 64vw)'
  return (
    <div style={{ position: 'relative', width: size, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 30 }}>
      {/* halo de fondo — currentColor: así el gradiente CRUZA de color junto con el resto */}
      <div style={{
        position: 'absolute', inset: '-18%', borderRadius: '50%',
        color: online ? glow : 'rgba(0,0,0,0)',
        background: 'radial-gradient(circle, currentColor 0%, transparent 65%)',
        transition: 'color 1.2s ease',
      }} />
      {/* capa de ondas por latido */}
      <div ref={ringsRef} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }} />
      {/* corazón + número: laten juntos */}
      <div ref={heartRef} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', transformOrigin: 'center', willChange: 'transform' }}>
        <svg viewBox="0 0 24 24" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible',
          filter: online ? `drop-shadow(0 0 26px ${glow})` : 'none',
        }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={online ? color : 'rgba(255,255,255,0.07)'} style={{ transition: 'stop-color 1.2s ease' }} />
              <stop offset="100%" stopColor={online ? darkenHex(color) : 'rgba(255,255,255,0.03)'} style={{ transition: 'stop-color 1.2s ease' }} />
            </linearGradient>
            <radialGradient id={shineId} cx="0.35" cy="0.25" r="0.7">
              <stop offset="0%"   stopColor="rgba(255,255,255,0.4)" />
              <stop offset="55%"  stopColor="rgba(255,255,255,0.06)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
          </defs>
          <path d={HEART_PATH} fill={`url(#${gradId})`} stroke={online ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)'} strokeWidth="0.4" strokeLinejoin="round" />
          {online && <path d={HEART_PATH} fill={`url(#${shineId})`} />}
        </svg>
        <div style={{ position: 'relative', textAlign: 'center', transform: 'translateY(-14%)', opacity: online ? 1 : 0.3, transition: 'opacity 1.2s ease' }}>
          <p style={{
            fontSize: 'clamp(44px, 15vw, 62px)', fontWeight: 800, lineHeight: 1, margin: 0,
            color: '#fff', fontVariantNumeric: 'tabular-nums',
            textShadow: online ? '0 2px 12px rgba(0,0,0,0.35)' : 'none',
          }}>{online ? hr : '–'}</p>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.65)', margin: '3px 0 0', letterSpacing: 2 }}>BPM</p>
        </div>
      </div>
    </div>
  )
}

function LiveViewInner({ overrideToken } = {}) {
  const [data,      setData]      = useState(null)
  const [connected, setConnected] = useState(false)
  const [history,   setHistory]   = useState([])
  const [hasEcg,    setHasEcg]    = useState(false)
  const ecgBuf = useRef([])
  const clock  = useClock()

  useEffect(() => {
    let es, retryTimer, alive = true
    function connect() {
      if (!alive) return
      const token = overrideToken ?? localStorage.getItem('polar_token') ?? ''
      es = new EventSource(`${API}/live/stream?token=${encodeURIComponent(token)}`)
      es.onopen    = () => setConnected(true)
      es.onmessage = e => {
        try {
          const d = JSON.parse(e.data)
          // _rx = hora de LLEGADA local: la frescura no depende del reloj del emisor
          setData({ ...d, _rx: Date.now() })
          // ~2 minutos de historia (los envíos llegan cada ~0,5 s)
          if (d.hr) setHistory(h => [...h.slice(-239), { hr: d.hr, ts: d.ts ?? Date.now() }])
          if (d.ecg?.length) {
            ecgBuf.current = ecgBuf.current.length > 1300
              ? ecgBuf.current.slice(-1300).concat(d.ecg)
              : ecgBuf.current.concat(d.ecg)
            setHasEcg(true)
          }
        } catch {}
      }
      es.onerror = () => {
        setConnected(false)
        ecgBuf.current = []
        es.close()
        retryTimer = setTimeout(connect, 3000)
      }
    }
    connect()
    return () => { alive = false; clearTimeout(retryTimer); es?.close() }
  }, [])

  // Stale after 5s — covers tab closed, device disconnected, reconnecting
  const isOnline  = !!(data?.hr && data?.status !== 'offline' && data?._rx && (clock.getTime() - data._rx) < 5000)
  const zone      = isOnline ? getZone(data.hr) : null
  const secsAgo   = data?._rx ? Math.max(0, Math.round((clock.getTime() - data._rx) / 1000)) : null

  return (
    <div className="vc-live-root" style={{
      background: isOnline ? zone.bg : '#0a0a0a',
      transition: 'background 1.2s ease',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      userSelect: 'none',
      position: 'relative', overflowX: 'hidden',
    }}>

      {/* Top bar */}
      <div style={{ position: 'absolute', top: 20, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
        <div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', fontWeight: 600, letterSpacing: 2, marginBottom: 2 }}>ÚLTIMA LECTURA</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: isOnline ? zone.color : 'rgba(255,255,255,0.25)', fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}>
            {data?.ts ? fmtHMS(data.ts) : '––:––:––'}
          </p>
        </div>
        {/* Estado real: conexión SSE + frescura de los datos (no solo la conexión) */}
        {(() => {
          const st = connected && isOnline ? { txt: 'EN VIVO', col: '#34c759', blink: true }
            : connected ? { txt: 'EN ESPERA', col: 'rgba(255,255,255,0.35)', blink: false }
            : { txt: 'RECONECTANDO', col: '#ff3b30', blink: false }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
                background: st.col,
                boxShadow: st.blink ? `0 0 8px ${st.col}` : 'none',
                animation: st.blink ? 'blink 2s ease-in-out infinite' : 'none',
              }} />
              <span style={{ fontSize: 10, color: st.col, fontWeight: 700, letterSpacing: 1 }}>{st.txt}</span>
            </div>
          )
        })()}
      </div>

      {/* Corazón latiendo al ritmo real */}
      <BeatingHeart hr={data?.hr} color={zone?.color ?? '#ff3b30'} glow={zone?.glow ?? 'rgba(255,59,48,0.35)'} online={isOnline} />

      {/* Zone badge con rango de frecuencia cardíaca */}
      {isOnline ? (
        <>
          <div style={{
            background: `${zone.color}20`,
            border: `1px solid ${zone.color}50`,
            borderRadius: 24, padding: '7px 22px', marginBottom: 8,
            animation: 'fadeIn 0.5s ease',
            display: 'inline-flex', alignItems: 'center', gap: 8
          }}>
            <svg viewBox="0 0 24 24" width="14" height="14" style={{ flexShrink: 0 }}><path d={HEART_PATH} fill={zone.color} /></svg>
            <span style={{ fontSize: 14, color: zone.color, fontWeight: 800, letterSpacing: 1.2 }}>
              {zone.label.toUpperCase()}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
              · {zone.range}
            </span>
          </div>

          {/* Badges en vivo: Pasos y Humedad */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            {data?.steps != null && data.steps > 0 && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(255, 149, 0, 0.15)', border: '1px solid rgba(255, 149, 0, 0.35)',
                borderRadius: 20, padding: '4px 14px',
                animation: 'fadeIn 0.4s ease'
              }}>
                <IconSteps size={14} color="#ff9f0a" />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#ff9f0a', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtLatidos(data.steps)} pasos
                </span>
              </div>
            )}
            {data?.posture?.label && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: `${data.posture.color || '#5856d6'}18`,
                border: `1px solid ${data.posture.color || '#5856d6'}40`,
                borderRadius: 20, padding: '4px 14px',
                animation: 'fadeIn 0.4s ease'
              }} title={`Postura actual: ${data.posture.label}`}>
                {renderPostureIcon(data.posture.key, data.posture.color, 14)}
                <span style={{ fontSize: 12, fontWeight: 700, color: data.posture.color || '#5856d6' }}>
                  {data.posture.label}
                </span>
              </div>
            )}
          </div>

          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 0, marginBottom: 14, animation: 'fadeIn 0.5s ease', fontWeight: 600 }}>
            Retraso: {secsAgo != null ? `${secsAgo}s` : '—'} vs tiempo real
          </p>
        </>
      ) : (
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.28)', marginBottom: 28, fontWeight: 500, textAlign: 'center', lineHeight: 1.5 }}>
          {connected
            ? (data?._rx ? 'La medición se detuvo — no hay transmisión en vivo ahora' : 'El monitor todavía no está transmitiendo')
            : 'Conectando con el servidor…'}
        </p>
      )}

      {/* Mensaje FIJADO del transmisor en vivo */}
      {data?.pinnedMessage?.text && (
        <div style={{
          background: 'rgba(255,159,10,0.14)',
          border: '1.5px solid rgba(255,159,10,0.45)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: 16,
          padding: '12px 18px',
          maxWidth: 380,
          width: '100%',
          marginBottom: 14,
          textAlign: 'center',
          animation: 'fadeIn 0.3s ease',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#ff9f0a', textTransform: 'uppercase', letterSpacing: 1 }}>
              Mensaje fijado
            </span>
          </div>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.4 }}>
            “{data.pinnedMessage.text}”
          </p>
        </div>
      )}

      {/* Feed de mensajes en vivo persistente */}
      {(() => {
        const msgs = Array.isArray(data?.messages) ? data.messages : []
        if (!msgs.length) return null
        return (
          <div style={{
            width: '100%', maxWidth: 360, marginBottom: 12,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 14, padding: '10px 14px',
            backdropFilter: 'blur(16px)',
            display: 'flex', flexDirection: 'column', gap: 6,
            animation: 'fadeIn 0.25s ease',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            maxHeight: 130, overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <IconChat size={13} color="rgba(255,255,255,0.7)" />
              <span style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Mensajes en vivo
              </span>
            </div>
            {msgs.slice(-5).map((m, idx) => (
              <div key={m.id || idx} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 12.5, color: '#fff', fontWeight: 600, flex: 1, lineHeight: 1.35 }}>
                  {m.text}
                </span>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {fmtHMS(m.ts)}
                </span>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Gráfico del pulso — siempre visible (conviven pulso + ECG) */}
      {history.length > 2 && (
        <div style={{
          width: '100%', maxWidth: 360, marginBottom: 14,
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 12, padding: '10px 10px 6px',
          border: `1px solid ${isOnline ? zone.color + '30' : 'rgba(255,255,255,0.06)'}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: 1.5, fontWeight: 600, margin: 0 }}>PULSO · ÚLTIMOS 2 MIN</p>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {Math.min(...history.map(r => r.hr))}–{Math.max(...history.map(r => r.hr))} bpm
            </p>
          </div>
          <Sparkline data={history.map(r => r.hr)} color={isOnline ? zone.color : '#444'} width={336} height={56} />
        </div>
      )}

      {/* ECG strip */}
      {hasEcg && (
        <div style={{
          width: '100%', maxWidth: 360, marginBottom: 20,
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 12, padding: '10px 10px 6px',
          border: `1px solid ${isOnline ? zone.color + '30' : 'rgba(255,255,255,0.06)'}`,
        }}>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: 1.5, fontWeight: 600, marginBottom: 6 }}>ECG · 130 Hz</p>
          <LiveECGCanvas ecgBuf={ecgBuf} color={isOnline ? zone.color : '#555'} />
        </div>
      )}

      {/* Stats */}
      {isOnline && (
        <div style={{ display: 'flex', gap: 32, marginBottom: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          {data.rmssd && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, marginBottom: 4, fontWeight: 600 }}>RMSSD</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                {data.rmssd}<span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 2 }}>ms</span>
              </p>
            </div>
          )}
          {history.length > 1 && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, marginBottom: 4, fontWeight: 600 }}>PROM.</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                {Math.round(history.reduce((a, b) => a + b.hr, 0) / history.length)}<span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 2 }}>bpm</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer (offline el mensaje ya está en el slot del badge — no lo duplicamos) */}
      {isOnline && (
        <div style={{ position: 'absolute', bottom: 20, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: zone.color, opacity: 0.4, fontVariantNumeric: 'tabular-nums' }}>actualizado hace {secsAgo}s</p>
        </div>
      )}
    </div>
  )
}

// ── Error boundary ────────────────────────────────────────────────────────────

export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(e) { return { error: e } }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info)
    try {
      fetch('/api/logs/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          errorName: error?.name || 'ReactErrorBoundary',
          message: error?.message || String(error),
          stack: error?.stack,
          url: window.location.href,
          extra: { componentStack: info?.componentStack }
        })
      }).catch(() => {})
    } catch (_) {}
  }
  render() {
    if (this.state.error) return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'inherit' }}>
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 18, padding: '30px 24px', maxWidth: 360, width: '100%', textAlign: 'center' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff3b30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px' }}>Algo se trabó</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
            No pudimos cargar esta sección. El error quedó registrado para revisarlo.
          </p>
          <button onClick={() => this.setState({ error: null })}
            style={{ width: '100%', background: 'var(--btn-strong)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 12 }}>
            Reintentar
          </button>
          {this.state.error && (
            <div style={{ textAlign: 'left', background: 'var(--inset-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', fontSize: 11, fontFamily: 'monospace', color: 'var(--text-faint)', wordBreak: 'break-all', overflow: 'auto', maxHeight: 100 }}>
              {this.state.error.message ?? String(this.state.error)}
            </div>
          )}
        </div>
      </div>
    )
    return this.props.children
  }
}

// ── Demo banner ───────────────────────────────────────────────────────────────

function DemoBanner({ expiresAt }) {
  const secsLeft = expiresAt - Math.floor(Date.now() / 1000)
  const hoursLeft = Math.max(0, Math.floor(secsLeft / 3600))
  const label = hoursLeft > 48 ? `${Math.floor(hoursLeft / 24)} días` : hoursLeft > 1 ? `${hoursLeft}h` : 'menos de 1h'
  return (
    <div style={{
      background: 'linear-gradient(90deg,#1d4ed8,#7c3aed)', color: '#fff',
      borderRadius: 10, padding: '10px 16px', marginBottom: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13,
    }}>
      <span><strong>Modo Demo</strong> — acceso completo por {label} más</span>
      <a href="/register" style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
        Crear cuenta gratis →
      </a>
    </div>
  )
}

const PLAN_META = {
  free:        { label: 'Free',             color: 'var(--text-faint)', bg: 'var(--chip-bg)' },
  trial:       { label: 'Prueba gratuita',  color: '#0891b2', bg: '#e0f7fa' },
  explorador:  { label: 'Explorador',       color: '#7c3aed', bg: '#f3e8ff' },
  // 3 planes principales
  atleta:      { label: 'Deportista',       color: '#059669', bg: '#d1fae5' },
  familiar:    { label: 'Coach Deportivo',  color: '#d97706', bg: '#fef3c7' },
  pro:         { label: 'Biohacker',        color: '#dc2626', bg: '#fee2e2' },
  // aliases
  biohackers:  { label: 'Biohacker',        color: '#dc2626', bg: '#fee2e2' },
  coach:       { label: 'Coach Deportivo',  color: '#d97706', bg: '#fef3c7' },
  deportista:  { label: 'Deportista',       color: '#059669', bg: '#d1fae5' },
  demo:        { label: 'Demo',             color: 'var(--text-muted)', bg: 'var(--chip-bg)' },
}

// ── Upgrade a premium (solo usuarios free): botón + modal de planes ───────────
function UpgradeButton({ user, token, style }) {
  const [open, setOpen]               = useState(false)
  const [loadingPlan, setLoadingPlan] = useState(null)
  const [err, setErr]                 = useState(null)
  if (user?.plan !== 'free') return null

  const upgrade = async (plan) => {
    if (plan === 'free') return
    setLoadingPlan(plan); setErr(null)
    try {
      const r = await fetch(`${API}/billing/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      window.location.href = d.checkoutUrl
    } catch (e) {
      setErr(e.message || 'No se pudo abrir el pago')
      setLoadingPlan(null)
    }
  }

  return (
    <>
      <button onClick={() => setOpen(true)} style={{
        width: '100%', border: 'none', borderRadius: 10, padding: '9px 12px',
        background: 'linear-gradient(135deg, #f59e0b, #ef4444)', color: '#fff',
        fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        boxShadow: '0 2px 10px rgba(239,68,68,0.35)',
        ...style,
      }}>
        Pasate a Premium
      </button>
      {open && createPortal(
        <div onClick={() => !loadingPlan && setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--chip-bg)', borderRadius: 20, padding: '20px 18px', width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto', fontFamily: 'inherit',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <p style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Pasate a Premium</p>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-faint)', cursor: 'pointer', padding: 4, lineHeight: 1 }}>×</button>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>
              Tu plan Free guarda los últimos 7 días. Con premium tenés todo tu historial y podés compartir en vivo.
            </p>
            {err && <p style={{ fontSize: 13, color: '#ff453a', margin: '0 0 10px' }}>[ERROR] {err}</p>}
            <PlanCards mode="upgrade" currentPlan="free" onSelect={upgrade} loadingPlan={loadingPlan} />
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

function PlanBadge({ plan }) {
  const isPremium = plan && plan !== 'free' && plan !== 'demo'
  const label = isPremium ? 'Premium' : 'Free'
  const color = isPremium ? '#dc2626' : 'var(--text-faint)'
  const bg = isPremium ? '#fee2e2' : 'var(--chip-bg)'
  return (
    <span style={{
      display: 'inline-block',
      background: bg, color: color,
      borderRadius: 20, fontSize: 9.5, fontWeight: 800,
      padding: '2px 7px', letterSpacing: 0.3,
      marginLeft: 6, flexShrink: 0
    }}>{label}</span>
  )
}

function TrialBanner({ trialUntil }) {
  const secsLeft  = trialUntil - Math.floor(Date.now() / 1000)
  const hoursLeft = Math.max(0, Math.floor(secsLeft / 3600))
  const label     = hoursLeft > 48 ? `${Math.floor(hoursLeft / 24)} días` : hoursLeft > 1 ? `${hoursLeft}h` : 'menos de 1h'
  const expired   = secsLeft <= 0
  return (
    <div style={{
      background: expired ? 'linear-gradient(90deg,#6b6b6b,#9b9b9b)' : 'linear-gradient(90deg,#059669,#0891b2)',
      color: '#fff', borderRadius: 10, padding: '10px 16px', marginBottom: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 13,
    }}>
      {expired
        ? <span>Tu prueba gratuita venció. Para seguir usá el plan Pro.</span>
        : <span><strong>Prueba gratuita</strong> — acceso completo por {label} más</span>
      }
      <a href="/register" style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
        Activar plan →
      </a>
    </div>
  )
}

// ── Profile tab — body metrics for training zone personalisation ──────────────
const API_PROFILE = apiBase

const SPORT_OPTIONS = [
  { value: '',         label: 'Sin especificar' },
  { value: 'running',  label: 'Running' },
  { value: 'ciclismo', label: 'Ciclismo' },
  { value: 'natacion', label: 'Natación' },
  { value: 'fuerza',   label: 'Fuerza / Musculación' },
  { value: 'crossfit', label: 'CrossFit / HIIT' },
  { value: 'futbol',   label: 'Fútbol' },
  { value: 'tennis',   label: 'Tenis / Pádel' },
  { value: 'hiking',   label: 'Senderismo / Montañismo' },
  { value: 'yoga',     label: 'Yoga / Pilates' },
  { value: 'otro',     label: 'Otro' },
]

function StreamKeyCard({ token, user }) {
  const [key, setKey] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copiedOverlay, setCopiedOverlay] = useState(false)
  const [planRequired, setPlanRequired] = useState(false)

  const loadKey = () => {
    setLoading(true)
    fetch('/api/user/stream-key', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.planRequired) {
          setPlanRequired(true)
        } else if (d.streamKey) {
          setKey(d.streamKey)
          setPlanRequired(false)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (token) loadKey()
  }, [token])

  const rollKey = () => {
    if (!window.confirm('¿Regenerar clave? La anterior dejará de funcionar en OBS.')) return
    fetch('/api/user/stream-key/roll', { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.streamKey) setKey(d.streamKey) })
      .catch(() => {})
  }

  const overlayUrl = key ? `${window.location.origin}/overlay/${key}` : ''

  const copy = (text, setFn) => {
    navigator.clipboard?.writeText(text)
    setFn(true)
    setTimeout(() => setFn(false), 2000)
  }

  const cardS = {
    background: 'var(--card-bg)', border: '1px solid var(--border)',
    borderRadius: 16, padding: '20px 22px', marginBottom: 14,
  }
  const labelS = {
    fontSize: 12, fontWeight: 700, color: 'var(--text-faint)',
    textTransform: 'uppercase', letterSpacing: 0.8, margin: 0,
  }

  if (loading) return null

  return (
    <div style={cardS}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={labelS}>API de Streaming & OBS (Twitch / YouTube)</p>
        <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 6, background: planRequired ? 'rgba(142,142,147,0.15)' : 'rgba(255,59,48,0.15)', color: planRequired ? 'var(--text-faint)' : '#ff3b30' }}>
          {planRequired ? 'REQUIERE PLAN PRO' : 'PREMIUM'}
        </span>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
        Transmití tu pulso y estrés en vivo a tu audiencia en OBS Studio, Streamlabs o plataformas externas.
      </p>

      {planRequired ? (
        <div style={{ background: 'var(--field-bg)', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--border-soft)' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>Función exclusiva para creadores y atletas</p>
          <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 12px' }}>
            Mejorá tu plan para obtener tu clave secreta de transmisión y widget transparente para OBS.
          </p>
          <a href="/register" style={{ display: 'inline-block', background: '#ff3b30', color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, textDecoration: 'none' }}>
            Ver planes disponibles →
          </a>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint)', marginBottom: 5 }}>Widget para OBS (Navegador / Browser Source):</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={overlayUrl}
                style={{ flex: 1, background: 'var(--field-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--text)', fontFamily: 'monospace', outline: 'none' }}
              />
              <button
                onClick={() => copy(overlayUrl, setCopiedOverlay)}
                style={{ background: copiedOverlay ? '#34c759' : 'var(--chip-bg)', color: copiedOverlay ? '#fff' : 'var(--text)', border: 'none', borderRadius: 8, padding: '0 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {copiedOverlay ? '✓ Copiado' : 'Copiar'}
              </button>
            </div>
            <p style={{ fontSize: 10.5, color: 'var(--text-faint)', marginTop: 4 }}>
              Agregá esta URL como fuente de navegador en OBS Studio. Fondo transparente incluido.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-soft)', paddingTop: 10 }}>
            <div>
              <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>Stream Key: </span>
              <code style={{ fontSize: 11, color: 'var(--text)', background: 'var(--field-bg)', padding: '2px 6px', borderRadius: 4 }}>{key ? key.slice(0, 8) + '••••••••' : ''}</code>
            </div>
            <button
              onClick={rollKey}
              style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
            >
              ↻ Regenerar clave
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ProfileTab({ token, user, onAuthUpdate, openExport, theme, onToggleTheme }) {
  const [form, setForm]     = useState({ weight_kg: '', height_cm: '', age: '', sport: '' })
  const [saved, setSaved]   = useState(false)
  const [loading, setLoading] = useState(true)

  const [userAvatar, setUserAvatar] = useState(user?.avatar || null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [showAvatarModal, setShowAvatarModal] = useState(false)
  const avatarInputRef = useRef(null)

  const handleAvatarFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxDim = 256
        let w = img.width, h = img.height
        if (w > h) {
          if (w > maxDim) { h = Math.round(h * (maxDim / w)); w = maxDim }
        } else {
          if (h > maxDim) { w = Math.round(w * (maxDim / h)); h = maxDim }
        }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, w, h)
        const base64 = canvas.toDataURL('image/jpeg', 0.85)
        uploadAvatar(base64)
      }
      img.src = event.target.result
    }
    reader.readAsDataURL(file)
  }

  const uploadAvatar = async (base64) => {
    setUploadingAvatar(true)
    try {
      const res = await fetch(`${API_PROFILE}/user/avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avatar: base64 })
      })
      const d = await res.json()
      if (d.ok) {
        setUserAvatar(base64)
        if (user) user.avatar = base64
        try { window.dispatchEvent(new CustomEvent('vc-avatar-updated', { detail: { avatar: base64 } })) } catch (_) {}
      }
    } catch (_) {}
    setUploadingAvatar(false)
  }

  const deleteAvatar = async () => {
    setUploadingAvatar(true)
    try {
      const res = await fetch(`${API_PROFILE}/user/avatar`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        setUserAvatar(null)
        if (user) user.avatar = null
        try { window.dispatchEvent(new CustomEvent('vc-avatar-updated', { detail: { avatar: null } })) } catch (_) {}
      }
    } catch (_) {}
    setUploadingAvatar(false)
  }

  // Exportar vive acá adentro como subvista (llega por la fila "Exportar datos"
  // o directo por la ruta /exportar). El globito avisa si hay una descarga en curso.
  const [showExport, setShowExport] = useState(!!openExport)
  useEffect(() => { setShowExport(!!openExport) }, [openExport])
  const [dataJob, setDataJob] = useState(() => exportJobMem.job?.status === 'busy' || burnMem.exporting)
  useEffect(() => {
    const refresh = () => setDataJob(exportJobMem.job?.status === 'busy' || burnMem.exporting)
    window.addEventListener('vc-export-job', refresh)
    const unsub = burnMem.subscribe(refresh)
    return () => { window.removeEventListener('vc-export-job', refresh); unsub() }
  }, [])

  // Change email state
  const [emailSection, setEmailSection] = useState(false)
  const [emailForm, setEmailForm]       = useState({ newEmail: '', password: '' })
  const [emailMsg, setEmailMsg]         = useState(null)  // { ok, text }
  const [emailBusy, setEmailBusy]       = useState(false)

  // Change password state
  const [passSection, setPassSection]   = useState(false)
  const [passForm, setPassForm]         = useState({ currentPassword: '', newPassword: '', confirm: '' })
  const [passMsg, setPassMsg]           = useState(null)
  const [passBusy, setPassBusy]         = useState(false)

  // (Exportar se movió a su propia pestaña — ver ExportTab)

  const API = apiBase

  useEffect(() => {
    fetch(`${API_PROFILE}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setForm({ weight_kg: d.weight_kg ?? '', height_cm: d.height_cm ?? '', age: d.age ?? '', sport: d.sport ?? '' })
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const save = async () => {
    await fetch(`${API_PROFILE}/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        age:       form.age       ? Number(form.age)       : null,
        sport:     form.sport     || null,
      }),
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const changeEmail = async () => {
    if (!emailForm.newEmail || !emailForm.password) return
    setEmailBusy(true); setEmailMsg(null)
    try {
      const res = await fetch(`${API}/auth/email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(emailForm),
      })
      const data = await res.json()
      if (res.ok) {
        setEmailMsg({ ok: true, text: 'Email actualizado' })
        setEmailForm({ newEmail: '', password: '' })
        if (onAuthUpdate && data.token) onAuthUpdate(data.token)
      } else {
        setEmailMsg({ ok: false, text: data.error || 'Error al actualizar' })
      }
    } catch { setEmailMsg({ ok: false, text: 'Error de red' }) }
    setEmailBusy(false)
  }

  const changePassword = async () => {
    if (!passForm.currentPassword || !passForm.newPassword) return
    if (passForm.newPassword !== passForm.confirm) { setPassMsg({ ok: false, text: 'Las contraseñas no coinciden' }); return }
    if (passForm.newPassword.length < 8) { setPassMsg({ ok: false, text: 'Mínimo 8 caracteres' }); return }
    setPassBusy(true); setPassMsg(null)
    try {
      const res = await fetch(`${API}/auth/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword }),
      })
      const data = await res.json()
      if (res.ok) {
        setPassMsg({ ok: true, text: 'Contraseña actualizada' })
        setPassForm({ currentPassword: '', newPassword: '', confirm: '' })
        // Backend issues a fresh token after password change — update it in auth state
        if (data.token && onAuthUpdate) onAuthUpdate(data.token)
      } else {
        setPassMsg({ ok: false, text: data.error || 'Error al actualizar' })
      }
    } catch { setPassMsg({ ok: false, text: 'Error de red' }) }
    setPassBusy(false)
  }

  const bmi = Number(form.weight_kg) > 0 && Number(form.height_cm) > 0
    ? (Number(form.weight_kg) / ((Number(form.height_cm) / 100) ** 2)).toFixed(1)
    : null

  const cardS  = { background: 'var(--card-bg)', borderRadius: 18, border: '1px solid var(--border)', padding: '20px' }
  const labelS = { fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }
  const inputS = {
    width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)',
    borderRadius: 10, padding: '11px 14px', fontSize: 15, fontFamily: 'inherit',
    outline: 'none', color: 'var(--text)', boxSizing: 'border-box',
  }
  const selectS = { ...inputS, appearance: 'none', cursor: 'pointer' }
  const rowBtnS = (active) => ({
    width: '100%', background: 'none', border: 'none', padding: '13px 0', fontSize: 14,
    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  })

  if (showExport) return (
    <div>
      <button onClick={() => setShowExport(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', padding: '4px 0', marginBottom: 4, fontFamily: 'inherit' }}>← Volver al perfil</button>
      <ExportTab token={token} />
    </div>
  )

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 14 }}>Cargando…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        .vc-profile-row {
          width: 100%; background: none; border: none; padding: 8px 12px; margin-left: -12px;
          width: calc(100% + 24px); border-radius: 10px; font-size: 13.5px; font-weight: 600;
          cursor: pointer; font-family: inherit; color: var(--text); display: flex;
          justify-content: space-between; align-items: center; transition: all 0.15s ease;
        }
        .vc-profile-row:hover {
          background: var(--inset-bg); padding-left: 18px;
        }
        .vc-profile-save-btn {
          background: var(--track); color: var(--text); border: 1px solid var(--border);
          border-radius: 10px; padding: 11px 24px; font-size: 13.5px; font-weight: 700;
          cursor: pointer; font-family: inherit; transition: all 0.15s ease;
        }
        .vc-profile-save-btn:hover {
          background: var(--chip-bg); border-color: var(--text-faint); transform: scale(1.02);
        }
        .vc-profile-save-btn-saved {
          background: #1a3d27; color: #30d158; border: 1px solid #30d158;
          border-radius: 10px; padding: 11px 24px; font-size: 13.5px; font-weight: 700;
          cursor: pointer; font-family: inherit; transition: all 0.15s ease;
        }
      `}</style>

      {/* Account info + Avatar */}
      <div style={cardS}>
        <p style={{ ...labelS, marginBottom: 14 }}>Cuenta y foto de perfil</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Avatar circular con botón de cambio */}
          <div
            onClick={() => { if (userAvatar) setShowAvatarModal(true) }}
            style={{ position: 'relative', width: 62, height: 62, flexShrink: 0, cursor: userAvatar ? 'pointer' : 'default' }}
            title={userAvatar ? 'Ver foto completa' : ''}
          >
            {userAvatar ? (
              <img
                src={userAvatar}
                alt="Foto de perfil"
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-soft)' }}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'linear-gradient(135deg, #ff3b30, #ff6b6b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{(user?.name || user?.email || '?')[0].toUpperCase()}</span>
              </div>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); avatarInputRef.current?.click() }}
              disabled={uploadingAvatar}
              title="Cambiar foto de perfil"
              style={{
                position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderRadius: '50%',
                background: 'var(--btn-strong)', color: '#fff', border: '2px solid var(--card-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
            <input
              type="file"
              ref={avatarInputRef}
              accept="image/*"
              onChange={handleAvatarFile}
              style={{ display: 'none' }}
            />
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'Sin nombre'}</p>
            <div style={{ marginTop: 4, marginBottom: 3 }}>
              <PlanBadge plan={user?.plan} style={{ marginLeft: 0 }} />
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {userAvatar && (
                <button
                  type="button"
                  onClick={() => setShowAvatarModal(true)}
                  style={{ background: 'none', border: 'none', color: '#007aff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >
                  Ver foto
                </button>
              )}
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
              >
                {uploadingAvatar ? 'Cargando…' : (userAvatar ? 'Cambiar foto' : 'Subir foto')}
              </button>
              {userAvatar && (
                <button
                  type="button"
                  onClick={deleteAvatar}
                  disabled={uploadingAvatar}
                  style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
        <UpgradeButton user={user} token={token} style={{ marginTop: 14 }} />

        {/* Modal de visualización de foto completa */}
        {showAvatarModal && userAvatar && (
          <div
            onClick={() => setShowAvatarModal(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 99999,
              background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--card-bg)', border: '1px solid var(--border-soft)',
                borderRadius: 24, padding: 24, maxWidth: 360, width: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                boxShadow: '0 16px 48px rgba(0,0,0,0.4)', animation: 'fadeIn 0.2s ease'
              }}
            >
              <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>Foto de perfil</span>
                <button
                  onClick={() => setShowAvatarModal(false)}
                  style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
              <img
                src={userAvatar}
                alt="Foto de perfil en grande"
                style={{
                  width: 240, height: 240, borderRadius: '50%', objectFit: 'cover',
                  border: '4px solid var(--border-soft)', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                  marginBottom: 16
                }}
              />
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', marginBottom: 2 }}>
                {user?.name || 'Matias Cagliero'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
                {user?.email}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Personal data ultra compacto */}
      <div style={{ ...cardS, padding: '14px 16px', border: '1px solid var(--border-soft)', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <p style={{ ...labelS, margin: 0 }}>Datos personales</p>
          {bmi && (
            <span style={{
              fontSize: 11, fontWeight: 800, color: 'var(--text)',
              background: 'var(--chip-bg)', padding: '2px 8px', borderRadius: 8, fontVariantNumeric: 'tabular-nums'
            }}>
              IMC: <strong style={{ color: '#007aff' }}>{bmi}</strong>
            </span>
          )}
        </div>

        {/* Fila 1: Deporte principal compacto */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)' }}>Deporte principal</span>
          </div>
          <div style={{ position: 'relative' }}>
            <select
              value={form.sport}
              onChange={e => setForm(f => ({ ...f, sport: e.target.value }))}
              style={{ ...selectS, padding: '7px 28px 7px 10px', fontSize: 12.5 }}
            >
              {SPORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none', fontSize: 11 }}>▾</span>
          </div>
        </div>

        {/* Fila 2: Cuadrícula compacta de Peso, Altura y Edad en una sola línea */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {[['Peso (kg)', 'weight_kg'], ['Altura (cm)', 'height_cm'], ['Edad', 'age']].map(([lbl, key]) => (
            <div key={key}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)', display: 'block', marginBottom: 3 }}>{lbl}</span>
              <input
                type="number" min="0" value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                style={{ ...inputS, padding: '6px 8px', fontSize: 13, height: 'auto' }}
              />
            </div>
          ))}
        </div>

        {/* Botón Guardar cambios compacto */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={save}
            className={saved ? 'vc-profile-save-btn-saved' : 'vc-profile-save-btn'}
            style={{ padding: '8px 18px', fontSize: 12.5, borderRadius: 8, width: 'auto' }}
          >
            {saved ? '✓ Guardado' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {/* Mis datos: exportación (antes era una pestaña propia) */}
      <div style={cardS}>
        <p style={{ ...labelS, marginBottom: 4 }}>Mis datos</p>
        <button className="vc-profile-row" onClick={() => setShowExport(true)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Exportar datos
            {dataJob && <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '1px 7px', background: '#007aff', color: '#fff' }}>↓ en curso</span>}
          </span>
          <span style={{ fontSize: 14, color: 'var(--text-fainter)' }}>›</span>
        </button>
        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: 0 }}>Subtítulos y video para creadores, o tus sesiones en un archivo JSON.</p>
      </div>

      {/* Stream API & OBS Widget for Creators / Athletes */}
      <StreamKeyCard token={token} user={user} />

      {/* Apariencia: en el celular no hay sidebar, así que el modo noche vive acá */}
      {onToggleTheme && (
        <div style={cardS}>
          <p style={{ ...labelS, marginBottom: 4 }}>Apariencia</p>
          <button className="vc-profile-row" onClick={onToggleTheme}>
            <span>Modo noche</span>
            <span style={{ width: 42, height: 25, borderRadius: 999, background: theme === 'dark' ? '#30d158' : 'var(--track)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2.5, left: theme === 'dark' ? 19.5 : 2.5, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s' }} />
            </span>
          </button>
        </div>
      )}

      {/* Access settings (email + password) */}
      <div style={cardS}>
        <p style={{ ...labelS, marginBottom: 4 }}>Acceso</p>

        {/* Change email */}
        <div style={{ borderBottom: '1px solid var(--border-soft)' }}>
          <button className="vc-profile-row" onClick={() => { setEmailSection(s => !s); setEmailMsg(null) }}>
            <span>Cambiar email</span>
            <span style={{ fontSize: 12, color: 'var(--text-fainter)' }}>{emailSection ? '▲' : '▼'}</span>
          </button>
          {emailSection && (
            <div style={{ paddingBottom: 16 }}>
              {[['Nuevo email', 'newEmail', 'email'], ['Contraseña actual', 'password', 'password']].map(([lbl, key, type]) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 5 }}>{lbl}</p>
                  <input
                    type={type} value={emailForm[key]}
                    onChange={e => setEmailForm(f => ({ ...f, [key]: e.target.value }))}
                    style={inputS}
                  />
                </div>
              ))}
              {emailMsg && <p style={{ fontSize: 12, color: emailMsg.ok ? '#30d158' : '#ff3b30', marginBottom: 10 }}>{emailMsg.text}</p>}
              <button onClick={changeEmail} disabled={emailBusy || !emailForm.newEmail || !emailForm.password} style={{
                background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10,
                padding: '11px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                opacity: emailBusy || !emailForm.newEmail || !emailForm.password ? 0.5 : 1,
              }}>
                {emailBusy ? 'Guardando…' : 'Actualizar email'}
              </button>
            </div>
          )}
        </div>

        {/* Change password */}
        <div>
          <button className="vc-profile-row" onClick={() => { setPassSection(s => !s); setPassMsg(null) }}>
            <span>Cambiar contraseña</span>
            <span style={{ fontSize: 12, color: 'var(--text-fainter)' }}>{passSection ? '▲' : '▼'}</span>
          </button>
          {passSection && (
            <div style={{ paddingBottom: 16 }}>
              {[['Contraseña actual', 'currentPassword'], ['Nueva contraseña', 'newPassword'], ['Confirmar nueva contraseña', 'confirm']].map(([lbl, key]) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 5 }}>{lbl}</p>
                  <input
                    type="password" value={passForm[key]}
                    onChange={e => setPassForm(f => ({ ...f, [key]: e.target.value }))}
                    style={inputS}
                  />
                </div>
              ))}
              {passMsg && <p style={{ fontSize: 12, color: passMsg.ok ? '#30d158' : '#ff3b30', marginBottom: 10 }}>{passMsg.text}</p>}
              <button onClick={changePassword} disabled={passBusy || !passForm.currentPassword || !passForm.newPassword} style={{
                background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10,
                padding: '11px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                opacity: passBusy || !passForm.currentPassword || !passForm.newPassword ? 0.5 : 1,
              }}>
                {passBusy ? 'Guardando…' : 'Actualizar contraseña'}
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}

// AdminPanel has been extracted and upgraded in src/features/admin/AdminTab.jsx

// ── Login ─────────────────────────────────────────────────────────────────────

function LoginScreen({ auth }) {
  const [pwd, setPwd] = useState('')
  return (
    <div style={{ minHeight: '100vh', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 20, padding: 36, width: '100%', maxWidth: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <IconHeart size={36} color="#ef4444" fill="#ef4444" />
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 6, color: 'var(--text)' }}>Monitor H10</h1>
        <p style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'center', marginBottom: 28 }}>Ingresá la contraseña para continuar</p>
        <form onSubmit={e => { e.preventDefault(); auth.login(pwd) }}>
          <input
            type="password"
            placeholder="Contraseña"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '13px 16px', borderRadius: 12, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 12, outline: 'none', fontFamily: 'inherit' }}
          />
          {auth.error && <p style={{ fontSize: 12, color: '#ff3b30', marginBottom: 10, textAlign: 'center' }}>{auth.error}</p>}
          <button
            type="submit"
            disabled={auth.loading || !pwd}
            style={{ width: '100%', background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: auth.loading || !pwd ? 0.6 : 1 }}
          >
            {auth.loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ── Sidebar icons ─────────────────────────────────────────────────────────────

function SidebarIconExport({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 2v7M5.5 6.5L8 9l2.5-2.5" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 10.5v2A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5v-2" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

// ── Video / SRT export (client-side) ──────────────────────────────────────────
// Reads the iPhone video's own metadata (QuickTime creation date, with timezone)
// straight from the file in the browser, matches it to the HR session by absolute
// time, and emits an .srt subtitle the user styles in Premiere.

async function readVideoMeta(file) {
  // Walk top-level MP4/MOV boxes via File.slice (don't load the huge mdat).
  const slice = async (start, len) => new Uint8Array(await file.slice(start, start + len).arrayBuffer())
  const rd32  = (b, o) => (b[o] * 0x1000000) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]
  const total = file.size
  let pos = 0, moov = null
  while (pos + 8 <= total) {
    const h = await slice(pos, 16)
    let boxSize = rd32(h, 0)
    const type  = String.fromCharCode(h[4], h[5], h[6], h[7])
    if (boxSize === 1) boxSize = rd32(h, 8) * 4294967296 + rd32(h, 12)
    else if (boxSize === 0) boxSize = total - pos
    if (type === 'moov') { moov = await slice(pos, Math.min(boxSize, 16 * 1024 * 1024)); break }
    if (boxSize < 8) break
    pos += boxSize
  }
  if (!moov) return { startMs: null, durationSec: null, hasDate: false }

  const str = new TextDecoder('iso-8859-1').decode(moov)
  let durationSec = null, mvhdMs = null
  const mi = str.indexOf('mvhd')
  if (mi >= 0) {
    const dv = new DataView(moov.buffer, moov.byteOffset + mi)
    const version = moov[mi + 4]
    try {
      if (version === 1) {
        const creation = Number(dv.getBigUint64(8)), timescale = dv.getUint32(24), duration = Number(dv.getBigUint64(28))
        durationSec = timescale ? duration / timescale : null; mvhdMs = (creation - 2082844800) * 1000
      } else {
        const creation = dv.getUint32(8), timescale = dv.getUint32(16), duration = dv.getUint32(20)
        durationSec = timescale ? duration / timescale : null; mvhdMs = (creation - 2082844800) * 1000
      }
    } catch (_) { /* ignore */ }
  }

  // Apple's local creation date with timezone (most reliable for sync).
  let startMs = null, hasDate = false
  const m = str.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})([+-]\d{2}:?\d{2}|Z)?/)
  if (m) {
    let tz = m[2] || ''
    if (/^[+-]\d{4}$/.test(tz)) tz = tz.slice(0, 3) + ':' + tz.slice(3)
    const t = Date.parse(m[1] + tz)
    if (!isNaN(t)) { startMs = t; hasDate = true }
  }
  if (startMs == null && mvhdMs && mvhdMs > 9.4e11) startMs = mvhdMs
  return { startMs, durationSec, hasDate }
}

function srtStamp(totalSec) {
  const p2 = n => String(n).padStart(2, '0')
  const h = Math.floor(totalSec / 3600), m = Math.floor((totalSec % 3600) / 60), s = Math.floor(totalSec % 60)
  return `${p2(h)}:${p2(m)}:${p2(s)},000`
}

function buildSrt(readings, { startMs, durationSec, offsetSec = 0, format = 'bpm' }) {
  if (!readings.length) return ''
  const base  = startMs ?? readings[0].tMs
  const total = Math.max(1, Math.floor(durationSec ?? (readings[readings.length - 1].tMs - readings[0].tMs) / 1000))
  const fmt   = hr => format === 'num' ? `${hr}` : `${hr} BPM`
  let ptr = 0, n = 0
  const cues = []
  for (let t = 0; t < total; t++) {
    const target = base + (t + offsetSec) * 1000
    while (ptr + 1 < readings.length && readings[ptr + 1].tMs <= target) ptr++
    let r = readings[ptr]
    if (ptr + 1 < readings.length && Math.abs(readings[ptr + 1].tMs - target) < Math.abs(r.tMs - target)) r = readings[ptr + 1]
    if (!r || Math.abs(r.tMs - target) > 5000) continue   // gap > 5 s → no cue
    n++
    cues.push(`${n}\n${srtStamp(t)} --> ${srtStamp(t + 1)}\n${fmt(r.hr)}\n`)
  }
  return cues.join('\n')
}

function VideoSrtTab({ token, embedded }) {
  const [sessions, setSessions]   = useState([])
  const [items, setItems]         = useState([])   // one entry per dropped video
  const [format, setFormat]       = useState('bpm')
  const [offsetSec, setOffsetSec] = useState(0)
  const cacheRef = useRef({})                       // sessionId -> readings[]

  useEffect(() => {
    const since = Date.now() - 365 * 24 * 3600 * 1000
    fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSessions((d.sessions || []).map(s => ({
        id: s.session_id, start: new Date(s.start_time).getTime(), end: new Date(s.end_time).getTime(),
        avg: s.avg_hr, count: s.reading_count,
      })).sort((a, b) => b.start - a.start)))
      .catch(() => {})
  }, [token, apiBase])

  const update = (id, patch) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it))

  const getReadings = async (sid) => {
    if (cacheRef.current[sid]) return cacheRef.current[sid]
    const d  = await fetch(`${apiBase}/sync?sessionId=${sid}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    const rd = (d.readings || []).map(x => ({ tMs: new Date(x.time).getTime(), hr: x.hr })).filter(x => x.hr > 0).sort((a, b) => a.tMs - b.tMs)
    cacheRef.current[sid] = rd
    return rd
  }

  // Pick the HR session that overlaps the video window the most — fully automatic.
  const matchSession = (startMs, durationSec) => {
    const winEnd = startMs + (durationSec || 0) * 1000
    let best = null, bestOv = 0
    for (const s of sessions) {
      const ov = Math.min(winEnd, s.end) - Math.max(startMs, s.start)
      if (ov > bestOv) { bestOv = ov; best = s }
    }
    return best
  }

  const processOne = async (file) => {
    const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`
    setItems(prev => [...prev, { id, name: file.name, status: 'reading' }])
    try {
      const m = await readVideoMeta(file)
      // Fallback: no embedded date → estimate from the file's modified time
      // (phones set it right after recording ends: start ≈ mtime − duration).
      let startMs = m.startMs, estimated = false
      if (!startMs && file.lastModified && m.durationSec) { startMs = file.lastModified - m.durationSec * 1000; estimated = true }
      if (!startMs)  { update(id, { status: 'nodate',  ...m }); return }
      const best = matchSession(startMs, m.durationSec)
      if (!best)       { update(id, { status: 'nomatch', ...m, startMs, estimated }); return }
      update(id, { status: 'loading', ...m, startMs, estimated, sessionId: best.id, sessionStart: best.start })
      const rd = await getReadings(best.id)
      update(id, { status: rd.length ? 'ok' : 'empty', readings: rd })
    } catch (e) { update(id, { status: 'error', error: e.message }) }
  }

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && /video|\.(mov|mp4|m4v)$/i.test((f.type || '') + ' ' + f.name))
    for (const f of files) await processOne(f)   // sequential → reuses the session cache
  }

  // Manual fallback only for videos whose date/session couldn't be detected.
  const assignSession = async (id, sid) => {
    if (!sid) return
    const s = sessions.find(x => x.id === sid)
    update(id, { status: 'loading', sessionId: sid, sessionStart: s?.start })
    const rd = await getReadings(sid)
    update(id, { status: rd.length ? 'ok' : 'empty', readings: rd })
  }

  const built = useMemo(() => items.map(it => {
    if (it.status !== 'ok' || !it.readings?.length) return { ...it, srt: '', cues: 0 }
    const startMs = it.startMs ?? it.sessionStart ?? it.readings[0].tMs
    const durationSec = it.durationSec ?? ((it.readings[it.readings.length - 1].tMs - it.readings[0].tMs) / 1000)
    const srt = buildSrt(it.readings, { startMs, durationSec, offsetSec, format })
    return { ...it, srt, cues: srt ? srt.trim().split(/\n\s*\n/).length : 0 }
  }), [items, offsetSec, format])

  const downloadOne = (it) => {
    if (!it.srt) return
    const url = URL.createObjectURL(new Blob([it.srt], { type: 'application/x-subrip' }))
    const a = document.createElement('a')
    a.href = url; a.download = `pulso_${it.name.replace(/\.[^.]+$/, '')}.srt`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }
  const downloadAll = () => built.filter(it => it.srt).forEach((it, i) => setTimeout(() => downloadOne(it), i * 250))

  const okCount = built.filter(it => it.srt).length
  const card    = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 14 }
  const fmtOpts = [{ k: 'num', l: '68' }, { k: 'bpm', l: '68 BPM' }]
  const dur     = s => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`

  const statusLine = (it) => {
    switch (it.status) {
      case 'reading': return <span style={{ color: 'var(--text-faint)' }}>Leyendo metadatos…</span>
      case 'loading': return <span style={{ color: 'var(--text-faint)' }}>Buscando tu pulso de ese momento…</span>
      case 'nodate':  return <span style={{ color: '#ff9f0a' }}>Sin fecha en los metadatos — elegí la sesión a mano.</span>
      case 'nomatch': return <span style={{ color: '#ff9f0a' }}>No hay pulso registrado para {fmtLocalTs(it.startMs)} — elegí a mano.</span>
      case 'empty':   return <span style={{ color: '#ff9f0a' }}>Esa sesión no tiene pulso válido.</span>
      case 'error':   return <span style={{ color: '#ff3b30' }}>Error: {it.error}</span>
      case 'ok':      return <span style={{ color: '#34c759' }}>✓ {it.startMs ? fmtLocalTs(it.startMs) + ' · ' : ''}{it.durationSec ? dur(it.durationSec) + ' · ' : ''}{it.cues} subtítulos</span>
      default: return null
    }
  }

  return (
    <div style={{ maxWidth: embedded ? '100%' : 640, margin: '0 auto' }}>
      {!embedded && <h2 style={{ fontSize: 20, fontWeight: 800, margin: '4px 0 4px' }}>Pulso para tus videos</h2>}
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
        <strong>Para creadores de contenido:</strong> soltá uno o varios videos. Detecta solo la hora de grabación
        de cada uno por sus metadatos, busca tu pulso de ese rango y arma el subtítulo <strong>.srt</strong> — sin
        que elijas nada. Lo importás y estilizás en Premiere. Todo en tu navegador; los videos no se suben.
      </p>

      {/* Drop / select (varios) */}
      <label style={{ display: 'block', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '22px', textAlign: 'center', cursor: 'pointer', background: 'var(--field-bg)', marginBottom: 14 }}
        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onFiles(e.dataTransfer.files) }}>
        <input type="file" accept="video/*,.mov,.mp4,.m4v" multiple style={{ display: 'none' }} onChange={e => onFiles(e.target.files)} />
        <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>Soltá o elegí tus videos</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>.mov / .mp4 · uno o varios a la vez</span>
      </label>

      {/* Formato + sincronía (global) */}
      {items.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {fmtOpts.map(o => (
              <button key={o.k} onClick={() => setFormat(o.k)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  background: format === o.k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: format === o.k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{o.l}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 86 }}>Desfase: {offsetSec > 0 ? '+' : ''}{offsetSec}s</span>
            <input type="range" min={-60} max={60} step={1} value={offsetSec} onChange={e => setOffsetSec(Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30', cursor: 'pointer' }} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '6px 0 0' }}>El formato y el desfase se aplican a todos los videos.</p>
        </div>
      )}

      {/* Resultado por video */}
      {built.map(it => (
        <div key={it.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</p>
              <p style={{ fontSize: 12, margin: '4px 0 0' }}>{statusLine(it)}</p>
            </div>
            {it.srt && (
              <button onClick={() => downloadOne(it)} style={{ flexShrink: 0, padding: '9px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: '#ff3b30', color: '#fff' }}>Descargar .srt</button>
            )}
          </div>
          {(it.status === 'nodate' || it.status === 'nomatch' || it.status === 'empty') && (
            <select defaultValue="" onChange={e => assignSession(it.id, e.target.value)}
              style={{ marginTop: 10, width: '100%', background: 'var(--chip-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
              <option value="">— Elegir sesión a mano (respaldo) —</option>
              {sessions.map(s => <option key={s.id} value={s.id}>{fmtLocalTs(s.start)} · {s.count} lecturas</option>)}
            </select>
          )}
        </div>
      ))}

      {okCount > 1 && (
        <button onClick={downloadAll} style={{ width: '100%', padding: '12px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--btn-strong)', color: '#fff', marginBottom: 14 }}>
          Descargar todos ({okCount})
        </button>
      )}

      {items.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: 0, lineHeight: 1.5 }}>
          En Premiere: Archivo → Importar → el .srt → arrastralo al timeline. Estilo en el panel de Subtítulos; para control total, clic derecho → “Convertir subtítulos en gráfico”.
        </p>
      )}
    </div>
  )
}

// Shared overlay painter — used by both the live preview and the export, so they
// look identical. Sizes are fractions of the frame height → resolution-independent.
const OVERLAY_FONTS = {
  system:    { label: 'Sans',      css: '-apple-system, system-ui, sans-serif' },
  rounded:   { label: 'Redonda',   css: '"SF Pro Rounded", "Nunito", system-ui, sans-serif' },
  mono:      { label: 'Mono',      css: '"SF Mono", "Roboto Mono", ui-monospace, monospace' },
  condensed: { label: 'Condensada',css: '"Roboto Condensed", "Arial Narrow", system-ui, sans-serif' },
}
function hexLum(hex) {
  const h = (hex || '#fff').replace('#', '')
  const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

function drawHrOverlay(ctx, W, H, hr, o) {
  if (hr == null) return
  const fontPx = Math.max(10, Math.round(H * o.size))
  const text = `${hr}${o.label ? ' BPM' : ''}`
  ctx.font = `${o.weight || 800} ${fontPx}px ${(OVERLAY_FONTS[o.font] || OVERLAY_FONTS.system).css}`
  ctx.textBaseline = 'middle'
  const tw = ctx.measureText(text).width
  const padX = fontPx * 0.5, padY = fontPx * 0.32
  const boxW = tw + padX * 2, boxH = fontPx + padY * 2
  const m = Math.round(H * 0.045)
  const right = W - boxW - m, bottom = H - boxH - m, cx = (W - boxW) / 2
  let x, y
  switch (o.pos) {
    case 'tl': x = m;     y = m; break
    case 'tr': x = right; y = m; break
    case 'tc': x = cx;    y = m; break
    case 'bl': x = m;     y = bottom; break
    case 'bc': x = cx;    y = bottom; break
    default:   x = right; y = bottom            // 'br'
  }
  // Fine X/Y nudge from the chosen anchor (fraction of W/H), clamped on-screen
  x += (o.dx || 0) * W
  y += (o.dy || 0) * H
  x = Math.max(0, Math.min(W - boxW, x))
  y = Math.max(0, Math.min(H - boxH, y))
  if (o.pill) {
    ctx.save()
    ctx.globalAlpha = o.pillOpacity
    ctx.fillStyle = o.pillColor || '#000000'
    const r = boxH * 0.3
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, boxW, boxH, r); ctx.fill() }
    else ctx.fillRect(x, y, boxW, boxH)
    ctx.restore()
  }
  const fill = o.zoneColor ? hrZoneColor(hr) : o.color
  const tx = x + padX, ty = y + boxH / 2
  ctx.textAlign = 'left'
  if (o.outline) {
    ctx.lineWidth = Math.max(2, fontPx * 0.11)
    ctx.lineJoin = 'round'
    ctx.strokeStyle = hexLum(fill) > 0.5 ? 'rgba(0,0,0,0.92)' : 'rgba(255,255,255,0.92)'
    ctx.strokeText(text, tx, ty)
  } else if (!o.pill) {
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = fontPx * 0.2; ctx.shadowOffsetY = fontPx * 0.04
  }
  ctx.fillStyle = fill
  ctx.fillText(text, tx, ty)
  ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
}

const BURN_PRESETS = [
  { name: 'Minimal',  opt: { pill: false, outline: true,  heart: true,  label: false, weight: 700, size: 0.075, color: '#ffffff', zoneColor: false } },
  { name: 'Cápsula',  opt: { pill: true,  outline: false, heart: true,  label: true,  weight: 800, size: 0.085, color: '#ffffff', zoneColor: false, pillColor: '#000000', pillOpacity: 0.45 } },
  { name: 'Neón',     opt: { pill: false, outline: true,  heart: true,  label: true,  weight: 900, size: 0.09,  color: '#39ff14', zoneColor: false } },
  { name: 'Por zona', opt: { pill: true,  outline: false, heart: true,  label: true,  weight: 800, size: 0.085, zoneColor: true,  pillColor: '#000000', pillOpacity: 0.4 } },
  { name: 'Grande',   opt: { pill: false, outline: true,  heart: true,  label: true,  weight: 900, size: 0.13,  color: '#ffffff', zoneColor: false } },
]

// ── Burn/overlay persistent state (module scope) ────────────────────────────
// The tab unmounts when you navigate away; everything lives here so the queue,
// matches and config survive — and the export worker keeps running because it
// uses its own detached <video> element, not the preview player.
const DEFAULT_BURN_OPT = { pos: 'br', dx: 0, dy: 0, size: 0.085, color: '#ffffff', heart: true, label: true, pill: true, pillOpacity: 0.45, font: 'system', weight: 800, outline: false, zoneColor: false, pillColor: '#000000' }
const burnMem = {
  videos: [], activeId: null, opt: { ...DEFAULT_BURN_OPT }, bgMode: 'transparent',
  exporting: false, doneCount: 0, progress: 0, queueRunning: false,
  subs: new Set(),
  emit() { this.subs.forEach(fn => { try { fn() } catch {} }) },
  subscribe(fn) { this.subs.add(fn); return () => this.subs.delete(fn) },
}
const burnPatch = (id, patch) => { burnMem.videos = burnMem.videos.map(v => v.id === id ? { ...v, ...patch } : v); burnMem.emit() }
const burnReadingsCache = new Map()   // sessionId -> readings[] (survives remounts)

let burnExportEl = null               // detached player used ONLY for exporting
function getBurnExportEl() {
  if (!burnExportEl) { burnExportEl = document.createElement('video'); burnExportEl.playsInline = true; burnExportEl.preload = 'auto'; burnExportEl.muted = true }
  return burnExportEl
}

const burnEnsureLoaded = (v) => new Promise((res, rej) => {
  const el = getBurnExportEl()
  if (el.src !== v.url) { el.src = v.url; el.load() }
  let tries = 0
  const t = setInterval(() => {
    if (el.readyState >= 2 && el.videoWidth > 0) { clearInterval(t); res(el) }
    else if (++tries > 300) { clearInterval(t); rej(new Error('el video no cargó')) }
  }, 50)
})

const burnHrAt = (v, tSec) => {
  if (!v || !v.readings?.length || !v.meta?.startMs) return null
  const target = v.meta.startMs + (tSec + (v.offsetSec || 0)) * 1000
  const rds = v.readings
  let lo = 0, hi = rds.length - 1
  while (lo < hi) { const mid = (lo + hi) >> 1; if (rds[mid].tMs < target) lo = mid + 1; else hi = mid }
  let r = rds[lo]
  if (lo > 0 && Math.abs(rds[lo - 1].tMs - target) < Math.abs(r.tMs - target)) r = rds[lo - 1]
  return r && Math.abs(r.tMs - target) < 5000 ? r.hr : null
}

async function burnRunExport(v, mode) {     // 'burn' | 'overlay' — uses the detached element
  const el = getBurnExportEl()
  if (!v.readings?.length) throw new Error('sin pulso sincronizado')
  if (typeof MediaRecorder === 'undefined') throw new Error('Tu navegador no soporta exportar video. Usá Chrome.')
  const W = el.videoWidth, H = el.videoHeight
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')
  const cstream = canvas.captureStream(30)
  const tracks = [...cstream.getVideoTracks()]
  if (mode === 'burn') {
    try { const vs = el.captureStream ? el.captureStream() : el.mozCaptureStream(); const at = vs.getAudioTracks()[0]; if (at) tracks.push(at) } catch (_) { /* no audio */ }
  }
  const stream = new MediaStream(tracks)
  const mime = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm'
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 12_000_000 })
  const chunks = []; rec.ondataavailable = e => { if (e.data.size) chunks.push(e.data) }
  const stopped = new Promise(res => { rec.onstop = res })

  el.pause(); el.currentTime = 0
  await new Promise(r => { el.onseeked = r })

  const pump = () => {
    const bgMode = burnMem.bgMode
    if (mode === 'overlay') {
      ctx.clearRect(0, 0, W, H)
      if (bgMode !== 'transparent') { ctx.fillStyle = bgMode === 'green' ? '#00ff00' : '#000000'; ctx.fillRect(0, 0, W, H) }
    } else ctx.drawImage(el, 0, 0, W, H)
    drawHrOverlay(ctx, W, H, burnHrAt(v, el.currentTime), burnMem.opt)
    const p = Math.min(1, el.currentTime / (el.duration || 1))
    if (Math.abs(p - burnMem.progress) > 0.01) { burnMem.progress = p; burnMem.emit() }
  }
  const useRVFC = 'requestVideoFrameCallback' in HTMLVideoElement.prototype
  let stopLoop = false
  const loop = () => { if (stopLoop) return; pump(); useRVFC ? el.requestVideoFrameCallback(loop) : requestAnimationFrame(loop) }

  pump()                     // prime first frame
  rec.start()
  await el.play()
  loop()
  await new Promise(res => { el.onended = () => { stopLoop = true; res() } })
  pump()                     // final frame
  rec.stop()
  await stopped
  el.pause()

  const blob = new Blob(chunks, { type: mime })
  const url = URL.createObjectURL(blob)
  const name = `${mode === 'burn' ? 'pulso_quemado' : 'pulso_overlay'}_${(v.name || 'video').replace(/\.[^.]+$/, '')}.webm`
  // Intenta la descarga automática, pero NO destruye el resultado: queda
  // guardado en la cola (botón Descargar) por si estabas en otra pestaña
  // cuando terminó — no se pierde nada al navegar.
  try { const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove() } catch { /* la cola conserva el archivo */ }
  return { url, name, size: blob.size }
}

// Queue worker: processes 'pendiente' videos one after another. Runs at module
// scope — navigating to another tab does NOT stop it.
async function burnStartWorker(mode) {
  if (burnMem.queueRunning) return
  burnMem.queueRunning = true; burnMem.exporting = true; burnMem.doneCount = 0; burnMem.emit()
  while (true) {
    await new Promise(r => setTimeout(r, 80))   // let state settle between items
    const next = burnMem.videos.find(v => v.exportState === 'pendiente')
    if (!next) break
    burnMem.activeId = next.id
    burnPatch(next.id, { exportState: 'procesando' })
    try {
      await burnEnsureLoaded(next)
      const fresh = burnMem.videos.find(x => x.id === next.id) ?? next   // pick up offset edits
      const out = await burnRunExport(fresh, mode)
      burnPatch(next.id, { exportState: 'listo', resultUrl: out.url, resultName: out.name, resultSize: out.size })
    } catch (e) {
      burnPatch(next.id, { exportState: 'error', note: 'Error al exportar: ' + e.message })
    }
    burnMem.doneCount++; burnMem.emit()
  }
  burnMem.queueRunning = false; burnMem.exporting = false; burnMem.progress = 0; burnMem.emit()
}

function VideoBurnTab({ token }) {
  const [sessions, setSessions] = useState([])
  // Re-render whenever the module-scope state changes (queue progress, patches…)
  const [, force] = useState(0)
  useEffect(() => burnMem.subscribe(() => force(x => x + 1)), [])

  const videos    = burnMem.videos
  const opt       = burnMem.opt
  const bgMode    = burnMem.bgMode
  const exporting = burnMem.exporting
  const progress  = burnMem.progress
  const doneCount = burnMem.doneCount
  const setOpt      = (fnOrVal) => { burnMem.opt = typeof fnOrVal === 'function' ? fnOrVal(burnMem.opt) : fnOrVal; burnMem.emit() }
  const setO        = (k, val) => setOpt(o => ({ ...o, [k]: val }))
  const setBgMode   = (v) => { burnMem.bgMode = v; burnMem.emit() }
  const setActiveId = (id) => { burnMem.activeId = id; burnMem.emit() }

  const videoRef  = useRef(null)    // preview player (separate from the export element)
  const canvasRef = useRef(null)
  const rafRef    = useRef(null)
  const sessionsRef   = useRef([])
  const sessionsReady = useRef(null)

  useEffect(() => {
    const since = Date.now() - 365 * 24 * 3600 * 1000
    sessionsReady.current = fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = (d.sessions || []).map(s => ({ id: s.session_id, start: new Date(s.start_time).getTime(), end: new Date(s.end_time).getTime() }))
        sessionsRef.current = list; setSessions(list); return list
      })
      .catch(() => [])
  }, [token, apiBase])

  const loadReadings = async (sessionId) => {
    if (burnReadingsCache.has(sessionId)) return burnReadingsCache.get(sessionId)
    const d  = await fetch(`${apiBase}/sync?sessionId=${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    const rd = (d.readings || []).map(x => ({ tMs: new Date(x.time).getTime(), hr: x.hr })).filter(x => x.hr > 0).sort((a, b) => a.tMs - b.tMs)
    burnReadingsCache.set(sessionId, rd)
    return rd
  }

  // Best overlapping session; if none overlaps, fall back to the NEAREST session
  // within 24 h (video filmed outside the recording window, or clock-shifted
  // metadata) — the video gets aligned to that session's start.
  const matchSession = (startMs, durationSec) => {
    const winEnd = startMs + (durationSec || 0) * 1000
    let best = null, bestOv = 0
    for (const s of sessionsRef.current) { const ov = Math.min(winEnd, s.end) - Math.max(startMs, s.start); if (ov > bestOv) { bestOv = ov; best = s } }
    if (best) return { session: best, aligned: false }
    let nearest = null, bestGap = Infinity
    for (const s of sessionsRef.current) {
      const gap = Math.max(s.start - winEnd, startMs - s.end, 0)
      if (gap < bestGap) { bestGap = gap; nearest = s }
    }
    if (nearest && bestGap <= 24 * 3600 * 1000) return { session: nearest, aligned: true }
    return { session: null, aligned: false }
  }

  const processVideo = async (id, f) => {
    try {
      const m = await readVideoMeta(f)
      // Fallback: no embedded date → estimate from the file's modified time
      // (phones set it right after recording ends: start ≈ mtime − duration).
      let startMs = m.startMs, estimated = false
      if (!startMs && f.lastModified && m.durationSec) { startMs = f.lastModified - m.durationSec * 1000; estimated = true }
      if (!startMs) { burnPatch(id, { status: 'manual', meta: m, note: 'Sin fecha en los metadatos — elegí la sesión abajo' }); return }
      await sessionsReady.current
      const { session: best, aligned } = matchSession(startMs, m.durationSec)
      if (!best) { burnPatch(id, { status: 'manual', meta: { ...m, startMs, estimated }, note: `Sin pulso cerca del ${fmtDate(startMs)} ${fmtTime(startMs)} — elegí la sesión abajo` }); return }
      const rd = await loadReadings(best.id)
      if (!rd.length) { burnPatch(id, { status: 'manual', meta: { ...m, startMs, estimated }, note: 'La sesión detectada no tiene pulso válido — elegí otra abajo' }); return }
      // Aligned fallback: pretend the video starts with the session (fine-tune with Desfase)
      const meta = { ...m, startMs: aligned ? best.start : startMs, estimated }
      burnPatch(id, {
        status: 'listo', meta, readings: rd,
        note: aligned
          ? `Sin pulso a la hora exacta del video (${fmtDate(startMs)} ${fmtTime(startMs)}) — sincronicé con la sesión más cercana (${fmtTime(best.start)}); ajustá el Desfase`
          : `${fmtDate(startMs)} ${fmtTime(startMs)}${estimated ? ' (estimada por fecha de archivo)' : ''} · ${rd.length} lecturas`,
        // If an export queue is running, new videos join the line automatically
        exportState: burnMem.queueRunning ? 'pendiente' : null,
      })
    } catch (e) { burnPatch(id, { status: 'error', note: 'Error: ' + e.message }) }
  }

  const onVideos = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && /video|\.(mov|mp4|m4v|webm)$/i.test((f.type || '') + ' ' + f.name))
    if (!files.length) return
    const items = files.map(f => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      file: f, url: URL.createObjectURL(f), name: f.name,
      meta: null, readings: [], offsetSec: 0,
      status: 'analizando', note: 'Leyendo metadatos…', exportState: null,
    }))
    burnMem.videos = [...burnMem.videos, ...items]
    if (!burnMem.activeId) burnMem.activeId = items[0].id
    burnMem.emit()
    for (const it of items) await processVideo(it.id, it.file)   // sequential → reuses caches
  }

  const removeVideo = (id) => {
    const v = burnMem.videos.find(x => x.id === id)
    if (v?.url && v.exportState !== 'procesando') URL.revokeObjectURL(v.url)
    if (v?.resultUrl) URL.revokeObjectURL(v.resultUrl)   // libera el export guardado
    burnMem.videos = burnMem.videos.filter(x => x.id !== id)
    if (burnMem.activeId === id) burnMem.activeId = burnMem.videos[0]?.id ?? null
    burnMem.emit()
  }

  // Manual sync: pick (or override) the session whose pulse to burn.
  const pickSession = async (videoId, sessionId) => {
    const s = sessionsRef.current.find(x => x.id === sessionId)
    if (!s) return
    burnPatch(videoId, { status: 'analizando', note: 'Cargando pulso…' })
    try {
      const rd = await loadReadings(sessionId)
      const v = burnMem.videos.find(x => x.id === videoId)
      burnPatch(videoId, rd.length
        ? { status: 'listo', readings: rd, offsetSec: 0, meta: { ...(v?.meta || {}), startMs: s.start }, note: `Sesión elegida a mano (${rd.length} lecturas) — ajustá el Desfase si hace falta`, exportState: burnMem.queueRunning ? 'pendiente' : null }
        : { status: 'manual', note: 'Esa sesión no tiene pulso válido — probá con otra' })
    } catch (e) { burnPatch(videoId, { status: 'error', note: 'Error: ' + e.message }) }
  }

  const active = videos.find(v => v.id === burnMem.activeId) ?? videos[0] ?? null
  const readyVideos = videos.filter(v => v.status === 'listo' && v.readings.length)

  // Live preview overlay on top of the playing video (the ACTIVE one).
  useEffect(() => {
    const v = videoRef.current, c = canvasRef.current
    if (!v || !c || !active?.url) return
    let stop = false
    const draw = () => {
      if (stop) return
      const W = c.clientWidth || 320, H = c.clientHeight || 180
      if (c.width !== W) c.width = W
      if (c.height !== H) c.height = H
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, W, H)
      drawHrOverlay(ctx, W, H, burnHrAt(active, v.currentTime), opt)
      rafRef.current = requestAnimationFrame(draw)
    }
    draw()
    return () => { stop = true; cancelAnimationFrame(rafRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opt, videos, burnMem.activeId])

  // Enqueue every ready video (skipping already-exported ones; if all are
  // exported, re-export everything) and start the worker.
  const runBatch = (mode) => {
    const ready = burnMem.videos.filter(v => v.status === 'listo' && v.readings.length)
    let ids = ready.filter(v => v.exportState !== 'listo' && v.exportState !== 'procesando').map(v => v.id)
    if (!ids.length) ids = ready.map(v => v.id)
    if (!ids.length) return
    burnMem.videos = burnMem.videos.map(v => ids.includes(v.id) ? { ...v, exportState: 'pendiente' } : v)
    burnMem.emit()
    burnStartWorker(mode)
  }

  const card    = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 14 }
  const posOpts = [['tl', 'Arr Izq'], ['tc', 'Arriba'], ['tr', 'Arr Der'], ['bl', 'Abj Izq'], ['bc', 'Abajo'], ['br', 'Abj Der']]
  const fieldLabel = { fontSize: 12, color: 'var(--text-muted)', margin: '0 0 6px' }
  const rowS = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }
  const chip = on => ({ fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 20, padding: '7px 13px', cursor: 'pointer', fontFamily: 'inherit', background: on ? 'var(--btn-strong)' : 'var(--chip-bg)', color: on ? '#fff' : 'var(--text-faint)', transition: 'all .12s' })

  const STATUS_BADGE = {
    analizando: { txt: 'Analizando…', color: 'var(--text-faint)' },
    listo:      { txt: '✓ pulso',  color: '#34c759' },
    manual:     { txt: 'Manual', color: '#ff9f0a' },
    error:      { txt: '✗ error',  color: '#ff3b30' },
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
        <strong>Para creadores de contenido:</strong> cargá uno o varios videos — la hora se detecta sola de los
        metadatos y el pulso se sincroniza automáticamente. Elegí un estilo y se aplica igual a todos.
        Todo local; los videos no se suben.
      </p>

      {/* Drop — acepta varios */}
      <label style={{ display: 'block', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '20px', textAlign: 'center', cursor: 'pointer', background: 'var(--field-bg)', marginBottom: 14 }}
        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onVideos(e.dataTransfer.files) }}>
        <input type="file" accept="video/*,.mov,.mp4,.m4v" multiple style={{ display: 'none' }} onChange={e => { onVideos(e.target.files); e.target.value = '' }} />
        <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>
          {videos.length ? `${videos.length} video${videos.length !== 1 ? 's' : ''} — agregar más` : 'Soltá o elegí tus videos (podés varios)'}
        </span>
      </label>

      {/* Cola de videos */}
      {videos.length > 0 && (
        <div style={{ ...card, padding: 8 }}>
          {videos.map(v => {
            const badge = STATUS_BADGE[v.status] ?? STATUS_BADGE.analizando
            const isActive = active?.id === v.id
            return (
              <div key={v.id} onClick={() => setActiveId(v.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, cursor: 'pointer',
                  background: isActive ? 'var(--track)' : 'transparent', border: `1px solid ${isActive ? 'var(--border-strong)' : 'transparent'}` }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', flexShrink: 0 }}>[VID]</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.note}</p>
                  {v.status === 'manual' && sessions.length > 0 && (
                    <select value="" onClick={e => e.stopPropagation()} onChange={e => e.target.value && pickSession(v.id, e.target.value)}
                      style={{ marginTop: 6, width: '100%', maxWidth: 320, background: 'var(--field-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', color: 'var(--text)' }}>
                      <option value="">Elegí la sesión a sincronizar…</option>
                      {[...sessions].sort((a, b) => b.start - a.start).slice(0, 50).map(s => (
                        <option key={s.id} value={s.id}>{fmtDate(s.start)} · {fmtTime(s.start)}</option>
                      ))}
                    </select>
                  )}
                </div>
                {v.exportState === 'procesando'
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#007aff', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>↑ {Math.round(progress * 100)}%</span>
                  : v.exportState === 'pendiente'
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#5856d6', flexShrink: 0 }}>En fila</span>
                  : v.exportState === 'listo'
                  ? (v.resultUrl
                      ? <a href={v.resultUrl} download={v.resultName || 'pulso.webm'} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#34c759', borderRadius: 8, padding: '5px 10px', textDecoration: 'none', flexShrink: 0 }}>
                          Descargar{v.resultSize ? ` (${Math.round(v.resultSize / 1048576)} MB)` : ''}
                        </a>
                      : <span style={{ fontSize: 11, fontWeight: 700, color: '#34c759', flexShrink: 0 }}>✓ exportado</span>)
                  : <span style={{ fontSize: 11, fontWeight: 700, color: badge.color, flexShrink: 0 }}>{badge.txt}</span>}
                {v.exportState !== 'procesando' && (
                  <button onClick={e => { e.stopPropagation(); removeVideo(v.id) }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-fainter)', padding: '2px 4px', flexShrink: 0 }}>✕</button>
                )}
              </div>
            )
          })}
          {videos.length > 1 && (
            <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '6px 10px 4px' }}>
              Tocá un video para previsualizarlo — el estilo que configures abajo se aplica a todos. La lista se guarda aunque cambies de pestaña.
            </p>
          )}
        </div>
      )}

      {/* Preview del video activo */}
      {active?.url && (
        <div style={{ position: 'relative', width: '100%', borderRadius: 14, overflow: 'hidden', marginBottom: 14, background: '#000' }}>
          <video ref={videoRef} src={active.url} controls playsInline style={{ display: 'block', width: '100%' }} />
          <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
        </div>
      )}

      {/* Customization panel — UNA config para todos los videos */}
      {active?.url && (
        <div style={card}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' }}>
            Personalización{videos.length > 1 ? ' (se aplica a todos los videos)' : ''}
          </p>

          {/* Estilos rápidos (presets) */}
          <p style={fieldLabel}>Estilos rápidos</p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
            {BURN_PRESETS.map(p => (
              <button key={p.name} onClick={() => setOpt(o => ({ ...o, ...p.opt }))}
                style={{ fontSize: 12, fontWeight: 600, border: '1px solid var(--field-border)', borderRadius: 20, padding: '7px 13px', cursor: 'pointer', fontFamily: 'inherit', background: 'var(--card-bg)', color: 'var(--text)' }}>{p.name}</button>
            ))}
          </div>

          {/* Posición: anclaje rápido */}
          <p style={fieldLabel}>Posición</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
            {posOpts.map(([k, ic]) => (
              <button key={k} onClick={() => setOpt(o => ({ ...o, pos: k, dx: 0, dy: 0 }))} style={{ padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 17, background: opt.pos === k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: opt.pos === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)', transition: 'all .12s' }}>{ic}</button>
            ))}
          </div>

          {/* Alineación fina X / Y */}
          <div style={{ background: 'var(--field-bg)', border: '1px solid #f0f0f3', borderRadius: 11, padding: '10px 12px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Alineación fina</span>
              <button onClick={() => setOpt(o => ({ ...o, dx: 0, dy: 0 }))} style={{ fontSize: 11, color: '#007aff', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Volver al ancla</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', minWidth: 14 }}>X</span>
              <input type="range" min={-0.5} max={0.5} step={0.005} value={opt.dx} onChange={e => setO('dx', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
              <span style={{ fontSize: 11, color: 'var(--text-soft)', minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{opt.dx > 0 ? '+' : ''}{Math.round(opt.dx * 100)}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', minWidth: 14 }}>Y</span>
              <input type="range" min={-0.5} max={0.5} step={0.005} value={opt.dy} onChange={e => setO('dy', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
              <span style={{ fontSize: 11, color: 'var(--text-soft)', minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{opt.dy > 0 ? '+' : ''}{Math.round(opt.dy * 100)}%</span>
            </div>
          </div>

          {/* Tamaño */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Tamaño</span>
            <input type="range" min={0.04} max={0.16} step={0.005} value={opt.size} onChange={e => setO('size', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
          </div>

          {/* Fuente */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Fuente</span>
            <select value={opt.font} onChange={e => setO('font', e.target.value)} style={{ flex: 1, background: 'var(--chip-bg)', border: '1px solid var(--field-border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
              {Object.entries(OVERLAY_FONTS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
            </select>
          </div>

          {/* Grosor */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Grosor</span>
            <div style={{ display: 'flex', gap: 6, flex: 1 }}>
              {[[400, 'Fino'], [700, 'Negrita'], [900, 'Black']].map(([w, l]) => (
                <button key={w} onClick={() => setO('weight', w)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: opt.weight === w ? 'var(--btn-strong)' : 'var(--chip-bg)', color: opt.weight === w ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{l}</button>
              ))}
            </div>
          </div>

          {/* Color del texto */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Color</span>
            <input type="color" value={opt.color} onChange={e => setO('color', e.target.value)} style={{ width: 40, height: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', padding: 0 }} />
            <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>color del texto</span>
          </div>

          {/* Estilo: chips on/off */}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
            {[['heart', 'Icono'], ['label', 'BPM'], ['pill', 'Fondo'], ['outline', 'Contorno'], ['zoneColor', 'Color por zona']].map(([k, l]) => (
              <button key={k} onClick={() => setO(k, !opt[k])} style={chip(opt[k])}>{l}</button>
            ))}
          </div>

          {/* Fondo (pill) */}
          {opt.pill && (
            <div style={rowS}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Fondo</span>
              <input type="color" value={opt.pillColor} onChange={e => setO('pillColor', e.target.value)} style={{ width: 40, height: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', padding: 0 }} />
              <input type="range" min={0} max={0.85} step={0.05} value={opt.pillOpacity} onChange={e => setO('pillOpacity', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} title="Opacidad del fondo" />
            </div>
          )}

          {/* Desfase (sincronización) — por video */}
          <div style={{ ...rowS, marginBottom: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Desfase{videos.length > 1 ? ' (este video)' : ''}</span>
            <input type="range" min={-300} max={300} step={1} value={active?.offsetSec ?? 0}
              onChange={e => active && burnPatch(active.id, { offsetSec: Number(e.target.value) })} style={{ flex: 1, accentColor: '#ff3b30' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {(active?.offsetSec ?? 0) < 0 ? '−' : '+'}{Math.floor(Math.abs(active?.offsetSec ?? 0) / 60)}:{String(Math.abs(active?.offsetSec ?? 0) % 60).padStart(2, '0')}
            </span>
          </div>
        </div>
      )}

      {/* Export */}
      {readyVideos.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' }}>Exportar</p>
          {exporting ? (() => {
            const enFila = videos.filter(v => v.exportState === 'pendiente').length
            const total  = doneCount + enFila + 1
            return (
            <div>
              {total > 1 && (
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>
                  Video {Math.min(doneCount + 1, total)} de {total}{enFila > 0 ? ` · ${enFila} en fila` : ''}
                </p>
              )}
              <div style={{ height: 8, background: 'var(--chip-bg)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${Math.round(progress * 100)}%`, background: '#ff3b30', transition: 'width 0.2s' }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                Procesando en tiempo real… {Math.round(progress * 100)}% — podés seguir cargando videos o cambiar de pestaña, la fila sigue sola (no cierres el navegador)
              </p>
            </div>
            )
          })() : (
            <>
              {(() => {
                const pendientes = readyVideos.filter(v => v.exportState !== 'listo').length
                const n = pendientes || readyVideos.length
                const otraVez = pendientes === 0
                return (
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <button onClick={() => runBatch('burn')} style={{ flex: 1, padding: '12px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: '#ff3b30', color: '#fff' }}>
                  {otraVez ? 'Volver a quemar' : 'Quemar'} {n > 1 ? `los ${n} videos` : otraVez ? '' : 'en el video'}
                </button>
                <button onClick={() => runBatch('overlay')} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid var(--field-border)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: 'var(--card-bg)', color: 'var(--text)' }}>
                  Overlay {n > 1 ? `× ${n}` : ''}
                </button>
              </div>
                )
              })()}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Fondo del overlay:</span>
                {[['transparent', 'Transparente'], ['green', 'Verde (chroma)'], ['black', 'Negro']].map(([k, l]) => (
                  <button key={k} onClick={() => setBgMode(k)} style={{ padding: '4px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: bgMode === k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: bgMode === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{l}</button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '10px 0 0', lineHeight: 1.5 }}>
                Sale en <strong>.webm</strong> y tarda lo que duran los videos (se procesan en fila, en tiempo real — cada uno se descarga al terminar{readyVideos.length > 1 ? '; si el navegador pregunta, permití descargas múltiples' : ''}). El overlay transparente anda en CapCut/DaVinci/After Effects; si tu editor no lo toma, usá <strong>Verde (chroma)</strong>. Recomendado: Chrome.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Descarga de datos en SEGUNDO PLANO ────────────────────────────────────────
// El pedido vive a nivel módulo: podés cambiar de pestaña y sigue solo; también
// se puede cancelar. Cuando termina, dispara la descarga del archivo.
const exportJobMem = { job: null, listeners: new Set() }
function exportJobNotify() {
  exportJobMem.listeners.forEach(fn => { try { fn() } catch { /* listener muerto */ } })
  try { window.dispatchEvent(new CustomEvent('vc-export-job', { detail: { active: exportJobMem.job?.status === 'busy' } })) } catch { /* SSR/nada */ }
}
function startExportJob(API, token, from, to) {
  if (exportJobMem.job?.status === 'busy') return
  const ctrl = new AbortController()
  exportJobMem.job = { from, to, status: 'busy', ctrl }
  exportJobNotify()
  fetch(`${API}/export?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal })
    .then(r => { if (!r.ok) throw new Error(`Error del servidor (HTTP ${r.status})`); return r.json() })
    .then(data => {
      // Convertir JSON a CSV tabular listo para Excel/Google Sheets
      const rows = [['timestamp_ms', 'fecha_hora', 'pulso_bpm', 'rr_intervalos_ms', 'session_id']]
      if (data.readings && data.readings.length > 0) {
        for (const r of data.readings) {
          const timeStr = r.time || ''
          const epoch = timeStr ? new Date(timeStr).getTime() : ''
          const rrStr = Array.isArray(r.rr) ? r.rr.join(';') : (r.rr ?? '')
          rows.push([epoch, timeStr, r.hr ?? '', `"${rrStr}"`, r.session_id || ''])
        }
      } else if (data.sessions && data.sessions.length > 0) {
        rows[0] = ['session_id', 'start_time', 'end_time', 'avg_hr', 'max_hr', 'min_hr', 'duration_min']
        for (const s of data.sessions) {
          const durMin = s.start_time && s.end_time ? Math.round((new Date(s.end_time) - new Date(s.start_time)) / 60000) : ''
          rows.push([s.session_id, s.start_time, s.end_time, s.avg_hr ?? '', s.max_hr ?? '', s.min_hr ?? '', durMin])
        }
      }
      const csvContent = rows.map(e => e.join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `manabi-export-${from}-${to}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000)
      exportJobMem.job = { from, to, status: 'done' }
    })
    .catch(e => { exportJobMem.job = ctrl.signal.aborted ? null : { from, to, status: 'error', msg: friendlyErr(e) } })
    .finally(exportJobNotify)
}
function cancelExportJob() {
  try { exportJobMem.job?.ctrl?.abort() } catch { /* ya cerrado */ }
  exportJobMem.job = null
  exportJobNotify()
}

function ExportTab({ token }) {
  const API = apiBase
  const [sub, setSub]   = useState('creadores')
  const [creatorMode, setCreatorMode] = useState('srt')
  const [purgeBusy, setPurgeBusy] = useState(false)
  const [purgeMsg, setPurgeMsg] = useState(null)
  const [purgeStep, setPurgeStep] = useState(0) // 0 = cerrado, 1 = paso 1, 2 = paso 2, 3 = paso 3 (palabra ELIMINAR)
  const [confirmWord, setConfirmWord] = useState('')

  const handleStartPurge = () => {
    setPurgeStep(1)
    setConfirmWord('')
  }

  const executePurge = async () => {
    setPurgeStep(0)
    setPurgeBusy(true); setPurgeMsg(null)
    try {
      const res = await fetch(`${API}/sessions?from=${from}&to=${to}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await res.json()
      if (res.ok) {
        setPurgeMsg({ ok: true, text: `✓ ¡Éxito! Se eliminaron permanentemente ${data.count} sesiones de tu historial.` })
      } else {
        throw new Error(data.error || 'Error al eliminar')
      }
    } catch (e) {
      setPurgeMsg({ ok: false, text: `Error: ${e.message}` })
    }
    setPurgeBusy(false)
    setConfirmWord('')
  }
  const today = new Date().toISOString().slice(0, 10)
  // Por defecto: el ÚLTIMO DÍA con datos (se ajusta solo cuando llega la lista)
  const [from, setFrom] = useState(today)
  const [to, setTo]     = useState(today)
  const [bounds, setBounds] = useState(null)          // { first, last } días con datos
  const touchedRef = useRef(false)
  useEffect(() => {
    fetch(`${API}/sync?since=${Date.now() - 366 * 86400000}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const dates = [...new Set((d?.sessions || []).map(s => localDateKey(new Date(s.start_time).getTime())))].sort()
        if (!dates.length) return
        setBounds({ first: dates[0], last: dates[dates.length - 1] })
        if (!touchedRef.current) { setFrom(dates[dates.length - 1]); setTo(dates[dates.length - 1]) }
      })
      .catch(() => {})
  }, [token, API])

  // Refresco cuando el trabajo de descarga cambia (aunque esta pestaña se haya ido y vuelto)
  const [, setTick] = useState(0)
  useEffect(() => {
    const fn = () => setTick(t => t + 1)
    exportJobMem.listeners.add(fn)
    return () => exportJobMem.listeners.delete(fn)
  }, [])
  const job = exportJobMem.job

  const setRange = (f, t) => { touchedRef.current = true; setFrom(f); setTo(t) }
  const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10) }
  const quick = [
    { l: 'Último día', on: () => bounds && setRange(bounds.last, bounds.last), sel: bounds && from === bounds.last && to === bounds.last },
    { l: '7 días',  on: () => setRange(isoDaysAgo(6), today),  sel: from === isoDaysAgo(6) && to === today },
    { l: '30 días', on: () => setRange(isoDaysAgo(29), today), sel: from === isoDaysAgo(29) && to === today },
    { l: 'Todo',    on: () => bounds && setRange(bounds.first, bounds.last), sel: bounds && from === bounds.first && to === bounds.last },
  ]

  const inputS = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-soft)', background: 'var(--card-bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }

  return (
    <div className="vc-center-desk" style={{ maxWidth: 640, margin: '0 auto', width: '100%' }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '4px 0 12px' }}>Exportar o borrar datos</h2>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[
          { k: 'creadores', l: 'Creadores' },
          { k: 'datos', l: 'Datos (CSV)' },
          { k: 'borrar', l: 'Borrar datos' }
        ].map(t => (
          <button key={t.k} onClick={() => setSub(t.k)}
            style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              background: sub === t.k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: sub === t.k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>
            {t.l}
          </button>
        ))}
      </div>

      {sub === 'creadores' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[{ k: 'srt', l: 'Subtítulo (.srt)' }, { k: 'burn', l: 'Quemar / overlay' }].map(t => (
              <button key={t.k} onClick={() => setCreatorMode(t.k)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: '1px solid var(--border-soft)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                  background: creatorMode === t.k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: creatorMode === t.k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>
                {t.l}
              </button>
            ))}
          </div>
          {creatorMode === 'srt' ? <VideoSrtTab token={token} embedded /> : <VideoBurnTab token={token} />}
        </>
      )}

      {sub === 'datos' && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Descargás tus sesiones y lecturas en un archivo tabular <strong>CSV</strong> compatible con Excel y Google Sheets. La descarga corre en segundo plano.
          </p>
          {/* Rangos rápidos */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {quick.map(q => (
              <button key={q.l} onClick={q.on} style={{
                fontSize: 12, fontWeight: 700, border: q.sel ? '1.5px solid var(--text)' : '1px solid var(--border-soft)', borderRadius: 8,
                padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit',
                background: q.sel ? 'var(--btn-strong)' : 'var(--chip-bg)', color: q.sel ? '#fff' : 'var(--text-muted)',
              }}>{q.l}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', marginBottom: 5 }}>Desde</p><input type="date" value={from} max={to} onChange={e => { touchedRef.current = true; setFrom(e.target.value) }} style={inputS} /></div>
            <div><p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', marginBottom: 5 }}>Hasta</p><input type="date" value={to} min={from} max={today} onChange={e => { touchedRef.current = true; setTo(e.target.value) }} style={inputS} /></div>
          </div>

          {job?.status === 'busy' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
                <span style={{ width: 14, height: 14, border: '2px solid var(--field-border)', borderTopColor: 'var(--text)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Preparando {job.from === job.to ? job.from : `${job.from} → ${job.to}`}…
              </span>
              <button onClick={cancelExportJob} style={{ fontSize: 12, fontWeight: 700, border: '1px solid var(--border-soft)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit', background: 'var(--card-bg)', color: '#ff3b30' }}>
                Cancelar
              </button>
              <span style={{ flexBasis: '100%', fontSize: 11, color: 'var(--text-faint)' }}>Podés cambiar de pestaña — sigue sola y el archivo baja al terminar.</span>
            </div>
          ) : (
            <>
              {job?.status === 'done'  && <p style={{ fontSize: 12, color: '#30d158', marginBottom: 10, fontWeight: 700 }}>✓ Listo — el archivo CSV se descargó.</p>}
              {job?.status === 'error' && <p style={{ fontSize: 12, color: '#ff3b30', marginBottom: 10 }}>{job.msg}</p>}
              <button onClick={() => startExportJob(API, token, from, to)} style={{ background: 'var(--btn-strong)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                Descargar CSV
              </button>
            </>
          )}
        </div>
      )}

      {sub === 'borrar' && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <p style={{ fontSize: 13, color: '#ff3b30', margin: '0 0 12px', lineHeight: 1.5, fontWeight: 600 }}>
            [ATENCIÓN] Esta opción eliminará permanentemente todas tus sesiones y registros del rango seleccionado de la base de datos en la nube. Esta acción no se puede deshacer.
          </p>
          {/* Rangos rápidos */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {quick.map(q => (
              <button key={q.l} onClick={q.on} style={{
                fontSize: 12, fontWeight: 700, border: q.sel ? '1.5px solid #ff3b30' : '1px solid var(--border-soft)', borderRadius: 8,
                padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit',
                background: q.sel ? '#ff3b30' : 'var(--chip-bg)', color: q.sel ? '#fff' : 'var(--text-muted)',
              }}>{q.l}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div><p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', marginBottom: 5 }}>Desde</p><input type="date" value={from} max={to} onChange={e => { touchedRef.current = true; setFrom(e.target.value) }} style={inputS} /></div>
            <div><p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', marginBottom: 5 }}>Hasta</p><input type="date" value={to} min={from} max={today} onChange={e => { touchedRef.current = true; setTo(e.target.value) }} style={inputS} /></div>
          </div>

          {purgeBusy ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
                <span style={{ width: 14, height: 14, border: '2px solid var(--field-border)', borderTopColor: '#ff3b30', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Eliminando datos del rango {from === to ? from : `${from} → ${to}`}…
              </span>
            </div>
          ) : (
            <>
              {purgeMsg && <p style={{ fontSize: 12, color: purgeMsg.ok ? '#30d158' : '#ff3b30', marginBottom: 10, fontWeight: 700 }}>{purgeMsg.text}</p>}
              <button onClick={handleStartPurge} style={{ background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                Borrar rango seleccionado…
              </button>
            </>
          )}

          {/* Modal de 3 Pasos de Seguridad Progresivos */}
          {purgeStep > 0 && (
            <div
              onClick={() => setPurgeStep(0)}
              style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: 'rgba(0, 0, 0, 0.72)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border-soft)',
                  borderRadius: 20, padding: 24, maxWidth: 420, width: '100%',
                  display: 'flex', flexDirection: 'column', gap: 14,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.35)', animation: 'fadeIn 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: '#ff3b30', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Paso {purgeStep} de 3 · Seguridad Crítica
                  </span>
                  <button
                    onClick={() => setPurgeStep(0)}
                    style={{ background: 'var(--chip-bg)', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>

                {purgeStep === 1 && (
                  <>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', margin: 0 }}>
                      ¿Confirmás iniciar el borrado?
                    </h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                      Estás a punto de borrar los registros biomédicos y sesiones correspondientes al período comprendido entre el <strong style={{ color: 'var(--text)' }}>{from}</strong> y el <strong style={{ color: 'var(--text)' }}>{to}</strong>.
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        onClick={() => setPurgeStep(0)}
                        style={{ background: 'var(--chip-bg)', color: 'var(--text)', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => setPurgeStep(2)}
                        style={{ background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}
                      >
                        Continuar al Paso 2 →
                      </button>
                    </div>
                  </>
                )}

                {purgeStep === 2 && (
                  <>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#ff3b30', margin: 0 }}>
                      Acción Definitiva e Irreversible
                    </h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                      Esta acción eliminará los datos directamente de la base de datos central en la nube. <strong>No habrá copia de respaldo ni forma de recuperarlos.</strong>
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        onClick={() => setPurgeStep(0)}
                        style={{ background: 'var(--chip-bg)', color: 'var(--text)', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => setPurgeStep(3)}
                        style={{ background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}
                      >
                        Entiendo el riesgo, ir al Paso 3 →
                      </button>
                    </div>
                  </>
                )}

                {purgeStep === 3 && (
                  <>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#ff3b30', margin: 0 }}>
                      Confirmación Final de Borrado
                    </h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                      Para autorizar el borrado definitivo, escribí la palabra <strong style={{ color: '#ff3b30', letterSpacing: 1 }}>ELIMINAR</strong> en mayúsculas:
                    </p>
                    <input
                      type="text"
                      placeholder="Escribí ELIMINAR"
                      value={confirmWord}
                      onChange={e => setConfirmWord(e.target.value)}
                      style={{
                        ...inputS,
                        fontSize: 14,
                        fontWeight: 800,
                        textAlign: 'center',
                        letterSpacing: 1,
                        border: '1.5px solid #ff3b30'
                      }}
                    />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        onClick={() => setPurgeStep(0)}
                        style={{ background: 'var(--chip-bg)', color: 'var(--text)', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Cancelar
                      </button>
                      <button
                        disabled={confirmWord.trim().toUpperCase() !== 'ELIMINAR'}
                        onClick={executePurge}
                        style={{
                          background: confirmWord.trim().toUpperCase() === 'ELIMINAR' ? '#ff3b30' : 'var(--chip-bg)',
                          color: confirmWord.trim().toUpperCase() === 'ELIMINAR' ? '#fff' : 'var(--text-faint)',
                          border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 12.5, fontWeight: 800,
                          cursor: confirmWord.trim().toUpperCase() === 'ELIMINAR' ? 'pointer' : 'not-allowed',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        ELIMINAR DEFINITIVAMENTE
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// "Nueva sesión" hub: choose between connecting live or importing files.
// ── Espectador: otra pantalla de la cuenta está midiendo — acá se mira en tiempo real ────
function LiveRemotePanel({ token, deviceLabel, onStopped }) {
  const [data, setData] = useState(null)
  const [stopping, setStopping] = useState(false)
  const [stopConfirm, setStopConfirm] = useState(false)
  const [remoteMsgInput, setRemoteMsgInput] = useState('')
  const [sendingMsg, setSendingMsg] = useState(false)
  const clock = useClock()

  const handleSendRemoteMsg = async (shouldPin = false) => {
    if (!remoteMsgInput.trim() || sendingMsg) return
    const text = remoteMsgInput.trim()
    setSendingMsg(true)
    try {
      await fetch(`${apiBase}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, pinned: shouldPin })
      })
      setRemoteMsgInput('')
    } catch (_) {}
    setSendingMsg(false)
  }

  const handleUnpinRemote = async () => {
    try {
      await fetch(`${apiBase}/live/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'unpin' })
      })
    } catch (_) {}
  }

  useEffect(() => {
    let es, retry, alive = true
    const connect = () => {
      if (!alive) return
      es = new EventSource(`${apiBase}/live/stream?token=${encodeURIComponent(token ?? '')}`)
      es.onmessage = e => {
        try {
          const parsed = JSON.parse(e.data)
          if (parsed.status === 'stopped') {
            onStopped?.()
            return
          }
          setData({ ...parsed, _rx: Date.now() })
        } catch (_) {}
      }
      es.onerror = () => { es.close(); retry = setTimeout(connect, 3000) }
    }
    connect()
    return () => { alive = false; clearTimeout(retry); es?.close() }
  }, [token, apiBase, onStopped])

  const online = !!(data?.hr && data?._rx && (clock.getTime() - data._rx) < 5000)
  const zone   = online ? getZone(data.hr) : null

  // Duración transcurrida
  const elapsedSec = data?.startTime ? Math.max(0, Math.floor((clock.getTime() - data.startTime) / 1000)) : 0
  const elapsedFormatted = `${String(Math.floor(elapsedSec / 3600)).padStart(2, '0')}:${String(Math.floor((elapsedSec % 3600) / 60)).padStart(2, '0')}:${String(elapsedSec % 60).padStart(2, '0')}`

  const handleStopRemote = async () => {
    setStopping(true)
    try {
      const res = await fetch(`${apiBase}/live/stop-remote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        onStopped?.()
      }
    } catch (_) {}
    setStopping(false)
    setStopConfirm(false)
  }

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', width: '100%', animation: 'fadeIn 0.3s ease' }}>
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 22,
        padding: '24px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column',
        alignItems: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
      }}>
        {/* Cabecera de estado */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: online ? '#34c759' : '#ff9f0a',
            boxShadow: online ? '0 0 10px #34c759' : 'none'
          }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: online ? '#34c759' : 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            {online ? 'Transmisión activa' : 'Esperando datos'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>· {deviceLabel || 'Otro dispositivo'}</span>
        </div>

        {/* Latido y pulsaciones */}
        <BeatingHeart hr={data?.hr} color={zone?.color ?? '#ff3b30'} glow={zone?.glow ?? 'rgba(255,59,48,0.35)'} online={online} />

        {/* Zona cardíaca */}
        {online && zone && (
          <div style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 20,
            background: `${zone.color}20`, color: zone.color,
            fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 16
          }}>
            {zone.name}
          </div>
        )}

        {/* Mensaje FIJADO si existe */}
        {data?.pinnedMessage?.text && (
          <div style={{
            background: 'rgba(255,159,10,0.12)', border: '1px solid rgba(255,159,10,0.35)',
            borderRadius: 14, padding: '10px 14px', width: '100%', marginBottom: 12, textAlign: 'center', boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#ff9f0a', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Mensaje fijado
              </span>
            </div>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>
              “{data.pinnedMessage.text}”
            </p>
            <button
              onClick={handleUnpinRemote}
              style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '2px 6px' }}
            >
              ✕ Desfijar
            </button>
          </div>
        )}

        {/* Feed de mensajes en vivo */}
        {(() => {
          const msgs = Array.isArray(data?.messages) ? data.messages : []
          if (!msgs.length) return null
          return (
            <div style={{
              background: 'var(--inset-bg)', border: '1px solid var(--border-soft)',
              borderRadius: 12, padding: '10px 12px', width: '100%', marginBottom: 12, textAlign: 'left',
              display: 'flex', flexDirection: 'column', gap: 6,
              boxSizing: 'border-box', animation: 'fadeIn 0.2s ease', maxHeight: 120, overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <IconChat size={12} color="var(--text-faint)" />
                <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Mensajes en vivo
                </span>
              </div>
              {msgs.slice(-5).map((m, idx) => (
                <div key={m.id || idx} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', flex: 1, lineHeight: 1.35 }}>
                    {m.text}
                  </span>
                  <span style={{ fontSize: 9.5, color: 'var(--text-faint)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtHMS(m.ts)}
                  </span>
                </div>
              ))}
            </div>
          )
        })()}

        {/* Formulario para enviar o fijar mensajes desde el celular */}
        <div style={{
          background: 'var(--inset-bg)', border: '1px solid var(--border-soft)',
          borderRadius: 14, padding: '10px 12px', width: '100%', marginBottom: 14, boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={remoteMsgInput}
              onChange={e => setRemoteMsgInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSendRemoteMsg(false) }}
              placeholder="Mensaje al vivo…"
              style={{
                flex: 1, background: 'var(--card-bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '6px 10px', fontSize: 12, color: 'var(--text)',
                outline: 'none', fontFamily: 'inherit'
              }}
            />
            <button
              onClick={() => handleSendRemoteMsg(false)}
              disabled={!remoteMsgInput.trim() || sendingMsg}
              style={{
                background: 'var(--btn-strong)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '6px 11px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', opacity: remoteMsgInput.trim() ? 1 : 0.4
              }}
            >
              Enviar
            </button>
            <button
              onClick={() => handleSendRemoteMsg(true)}
              disabled={!remoteMsgInput.trim() || sendingMsg}
              title="Fijar mensaje en el encabezado"
              style={{
                background: '#ff9f0a', color: '#fff', border: 'none',
                borderRadius: 8, padding: '6px 9px', fontSize: 11.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', opacity: remoteMsgInput.trim() ? 1 : 0.4
              }}
            >
              Fijar
            </button>
          </div>
        </div>

        {/* Rejilla de telemetría remota */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, width: '100%',
          background: 'var(--inset-bg)', border: '1px solid var(--border-soft)', borderRadius: 14,
          padding: '12px 8px', marginBottom: 18
        }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 3px' }}>Tiempo</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {elapsedSec > 0 ? elapsedFormatted : '—'}
            </p>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 3px' }}>HRV (RMSSD)</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: '#64d2ff', margin: 0 }}>
              {data?.rmssd ? `${data.rmssd} ms` : '—'}
            </p>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', margin: '0 0 3px' }}>Espectadores</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', margin: 0 }}>
              {data?.viewers ?? 0}
            </p>
          </div>
        </div>

        {/* Botón para detener la sesión remota y liberar el sensor */}
        {!stopConfirm ? (
          <button
            onClick={() => setStopConfirm(true)}
            style={{
              background: 'transparent', border: '1px solid var(--border-soft)', borderRadius: 10,
              padding: '9px 16px', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ff3b30'; e.currentTarget.style.borderColor = '#ff3b30' }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-soft)' }}
          >
            Finalizar sesión remota y conectar aquí
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', justifyContent: 'center' }}>
            <button
              onClick={handleStopRemote}
              disabled={stopping}
              style={{
                background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10,
                padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              {stopping ? 'Deteniendo…' : 'Confirmar detención'}
            </button>
            <button
              onClick={() => setStopConfirm(false)}
              style={{
                background: 'var(--chip-bg)', color: 'var(--text)', border: 'none', borderRadius: 10,
                padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function NuevaSesionTab({ token, pip, sessionTime, sessionSec, onNavigate, ...monitorProps }) {
  // If an import is running (or files are loaded), come back to the import view
  const [mode, setMode] = useState(() => (importMem.importing || importMem.sessions.length) ? 'import' : 'live')
  const [existing, setExisting] = useState([])
  useEffect(() => {
    const since = Date.now() - 365 * 24 * 3600 * 1000
    fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setExisting((d.sessions || []).map(s => ({ id: s.session_id, start_time: s.start_time }))))
      .catch(() => {})
  }, [token, apiBase])
  const clearHistCache = () => {
    try { for (const k of [..._vcMem.keys()]) if (k.startsWith('vc_segs') || k.startsWith('vc_day')) _vcMem.delete(k) } catch (_) {}
    try { Object.keys(sessionStorage).filter(k => k.startsWith('vc_segs') || k.startsWith('vc_day')).forEach(k => sessionStorage.removeItem(k)) } catch (_) {}
  }
  // Con el Polar ya conectado (o demo activo), el selector de modo estorba: lo escondemos
  // y dejamos sólo un enlace discreto por si quieren volver a importar.
  const p = monitorProps.polar
  const liveConnected = mode === 'live' && (monitorProps.demo?.active || ['connected', 'calibrating', 'reconnecting'].includes(p?.status))

  // ¿Otro dispositivo de la cuenta está midiendo AHORA? → modo espectador.
  // Se consulta cada 5 s mientras no haya medición local (en segundo plano el
  // sistema ya espacia los timers solo, no hace falta frenarlo a mano).
  const [remoteLive, setRemoteLive] = useState(null)
  useEffect(() => {
    if (liveConnected) { setRemoteLive(null); return }
    let alive = true
    const check = () => {
      fetch(`${apiBase}/live/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!alive) return
          const mine = deviceIdentity().id
          setRemoteLive(d?.active && d.deviceId && d.deviceId !== mine ? d : null)
        })
        .catch(() => { /* sin señal: se reintenta en el próximo tick */ })
    }
    check()
    const id = setInterval(check, 5000)
    return () => { alive = false; clearInterval(id) }
  }, [liveConnected, token, apiBase])

  // Otro dispositivo transmite → espectador (sin selector ni botón de conectar)
  if (remoteLive) {
    return (
      <div>
        <div style={{ maxWidth: 720, margin: '0 auto 18px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 21, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px', letterSpacing: -0.3 }}>Nueva sesión</h2>
          <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>Ya estás midiendo desde {remoteLive.deviceLabel || 'otro dispositivo'}</p>
        </div>
        <LiveRemotePanel token={token} deviceLabel={remoteLive.deviceLabel} onStopped={() => setRemoteLive(null)} />
      </div>
    )
  }

  return (
    <div>
      {mode === 'import' && (
        <div style={{ maxWidth: 720, margin: '0 auto 14px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 21, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px', letterSpacing: -0.3 }}>Importar archivos</h2>
          <button onClick={() => setMode('live')} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            ← Volver a conectar en vivo
          </button>
        </div>
      )}
      {mode === 'live'
        ? <MonitorTab token={token} onImportFiles={() => setMode('import')} pip={pip} sessionTime={sessionTime} sessionSec={sessionSec} onNavigate={onNavigate} {...monitorProps} />
        : <div style={{ maxWidth: 720, margin: '0 auto' }}><IosImportPanel token={token} existingSessions={existing} onImported={clearHistCache} inline /></div>}
    </div>
  )
}

// AmigosTab and GrupoDetalle have been extracted to src/features/friends/AmigosTab.jsx

function SidebarIconResumen({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke={c} strokeWidth="1.4" />
      <path d="M5 8.5 L7 10.5 L11 6" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function SidebarIconLive({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M2 8 Q4 3 6 8 Q8 13 10 8 Q12 3 14 8" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function SidebarIconHistory({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="10" rx="2" stroke={c} strokeWidth="1.4" />
      <path d="M5 7h6M5 10h4" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function SidebarIconPerfil({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="6" r="2.5" stroke={c} strokeWidth="1.4" />
      <path d="M3 13c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
function SidebarIconAmigos({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="5.6" cy="5.8" r="2.3" stroke={c} strokeWidth="1.4" />
      <path d="M1.7 13.2c0-2.15 1.75-3.9 3.9-3.9s3.9 1.75 3.9 3.9" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="11.4" cy="6.3" r="1.9" stroke={c} strokeWidth="1.4" />
      <path d="M11.9 9.6c1.5.5 2.6 1.9 2.6 3.6" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function SidebarIconGames({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="4" />
      <path d="M6 12h4M8 10v4" />
      <circle cx="15" cy="12" r="1.2" fill={c} />
      <circle cx="18" cy="12" r="1.2" fill={c} />
    </svg>
  )
}

const GAMES_CATALOG = [
  {
    id: 'meditacion',
    title: 'Coherencia Cardíaca (Zen)',
    badge: 'Juego 1 · Disponible',
    tagline: 'Sincronizá tu respiración a 5.5 rpm con tus latidos',
    description: 'Entrená tu sistema nervioso parasimpático en tiempo real. Respirá siguiendo el orbe para maximizar tu HRV (RMSSD), reducir el estrés y sincronizar el pulso con la respiración.',
    color: '#8b5cf6',
    secondaryColor: '#3b82f6',
    ambientGlow: 'rgba(139, 92, 246, 0.45)',
    gradientBg: 'radial-gradient(circle at 65% 25%, rgba(139, 92, 246, 0.50) 0%, rgba(59, 130, 246, 0.25) 45%, rgba(11, 11, 18, 0.96) 75%)',
    playable: true,
    iconType: 'meditation',
    features: ['Respiración guiada 5.5 rpm', 'Curva de HRV en vivo', 'Puntuación Zen']
  },
  {
    id: 'calma',
    title: 'Desafío de Calma',
    badge: 'Juego 2 · Próximamente',
    tagline: 'Resiliencia cardiovascular ante estímulos visuales',
    description: 'Mantené tus pulsaciones por debajo de tu zona de reposo mientras la pantalla te desafía con retos visuales de reflejos. Cuanto más sereno mantengas el pulso, mayor es tu puntaje.',
    color: '#007aff',
    secondaryColor: '#00f5d4',
    ambientGlow: 'rgba(0, 122, 255, 0.45)',
    gradientBg: 'radial-gradient(circle at 65% 25%, rgba(0, 122, 255, 0.50) 0%, rgba(0, 245, 212, 0.22) 45%, rgba(8, 12, 22, 0.96) 75%)',
    playable: false,
    iconType: 'zap',
    features: ['Biofeedback reactivo', 'Control de estrés', 'Retos de concentración']
  },
  {
    id: 'cadencia',
    title: 'Carrera de Cadencia',
    badge: 'Juego 3 · Próximamente',
    tagline: 'Ritmo de zancadas en Zona 2 de quema aeróbica',
    description: 'Ajustá tu cadencia de pasos al ritmo óptimo del acelerómetro Polar H10 para sostener tu frecuencia cardíaca exactamente en Zona 2 aeróbica de quema de grasas acumulando puntos.',
    color: '#34c759',
    secondaryColor: '#10b981',
    ambientGlow: 'rgba(52, 199, 89, 0.45)',
    gradientBg: 'radial-gradient(circle at 65% 25%, rgba(52, 199, 89, 0.50) 0%, rgba(16, 185, 129, 0.22) 45%, rgba(10, 18, 12, 0.96) 75%)',
    playable: false,
    iconType: 'walking',
    features: ['Metrónomo biomecánico', 'Zona 2 aeróbica', 'Quema eficiente']
  },
  {
    id: 'duelo',
    title: 'Duelo de Pulso (1 vs 1)',
    badge: 'Juego 4 · Multijugador',
    tagline: 'Batalla en vivo de relajación y variabilidad',
    description: 'Conectate con un amigo en tiempo real a través de tu link público. En una ronda de 3 minutos, quien logre bajar más su pulso y elevar su variabilidad cardíaca gana la partida.',
    color: '#ff9f0a',
    secondaryColor: '#ff3b30',
    ambientGlow: 'rgba(255, 159, 10, 0.45)',
    gradientBg: 'radial-gradient(circle at 65% 25%, rgba(255, 159, 10, 0.50) 0%, rgba(255, 59, 48, 0.22) 45%, rgba(20, 12, 8, 0.96) 75%)',
    playable: false,
    iconType: 'activity',
    features: ['Multijugador en vivo', 'Comparativa instantánea', 'Trofeo de Calma']
  }
]

function renderGameIcon(type, color = '#fff', size = 22) {
  switch (type) {
    case 'meditation': return <IconMeditation size={size} color={color} />
    case 'zap': return <IconZap size={size} color={color} />
    case 'walking': return <IconPostureWalking size={size} color={color} />
    case 'activity':
    default:
      return <IconActivity size={size} color={color} />
  }
}

function _LegacyJuegosMockup({ polar, demo, hrv, isFullscreen, setIsFullscreen, onUploadSession, onNavigate }) {
  const [hoveredGameId, setHoveredGameId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const activeGame = hoveredGameId ? GAMES_CATALOG.find(g => g.id === hoveredGameId) : null

  if (isPlaying) {
    return (
      <div style={{ position: 'relative', width: '100%', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ maxWidth: 680, width: '100%', margin: '0 auto 6px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => setIsPlaying(false)}
            style={{
              background: 'var(--chip-bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '5px 12px',
              color: 'var(--text-soft)',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              transition: 'all 0.15s ease'
            }}
          >
            ← Volver a Juegos
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
          <MeditacionTab
            polar={polar}
            demo={demo}
            hrv={hrv}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            onUploadSession={onUploadSession}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      onMouseLeave={() => setHoveredGameId(null)}
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - 120px)',
        width: '100%',
        padding: '16px 14px 60px',
        maxWidth: 1080,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      {/* CAPA 1: Fondo cinematográfico PlayStation profundo (Transición suave de 1.2s) */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: activeGame
            ? `radial-gradient(circle at 65% 25%, ${activeGame.color}55 0%, rgba(15, 20, 32, 0.95) 45%, rgba(6, 8, 14, 0.98) 100%)`
            : 'transparent',
          opacity: activeGame ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 1.2s cubic-bezier(0.25, 1, 0.5, 1), background 1.2s cubic-bezier(0.25, 1, 0.5, 1)',
          zIndex: 0,
        }}
      />
      {/* CAPA 2: Orbe bioluminiscente difuminado ambiental estilo PS5 */}
      <div
        style={{
          position: 'fixed',
          top: '-15%',
          right: '-10%',
          width: '75vw',
          height: '75vw',
          maxWidth: 900,
          maxHeight: 900,
          borderRadius: '50%',
          background: activeGame ? activeGame.color : 'transparent',
          filter: 'blur(130px)',
          opacity: activeGame ? 0.35 : 0,
          pointerEvents: 'none',
          transition: 'all 1.2s cubic-bezier(0.25, 1, 0.5, 1)',
          zIndex: 0,
        }}
      />
      {/* CAPA 3: Viñeta para máximo contraste de textos */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to top, rgba(6,8,14,0.92) 0%, rgba(6,8,14,0.3) 50%, rgba(6,8,14,0.7) 100%)',
          opacity: activeGame ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 1.2s cubic-bezier(0.25, 1, 0.5, 1)',
          zIndex: 0,
        }}
      />

      {/* 1. TÍTULO FIJO SUPERIOR: Sin saltos ni movimientos bruscos */}
      <div style={{ position: 'relative', zIndex: 2, marginBottom: 16 }}>
        <h1 style={{
          fontSize: 24, fontWeight: 800, margin: '0 0 4px',
          color: activeGame ? '#ffffff' : 'var(--text)',
          letterSpacing: -0.5,
          transition: 'color 0.4s ease',
          textShadow: activeGame ? '0 2px 14px rgba(0,0,0,0.8)' : 'none'
        }}>
          Juegos y Biofeedback
        </h1>
        <p style={{
          fontSize: 13.5,
          color: activeGame ? 'rgba(255,255,255,0.75)' : 'var(--text-faint)',
          margin: 0,
          transition: 'color 0.4s ease',
          textShadow: activeGame ? '0 1px 8px rgba(0,0,0,0.8)' : 'none'
        }}>
          {activeGame ? 'Hacé clic para jugar o explorá los otros modos.' : 'Pasá el cursor o tocá sobre cualquier juego para ver su ambientación y empezar a entrenar tu cuerpo.'}
        </p>
      </div>

      {/* 2. FILA DE RECUADROS UNO AL LADO DEL OTRO (POSICIÓN ESTABLE: NUNCA SE MUEVEN) */}
      <div style={{ position: 'relative', zIndex: 2, marginBottom: 20 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
        }}>
          {GAMES_CATALOG.map(game => {
            const isHovered = game.id === hoveredGameId
            return (
              <div
                key={game.id}
                onMouseEnter={() => setHoveredGameId(game.id)}
                onClick={() => {
                  setHoveredGameId(game.id)
                  if (game.playable) setIsPlaying(true)
                }}
                style={{
                  background: isHovered
                    ? 'rgba(255, 255, 255, 0.22)'
                    : activeGame
                      ? 'rgba(20, 24, 38, 0.85)'
                      : 'var(--card-bg)',
                  border: isHovered
                    ? `2.5px solid ${game.color}`
                    : `1px solid ${activeGame ? 'rgba(255,255,255,0.18)' : 'var(--border)'}`,
                  borderRadius: 16,
                  padding: '18px 16px',
                  cursor: 'pointer',
                  transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: isHovered
                    ? `0 16px 36px ${game.ambientGlow}`
                    : activeGame
                      ? '0 6px 20px rgba(0,0,0,0.3)'
                      : '0 2px 8px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140,
                  position: 'relative',
                  overflow: 'hidden',
                  backdropFilter: activeGame ? 'blur(16px)' : 'none',
                }}
              >
                {/* Borde superior luminoso al estar seleccionado */}
                {isHovered && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 3.5,
                    background: game.color, boxShadow: `0 0 14px ${game.color}`
                  }} />
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: isHovered ? `${game.color}35` : `${game.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {renderGameIcon(game.iconType, game.color, 22)}
                  </div>
                  {game.playable ? (
                    <span style={{ fontSize: 9.5, fontWeight: 900, background: '#34c759', color: '#000', padding: '2px 8px', borderRadius: 6 }}>
                      JUGAR
                    </span>
                  ) : (
                    <span style={{
                      fontSize: 9.5, fontWeight: 700,
                      background: activeGame ? 'rgba(255,255,255,0.14)' : 'var(--chip-bg)',
                      color: activeGame ? '#e2e8f0' : 'var(--text-faint)',
                      padding: '2px 8px', borderRadius: 6
                    }}>
                      PRÓX
                    </span>
                  )}
                </div>

                <div>
                  <h3 style={{
                    fontSize: 15, fontWeight: 800, margin: '0 0 4px',
                    color: isHovered || activeGame ? '#ffffff' : 'var(--text)',
                    letterSpacing: -0.3,
                    textShadow: activeGame ? '0 1px 4px rgba(0,0,0,0.7)' : 'none'
                  }}>
                    {game.title}
                  </h3>
                  <p style={{
                    fontSize: 11.5,
                    color: isHovered ? game.color : activeGame ? '#cbd5e1' : 'var(--text-muted)',
                    margin: 0, lineHeight: 1.3
                  }}>
                    {game.tagline}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* 3. SHOWCASE INMERSIVO: Se despliega suavemente ABAJO de las tarjetas SIN MOVERLAS */}
      {activeGame && (
        <div style={{
          position: 'relative',
          zIndex: 2,
          animation: 'fadeIn 0.35s ease',
          background: 'rgba(16, 20, 32, 0.82)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          border: `1.5px solid ${activeGame.color}60`,
          boxShadow: `0 24px 60px rgba(0,0,0,0.6), 0 0 35px ${activeGame.ambientGlow}`,
          borderRadius: 20,
          padding: '26px 28px',
          maxWidth: 860,
          color: '#ffffff'
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 20,
            background: `${activeGame.color}35`, border: `1.5px solid ${activeGame.color}`,
            boxShadow: `0 0 16px ${activeGame.color}50`,
            marginBottom: 10
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ffffff', boxShadow: '0 0 6px #fff' }} />
            <span style={{ fontSize: 11, fontWeight: 900, color: '#ffffff', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {activeGame.badge}
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(22px, 3.8vw, 32px)', fontWeight: 900, margin: '0 0 8px',
            color: '#ffffff', letterSpacing: -0.6,
            textShadow: '0 4px 24px rgba(0,0,0,0.95)'
          }}>
            {activeGame.title}
          </h2>

          <p style={{
            fontSize: 15, fontWeight: 800, color: activeGame.color, margin: '0 0 10px',
            textShadow: '0 2px 14px rgba(0,0,0,0.9)'
          }}>
            {activeGame.tagline}
          </p>

          <p style={{
            fontSize: 13.5, color: '#f1f5f9', margin: '0 0 16px', lineHeight: 1.6, maxWidth: 680,
            textShadow: '0 2px 14px rgba(0,0,0,0.95)'
          }}>
            {activeGame.description}
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {activeGame.features.map((feat, idx) => (
              <span key={idx} style={{
                fontSize: 11, fontWeight: 700, color: '#ffffff',
                background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
                padding: '4px 12px', textShadow: '0 1px 4px rgba(0,0,0,0.8)'
              }}>
                {feat}
              </span>
            ))}
          </div>

          {activeGame.playable ? (
            <button
              onClick={() => setIsPlaying(true)}
              style={{
                background: activeGame.color,
                color: '#ffffff',
                border: 'none',
                borderRadius: 12,
                padding: '11px 28px',
                fontSize: 14,
                fontWeight: 900,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: `0 8px 28px ${activeGame.ambientGlow}`,
                transition: 'transform 0.15s ease',
                letterSpacing: 0.3
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)' }}
            >
              <span>▶</span> JUGAR AHORA
            </button>
          ) : (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 12, padding: '9px 20px', color: '#cbd5e1',
              fontSize: 13, fontWeight: 800
            }}>
              <span>🔒</span> Próximamente
            </div>
          )}
        </div>
      )}
    </div>
  )
}
function SidebarIconShare({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="11.5" cy="4" r="1.5" stroke={c} strokeWidth="1.4" />
      <circle cx="4.5" cy="8" r="1.5" stroke={c} strokeWidth="1.4" />
      <circle cx="11.5" cy="12" r="1.5" stroke={c} strokeWidth="1.4" />
      <line x1="5.9" y1="7.3" x2="10.1" y2="4.7" stroke={c} strokeWidth="1.4" />
      <line x1="5.9" y1="8.7" x2="10.1" y2="11.3" stroke={c} strokeWidth="1.4" />
    </svg>
  )
}
function SidebarIconAdmin({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="#6c6c70" strokeWidth="1.4" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="#6c6c70" strokeWidth="1.4" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="#6c6c70" strokeWidth="1.4" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="#6c6c70" strokeWidth="1.4" />
    </svg>
  )
}
function SidebarIconLogout({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M6 3H3a1 1 0 00-1 1v8a1 1 0 001 1h3" stroke="#6c6c70" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10 5l3 3-3 3M13 8H6" stroke="#6c6c70" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SidebarIconMeditacion({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 2a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z" fill={c} />
      <path d="M4.5 11c.4-.4 1.6-.8 3.5-.8s3.1.4 3.5.8a1 1 0 0 1-.8.8H5.3a1 1 0 0 1-.8-.8z" fill={c} />
      <path d="M8 4.5c1.2 0 1.8 1 1.8 2.2V8a1.8 1.8 0 0 1-3.6 0V6.7c0-1.2.6-2.2 1.8-2.2z" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M3.5 7c-.4.4-.8 1.2-.8 2a1.2 1.2 0 0 0 2.4 0" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M12.5 7c.4.4.8 1.2.8 2a1.2 1.2 0 0 1-2.4 0" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function MeditacionTab(props) {
  return <MeditacionTabImported {...props} />
}



// ── Constancia: racha de días registrados + horas por día ──

const WEEK_GOAL_DAYS = 5    // meta de días activos por semana

function ConstanciaCard({ sessions, initialView = 'day', activeDate, onSelectTimeRange }) {
  const view = initialView
  const [selDay, setSelDay] = useState(null)

  useEffect(() => {
    if (activeDate) setSelDay(activeDate)
  }, [activeDate])

  const c = useMemo(() => {
    if (!sessions || !sessions.length) return null
    // ── Minutos registrados por día (las sesiones nocturnas se parten a medianoche) ──
    const minByDay = {}, sesByDay = {}
    sessions.forEach(s => {
      if (!(s.end > s.start)) return
      sesByDay[localDateKey(s.start)] = (sesByDay[localDateKey(s.start)] || 0) + 1
      let t = s.start, guard = 0
      while (t < s.end && guard++ < 400) {
        const d = new Date(t); d.setHours(24, 0, 0, 0)
        const cut = Math.min(s.end, d.getTime())
        const k = localDateKey(t)
        minByDay[k] = (minByDay[k] || 0) + (cut - t) / 60000
        t = cut
      }
    })
    Object.keys(minByDay).forEach(k => {
      const val = Math.min(1440, minByDay[k])
      minByDay[k] = val < 1 ? 0 : val
    })

    const todayKey  = localDateKey(Date.now())
    const activeSet = new Set(Object.keys(minByDay).filter(k => minByDay[k] >= STREAK_MIN_MIN))

    // ── Racha actual (si hoy aún no cumpliste pero ayer sí, sigue viva "en riesgo") ──
    let streak = 0
    const startKey = activeSet.has(todayKey) ? todayKey : localDateKey(Date.now() - 86400000)
    if (activeSet.has(startKey)) {
      let t = new Date(startKey + 'T12:00:00').getTime()
      while (activeSet.has(localDateKey(t))) { streak++; t -= 86400000 }
    }
    const atRisk = streak > 0 && !activeSet.has(todayKey)

    // ── Mejor racha histórica ──
    let best = 0, run = 0, prevOrd = null
    ;[...activeSet].sort().forEach(k => {
      const o = Math.round(new Date(k + 'T12:00:00').getTime() / 86400000)
      run = prevOrd != null && o === prevOrd + 1 ? run + 1 : 1
      if (run > best) best = run
      prevOrd = o
    })

    // ── Totales (ventana de datos: últimos 12 meses) ──
    const totalDays = activeSet.size
    const totalMin  = Object.values(minByDay).reduce((a, b) => a + b, 0)
    const avgMin    = totalDays ? Object.keys(minByDay).filter(k => activeSet.has(k)).reduce((a, k) => a + minByDay[k], 0) / totalDays : 0

    // Use activeDate or default to today to determine what week and month are currently analyzed
    const activeKey = activeDate || todayKey

    // ── Semana actual / seleccionada (lun–dom) ──
    const monActive = new Date(weekKeyOf(activeKey) + 'T12:00:00').getTime()
    const week7 = Array.from({ length: 7 }, (_, i) => {
      const ms = monActive + i * 86400000
      const k  = localDateKey(ms)
      return { k, ms, min: minByDay[k] || 0, future: k > todayKey }
    })

    // ── Mes calendario seleccionado / en curso ──
    const [yy, mm] = activeKey.split('-').map(Number)
    const nDays = new Date(yy, mm, 0).getDate()
    const monthDays = Array.from({ length: nDays }, (_, i) => {
      const ms = new Date(yy, mm - 1, i + 1, 12).getTime()
      const k  = localDateKey(ms)
      return { k, ms, day: i + 1, min: minByDay[k] || 0, future: k > todayKey }
    })

    return { minByDay, sesByDay, todayKey, streak, atRisk, best, totalDays, totalMin, avgMin, week7, monthDays }
  }, [sessions, activeDate])

  if (!c) return null

  const currentActiveDate = activeDate || (c ? c.todayKey : localDateKey(Date.now()))

  // ── Día del detalle: en Día siempre el activo; en Semana/Mes, el que toques ──
  const dk     = view === 'day' ? currentActiveDate : (selDay || currentActiveDate)
  const dmin   = c.minByDay[dk] || 0
  const dses   = c.sesByDay[dk] || 0
  const dLabel = new Date(dk + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })

  const isWeek = view === 'week'
  const isMonth = view === 'month'
  
  // Calculate period statistics for the top-level progress bar
  const daysInPeriod = (isWeek ? c.week7 : isMonth ? c.monthDays : []).filter(d => !d.future)
  const periodMin = view === 'day' ? dmin : daysInPeriod.reduce((a, d) => a + d.min, 0)
  const targetMin = view === 'day' ? STREAK_MIN_MIN : isWeek ? 7 * STREAK_MIN_MIN : 30 * STREAK_MIN_MIN // 6h Day, 42h Week, 180h Month
  const pct = Math.min(100, periodMin / targetMin * 100)
  const barColor = periodMin >= targetMin ? '#30d158' : (pct < 35 ? '#ff3b30' : '#ff9f0a')

  const labelLeft = view === 'day'
    ? (dk === c.todayKey ? 'registrados hoy' : 'registrados ese día')
    : (isWeek ? 'registrados esta semana' : 'registrados este mes')

  const labelRight = periodMin >= targetMin
    ? (view === 'day' ? '✓ objetivo diario cumplido' : isWeek ? '✓ objetivo semanal cumplido' : '✓ objetivo mensual cumplido')
    : `objetivo ${fmtH(targetMin)} — falta ${fmtH(Math.max(1, targetMin - periodMin))}`

  const statBox = (v, label) => (
    <div style={{ background: 'var(--inset-bg)', borderRadius: 12, padding: '10px 8px', textAlign: 'center' }}>
      <p style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)', margin: 0, lineHeight: 1.1 }}>{v}</p>
      <p style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-faint)', margin: '2px 0 0' }}>{label}</p>
    </div>
  )

  // ── Barras con línea de objetivo (6 h): verde = día cumplido ──
  const GoalBars = ({ days, thin, labels }) => {
    const [hoveredKey, setHoveredKey] = useState(null)
    const [activeTooltipKey, setActiveTooltipKey] = useState(null)
    const [hover, setHover] = useState(false)
    const H = 85
    const scale = Math.max(STREAK_MIN_MIN * 1.5, ...days.map(d => d.min))
    const goalY = Math.round(H * STREAK_MIN_MIN / scale)
    const barsRef = useRef(null)
    const curScrubRef = useRef(null)

    const processTouchAt = (clientX) => {
      const el = barsRef.current
      if (!el || !days.length) return
      const rect = el.getBoundingClientRect()
      const relX = clientX - rect.left
      const clampedX = Math.max(0, Math.min(rect.width - 1, relX))
      const idx = Math.floor((clampedX / rect.width) * days.length)
      const targetDay = days[idx]
      if (targetDay && targetDay.k !== curScrubRef.current) {
        curScrubRef.current = targetDay.k
        setActiveTooltipKey(targetDay.k)
        setHoveredKey(targetDay.k)
        setSelDay(targetDay.k)
        try { navigator.vibrate?.(10) } catch (_) {}
      }
    }

    useEffect(() => {
      const el = barsRef.current
      if (!el) return
      const onMove = (e) => {
        if (!e.touches || !e.touches[0]) return
        e.preventDefault()
        processTouchAt(e.touches[0].clientX)
      }
      const onStart = (e) => {
        if (!e.touches || !e.touches[0]) return
        processTouchAt(e.touches[0].clientX)
      }
      const onEnd = () => {
        curScrubRef.current = null
        setHoveredKey(null)
      }
      el.addEventListener('touchstart', onStart, { passive: true })
      el.addEventListener('touchmove', onMove, { passive: false })
      el.addEventListener('touchend', onEnd, { passive: true })
      el.addEventListener('touchcancel', onEnd, { passive: true })
      return () => {
        el.removeEventListener('touchstart', onStart)
        el.removeEventListener('touchmove', onMove)
        el.removeEventListener('touchend', onEnd)
        el.removeEventListener('touchcancel', onEnd)
      }
    }, [days])

    return (
      <div
        className="vc-touch-scrubber"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); setHoveredKey(null); setActiveTooltipKey(null); }}
        style={{
          background: hover ? 'var(--chip-bg)' : 'var(--inset-bg)', borderRadius: 14, padding: '10px 14px', marginTop: 4,
          boxShadow: hover ? 'inset 0 0 0 1px var(--border-soft)' : 'none',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
      >
        <div style={{ position: 'relative', height: H, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
          <div ref={barsRef} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 34, display: 'flex', alignItems: 'flex-end', gap: thin ? 2 : 6, touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
            {days.map((d, i) => {
              const h   = d.future ? 0 : d.min <= 0 ? 3 : Math.max(5, Math.round(H * Math.min(d.min, scale) / scale))
              const sel = d.k === dk && !d.future && view !== 'day'
              const varSign = (d.min - STREAK_MIN_MIN) >= 0 ? '+' : ''
              const varText = d.min >= 60 ? `${varSign}${((d.min - STREAK_MIN_MIN) / 60).toFixed(1)}h` : `${varSign}${Math.round(d.min - STREAK_MIN_MIN)} min`
              const isHovered = (d.k === hoveredKey || d.k === activeTooltipKey) && !d.future
              
              const pct = d.min / STREAK_MIN_MIN * 100
              const barColor = d.min >= STREAK_MIN_MIN ? '#30d158' : (pct < 35 ? '#ff3b30' : '#ff9f0a')

              // Tooltip positioning clamp (no salirse a la izquierda o derecha en meses)
              const tooltipAlign = i < 3 ? 'left' : (i > days.length - 4 ? 'right' : 'center')
              const tooltipLeft = tooltipAlign === 'left' ? '0%' : (tooltipAlign === 'right' ? '100%' : '50%')
              const tooltipTransform = tooltipAlign === 'left' ? 'translateX(0)' : (tooltipAlign === 'right' ? 'translateX(-100%)' : 'translateX(-50%)')

              return (
                <button
                  key={d.k}
                  data-day-key={d.k}
                  disabled={d.future}
                  onClick={() => { setSelDay(d.k); setActiveTooltipKey(prev => prev === d.k ? null : d.k); }}
                  onMouseEnter={() => setHoveredKey(d.k)}
                  onMouseLeave={() => setHoveredKey(null)}
                  onFocus={() => setHoveredKey(d.k)}
                  onBlur={() => setHoveredKey(null)}
                  style={{
                    position: 'relative',
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: d.future ? 'default' : 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{
                    width: '100%', maxWidth: thin ? 16 : 44, height: h,
                    borderRadius: thin ? 3 : 6,
                    background: d.future ? 'transparent' : (d.min <= 0 ? 'var(--track)' : barColor),
                    boxShadow: sel ? 'inset 0 0 0 1.5px var(--text)' : (isHovered ? `0 0 8px ${barColor}` : 'none'),
                    filter: isHovered ? 'brightness(1.3)' : 'none',
                    opacity: isHovered ? 1 : (d.min <= 0 ? 0.35 : 0.88),
                    transition: 'opacity .15s ease, filter .15s ease, box-shadow .15s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {!d.future && d.min >= STREAK_MIN_MIN && h > 24 && (
                      <span style={{ fontSize: thin ? 7.5 : 12, fontWeight: 900, color: '#fff', lineHeight: 1 }}>✓</span>
                    )}
                  </span>
                  
                  {/* Custom In-App Tooltip */}
                  {isHovered && (
                    <div className="vc-custom-tooltip" style={{
                      position: 'absolute', bottom: '100%', left: tooltipLeft, transform: tooltipTransform, marginBottom: 8, zIndex: 100, pointerEvents: 'none',
                      background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', minWidth: 150,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.18)', textAlign: 'left'
                    }}>
                      <p style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'capitalize', margin: '0 0 4px' }}>
                        {new Date(d.ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', weekday: 'long' })}
                      </p>
                      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                        Tiempo: {d.min >= 60 ? `${Math.floor(d.min / 60)}h ${Math.round(d.min % 60)}m` : `${Math.round(d.min)} min`}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                        Objetivo: {STREAK_MIN_MIN >= 60 ? `${Math.floor(STREAK_MIN_MIN / 60)}h` : `${STREAK_MIN_MIN} min`}
                      </p>
                      <p style={{ fontSize: 11, fontWeight: 700, color: d.min >= STREAK_MIN_MIN ? '#30d158' : '#ff9f0a', margin: '2px 0 0' }}>
                        Variación: {varText}
                      </p>
                      <div style={{ position: 'absolute', top: '100%', left: tooltipAlign === 'left' ? '15%' : (tooltipAlign === 'right' ? '85%' : '50%'), transform: 'translateX(-50%)', border: '5px solid transparent', borderTopColor: 'var(--border)' }} />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
          <div style={{ position: 'absolute', left: 0, right: 34, bottom: goalY, borderTop: '1.5px dashed var(--text)', opacity: 0.45, pointerEvents: 'none', zIndex: 5 }} />
          <span style={{ position: 'absolute', right: 0, bottom: goalY - 6, fontSize: 10.5, fontWeight: 800, color: 'var(--text-soft)', pointerEvents: 'none', zIndex: 5 }}>2 h</span>
        </div>
        <div style={{ display: 'flex', gap: thin ? 2 : 6, marginTop: 8, marginRight: 34 }}>
          {days.map((d, i) => {
            const hrText = d.future || d.min <= 0 ? '' : `${(d.min / 60).toFixed(1)}h`
            const isTickVisible = !thin || (i === 0 || (i + 1) % 5 === 0 || i === days.length - 1)
            return (
              <span key={d.k} style={{ flex: 1, minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {isTickVisible ? (
                  <span style={{ fontSize: thin ? 9 : 11.5, fontWeight: 700, color: d.k === c.todayKey ? '#ff3b30' : 'var(--text-soft)' }}>
                    {thin ? String(i + 1) : labels(d, i)}
                  </span>
                ) : (
                  <span style={{ height: thin ? 11 : 14 }} />
                )}
                {!thin && !d.future && d.min > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                    {hrText}
                  </span>
                )}
              </span>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Línea de 24 h del día elegido: CUÁNDO registraste, coloreado por tipo ──
  const DayTimeline = ({ showTitle }) => {
    const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark'
    const [hover, setHover] = useState(false)
    const [hoveredStrip, setHoveredStrip] = useState(null)
    const dayStart = new Date(dk + 'T00:00:00').getTime()
    const segs = []
    let fcMin = null, fcMax = null, fcSum = 0, fcN = 0
    ;(sessions || []).forEach(s => {
      const a = Math.max(s.start, dayStart), b = Math.min(s.end, dayStart + 86400000)
      if (b <= a) return
      const sleep = nightOverlapMin(s.start, s.end) >= 60 && s.avgHr > 0 && s.avgHr < 75
      const type = sleep ? 'sueño' : (s.avgHr >= 100 ? 'entrenamiento' : 'registro')
      segs.push({ from: (a - dayStart) / 864000, w: (b - a) / 864000, type })
      if (s.avgHr > 0) { fcSum += s.avgHr; fcN++ }
      if (s.minHr > 0) fcMin = fcMin == null ? s.minHr : Math.min(fcMin, s.minHr)
      if (s.maxHr > 0) fcMax = fcMax == null ? s.maxHr : Math.max(fcMax, s.maxHr)
    })
    const COLORS = { 'sueño': '#5e5ce6', 'entrenamiento': '#ff9f0a', 'registro': '#34c759' }
    const types = [...new Set(segs.map(s => s.type))]
    const timelineRef = useRef(null)
    const curStripRef = useRef(null)

    const processTimelineTouch = (clientX) => {
      const el = timelineRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const relX = clientX - rect.left
      const clampedX = Math.max(0, Math.min(rect.width - 1, relX))
      const idx = Math.floor((clampedX / rect.width) * 4) // 4 franjas de 6 horas
      if (idx !== curStripRef.current) {
        curStripRef.current = idx
        setHoveredStrip(idx)
        try { navigator.vibrate?.(10) } catch (_) {}
      }
    }

    useEffect(() => {
      const el = timelineRef.current
      if (!el) return
      const onMove = (e) => {
        if (!e.touches || !e.touches[0]) return
        e.preventDefault()
        processTimelineTouch(e.touches[0].clientX)
      }
      const onStart = (e) => {
        if (!e.touches || !e.touches[0]) return
        processTimelineTouch(e.touches[0].clientX)
      }
      const onEnd = () => {
        curStripRef.current = null
        setHoveredStrip(null)
      }
      el.addEventListener('touchstart', onStart, { passive: true })
      el.addEventListener('touchmove', onMove, { passive: false })
      el.addEventListener('touchend', onEnd, { passive: true })
      el.addEventListener('touchcancel', onEnd, { passive: true })
      return () => {
        el.removeEventListener('touchstart', onStart)
        el.removeEventListener('touchmove', onMove)
        el.removeEventListener('touchend', onEnd)
        el.removeEventListener('touchcancel', onEnd)
      }
    }, [])

    return (
      <div
        className="vc-touch-scrubber"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); setHoveredStrip(null); }}
        style={{
          background: hover ? 'var(--chip-bg)' : 'var(--inset-bg)', borderRadius: 14, padding: '8px 12px', marginTop: 6,
          boxShadow: hover ? 'inset 0 0 0 1px var(--border-soft)' : 'none',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
      >
        {showTitle && (
          <p style={{ fontSize: 12, color: 'var(--text)', margin: '0 0 8px', fontWeight: 600, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
            <span style={{ textTransform: 'capitalize' }}>{dLabel}</span>
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> — {dmin > 0 ? `${fmtH(dmin)} · ${dses} sesi${dses !== 1 ? 'ones' : 'ón'}` : 'sin registro'}</span>
          </p>
        )}
        <div
          ref={timelineRef}
          onMouseMove={(e) => {
            const el = timelineRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const relX = e.clientX - rect.left
            const clampedX = Math.max(0, Math.min(rect.width - 1, relX))
            const idx = Math.floor((clampedX / rect.width) * 4)
            setHoveredStrip(idx)
          }}
          onMouseLeave={() => setHoveredStrip(null)}
          style={{ position: 'relative', height: 24, background: 'var(--track)', borderRadius: 6, overflow: 'visible', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
        >
          {/* 4 franjas de 6 horas que se oscurecen al pasar el mouse o dedo */}
          {[0, 1, 2, 3].map(idx => {
            const isHovered = hoveredStrip === idx
            
            // Calcular el solapamiento de sesiones con este intervalo de 6 horas
            const intervalStart = dayStart + idx * 6 * 3600000
            const intervalEnd   = dayStart + (idx + 1) * 6 * 3600000
            let overlapMs = 0
            ;(sessions || []).forEach(s => {
              const a = Math.max(s.start, intervalStart)
              const b = Math.min(s.end, intervalEnd)
              if (b > a) overlapMs += (b - a)
            })
            const overlapMin = Math.round(overlapMs / 60000)
            const isFilled = overlapMin >= 330 // Requiere al menos 5.5 horas de datos (llenar toda la franja)

            return (
              <span
                key={idx}
                data-strip-idx={idx}
                onClick={(e) => {
                  e.stopPropagation()
                  const startHour = idx * 6
                  const endHour = (idx + 1) * 6
                  onSelectTimeRange?.(dk, startHour, endHour)
                }}
                title={`Ver tramo de ${idx*6}h a ${(idx+1)*6}h (${overlapMin} min registrados)`}
                style={{
                  position: 'absolute',
                  left: `${idx * 25}%`,
                  width: '25%',
                  top: 0,
                  bottom: 0,
                  background: isHovered 
                    ? (isDarkTheme ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.22)')
                    : isFilled 
                      ? 'rgba(48, 209, 88, 0.45)' 
                      : 'transparent',
                  boxShadow: isHovered ? 'inset 0 0 0 1.5px #007aff' : 'none',
                  borderRight: idx < 3 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                  zIndex: 3,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Tooltip flotante en hover / toque */}
                {isHovered && (
                  <div className="vc-custom-tooltip" style={{
                    position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', zIndex: 100, pointerEvents: 'none',
                    background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', whiteSpace: 'nowrap',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.18)', textAlign: 'center'
                  }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                      {idx * 6}h a {(idx + 1) * 6}h · {overlapMin} min
                    </p>
                    <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', border: '4px solid transparent', borderTopColor: 'var(--border)' }} />
                  </div>
                )}
                {isFilled && (
                  <span style={{ 
                    fontSize: 11, 
                    fontWeight: 900, 
                    color: '#1ca345', 
                    background: '#ffffff',
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    lineHeight: 1
                  }}>
                    ✓
                  </span>
                )}
              </span>
            )
          })}
          {segs.map((s, i) => (
            <span key={i} style={{ position: 'absolute', left: `${s.from}%`, width: `${Math.max(0.7, s.w)}%`, top: 0, bottom: 0, background: COLORS[s.type], zIndex: 2 }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
          {['0', '6', '12', '18', '24 h'].map(t => <span key={t} style={{ fontSize: 8.5, fontWeight: 700, color: 'var(--text-soft)' }}>{t}</span>)}
        </div>
        {(types.length > 0 || fcN > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 7 }}>
            {types.map(t => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: COLORS[t] }} />{t}
              </span>
            ))}
            {fcN > 0 && <span style={{ fontSize: 10, color: 'var(--text-faint)', marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>FC prom {Math.round(fcSum / fcN)}{fcMin != null && fcMax != null ? ` · ${fcMin}–${fcMax}` : ''} bpm</span>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--card-bg)', borderRadius: 18, border: '1px solid var(--border)', padding: '16px 20px' }}>
      {/* ── Título ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8, margin: 0 }}>Cobertura</p>
        </div>
      </div>

      {/* Aclara la semana (rango de fechas de la semana actual) */}
      {view === 'week' && c.week7.length > 0 && (
        <p style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-soft)', margin: '-6px 0 10px 0' }}>
          Semana del {new Date(c.week7[0].ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} al {new Date(c.week7[6].ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
        </p>
      )}

      {/* ── OBJETIVO / PROGRESO (Period-level, shown at the top in all views) ── */}
      {(() => {
        // Racha info
        const hoy = c.minByDay[c.todayKey] || 0
        const rachaStatus = c.streak === 0
          ? { txt: hoy > 0 ? `Falta ${fmtH(Math.max(1, STREAK_MIN_MIN - hoy))} para racha` : 'Registrá 6 h hoy para racha', color: 'var(--text-faint)' }
          : c.atRisk
            ? { txt: `Falta ${fmtH(Math.max(1, STREAK_MIN_MIN - hoy))} para mantener racha`, color: '#ff9f0a' }
            : { txt: '✓ Racha asegurada', color: '#34c759' }

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginTop: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {/* Horas Registradas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
              <p style={{ fontSize: 34, fontWeight: 800, color: 'var(--text)', margin: 0, lineHeight: 1.1, letterSpacing: -0.5 }}>
                {fmtH(periodMin)}
              </p>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-faint)' }}>
                {labelLeft}
              </span>
            </div>

            {/* Barra de Progreso a la misma altura */}
            <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', margin: 0 }}>Progreso del objetivo</p>
                <p style={{ fontSize: 12.5, fontWeight: 700, color: barColor, margin: 0, textAlign: 'right' }}>
                  {labelRight}
                </p>
              </div>
              <div style={{ height: 10, background: 'var(--track)', borderRadius: 5, overflow: 'hidden', width: '100%' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 5, transition: 'width .4s ease' }} />
              </div>
              
              {/* Racha integrada debajo de la barra de progreso a la misma altura que la hora */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 2 }}>
                <span style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 600 }}>
                  Racha: <strong style={{ fontWeight: 800 }}>{c.streak}</strong> día{c.streak !== 1 ? 's' : ''} <span style={{ color: 'var(--text-faint)', fontWeight: 500 }}>(récord {c.best})</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: rachaStatus.color, textAlign: 'right' }}>
                  {rachaStatus.txt}
                </span>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── GRÁFICOS (Segundo en Día, primero en Semana/Mes) ── */}
      <div style={{ marginTop: view === 'day' ? 0 : 6 }}>
        {view === 'day' ? (
          <DayTimeline showTitle={false} />
        ) : view === 'week' ? (
          <GoalBars days={c.week7} labels={(d, i) => {
            const date = new Date(d.ms)
            const dd = String(date.getDate()).padStart(2, '0')
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            return `${dd}/${mm}`
          }} />
        ) : (
          <GoalBars days={c.monthDays} thin labels={(d) => {
            const date = new Date(d.ms)
            const dd = String(date.getDate()).padStart(2, '0')
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            // Stack vertically on mobile to prevent overlapping in monthly view
            if (window.innerWidth <= 640) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1, gap: 1 }}>
                  <span>{dd}</span>
                  <span style={{ fontSize: '6.5px', opacity: 0.65 }}>{mm}</span>
                </div>
              )
            }
            return `${dd}/${mm}`
          }} />
        )}
      </div>




      {/* ── Fila chica de estadísticas compactas (Cuarto en Semana/Mes) ── */}
      {view !== 'day' && (() => {
        const days    = (view === 'week' ? c.week7 : c.monthDays).filter(d => !d.future)
        const met     = days.filter(d => d.min >= STREAK_MIN_MIN).length
        const totMin  = days.reduce((a, d) => a + d.min, 0)
        const avg     = days.length ? totMin / days.length : 0
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10, background: 'var(--inset-bg)', borderRadius: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text)' }}>
              <strong style={{ fontWeight: 800 }}>{view === 'week' ? `${met}/7` : met}</strong> día{met !== 1 ? 's' : ''} cumplido{met !== 1 ? 's' : ''}
              <span style={{ color: 'var(--text-faint)' }}> · total {fmtH(totMin)} · prom. {fmtH(avg)}/día</span>
            </p>
          </div>
        )
      })()}



    </div>
  )
}

// ── Resumen tab ───────────────────────────────────────────────────────────────

const API_RECOVERY = apiBase

function ResumenTab({ token, user, onNavigate, refreshKey }) {
  // ── Sessions list → día / semana / mes summaries (todo como índices 0–100) ──
  const [sessions, setSessions] = useState(null)
  useEffect(() => {
    const mapSes = (arr) => (arr || []).map(s => ({
      start: new Date(s.start_time).getTime(), end: new Date(s.end_time).getTime(),
      avgHr: s.avg_hr, minHr: s.min_hr, maxHr: s.max_hr, rmssd: s.rmssd, steps: s.steps,
    }))
    const userId = user?.id ?? 'anon'
    const ck = `vc_resumen_segs_${userId}`
    const cached = _vcMem.get(ck)
    if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
      setSessions(cached.data)
      return
    }

    const since = Date.now() - 9999 * 24 * 3600 * 1000
    fetch(`${API_RECOVERY}/sync?since=${since}&sessionsOnly=1&_t=${Date.now()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(d => {
        saveSessionsOffline(uidOf(user), d.sessions || [])          // copia local para abrir sin señal
        const mapped = mapSes(d.sessions)
        _vcMem.set(ck, { ts: Date.now(), data: mapped })
        setSessions(mapped)
      })
      .catch(async () => {
        const off = await loadSessionsOffline(uidOf(user))          // sin señal: se usa la lista guardada
        const mapped = off ? mapSes(off) : []
        _vcMem.set(ck, { ts: Date.now(), data: mapped })
        setSessions(mapped)
      })
  }, [token, user, refreshKey])

  // Día ancla del resumen: HOY por defecto (el día actual en curso).
  // Declarado ANTES del useMemo que lo usa (si no, TDZ → pantalla rota).
  const [anchor, setAnchor] = useState(() => localDateKey(Date.now()))

  const periods = useMemo(() => {
    if (!sessions || !sessions.length) return null

    // Deduplicar sesiones (coincidencia de ±5 min de inicio) para evitar inflado de datos
    const richness = s => (s.rmssd ? 1e9 : 0) + (s.end - s.start)
    const deduped = []
    const sorted = [...sessions].sort((a, b) => a.start - b.start)
    sorted.forEach(s => {
      const dupIdx = deduped.findIndex(x => Math.abs(x.start - s.start) < 5 * 60 * 1000)
      if (dupIdx === -1) deduped.push(s)
      else if (richness(s) > richness(deduped[dupIdx])) deduped[dupIdx] = s
    })

    // Virtual split of midnight-crossing sessions for 100% data consistency with HistoryTab (Análisis)
    const splitDeduped = []
    deduped.forEach(s => {
      const startLocal = localDateKey(s.start)
      const endLocal   = localDateKey(s.end)
      if (startLocal !== endLocal && s.end > s.start) {
        // Split session across midnight
        const sStart = s.start
        const sEnd   = s.end
        
        // Midnight of the first day in local timezone
        const nextDay = new Date(startLocal + 'T12:00:00')
        nextDay.setDate(nextDay.getDate() + 1)
        const midnight = new Date(localDateKey(nextDay.getTime()) + 'T00:00:00').getTime()
        
        // First part (day 1)
        splitDeduped.push({
          ...s,
          id: s.id ? `${s.id}_part1` : undefined,
          start: sStart,
          end: midnight - 1000,
          originalSession: s,
          part: 1
        })
        
        // Second part (day 2)
        splitDeduped.push({
          ...s,
          id: s.id ? `${s.id}_part2` : undefined,
          start: midnight,
          end: sEnd,
          originalSession: s,
          part: 2
        })
      } else {
        splitDeduped.push(s)
      }
    })

    const med = arr => { const a = arr.filter(v => v > 0).sort((x, y) => x - y); return a.length ? a[Math.floor(a.length / 2)] : null }
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
    const rmssdBase = med(splitDeduped.map(s => s.rmssd))
    const hrBase    = med(splitDeduped.map(s => s.minHr))
    const isSleep   = s => nightOverlapMin(s.start, s.end) >= 60 && s.avgHr > 0 && s.avgHr < 75

    // ── Serie de RMSSD por día (mediana) → velocidad de recuperación ──
    const dayBuckets = {}
    splitDeduped.forEach(s => { if (s.rmssd > 0) (dayBuckets[localDateKey(s.start)] ||= []).push(s.rmssd) })
    const dayRmssd = {}
    Object.keys(dayBuckets).forEach(k => { dayRmssd[k] = med(dayBuckets[k]) })
    const sortedDays = Object.keys(dayRmssd).sort()
    const ord = d => Math.round(new Date(d + 'T12:00:00').getTime() / 86400000)
    const slope = pts => {
      const n = pts.length; if (n < 2) return null
      const sx = pts.reduce((a, p) => a + p.x, 0), sy = pts.reduce((a, p) => a + p.y, 0)
      const sxx = pts.reduce((a, p) => a + p.x * p.x, 0), sxy = pts.reduce((a, p) => a + p.x * p.y, 0)
      const den = n * sxx - sx * sx
      return den === 0 ? null : (n * sxy - sx * sy) / den
    }
    const velocityFor = (dayKeys) => {
      const ds = [...new Set(dayKeys)].filter(d => dayRmssd[d] != null).sort()
      if (ds.length >= 2) { const v = slope(ds.map(d => ({ x: ord(d), y: dayRmssd[d] }))); return v == null ? null : { ms: v, days: ds.length } }
      if (ds.length === 1) {
        const i = sortedDays.indexOf(ds[0])
        if (i > 0) { const cur = ds[0], prev = sortedDays[i - 1], gap = ord(cur) - ord(prev); if (gap > 0) return { ms: (dayRmssd[cur] - dayRmssd[prev]) / gap, days: 2, vsPrev: true } }
      }
      return null
    }

    const summarize = (subset, totalMin, partial) => {
      if (!subset.length) return null
      const recordedMin = Math.round(subset.reduce((a, s) => a + Math.max(0, s.end - s.start), 0) / 60000)
      const coverage    = totalMin > 0 ? Math.min(100, Math.round(recordedMin / totalMin * 100)) : null
      const sleepSess   = subset.filter(isSleep)
      const sleepMin    = Math.round(sleepSess.reduce((a, s) => a + nightOverlapMin(s.start, s.end), 0))
      const nights      = new Set(sleepSess.map(s => localDateKey(s.start))).size
      const sleepAvg    = sleepSess.length ? Math.round(sleepSess.reduce((a, s) => a + (s.avgHr || 0), 0) / sleepSess.length) : null
      const sleepPeak   = sleepSess.length ? Math.max(...sleepSess.map(s => s.maxHr || 0)) : 0
      const hadSpike    = sleepPeak > 0 && (sleepPeak > 110 || (sleepAvg && sleepPeak > sleepAvg + 25))
      const mean = (vals) => { const v = vals.filter(x => x > 0); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null }
      const scopeRmssd = mean(subset.map(s => s.rmssd))
      const scopeMinHr = mean(subset.map(s => s.minHr))
      const avgHr      = mean(subset.map(s => s.avgHr))
      const hrLows     = subset.map(s => s.minHr).filter(v => v >= 30 && v <= 220)
      const hrHighs    = subset.map(s => s.maxHr).filter(v => v >= 30 && v <= 220)
      const minHrAbs   = hrLows.length ? Math.min(...hrLows) : null
      const maxHrAbs   = hrHighs.length ? Math.max(...hrHighs) : null
      const trainSess  = subset.filter(s => !isSleep(s) && s.avgHr >= 100)
      const trainMin   = Math.round(trainSess.reduce((a, s) => a + Math.max(0, s.end - s.start), 0) / 60000)
      const steps      = subset.reduce((sum, s) => sum + (s.steps || 0), 0)
      let stress = null
      if (scopeRmssd && rmssdBase) {
        const hrvStress = clamp(50 - (scopeRmssd - rmssdBase) / rmssdBase * 50, 5, 95)
        const hrStress  = (scopeMinHr && hrBase) ? clamp(50 + (scopeMinHr - hrBase) / hrBase * 50, 5, 95) : 50
        stress = Math.round(hrvStress * 0.7 + hrStress * 0.3)
      }
      return {
        count: subset.length, days: new Set(subset.map(s => localDateKey(s.start))).size,
        recordedMin, totalMin, coverage, partial, sleepMin, nights, sleepPeak, hadSpike, sleepAvg, stress,
        avgHr: avgHr ? Math.round(avgHr) : null, minHr: scopeMinHr ? Math.round(scopeMinHr) : null,
        minHrAbs, maxHrAbs, trainMin, trainCount: trainSess.length, rmssd: scopeRmssd,
        steps,
      }
    }

    const validStarts = splitDeduped.map(s => s.start).filter(v => !isNaN(v) && v > 0)
    const lastStart   = validStarts.length ? Math.max(...validStarts) : Date.now()
    const validEnds   = splitDeduped.map(s => s.end).filter(v => !isNaN(v) && v > 0)
    const lastEnd     = validEnds.length ? Math.max(...validEnds) : Date.now()
    const lastDate    = localDateKey(lastEnd)              // último día CON datos (soportando sesiones nocturnas cruzadas) → botón de salto
    // El resumen se ancla en un día elegido (por defecto AYER, el último día completo)
    const anchorNoon = new Date(anchor + 'T12:00:00').getTime()
    const lastWeek  = weekKeyOf(anchor)
    const lastMonth = anchor.slice(0, 7)
    // ── Horas totales del período (transcurridas si está en curso) → cobertura ──
    const now        = Date.now()
    const dayStart   = new Date(anchor + 'T00:00:00').getTime()
    const dayEnd     = dayStart + 24 * 3600 * 1000
    const weekStart  = new Date(lastWeek + 'T00:00:00').getTime()
    const weekEnd    = weekStart + 7 * 24 * 3600 * 1000
    const [my, mm]   = lastMonth.split('-').map(Number)
    const monthStart = new Date(my, mm - 1, 1).getTime()
    const monthEnd   = new Date(my, mm, 1).getTime()
    const span = (start, end) => Math.max(1, Math.round((Math.min(now, end) - start) / 60000))
    const mk = (sub, totalMin, partial, scope, prev) => {
      const base = summarize(sub, totalMin, partial)
      if (!base) return null
      const vel = velocityFor(sub.map(s => localDateKey(s.start)))
      const velMs = vel?.ms ?? null
      const velPct = (velMs != null && rmssdBase) ? velMs / rmssdBase * 100 : null
      const ratio  = (base.rmssd && rmssdBase) ? base.rmssd / rmssdBase : null
      const trendScore = velPct != null ? clamp(50 + velPct * 10, 5, 95) : 50
      const levelScore = ratio  != null ? clamp(50 + (ratio - 1) * 100, 5, 95) : 50
      const recIndex = (velMs != null || ratio != null) ? Math.round(trendScore * 0.6 + levelScore * 0.4) : null
      // ── Todas las métricas como índice 0–100 ──
      const restScore  = (base.minHr && hrBase) ? clamp(50 - (base.minHr - hrBase) / hrBase * 100, 5, 95) : null
      const recovery   = (ratio != null || restScore != null)
        ? Math.round(levelScore * 0.5 + (restScore ?? 50) * 0.25 + trendScore * 0.25) : null
      const sleepHrs   = (scope === 'day' ? base.sleepMin : base.sleepMin / Math.max(1, base.nights)) / 60
      const sleepScore = base.sleepMin > 0 ? clamp(Math.round(sleepHrs / 8 * 100) - (base.hadSpike ? 15 : 0), 5, 100) : null
      const actTarget  = scope === 'day' ? 45 : scope === 'week' ? 210 : 900
      const actScore   = clamp(Math.round(base.trainMin / actTarget * 100), 0, 100)
      const fcScore    = restScore != null ? Math.round(restScore) : null
      return {
        ...base, prev: prev || null,
        velMs, velDays: vel?.days ?? 0, velVsPrev: !!vel?.vsPrev, velPct,
        ratioPct: ratio != null ? Math.round((ratio - 1) * 100) : null,
        recIndex, trendScore: Math.round(trendScore), levelScore: Math.round(levelScore),
        recovery, sleepScore, actScore, fcScore,
      }
    }
    // ── Períodos anteriores (para comparar) ──
    const allDays      = [...new Set(splitDeduped.map(s => localDateKey(s.start)))].sort()
    const prevDayKey   = allDays.filter(d => d < anchor).pop()
    const dayPrev      = prevDayKey ? splitDeduped.filter(s => localDateKey(s.start) === prevDayKey) : []
    const prevWeekMon  = localDateKey(weekStart - 7 * 86400000)
    const weekPrev     = splitDeduped.filter(s => weekKeyOf(localDateKey(s.start)) === prevWeekMon)
    const prevMonthKey = localMonthKey(new Date(my, mm - 2, 1).getTime())
    const monthPrev    = splitDeduped.filter(s => localMonthKey(s.start) === prevMonthKey)
    const prevMonthDays = new Date(my, mm - 1, 0).getDate()
    return {
      lastStart, lastDate, lastWeek, anchorNoon,
      deduped: splitDeduped,
      day:   mk(splitDeduped.filter(s => localDateKey(s.start) === anchor), span(dayStart, dayEnd), now < dayEnd, 'day', mk(dayPrev, 1440, false, 'day')),
      week:  mk(splitDeduped.filter(s => weekKeyOf(localDateKey(s.start)) === lastWeek), span(weekStart, weekEnd), now < weekEnd, 'week', mk(weekPrev, 7 * 1440, false, 'week')),
      month: mk(splitDeduped.filter(s => localMonthKey(s.start) === lastMonth), span(monthStart, monthEnd), now < monthEnd, 'month', mk(monthPrev, prevMonthDays * 1440, false, 'month')),
    }
  }, [sessions, anchor])

  const [resumenPeriod, setResumenPeriod] = useState(() => localStorage.getItem('vc_resumen_period') || 'day')
  useEffect(() => {
    localStorage.setItem('vc_resumen_period', resumenPeriod)
  }, [resumenPeriod])

  const [exporting, setExporting] = useState(null)   // 'copy' | 'copied' | 'save' | 'saved' | null
  const viewRef = useRef(null)

  const fmtDur = (min) => {
    if (!min) return '—'
    if (min < 60) return `${min} min`
    return `${Math.floor(min / 60)}h${min % 60 > 0 ? ` ${min % 60}m` : ''}`
  }
  const card      = { background: 'var(--card-bg)', borderRadius: 18, border: '1px solid var(--border)', padding: '16px 20px' }
  const exportBtn = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '7px 13px', fontSize: 12.5, fontWeight: 700, color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit' }
  const R = 80, C = 2 * Math.PI * R

  // ── Cada métrica → etiqueta + color por índice (prioriza color/tamaño en lo importante) ──
  const recoveryInfo = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 70 ? { label: 'Recuperado', color: '#30d158' } : v >= 50 ? { label: 'Estable', color: '#007aff' } : v >= 30 ? { label: 'Cansado', color: '#ff9f0a' } : { label: 'Agotado', color: '#ff453a' }
  const stressInfo   = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v < 35 ? { label: 'Bajo', color: '#30d158' } : v < 55 ? { label: 'Normal', color: '#007aff' } : v < 75 ? { label: 'Elevado', color: '#ff9f0a' } : { label: 'Alto', color: '#ff453a' }
  const recIndexInfo = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 65 ? { label: 'Rápida', color: '#30d158' } : v >= 45 ? { label: 'Estable', color: '#007aff' } : v >= 30 ? { label: 'Lenta', color: '#ff9f0a' } : { label: 'Muy lenta', color: '#ff453a' }
  const sleepIdxInfo = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 70 ? { label: 'Bueno', color: '#30d158' } : v >= 50 ? { label: 'Aceptable', color: '#007aff' } : v >= 30 ? { label: 'Escaso', color: '#ff9f0a' } : { label: 'Muy poco', color: '#ff453a' }
  const actInfo      = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 70 ? { label: 'Activo', color: '#30d158' } : v >= 40 ? { label: 'Moderado', color: '#007aff' } : v >= 15 ? { label: 'Bajo', color: '#ff9f0a' } : { label: 'Sedentario', color: '#ff453a' }
  const covInfo      = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 40 ? { label: 'Alta', color: '#30d158' } : v >= 20 ? { label: 'Media', color: '#007aff' } : v >= 8 ? { label: 'Baja', color: '#ff9f0a' } : { label: 'Muy baja', color: '#ff453a' }
  const fcInfo       = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 70 ? { label: 'Excelente', color: '#30d158' } : v >= 50 ? { label: 'Bueno', color: '#007aff' } : v >= 30 ? { label: 'Normal', color: '#ff9f0a' } : { label: 'Elevado', color: '#ff453a' }
  const stepsInfo    = (v) => v == null ? { label: 'sin datos', color: 'var(--text-soft)' } : v >= 100 ? { label: 'Excelente', color: '#30d158' } : v >= 70 ? { label: 'Bueno', color: '#007aff' } : v >= 40 ? { label: 'Activo', color: '#ff9f0a' } : { label: 'Bajo', color: '#ff453a' }

  // ── Exportar la sección (copiar / descargar PNG) ──
  // Siempre con un aviso claro de QUÉ pasó y DÓNDE quedó. Dentro de la app
  // (WebView) la descarga clásica falla muda → se abre el menú de compartir del
  // teléfono (el usuario elige guardar/mandar); si tampoco está, se avisa.
  const [exportNote, setExportNote] = useState(null)
  useEffect(() => {
    if (!exportNote) return
    const t = setTimeout(() => setExportNote(null), 8000)
    return () => clearTimeout(t)
  }, [exportNote])
  const exportView = async (mode) => {
    if (!viewRef.current) return
    setExporting(mode)
    const opts = { backgroundColor: 'var(--chip-bg)', pixelRatio: 2, cacheBust: true }
    const inShell = !!(window.__MANABI_SHELL__ || window.__VITALCORE_SHELL__ || window.ReactNativeWebView)
    try {
      const blob = await domToBlob(viewRef.current, opts)
      if (!blob) throw new Error('No se pudo generar la imagen')
      const file = new File([blob], `manabi-resumen-${resumenPeriod}.png`, { type: 'image/png' })
      const canShareFile = typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })
      if (mode === 'copy') {
        if (!inShell && navigator.clipboard?.write) {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
          setExporting('copied'); setExportNote({ ok: true, text: '✓ Imagen copiada — pegala donde quieras' })
        } else if (canShareFile) {
          await navigator.share({ files: [file], title: 'Resumen Manabi' })
          setExporting('copied'); setExportNote({ ok: true, text: '✓ Se abrió el menú del teléfono — elegí a dónde mandarla' })
        } else throw new Error('COPY_UNSUPPORTED')
      } else {
        if (inShell) {
          if (canShareFile) {
            await navigator.share({ files: [file], title: 'Resumen Manabi' })
            setExporting('saved'); setExportNote({ ok: true, text: '✓ Se abrió el menú del teléfono — elegí dónde guardarla' })
          } else {
            setExportNote({ ok: false, text: 'En la app todavía no se puede descargar la imagen — sacá una captura de pantalla, o abrí manabi.cagliero.ar desde el navegador' })
          }
        } else {
          const url = await domToPng(viewRef.current, opts)
          const a = document.createElement('a'); a.href = url; a.download = `resumen-${resumenPeriod}.png`; a.click()
          setExporting('saved'); setExportNote({ ok: true, text: '✓ Imagen descargada — buscala en tu carpeta Descargas' })
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') { /* cerró el menú de compartir: nada que avisar */ }
      else if (e?.name === 'NotAllowedError') setExportNote({ ok: false, text: 'El teléfono no dejó abrir el menú — tocá de nuevo (la primera vez a veces tarda)' })
      else if (e?.message === 'COPY_UNSUPPORTED') setExportNote({ ok: false, text: 'Acá no se puede copiar la imagen — usá el botón ↓' })
      else setExportNote({ ok: false, text: 'No se pudo exportar la imagen' })
      setExporting(null)
    }
    setTimeout(() => setExporting(null), 1600)
  }

  if (sessions == null) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 90, color: 'var(--text-faint)' }}>
      <div style={{ width: 26, height: 26, border: '2px solid var(--field-border)', borderTopColor: 'var(--text-muted)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ fontSize: 13, margin: 0 }}>Cargando tu resumen…</p>
    </div>
  )
  if (!periods) return (
    <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
      <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px' }}>Todavía no hay datos</p>
      <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: '0 0 16px' }}>Hacé tu primera medición para ver tu resumen diario, semanal y mensual.</p>
      <button onClick={() => onNavigate('monitor')} style={{ background: 'linear-gradient(135deg, #ff3b30, #ff6b6b)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Hacer primera medición</button>
    </div>
  )

  const s = periods[resumenPeriod]
  const subtitle = resumenPeriod === 'day'
    ? new Date(periods.anchorNoon).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
    : resumenPeriod === 'week' ? weekFullLabel(periods.lastWeek).label
    : new Date(anchor.slice(0, 7) + '-15T12:00:00').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
  const periodWord = resumenPeriod === 'day' ? 'día' : resumenPeriod === 'week' ? 'semana' : 'mes'

  // ── Tile de un índice (etiqueta + número grande + barra + delta vs anterior + info) ──
  const IndexTile = ({ name, v, prev, info, higherBetter, raw, suffix, description }) => {
    const [showInfo, setShowInfo] = useState(false)
    const { label, color } = info(v)
    const d = (v != null && prev != null) ? v - prev : null
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 14, padding: '11px 12px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            {description && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowInfo(o => !o) }}
                title="Ver qué significa y cómo se calcula"
                style={{
                  background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer',
                  color: showInfo ? 'var(--text)' : 'var(--text-fainter)', display: 'flex', alignItems: 'center',
                  transition: 'color 0.15s'
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </button>
            )}
          </div>
          {d != null && d !== 0 && (() => { const good = higherBetter ? d > 0 : d < 0; return <span style={{ fontSize: 10, fontWeight: 800, color: good ? '#30d158' : '#ff453a', flexShrink: 0 }}>{d > 0 ? '▲' : '▼'}{Math.abs(d)}</span> })()}
        </div>

        {/* Ventana flotante de información (no modifica el diseño ni la altura de la tarjeta) */}
        {showInfo && description && (
          <>
            <div
              onClick={(e) => { e.stopPropagation(); setShowInfo(false) }}
              style={{ position: 'fixed', inset: 0, zIndex: 70 }}
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 8,
                left: 6,
                right: 6,
                zIndex: 80,
                background: 'var(--card-bg)',
                border: '1px solid var(--border-strong)',
                borderRadius: 12,
                padding: '9px 11px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                backdropFilter: 'blur(16px)',
                animation: 'fadeIn 0.15s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{name}</span>
                <button
                  type="button"
                  onClick={() => setShowInfo(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 13, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text)', margin: 0, lineHeight: 1.45 }}>
                {description}
              </p>
            </div>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 27, fontWeight: 800, color, lineHeight: 1 }}>{v ?? '—'}{v != null && suffix ? suffix : ''}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color }}>{label}</span>
        </div>
        <div style={{ height: 5, background: 'var(--chip-bg)', borderRadius: 3, overflow: 'hidden', margin: '7px 0 5px' }}>
          <div style={{ width: `${v != null ? Math.min(100, v) : 0}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .6s ease' }} />
        </div>
        <p style={{ fontSize: 10.5, color: 'var(--text-soft)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{raw || '—'}</p>
      </div>
    )
  }

  // ── Hero: índice de Recuperación con anillo e info ──
  const HeroRecovery = ({ s }) => {
    const [showInfo, setShowInfo] = useState(false)
    const v = s?.recovery ?? null
    const { label, color } = recoveryInfo(v)
    const d = (v != null && s?.prev?.recovery != null) ? v - s.prev.recovery : null
    const dash = `${((v ?? 0) / 100) * C} ${C}`
    const tip = v == null ? 'Necesitás registrar datos en este período para calcular tu recuperación.'
      : v >= 70 ? 'Tu cuerpo está listo para entrenar fuerte.'
      : v >= 50 ? 'Recuperación estable — entrená a intensidad moderada.'
      : v >= 30 ? 'Vas algo cansado — bajá la intensidad hoy.'
      : 'Tu cuerpo pide descanso — evitá esfuerzos intensos.'
    return (
      <div style={{ ...card, background: `linear-gradient(135deg, ${color}15, var(--card-bg) 65%)`, borderColor: `${color}33`, position: 'relative' }}>
        {/* Ventana flotante de información para Recuperación */}
        {showInfo && (
          <>
            <div
              onClick={(e) => { e.stopPropagation(); setShowInfo(false) }}
              style={{ position: 'fixed', inset: 0, zIndex: 70 }}
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                right: 12,
                zIndex: 80,
                background: 'var(--card-bg)',
                border: '1px solid var(--border-strong)',
                borderRadius: 14,
                padding: '12px 14px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                backdropFilter: 'blur(16px)',
                animation: 'fadeIn 0.15s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Recuperación</span>
                <button
                  type="button"
                  onClick={() => setShowInfo(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 14, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text)', margin: 0, lineHeight: 1.45 }}>
                Qué tan listo está tu cuerpo: 50% tu HRV vs basal + 25% pulso de reposo + 25% tendencia de HRV. Más alto = más recuperado.
              </p>
            </div>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', width: 78, height: 78, flexShrink: 0 }}>
            <svg width="78" height="78" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r={R} fill="none" stroke="var(--track)" strokeWidth="18" />
              <circle cx="100" cy="100" r={R} fill="none" stroke={color} strokeWidth="18" strokeLinecap="round" strokeDasharray={dash} transform="rotate(-90 100 100)" style={{ transition: 'stroke-dasharray 1s ease' }} />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 27, fontWeight: 800, color, lineHeight: 1 }}>{v ?? '—'}</span>
              <span style={{ fontSize: 8, color: 'var(--text-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>índice</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8, margin: 0 }}>Recuperación</p>
                <button
                  type="button"
                  onClick={() => setShowInfo(o => !o)}
                  title="Ver qué significa y cómo se calcula"
                  style={{
                    background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer',
                    color: showInfo ? 'var(--text)' : 'var(--text-fainter)', display: 'flex', alignItems: 'center',
                    transition: 'color 0.15s'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </button>
              </div>
              {d != null && d !== 0 && <span style={{ fontSize: 10.5, fontWeight: 800, color: d > 0 ? '#30d158' : '#ff453a' }}>{d > 0 ? '▲' : '▼'}{Math.abs(d)}</span>}
            </div>

            <p style={{ fontSize: 21, fontWeight: 800, color, margin: '2px 0 3px', letterSpacing: -0.3 }}>{label}</p>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>{tip}</p>
          </div>
        </div>
      </div>
    )
  }

  const sleepRaw = s?.sleepMin > 0
    ? (resumenPeriod === 'day' ? fmtDur(s.sleepMin) : `${fmtDur(Math.round(s.sleepMin / Math.max(1, s.nights)))}/noche`) + (s.hadSpike ? ` (pico ${s.sleepPeak} bpm)` : '')
    : 'sin registro'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 'calc(100vh - 84px)', flex: 1 }}>

      {/* ── Selector de período: las 3 secciones (compartido con Análisis) ── */}
      <PeriodToggle value={resumenPeriod} onChange={setResumenPeriod} />

      {/* ── Encabezado compacto: fecha en UNA línea (menos ruido) ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '2px 2px 2px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: 0, textTransform: 'capitalize', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</p>
      </div>

      {/* Qué pasó con la imagen (copiada / descargada / no se pudo) */}
      {exportNote && (
        <p style={{ fontSize: 12, fontWeight: 600, color: exportNote.ok ? '#30d158' : '#b45309', background: exportNote.ok ? '#f0faf2' : '#fff8e6', border: `1px solid ${exportNote.ok ? '#c9ecd2' : '#ffe1a6'}`, borderRadius: 10, padding: '8px 12px', margin: '0 0 2px', lineHeight: 1.45 }}>
          {exportNote.text}
        </p>
      )}

      {/* ── Vista exportable de la sección: las tarjetas SIEMPRE visibles ── */}
      <div ref={viewRef} style={{ display: 'flex', flexDirection: 'column', gap: 12, background: 'transparent' }}>
        {/* ── Constancia primero: lo más accionable del resumen ── */}
        <ConstanciaCard sessions={periods.deduped} initialView={resumenPeriod} activeDate={anchor} onSelectTimeRange={(dateIso, startHour, endHour) => {
          window.__pendingFocusRange = { dateIso, startHour, endHour }
          onNavigate('historial')
        }} />

        {(() => {
          const safeS = s || {}
          const dailyAvgSteps = safeS.steps ? Math.round(safeS.steps / Math.max(1, safeS.days || 1)) : 0
          const stepsScore = safeS.steps > 0 ? Math.min(100, Math.round(dailyAvgSteps / 10000 * 100)) : null

          const prevDailyAvgSteps = safeS.prev?.steps ? Math.round(safeS.prev.steps / Math.max(1, safeS.prev.days || 1)) : 0
          const prevStepsScore = safeS.prev?.steps > 0 ? Math.min(100, Math.round(prevDailyAvgSteps / 10000 * 100)) : null
          
          const stepsRaw = safeS.steps
            ? `${safeS.steps.toLocaleString()} pasos ${resumenPeriod === 'day' ? 'totales' : 'prom. diario'}`
            : 'sin registro'

          return (
            <>
              <HeroRecovery s={safeS} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                <IndexTile
                  name="Estrés"
                  v={safeS.stress ?? null}
                  prev={safeS.prev?.stress}
                  info={stressInfo}
                  higherBetter={false}
                  raw={safeS.ratioPct != null ? `HRV ${safeS.ratioPct > 0 ? '+' : ''}${safeS.ratioPct}% vs basal` : (safeS.avgHr ? `${safeS.avgHr} bpm prom` : 'sin registro')}
                  description="Activación del sistema nervioso simpático: 70% tu HRV vs basal + 30% pulso de reposo elevado. Más bajo = más relajado."
                />
                <IndexTile
                  name="Velocidad recup."
                  v={safeS.recIndex ?? null}
                  prev={safeS.prev?.recIndex}
                  info={recIndexInfo}
                  higherBetter={true}
                  raw={safeS.velMs != null ? `${safeS.velMs > 0 ? '+' : ''}${Math.round(safeS.velMs)} ms/día` : 'sin registro'}
                  description="Qué tan rápido mejorás día a día: 60% la pendiente de tu HRV día a día + 40% tu nivel de HRV vs basal."
                />
                <IndexTile
                  name="Sueño"
                  v={safeS.sleepScore ?? null}
                  prev={safeS.prev?.sleepScore}
                  info={sleepIdxInfo}
                  higherBetter={true}
                  raw={sleepRaw}
                  description="Calidad del sueño: horas dormidas vs 8 h objetivo, con penalización si hubo picos de pulso. Requiere sesiones nocturnas con FC < 75 bpm."
                />
                <IndexTile
                  name="Actividad"
                  v={safeS.actScore ?? null}
                  prev={safeS.prev?.actScore}
                  info={actInfo}
                  higherBetter={true}
                  raw={safeS.trainMin ? `${fmtDur(safeS.trainMin)} · ${safeS.trainCount} ses.` : 'sin registro'}
                  description={`Entrenamiento vs objetivo (${resumenPeriod === 'day' ? '45 min/día' : resumenPeriod === 'week' ? '210 min/sem' : '900 min/mes'}). Cuenta sesiones con FC ≥ 100 bpm.`}
                />
                <IndexTile
                  name="FC reposo"
                  v={safeS.fcScore ?? null}
                  prev={safeS.prev?.fcScore}
                  info={fcInfo}
                  higherBetter={true}
                  raw={safeS.minHrAbs != null ? `${safeS.minHrAbs}–${safeS.maxHrAbs ?? '—'} bpm` : 'sin registro'}
                  description="Tu pulso de reposo comparado con tu frecuencia basal histórica. Más bajo = mejor adaptación aeróbica."
                />
                <IndexTile
                  name="Pasos"
                  v={stepsScore}
                  prev={prevStepsScore}
                  info={stepsInfo}
                  higherBetter={true}
                  raw={stepsRaw}
                  description="Pasos acumulados en el período frente al objetivo de referencia de 10.000 pasos diarios."
                />
              </div>
            </>
          )
        })()}
      </div>

      {/* Botones minimalistas de exportación pegados al bottom */}
      <div style={{
        marginTop: 'auto',
        position: 'sticky',
        bottom: 0,
        background: 'var(--nav-bg)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid var(--border-soft)',
        padding: '12px 0',
        display: 'flex',
        justifyContent: 'center',
        gap: 24,
        zIndex: 40,
        width: '100%',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => exportView('save')}
          disabled={!!exporting}
          style={{
            background: 'var(--chip-bg)', border: '1px solid var(--border-soft)', borderRadius: 7,
            color: 'var(--text-faint)', fontSize: 10.5, fontWeight: 600,
            padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
            opacity: exporting ? 0.5 : 1, transition: 'color 0.2s'
          }}
          onMouseEnter={e => { if (!exporting) e.currentTarget.style.color = 'var(--text)' }}
          onMouseLeave={e => { if (!exporting) e.currentTarget.style.color = 'var(--text-faint)' }}
        >
          {exporting === 'saved' ? '✓ Descargado' : '↓ PNG'}
        </button>
        <button
          onClick={() => window.print()}
          style={{
            background: 'var(--chip-bg)', border: '1px solid var(--border-soft)', borderRadius: 7,
            color: 'var(--text-faint)', fontSize: 10.5, fontWeight: 600,
            padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
            transition: 'color 0.2s'
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)' }}
        >
          PDF
        </button>
      </div>

    </div>
  )
}

// ── Widget Flotante Arrastrable (Drag & Drop en pantalla) ─────────────────────
function FloatingLivePill({ displayHr, zone, sessionTime, onNavigate }) {
  const isDarkTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  const [pos, setPos] = useState(() => {
    const isClient = typeof window !== 'undefined'
    // Ubicación por defecto: abajo en el centro de la pantalla
    const pillWidth = 210
    const defaultX = isClient ? Math.max(10, Math.round((window.innerWidth - pillWidth) / 2)) : 100
    const defaultY = isClient ? Math.max(10, window.innerHeight - 95) : 500
    return { x: defaultX, y: defaultY }
  })

  const [isHovered, setIsHovered] = useState(false)
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef({ startX: 0, startY: 0, posX: 0, posY: 0 })
  const movedRef = useRef(false)

  const handlePointerDown = (e) => {
    isDraggingRef.current = true
    movedRef.current = false
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    dragStartRef.current = {
      startX: clientX,
      startY: clientY,
      posX: pos.x,
      posY: pos.y,
    }

    const onPointerMove = (ev) => {
      if (!isDraggingRef.current) return
      const curX = ev.touches ? ev.touches[0].clientX : ev.clientX
      const curY = ev.touches ? ev.touches[0].clientY : ev.clientY
      const dx = curX - dragStartRef.current.startX
      const dy = curY - dragStartRef.current.startY

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        movedRef.current = true
      }

      const nextX = Math.max(10, Math.min(window.innerWidth - 210, dragStartRef.current.posX + dx))
      const nextY = Math.max(10, Math.min(window.innerHeight - 65, dragStartRef.current.posY + dy))
      setPos({ x: nextX, y: nextY })
    }

    const onPointerUp = () => {
      isDraggingRef.current = false
      window.removeEventListener('mousemove', onPointerMove)
      window.removeEventListener('mouseup', onPointerUp)
      window.removeEventListener('touchmove', onPointerMove)
      window.removeEventListener('touchend', onPointerUp)
    }

    window.addEventListener('mousemove', onPointerMove, { passive: true })
    window.addEventListener('mouseup', onPointerUp)
    window.addEventListener('touchmove', onPointerMove, { passive: false })
    window.addEventListener('touchend', onPointerUp)
  }

  const handleClick = (e) => {
    if (movedRef.current) {
      e.stopPropagation()
      return
    }
    onNavigate('monitor')
  }

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
      onClick={handleClick}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 9999,
        background: isDarkTheme ? 'rgba(15, 20, 32, 0.55)' : 'rgba(255, 255, 255, 0.65)',
        opacity: isHovered ? 0.98 : 0.60,
        border: `1.5px solid ${zone?.color ? `${zone.color}99` : 'var(--border)'}`,
        borderRadius: 16,
        padding: '7px 12px',
        boxShadow: '0 4px 18px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        cursor: 'grab',
        touchAction: 'none',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        animation: 'fadeIn 0.25s ease',
        transition: 'opacity 0.25s ease, background 0.25s ease, box-shadow 0.25s ease',
      }}
      title="Arrastrá para mover. Clic para volver a Nueva sesión."
    >
      {/* Corazón latiendo + BPM */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
        <span style={{ display: 'inline-block', animation: displayHr ? 'pip-pulse 1s ease-in-out infinite' : 'none' }}>
          <IconHeart size={18} color={zone?.color || '#ff3b30'} fill={zone?.color || '#ff3b30'} />
        </span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontSize: 20, fontWeight: 900, color: zone?.color || 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {displayHr ?? '–'}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-faint)', fontWeight: 700 }}>bpm</span>
        </div>
      </div>

      {/* Separador vertical */}
      <div style={{ width: 1, height: 22, background: 'var(--border)', pointerEvents: 'none' }} />

      {/* Tiempo de Sesión (sin humedad) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'none' }}>
        <IconClock size={11.5} color="var(--text-faint)" />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', fontVariantNumeric: 'tabular-nums' }}>
          {sessionTime}
        </span>
      </div>

      {/* Botón Ver vivo */}
      <span style={{
        fontSize: 10, fontWeight: 800, color: '#007aff',
        background: 'rgba(0,122,255,0.12)', padding: '3px 8px', borderRadius: 7,
        marginLeft: 2, pointerEvents: 'none'
      }}>
        Ver vivo →
      </span>
    </div>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  const auth    = useAuth()
  const location = useLocation()
  const polar   = usePolarH10()
  const demo    = useDemoMode()
  const offline = usePolarOffline()
  const bridge  = useBridge()
  const navigate = useNavigate()

  const [theme, setTheme] = useState(() => { try { return localStorage.getItem('vc_theme') === 'dark' ? 'dark' : 'light' } catch (_) { return 'light' } })
  const dark = theme === 'dark'

  const isSessionActive = (polar.status === 'connected' && polar.skinContact !== false && Boolean(polar.hr)) || demo.active
  const sessionHistoryCount = demo.active ? demo.history.length : polar.hrHistory.length
  const sessionTimer  = useSessionTimer(isSessionActive, sessionHistoryCount)
  const sessionTime   = sessionTimer.fmt
  const sessionSec    = sessionTimer.sec
  const pip     = useDocumentPip(theme)
  const [profileHover, setProfileHover] = useState(false)

  // Auto PiP opcional al cambiar de pestaña (si el usuario lo tiene habilitado), pero NUNCA auto-cerrar al cambiar de ventana
  useEffect(() => {
    const onVisibility = () => {
      const autoPip = localStorage.getItem('vc_auto_pip') !== 'false'
      if (document.visibilityState === 'hidden' && isSessionActive && pip.supported && !pip.pipWin && autoPip) {
        pip.open(240, 150).catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isSessionActive, pip])

  // Lifted period/date selection states so they persist when navigating between tabs
  const [periodMode,     setPeriodMode]     = useState(() => localStorage.getItem('vc_period_mode') || 'day')   // 'day' | 'week' | 'month'
  const [selectedDate,   setSelectedDate]   = useState(() => localStorage.getItem('vc_selected_date') || null)
  const [selectedWeek,   setSelectedWeek]   = useState(() => localStorage.getItem('vc_selected_week') || null)    // 'YYYY-Www'
  const [selectedMonth,  setSelectedMonth]  = useState(() => localStorage.getItem('vc_selected_month') || null)    // 'YYYY-MM'

  useEffect(() => {
    localStorage.setItem('vc_period_mode', periodMode)
  }, [periodMode])

  useEffect(() => {
    if (selectedDate) localStorage.setItem('vc_selected_date', selectedDate)
  }, [selectedDate])

  useEffect(() => {
    if (selectedWeek) localStorage.setItem('vc_selected_week', selectedWeek)
  }, [selectedWeek])

  useEffect(() => {
    if (selectedMonth) localStorage.setItem('vc_selected_month', selectedMonth)
  }, [selectedMonth])

  const rawTab = location.pathname.replace('/', '') || 'resumen'
  // /exportar sigue existiendo pero vive adentro de Perfil (abre esa subvista)
  const tab = rawTab === 'personas' || rawTab === 'exportar' ? 'perfil' : rawTab === 'inicio' ? 'resumen' : rawTab
  const [meditationFullscreen, setMeditationFullscreen] = useState(false)
  const [scrubbedTab, setScrubbedTab] = useState(null)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  // Cerrar confirmación con tecla Escape
  useEffect(() => {
    if (!showLogoutConfirm) return
    const onKey = (e) => {
      if (e.key === 'Escape') setShowLogoutConfirm(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showLogoutConfirm])

  // Restaurar barra de navegación y salir de modo demo si navega fuera de sesión o meditación
  useEffect(() => {
    if (tab !== 'meditacion') {
      setMeditationFullscreen(false)
    }
    const isRealUser = !auth.user?.demo
    if (tab !== 'monitor' && tab !== 'meditacion' && isRealUser && demo.active) {
      demo.stop()
    }
  }, [tab, auth.user, demo])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try { localStorage.setItem('vc_theme', theme) } catch (_) {}
    // Dentro de la app nativa: avisa al caparazón para que la barra de estado
    // del teléfono acompañe el tema (íconos claros en modo noche y viceversa).
    try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ui:theme', dark: theme === 'dark' })) } catch (_) {}
  }, [theme])
  const setTab = (key) => {
    // Las sesiones cargadas se preservan en importMem cuando se navega internamente
    setMeditationFullscreen(false)

    if (key === 'monitor') {
      try { window.dispatchEvent(new CustomEvent('vc-reset-session-view')) } catch (_) {}
    }

    // Registrar silenciosamente el cambio de pestaña en la base de datos de auditoría
    if (auth.token) {
      const API_AUDIT = apiBase
      fetch(`${API_AUDIT}/audit/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
        body: JSON.stringify({ action: 'page_view', details: { tab: key } }),
      }).catch(() => {})
    }

    navigate('/' + key)
  }
  const [syncing,    setSyncing]    = useState(false)
  const [pendingDraft, setPendingDraft] = useState(null)
  const [savedNotice,  setSavedNotice]  = useState(null)
  // Badge "↓" en el nav mientras una descarga JSON corre en segundo plano
  const [navExportJob, setNavExportJob] = useState(false)
  useEffect(() => {
    const h = (e) => setNavExportJob(!!e.detail?.active)
    window.addEventListener('vc-export-job', h)
    return () => window.removeEventListener('vc-export-job', h)
  }, [])
  // Ningún mensaje queda pegado: el aviso de "sesión guardada" se va solo a los 10 s
  useEffect(() => {
    if (!savedNotice) return
    const t = setTimeout(() => setSavedNotice(null), 10_000)
    return () => clearTimeout(t)
  }, [savedNotice])
  const [uploadError,  setUploadError]  = useState(null)
  const [refreshKey,   setRefreshKey]   = useState(0)

  // Clear segment and summary caches from RAM memory and sessionStorage when refreshKey bumps (new uploads/saves/deletes)
  useEffect(() => {
    if (refreshKey > 0) {
      try {
        for (const k of [..._vcMem.keys()]) {
          if (k.startsWith('vc_segs') || k.startsWith('vc_day') || k.startsWith('vc_resumen_segs')) {
            _vcMem.delete(k)
          }
        }
      } catch (_) {}
      try {
        Object.keys(sessionStorage)
          .filter(k => k.startsWith('vc_segs') || k.startsWith('vc_day') || k.startsWith('vc_resumen_segs'))
          .forEach(k => sessionStorage.removeItem(k))
      } catch (_) {}
    }
  }, [refreshKey])

  const [navImport,    setNavImport]    = useState(importBus.state)   // import progress for nav badge
  useEffect(() => importBus.subscribe(setNavImport), [])

  // ── Global beforeunload handler for imports/uploads ──────────────────────────
  useEffect(() => {
    const handler = e => {
      const hasReady = importMem.sessions?.some(s => s.status === 'ready' || (s.status === 'dupe' && s.dupeAction === 'replace'))
      const isUploading = importMem.importing
      if (hasReady || isUploading) {
        e.preventDefault()
        e.returnValue = 'Tenés sesiones cargadas sin subir a la nube.'
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // ── Screen Wake Lock API (Evita que la pantalla del celular se apague/bloquee en medición en vivo) ──
  useEffect(() => {
    if (!('wakeLock' in navigator)) return
    let wakeLock = null
    const requestWakeLock = async () => {
      try {
        const isLiveActive = polar.status === 'connected' || polar.status === 'calibrating' || polar.status === 'reconnecting' || demo.active
        if (isLiveActive) {
          wakeLock = await navigator.wakeLock.request('screen')
        }
      } catch (err) {
        // Falla silenciosa si el navegador lo bloquea (p. ej. batería muy baja)
      }
    }
    requestWakeLock()
    
    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') {
        requestWakeLock()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLock !== null) {
        wakeLock.release().then(() => { wakeLock = null })
      }
    }
  }, [polar.status, demo.active])
  // Badge de exportación de video (quemado corre en segundo plano, módulo burnMem)
  const [, forceNavBurn] = useState(0)
  useEffect(() => burnMem.subscribe(() => forceNavBurn(x => x + 1)), [])
  const navBurn = burnMem.exporting ? Math.round(burnMem.progress * 100) : null

  // ── Modo offline: buzón de salida ──────────────────────────────────────────
  // Las sesiones que no pudieron subirse (sin señal) esperan en IndexedDB y se
  // suben solas: al abrir la app, cuando vuelve la conexión, y cada 60 s.
  const [outboxCount, setOutboxCount] = useState(0)
  const [outboxSent,  setOutboxSent]  = useState(0)
  const flushingRef = useRef(false)
  const flushOutbox = useCallback(async () => {
    if (flushingRef.current || !auth.token || !navigator.onLine) return
    flushingRef.current = true
    try {
      const items = await getOutbox()
      setOutboxCount(items.length)
      let sent = 0
      for (const it of items) {
        try {
          await uploadSession(auth.token, it.session, it.readings)
          await removeFromOutbox(it.id)
          sent++
          setOutboxCount(c => Math.max(0, c - 1))
        } catch { break }   // se cortó la señal de nuevo: probamos más tarde
      }
      if (sent) {
        setRefreshKey(k => k + 1)
        setOutboxSent(sent)
        setTimeout(() => setOutboxSent(0), 6000)
      }
    } catch { /* IndexedDB no disponible: nada que subir */ }
    finally { flushingRef.current = false }
  }, [auth.token])
  useEffect(() => {
    getOutbox().then(items => setOutboxCount(items.length)).catch(() => {})   // contador aunque no haya señal
    flushOutbox()
    const onOnline = () => flushOutbox()
    window.addEventListener('online', onOnline)
    const t = setInterval(flushOutbox, 60_000)
    return () => { window.removeEventListener('online', onOnline); clearInterval(t) }
  }, [flushOutbox])
  // Estado de conexión, para avisar cuando se está trabajando sin señal
  const [netOnline, setNetOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const up = () => setNetOnline(true), down = () => setNetOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])
  const alertSystem = useAlerts(demo.active ? demo.history : polar.hrHistory, 'sentado')

  const readings = demo.active ? demo.history : polar.hrHistory
  const displayHr = demo.active ? demo.hr : (polar.hr ?? (readings.length ? readings[readings.length - 1].hr : null))
  const zone = displayHr ? getZone(displayHr) : null
  const beats = useMemo(() => expandToBeats(readings), [readings])
  const stats = useMemo(() => computeStats(beats), [beats])

  // Calcular el retraso real de datos de la sesión activa
  const lastSampleTime = readings.length ? readings[readings.length - 1].time : null
  const delaySecs = lastSampleTime ? Math.max(0, Math.round((Date.now() - lastSampleTime) / 1000)) : 0

  const hrv = useMemo(() => computeHRV(demo.active ? demo.history : polar.hrHistory), [demo.active, demo.history, polar.hrHistory])
  const [liveViewers, setLiveViewers] = useState(0)
  // Candado de transmisor único: si otra pantalla de la cuenta ya está midiendo,
  // el servidor rechaza esta RETRANSMISIÓN (el hook la pausa solo). OJO: acá no
  // se toca el sensor — la grabación local es sagrada, solo se avisa (máx. 15 s).
  const [liveBlocked, setLiveBlocked] = useState(null)
  const onLiveBlocked = useCallback((label) => {
    setLiveBlocked(label)
    setTimeout(() => setLiveBlocked(null), 15_000)
  }, [])
  const rootMoisture = useMemo(() => {
    return computeMoistureIndex(polar?.ecgSamples, polar?.skinContact, polar?.hr)
  }, [polar?.ecgSamples, polar?.skinContact, polar?.hr, polar?.ecgTick])

  const [rootPosture, setRootPosture] = useState('sentado')
  const prevRootSteps = useRef(0)

  useEffect(() => {
    const evaluateRootPosture = () => {
      const isStepping = (polar?.steps ?? 0) > prevRootSteps.current
      prevRootSteps.current = polar?.steps ?? 0

      const accX = polar?.accSamples?.current?.x || []
      const accY = polar?.accSamples?.current?.y || []
      const accZ = polar?.accSamples?.current?.z || []

      let meanX = 0, meanY = -950, meanZ = 0, motionRms = 0, sitImpact = false
      if (accX.length >= 10) {
        const win = Math.min(accX.length, 75)
        const sliceX = accX.slice(-win)
        const sliceY = accY.slice(-win)
        const sliceZ = accZ.slice(-win)
        const N = sliceX.length

        let sx = 0, sy = 0, sz = 0, sumM = 0
        const mags = new Float32Array(N)
        for (let i = 0; i < N; i++) {
          const xi = sliceX[i]; const yi = sliceY[i]; const zi = sliceZ[i]
          sx += xi; sy += yi; sz += zi
          const m = Math.sqrt(xi * xi + yi * yi + zi * zi)
          mags[i] = m
          sumM += m
        }
        meanX = sx / N; meanY = sy / N; meanZ = sz / N
        const meanM = sumM / N

        let sumSq = 0, maxM = 0
        for (let i = 0; i < N; i++) {
          const diff = mags[i] - meanM
          sumSq += diff * diff
          if (mags[i] > maxM) maxM = mags[i]
        }
        motionRms = Math.sqrt(sumSq / N)
        if (maxM > 1350 && motionRms < 18) sitImpact = true
      }

      const state = classifyInstantPosture({
        x: meanX, y: meanY, z: meanZ, motionRms, isStepping, sitImpact, hr: displayHr
      })
      setRootPosture(state)
    }

    evaluateRootPosture()
    const t = setInterval(evaluateRootPosture, 1000)
    return () => clearInterval(t)
  }, [displayHr, polar?.steps])

  const rootPostureMeta = useMemo(() => ({
    key: rootPosture,
    label: getPostureLabel(rootPosture),
    color: getPostureColor(rootPosture),
  }), [rootPosture])

  const sensorMeta = useMemo(() => ({
    deviceName: polar.deviceName ?? 'Polar H10',
    sensorId: polar.deviceId ?? null,
    battery: polar.battery,
    steps: polar.steps ?? 0,
    moisture: rootMoisture,
    posture: rootPostureMeta,
  }), [polar.deviceName, polar.deviceId, polar.battery, polar.steps, rootMoisture, rootPostureMeta])
  useLiveStream(auth.token, polar.hr, polar.status, hrv.rmssd, polar.ecgSamples, setLiveViewers, onLiveBlocked, sensorMeta)

  const isAdmin   = auth.user?.role === 'admin'
  const isDemo    = !!auth.user?.demo
  const canShare  = auth.user?.plan && auth.user.plan !== 'free'

  // ── Modo offline: los últimos 7 días CON datos viajan en el teléfono ────────
  // Con conexión, baja la lista de sesiones (90 días) y las lecturas +
  // anotaciones de los últimos 7 días que TIENEN registros (si esta semana no
  // grabaste, igual llevás la última semana disponible). Todo va a IndexedDB
  // con claves fijas por fecha — antes dependía del caché del service worker y
  // la clave de la lista cambiaba cada día, por eso offline tiraba
  // "Failed to fetch" aunque los días estuvieran guardados. El ECG queda
  // afuera (pesa demasiado). Los días pasados no cambian: se bajan una vez.
  useEffect(() => {
    if (!auth.token || isDemo) return
    const uid = uidOf(auth.user)
    let cancelled = false
    const warm = async () => {
      try {
        if (!navigator.onLine) return
        // Corre en CADA apertura (sin throttle): los días completos no se
        // re-bajan, así que el costo es la lista + el día en curso. El retraso
        // de 6 s de abajo garantiza que la app ya trajo lo suyo y no se traba.
        const base = apiBase
        const hdrs = { Authorization: `Bearer ${auth.token}` }
        // 1) Lista de sesiones — es lo primero que la app necesita al abrir sin señal
        const rl = await fetch(`${base}/sync?since=${Date.now() - 90 * 86400000}&sessionsOnly=1&_t=${Date.now()}`, { headers: hdrs })
        if (!rl.ok || cancelled) return
        const { sessions = [] } = await rl.json()
        await saveSessionsOffline(uid, sessions)
        // 2) Últimos 7 días CON datos (+ hoy siempre, por si estás grabando ahora)
        const today  = localDateKey(Date.now())
        const fechas = [...new Set(sessions.map(s => localDateKey(new Date(s.start_time).getTime())))].sort().slice(-7)
        if (!fechas.includes(today)) fechas.push(today)
        for (const fecha of fechas) {
          if (cancelled) return
          const prev = await loadDayOffline(uid, fecha)
          if (prev?.complete && fecha < today) continue   // día cerrado ya guardado: no se vuelve a bajar
          const dayStart = new Date(fecha + 'T00:00:00').getTime()
          const dayEnd   = new Date(fecha + 'T23:59:59').getTime()
          const rd = await fetch(`${base}/sync?since=${dayStart}&until=${dayEnd}`, { headers: hdrs })
          if (!rd.ok) continue
          const { readings = [] } = await rd.json()
          let annotations = prev?.annotations ?? null
          try {
            const ra = await fetch(`${base}/sessions/${fecha}/annotations`, { headers: hdrs })
            if (ra.ok) annotations = await ra.json()
          } catch { /* sin anotaciones frescas: quedan las que había */ }
          await saveDayOffline(uid, fecha, { readings, annotations, complete: fecha < today })
        }
        await pruneDaysOffline(uid, fechas)
      } catch { /* sin conexión: se reintenta en la próxima apertura */ }
    }
    const t = setTimeout(warm, 6000)                      // deja que la app pinte primero
    window.addEventListener('online', warm)
    return () => { cancelled = true; clearTimeout(t); window.removeEventListener('online', warm) }
  }, [auth.token, isDemo])

  // On mount: redirect / to /monitor (guard: only when authed)
  useEffect(() => {
    if (!auth.isAuthed) return
    if (location.pathname === '/' || location.pathname === '' || location.pathname === '/inicio') navigate('/resumen', { replace: true })
  }, [auth.isAuthed])

  // Auto-start demo with pre-loaded history for demo users
  useEffect(() => {
    if (isDemo && !demo.active) demo.start(true)
  }, [isDemo])

  // Live HR in browser tab title (shows in taskbar/dock when minimized)
  const liveHr = demo.active ? demo.hr : polar.hr
  useEffect(() => {
    if (liveHr) {
      document.title = `${liveHr} BPM — Manabi`
    } else {
      document.title = 'Manabi'
    }
    return () => { document.title = 'Manabi' }
  }, [liveHr])

  // On mount: load any saved draft into state and show banner
  useEffect(() => {
    if (!auth.isAuthed) return
    getDraft().then(draft => {
      if (!draft || draft.readings.length < 5) return
      // La sesión que quedó a medias (la app se cerró grabando) se guarda SOLA:
      // sin banner ni botón — directo a la nube (o al buzón si no hay señal).
      clearDraft()
      setSavedNotice({ deviceName: draft.deviceName ?? 'Polar H10', start: draft.readings[0].time, end: draft.readings[draft.readings.length - 1].time, count: draft.readings.length })
      _uploadToCloud(draft.readings, { deviceName: draft.deviceName ?? 'Polar H10', source: draft.source ?? 'polar' })
    })
  }, [auth.isAuthed])

  // Auto-save draft every 30 readings to protect against unexpected reload
  useEffect(() => {
    if (!auth.isAuthed) return
    if (polar.hrHistory.length < 5) return
    if (polar.hrHistory.length % 30 !== 0) return
    saveDraft(polar.hrHistory, 'polar', polar.deviceName ?? 'Polar H10')
  }, [auth.isAuthed, polar.hrHistory.length, polar.deviceName])

  // Save draft when tab goes to background (catches most "close tab" scenarios)
  useEffect(() => {
    if (!auth.isAuthed) return
    const handler = () => {
      if (document.visibilityState === 'hidden' && polar.hrHistory.length > 5) {
        saveDraft(polar.hrHistory, 'polar', polar.deviceName ?? 'Polar H10')
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [auth.isAuthed, polar.hrHistory, polar.deviceName])

  // Flujo unificado del sensor: al conectar, si el H10 tenía grabado un tramo de
  // cuando estabas desconectado, llega acá — se sube solo como sesión y se avisa.
  useEffect(() => {
    const imp = polar.sensorImport
    if (!imp || !auth.isAuthed) return
    polar.clearSensorImport()
    setSavedNotice({ deviceName: `${imp.deviceName} — memoria del sensor`, start: imp.from, end: imp.to, count: imp.readings.length })
    _uploadToCloud(imp.readings, { deviceName: imp.deviceName, source: 'sensor' })
  }, [polar.sensorImport, auth.isAuthed])

  // Refs para seguimiento de guardado incremental (Checkpoints de sesiones en curso)
  const liveSessionIdRef    = useRef(null)
  const lastFlushedIndexRef = useRef(0)
  const isFlushingRef       = useRef(false)

  // Guardado incremental automático de sesiones en curso (Checkpoints cada ~3 minutos / 180 lecturas)
  // ── Auto-checkpointing cada ~3 minutos (180 lecturas) para sesiones largas en curso ──
  const hrHistoryRef = useRef(polar.hrHistory)
  useEffect(() => { hrHistoryRef.current = polar.hrHistory }, [polar.hrHistory])

  const flushCheckpoint = useCallback(async () => {
    if (isFlushingRef.current) return
    const totalReadings = hrHistoryRef.current || []
    const startIndex = lastFlushedIndexRef.current
    const newChunk = totalReadings.slice(startIndex)

    // Guardamos un checkpoint cada 180 lecturas (~3 minutos exactos de datos acumulados)
    if (newChunk.length < 180) return

    if (!liveSessionIdRef.current) {
      liveSessionIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
          })
    }

    const sessionId = liveSessionIdRef.current
    const isFirst = (startIndex === 0)
    const hrs = totalReadings.map(r => r.hr).filter(Boolean)
    const hrv = computeHRV(totalReadings)
    
    const session = {
      startTime:    totalReadings[0].time,
      endTime:      totalReadings[totalReadings.length - 1].time,
      durationSec:  Math.round((totalReadings[totalReadings.length - 1].time - totalReadings[0].time) / 1000),
      deviceName:   polar.deviceName ?? 'Polar H10',
      source:       'polar',
      steps:        Number.isFinite(polar.steps) && polar.steps > 0 ? Math.round(polar.steps) : null,
      avgHr:        hrs.length ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length) : null,
      minHr:        hrs.length ? Math.min(...hrs) : null,
      maxHr:        hrs.length ? Math.max(...hrs) : null,
      rmssd:        hrv.rmssd,
      sdnn:         hrv.sdnn,
      pnn50:        hrv.pnn50,
      respRate:     hrv.respRate,
      readingCount: totalReadings.length,
    }

    isFlushingRef.current = true
    try {
      console.log(`[Checkpoint] Subiendo bloque de ${newChunk.length} lecturas a BigQuery (sesión: ${sessionId})...`)
      await uploadSessionCheckpoint(auth.token, {
        sessionId,
        isFirst,
        isFinal: false,
        session,
        readings: newChunk,
      })
      lastFlushedIndexRef.current = startIndex + newChunk.length
      setSavedNotice({ deviceName: session.deviceName, start: session.startTime, end: session.endTime, count: lastFlushedIndexRef.current, isCheckpoint: true })
      // Refrescar en segundo plano para que la sesión aparezca en la lista de Sesiones
      setRefreshKey(k => k + 1)
    } catch (err) {
      console.warn('[Checkpoint] Error guardando bloque de sesión en curso:', err?.message)
    } finally {
      isFlushingRef.current = false
    }
  }, [auth.token, polar.deviceName, polar.steps])

  // Disparo 1: Inmediato al alcanzar las 180 lecturas acumuladas (exactamente al minuto 3, 6, 9...)
  useEffect(() => {
    if (!auth.isAuthed || !auth.token || polar.status !== 'connected' || demo.active || auth.user?.demo) return
    const currentLen = polar.hrHistory.length
    const unFlushed = currentLen - lastFlushedIndexRef.current
    if (unFlushed >= 180 && !isFlushingRef.current) {
      flushCheckpoint()
    }
  }, [auth.isAuthed, auth.token, polar.status, polar.hrHistory.length, demo.active, auth.user?.demo, flushCheckpoint])

  // Disparo 2: Watchdog de fondo cada 15 segundos (sin reiniciarse por cambios de latidos)
  useEffect(() => {
    if (!auth.isAuthed || !auth.token || polar.status !== 'connected' || demo.active || auth.user?.demo) return
    const timer = setInterval(() => {
      flushCheckpoint()
    }, 15000)
    return () => clearInterval(timer)
  }, [auth.isAuthed, auth.token, polar.status, demo.active, auth.user?.demo, flushCheckpoint])

  // Auto-save on disconnect — upload final session / checkpoint to BigQuery
  useEffect(() => {
    if (!auth.isAuthed) return
    if (polar.status === 'disconnected' && polar.hrHistory.length > 5) {
      const deviceName = polar.deviceName ?? 'Polar H10'
      const readings   = polar.hrHistory
      const steps      = polar.steps                  // capturar ANTES de limpiar (clearHistory los resetea)
      const ecgSamples = [...(polar.ecgSamples?.current || [])]
      const accData    = {
        x: [...(polar.accSamples.current?.x || [])],
        y: [...(polar.accSamples.current?.y || [])],
        z: [...(polar.accSamples.current?.z || [])],
        startTime: polar.accSamples.current?.startTime || readings[0]?.time,
        fs: 50
      }
      clearDraft()
      polar.clearHistory()
      setSavedNotice({ deviceName, start: readings[0].time, end: readings[readings.length - 1].time, count: readings.length })

      // Si teníamos una sesión activa con checkpoints, sellamos el tramo final con el mismo sessionId
      const existingSessionId = liveSessionIdRef.current
      const startIndex = lastFlushedIndexRef.current
      const remainingChunk = readings.slice(startIndex)

      liveSessionIdRef.current = null
      lastFlushedIndexRef.current = 0

      _uploadToCloud(readings, {
        deviceName,
        source: 'polar',
        steps,
        accData,
        ecgSamples,
        existingSessionId,
        remainingChunk,
        isCheckpointContinuation: Boolean(existingSessionId && startIndex > 0)
      })
    }
  }, [auth.isAuthed, polar.status])

  useEffect(() => {
    if (!auth.isAuthed) navigate('/login', { replace: true })
  }, [auth.isAuthed])

  if (!auth.isAuthed) return null

  function _uploadToCloud(readings, { deviceName, source, steps, accData, ecgSamples, existingSessionId, remainingChunk, isCheckpointContinuation }) {
    if (demo.active || source === 'demo' || auth.user?.demo) return // La demo nunca se guarda
    if (!auth.token || readings.length < 5) return
    const hrs  = readings.map(r => r.hr).filter(Boolean)
    const hrv  = computeHRV(readings)
    const session = {
      startTime:    readings[0].time,
      endTime:      readings[readings.length - 1].time,
      durationSec:  Math.round((readings[readings.length - 1].time - readings[0].time) / 1000),
      deviceName,
      source,
      steps:        Number.isFinite(steps) && steps > 0 ? Math.round(steps) : null,
      avgHr:        hrs.length ? Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length) : null,
      minHr:        hrs.length ? Math.min(...hrs) : null,
      maxHr:        hrs.length ? Math.max(...hrs) : null,
      rmssd:        hrv.rmssd,
      sdnn:         hrv.sdnn,
      pnn50:        hrv.pnn50,
      respRate:     hrv.respRate,
      readingCount: readings.length,
    }

    const uploadPromise = (isCheckpointContinuation && existingSessionId)
      ? uploadSessionCheckpoint(auth.token, {
          sessionId: existingSessionId,
          isFirst: false,
          isFinal: true,
          session,
          readings: remainingChunk || []
        })
      : uploadSession(auth.token, session, readings)

    uploadPromise
      .then(async (res) => {
        const sessionId = res?.sessionId || existingSessionId
        if (sessionId) {
          // 1. Subir ECG de la sesión en vivo a BigQuery
          if (Array.isArray(ecgSamples) && ecgSamples.length > 100) {
            try {
              const API_BASE = apiBase
              await fetch(`${API_BASE}/sessions/${encodeURIComponent(sessionId)}/ecg`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
                body: JSON.stringify({
                  fs: 130,
                  startTime: session.startTime,
                  samples: ecgSamples,
                })
              })
            } catch (ecgErr) {
              console.warn('Error subiendo ECG:', ecgErr)
            }
          }
          // 2. Subir acelerómetro crudo
          if (accData?.x?.length > 50) {
            try {
              await saveAccRecording(sessionId, accData)
              const compressed = await packAndCompressAcc(accData)
              if (compressed) {
                await uploadAccRecording(auth.token, sessionId, compressed)
              }
            } catch (accErr) {
              console.warn('Error guardando acelerómetro crudo:', accErr)
            }
          }
        }
        // El flujo unificado del sensor usa esto para no re-importar minutos que
        // ya están en la nube (recorta lo traído de la memoria a partir de acá)
        try { localStorage.setItem('vc_last_session_end', String(session.endTime)) } catch { /* sin storage */ }
        setRefreshKey(k => k + 1)
      })
      .catch(async (e) => {
        // Sin señal (o servidor caído): la sesión NO se pierde — va al buzón
        // local del teléfono y se sube sola cuando vuelve la conexión.
        const retryable = !navigator.onLine || /fetch|network|load failed|HTTP 5\d\d/i.test(e.message || '')
        if (retryable) {
          try { await addToOutbox(session, readings); setOutboxCount(c => c + 1); return } catch (_) { /* sigue al cartel de error */ }
        }
        setUploadError(e.message)
      })
  }

  async function handleRecoverDraft() {
    const draft = pendingDraft
    setPendingDraft(null)
    await clearDraft()
    setSavedNotice({ deviceName: draft.deviceName ?? 'Polar H10', start: draft.readings[0].time, end: draft.readings[draft.readings.length - 1].time, count: draft.readings.length })
    _uploadToCloud(draft.readings, { deviceName: draft.deviceName ?? 'Polar H10', source: draft.source ?? 'polar' })
  }

  async function handleDiscardDraft() {
    setPendingDraft(null)
    await clearDraft()
  }

  async function handleSaveOfflineToHistory(hrSamples, device, sourceOverride) {
    if (!hrSamples.length) return
    const deviceName = device ?? 'Polar H10'
    const source = sourceOverride ?? 'offline'
    offline.reset()
    setSavedNotice({ deviceName, start: hrSamples[0].time, end: hrSamples[hrSamples.length - 1].time, count: hrSamples.length })
    _uploadToCloud(hrSamples, { deviceName, source })
  }

  async function handleSync() {
    // El modo demo NUNCA se guarda: son datos de ejemplo, no del usuario.
    if (demo.active) return
    const readings = polar.hrHistory
    if (readings.length < 5) return
    const steps = polar.steps
    const ecgSamples = [...(polar.ecgSamples?.current || [])]
    const accData = {
      x: [...(polar.accSamples.current?.x || [])],
      y: [...(polar.accSamples.current?.y || [])],
      z: [...(polar.accSamples.current?.z || [])],
      startTime: polar.accSamples.current?.startTime || readings[0]?.time,
      fs: 50
    }
    setSyncing(true)
    await clearDraft()
    polar.clearHistory()
    setSyncing(false)
    _uploadToCloud(readings, { deviceName: polar.deviceName ?? 'Polar H10', source: 'polar', steps, accData, ecgSamples })
  }

  // Menú principal unificado: 5 pestañas exactas (Sesiones, Alertas, Nueva sesión al medio, Amigos, Juegos)
  const navItems = [
    { key: 'resumen',   label: 'Sesiones',     icon: SidebarIconHistory },
    { key: 'alertas',   label: 'Alertas',      icon: SidebarIconAlertas },
    { key: 'monitor',   label: 'Nueva sesión', icon: SidebarIconLive, center: true },
    { key: 'amigos',    label: 'Grupos',       icon: SidebarIconAmigos },
    { key: 'juegos',    label: 'Juegos',       icon: SidebarIconGames },
  ]

  return (
    <NavigateContext.Provider value={navigate}>
    <div style={{ minHeight: '100vh', background: 'var(--app-bg)' }}>
      {/* Mobile Top Header */}
      <header className="vc-mobile-header" style={meditationFullscreen ? { display: 'none' } : {}}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#ff3b30', letterSpacing: -0.5 }}>Manabi</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Botón Mi Perfil con avatar/ícono de usuario y nombre */}
          <button
            onClick={() => setTab('perfil')}
            style={{
              background: tab === 'perfil' ? 'var(--track)' : 'var(--chip-bg)',
              border: `1px solid ${tab === 'perfil' ? 'var(--border-strong)' : 'var(--border-soft)'}`,
              borderRadius: 20,
              padding: '4px 11px',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s ease'
            }}
            title="Mi Perfil"
          >
            {auth.user?.avatar ? (
              <img
                src={auth.user.avatar}
                alt="Avatar"
                style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: tab === 'perfil' ? '#ff3b30' : 'var(--text-soft)' }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
            <span style={{ fontSize: 12, fontWeight: 700, color: tab === 'perfil' ? '#ff3b30' : 'var(--text)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {auth.user?.name || auth.user?.email?.split('@')[0] || 'Mi Perfil'}
            </span>
          </button>

          {/* Botón Compartir al lado del nombre */}
          <button
            onClick={() => setTab('compartir')}
            style={{
              background: tab === 'compartir' ? 'var(--btn-strong)' : 'var(--chip-bg)',
              color: tab === 'compartir' ? '#fff' : 'var(--text)',
              border: `1px solid ${tab === 'compartir' ? 'var(--btn-strong)' : 'var(--border-soft)'}`,
              borderRadius: '50%',
              width: 34,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s ease'
            }}
            title="Compartir pulso y acceso"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="vc-sidebar" style={meditationFullscreen ? { display: 'none' } : {}}>
        <div style={{ marginBottom: 16, paddingLeft: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.5 }}>Manabi</span>
        </div>

        <nav style={{ flex: 1 }}>
          {navItems.map(t => {
            const Icon = t.icon
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className={`vc-nav-item${tab === t.key ? ' active' : ''}`}>
                <Icon active={tab === t.key} />
                {t.label}
                {t.key === 'monitor' && navImport && (
                  <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 7px', background: navImport.importing ? '#007aff' : '#34c759', color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                    {navImport.importing ? `↑ ${Math.round(navImport.done / (navImport.total || 1) * 100)}%` : '✓'}
                  </span>
                )}
              </button>
            )
          })}
        </nav>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <button onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} className="vc-nav-item" title="Cambiar entre modo día y modo noche">
            <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {dark ? <IconSun size={15} color="currentColor" /> : <IconMoon size={15} color="currentColor" />}
            </span>
            {dark ? 'Modo día' : 'Modo noche'}
          </button>

          {/* Botón Compartir en la PC abajo de modo día */}
          <button onClick={() => setTab('compartir')} className={`vc-nav-item${tab === 'compartir' ? ' active' : ''}`} title="Compartir pulso y acceso">
            <SidebarIconShare active={tab === 'compartir'} size={15} />
            Compartir
          </button>

          <UpgradeButton user={auth.user} token={auth.token} style={{ margin: '10px 0 2px' }} />
          
          {/* Clickable Profile link button with hover effect */}
          <button
            onClick={() => setTab('perfil')}
            onMouseEnter={() => setProfileHover(true)}
            onMouseLeave={() => setProfileHover(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 10,
              background: profileHover || tab === 'perfil' ? 'var(--chip-bg)' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
              textAlign: 'left',
              margin: '6px 0 2px',
              transition: 'all 0.15s'
            }}
            title="Ver y editar tu perfil"
          >
            <span style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: profileHover || tab === 'perfil' ? '#ff3b30' : 'var(--text-soft)' }}>
              {auth.user?.avatar ? (
                <img src={auth.user.avatar} alt="Avatar" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <SidebarIconPerfil active={profileHover || tab === 'perfil'} size={18} />
              )}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              {/* Nombre en una sola línea */}
              <p style={{
                fontSize: 12.5, fontWeight: 700, margin: 0,
                color: profileHover || tab === 'perfil' ? '#ff3b30' : 'var(--text)',
                textDecoration: profileHover ? 'underline' : 'none',
                transition: 'color 0.15s',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                {auth.user?.name || auth.user?.email || 'Mi Perfil'}
              </p>
              {/* Premium / Plan debajo */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginTop: 2 }}>
                <PlanBadge plan={auth.user?.plan} style={{ marginLeft: 0 }} />
                {navBurn !== null && (
                  <span style={{ fontSize: 9, fontWeight: 700, borderRadius: 8, padding: '1px 5px', background: '#ff9f0a', color: '#fff', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {navBurn}%
                  </span>
                )}
                {navBurn === null && navExportJob && (
                  <span style={{ fontSize: 9, fontWeight: 700, borderRadius: 8, padding: '1px 5px', background: '#007aff', color: '#fff', flexShrink: 0 }}>↓</span>
                )}
              </div>
            </div>
          </button>

          <button onClick={() => setShowLogoutConfirm(true)} className="vc-nav-item" style={{ color: 'var(--text-faint)' }}>
            <SidebarIconLogout />
            Cerrar sesión
          </button>
          <div style={{ fontSize: 9, color: 'var(--text-faint)', textAlign: 'left', paddingLeft: '34px', marginTop: 4, opacity: 0.45, fontVariantNumeric: 'tabular-nums', width: '100%' }}>
            v1.1.0
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="vc-main" style={meditationFullscreen ? { marginLeft: 0, padding: 0 } : {}}>
        <div className="vc-content" style={meditationFullscreen ? { padding: 0 } : {}}>
          {isDemo && auth.user?.demoExpiresAt && <DemoBanner expiresAt={auth.user.demoExpiresAt} />}
          {auth.user?.plan === 'trial' && auth.user?.trialUntil && <TrialBanner trialUntil={auth.user.trialUntil} />}
          {pendingDraft && <DraftRecoveryBanner draft={pendingDraft} onRecover={handleRecoverDraft} onDiscard={handleDiscardDraft} />}
          {savedNotice && !pendingDraft && <SavedSessionNotice notice={savedNotice} onDismiss={() => setSavedNotice(null)} />}
          {uploadError && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#7a5c00', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>[AVISO] No se pudo subir a la nube: {uploadError}</span>
              <button onClick={() => setUploadError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#7a5c00' }}>×</button>
            </div>
          )}
          {!netOnline && (
            <div style={{ background: '#f4f4f6', border: '1px solid #dcdce1', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#4b4b52' }}>
              [OFFLINE] Sin conexión — estás viendo la última semana guardada en el teléfono. Podés grabar igual: se sube solo cuando vuelva la señal.
            </div>
          )}
          {liveBlocked && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#7a5c00' }}>
              [AVISO] Ya hay una medición en vivo desde {liveBlocked} — tu grabación acá sigue normal, pero la retransmisión quedó pausada para no duplicar.
            </div>
          )}
          {outboxCount > 0 && (
            <div style={{ background: '#eef4ff', border: '1px solid #c9dcff', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#1d4f9e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>[OFFLINE] {outboxCount === 1 ? '1 sesión guardada' : `${outboxCount} sesiones guardadas`} en el teléfono — se {outboxCount === 1 ? 'sube sola' : 'suben solas'} cuando haya señal</span>
              <button onClick={flushOutbox} style={{ background: '#1d4f9e', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Subir ahora</button>
            </div>
          )}
          {outboxSent > 0 && (
            <div style={{ background: '#eafaf0', border: '1px solid #b9ecca', borderRadius: 10, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#1c7c3c' }}>
              ✓ {outboxSent === 1 ? 'Se subió 1 sesión que estaba guardada' : `Se subieron ${outboxSent} sesiones que estaban guardadas`} en el teléfono
            </div>
          )}

          {/* Alertas Fisiológicas y Clínicas en Vivo (banner con severidad y descarte) */}
          {alertSystem.active.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {alertSystem.active.map(a => {
                const isCrit = a.type === 'tachycardia' || a.severity === 'alta'
                const borderCol = isCrit ? '#ff3b30' : '#ff9500'
                const bgCol = isCrit ? 'rgba(255,59,48,0.12)' : 'rgba(255,149,0,0.12)'
                return (
                  <div
                    key={a.id}
                    style={{
                      background: bgCol,
                      border: `1.5px solid ${borderCol}`,
                      borderRadius: 14,
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      boxShadow: `0 4px 16px ${borderCol}22`,
                      animation: 'fadeIn 0.25s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: borderCol, boxShadow: `0 0 8px ${borderCol}`, flexShrink: 0 }} />
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 800, color: borderCol, marginRight: 6 }}>
                          {a.type === 'tachycardia' ? 'Taquicardia en Reposo' : a.type === 'bradycardia' ? 'Bradicardia en Vigilia' : a.type === 'irregular' ? 'Inestabilidad de Ritmo' : 'Alerta Fisiológica'}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text)' }}>
                          {a.desc || `${a.value} bpm registrados`}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => alertSystem.dismiss(a.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
                      title="Descartar aviso"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          <ErrorBoundary key={tab}>
            {(tab === 'resumen' || tab === 'historial') && (
              <HistoryTab
                token={auth.token}
                user={auth.user}
                refreshKey={refreshKey}
                periodMode={periodMode}
                setPeriodMode={setPeriodMode}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                theme={theme}
              />
            )}
            {(tab === 'juegos' || tab === 'meditacion') && (
              <JuegosTab
                polar={polar}
                demo={demo}
                hrv={hrv}
                isFullscreen={meditationFullscreen}
                setIsFullscreen={setMeditationFullscreen}
                onUploadSession={_uploadToCloud}
                onNavigate={setTab}
              />
            )}
            {tab === 'monitor'  && <NuevaSesionTab polar={polar} demo={demo} alerts={alertSystem} onSync={handleSync} syncing={syncing} offline={offline} onSaveOfflineToHistory={handleSaveOfflineToHistory} bridge={bridge} token={auth.token} liveViewers={liveViewers} pip={pip} sessionTime={sessionTime} sessionSec={sessionSec} onNavigate={setTab} theme={theme} />}
            {tab === 'alertas'  && <AlertasTab   token={auth.token} user={auth.user} />}
            {tab === 'amigos'   && <AmigosTab    token={auth.token} user={auth.user} onNavigate={setTab} />}
            {tab === 'compartir'&& (
              <div style={{ maxWidth: 980, margin: '40px auto 24px', width: '100%', padding: '0 12px' }}>
                <div style={{ marginBottom: 20 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px', color: 'var(--text)', letterSpacing: -0.4 }}>Compartir pulso y acceso</h2>
                  <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>Crea accesos de solo lectura para médicos o comparte tu transmisión en vivo.</p>
                </div>
                <PersonasTab token={auth.token} user={auth.user} inline />
              </div>
            )}
            {tab === 'perfil'   && <ProfileTab   token={auth.token} user={auth.user} onAuthUpdate={auth.updateToken} openExport={rawTab === 'exportar'} theme={theme} onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} />}
            {tab === 'guia'     && (
              <div>
                <button onClick={() => setTab('historial')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', padding: '4px 0', marginBottom: 16, fontFamily: 'inherit' }}>← Volver</button>
                <GuideTab />
              </div>
            )}
          </ErrorBoundary>
        </div>
      </main>

      {/* Widget Flotante Arrastrable en Pantalla (Drag & Drop) — solo si no está abierta la ventana externa */}
      {tab !== 'monitor' && !pip.pipWin && (polar.hr || isDemo || polar.status === 'connected') && (
        <FloatingLivePill
          displayHr={displayHr}
          zone={zone}
          sessionTime={sessionTime}
          onNavigate={setTab}
        />
      )}

      {/* Dock Flotante (mobile) — estilo Instagram / Dynamic Island, con deslizamiento táctil y háptica */}
      {(() => {
        return (
          <nav
            className="vc-bottom-nav"
            style={meditationFullscreen ? { display: 'none' } : {}}
            onTouchStart={(e) => {
              if (!e.touches || !e.touches[0]) return
              const touch = e.touches[0]
              const el = document.elementFromPoint(touch.clientX, touch.clientY)
              const btn = el?.closest('[data-tab-key]')
              if (btn) {
                const key = btn.getAttribute('data-tab-key')
                if (key) {
                  setScrubbedTab(key)
                  try { navigator.vibrate?.(10) } catch (_) {}
                }
              }
            }}
            onTouchMove={(e) => {
              if (!e.touches || !e.touches[0]) return
              const touch = e.touches[0]
              const el = document.elementFromPoint(touch.clientX, touch.clientY)
              const btn = el?.closest('[data-tab-key]')
              if (btn) {
                const key = btn.getAttribute('data-tab-key')
                if (key && key !== scrubbedTab) {
                  setScrubbedTab(key)
                  try { navigator.vibrate?.(10) } catch (_) {}
                }
              }
            }}
            onTouchEnd={() => {
              if (scrubbedTab && scrubbedTab !== tab) {
                setTab(scrubbedTab)
                try { navigator.vibrate?.(18) } catch (_) {}
              }
              setScrubbedTab(null)
            }}
            onTouchCancel={() => setScrubbedTab(null)}
          >
            {navItems.map(t => {
              const Icon = t.icon
              const isHighlighted = scrubbedTab ? scrubbedTab === t.key : tab === t.key
              if (t.center) return (
                <button
                  key={t.key}
                  data-tab-key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`vc-bottom-nav-center${isHighlighted ? ' active is-scrubbed' : ''}`}
                  aria-label="Nueva sesión"
                >
                  <span className="vc-bottom-nav-fab">
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8 Q4 3 6 8 Q8 13 10 8 Q12 3 14 8" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {navImport && (
                      <span className="vc-bnav-badge" style={{ background: navImport.importing ? '#007aff' : '#34c759' }}>
                        {navImport.importing ? `${Math.round(navImport.done / (navImport.total || 1) * 100)}%` : '✓'}
                      </span>
                    )}
                  </span>
                  <span className="vc-bnav-lbl">{t.label}</span>
                </button>
              )
              return (
                <button
                  key={t.key}
                  data-tab-key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`vc-bottom-nav-item${isHighlighted ? ' active is-scrubbed' : ''}`}
                >
                  <span className="vc-bnav-ico">
                    <Icon active={isHighlighted} size={21} />
                  </span>
                  <span className="vc-bnav-lbl">{t.label}</span>
                </button>
              )
            })}
          </nav>
        )
      })()}

      {/* Render matching Portal for the Document PiP window at root level to survive page/tab switches */}
      {pip.pipWin && createPortal(
        <MiniLive
          hr={displayHr}
          color={zone?.color ?? '#ff3b30'}
          label={zone?.label ?? 'EN VIVO'}
          samples={readings}
          min={stats.min}
          max={stats.max}
          time={sessionTime}
          delay={delaySecs}
          theme={theme}
        />,
        pip.pipWin.document.body,
      )}

      {/* Modal de confirmación para Cerrar Sesión */}
      {showLogoutConfirm && (
        <div
          onClick={() => setShowLogoutConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            animation: 'fadeIn 0.15s ease'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              borderRadius: 20,
              padding: '24px 22px',
              maxWidth: 360,
              width: '100%',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.28)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12
            }}
          >
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 15,
              background: 'rgba(255, 59, 48, 0.12)',
              color: '#ff3b30',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SidebarIconLogout size={22} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', margin: 0, letterSpacing: -0.3 }}>
                ¿Cerrar sesión?
              </h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>
                Vas a salir de tu cuenta{auth.user?.email ? ` (${auth.user.email})` : ''}. Podrás volver a entrar en cualquier momento con tus credenciales.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 6 }}>
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  borderRadius: 12,
                  border: '1px solid var(--border)',
                  background: 'var(--chip-bg)',
                  color: 'var(--text)',
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background 0.15s ease'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLogoutConfirm(false)
                  auth.logout()
                }}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  borderRadius: 12,
                  border: 'none',
                  background: '#ff3b30',
                  color: '#fff',
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(255, 59, 48, 0.35)',
                  transition: 'opacity 0.15s ease'
                }}
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </NavigateContext.Provider>
  )
}

// ── Standalone admin page at /admin ──────────────────────────────────────────

export function AdminPage() {
  const auth = useAuth()
  const navigate = useNavigate()

  const [stats,     setStats]     = useState(null)
  const [users,     setUsers]     = useState([])
  const [links,     setLinks]     = useState([])
  const [auditLogs, setAuditLogs] = useState(null)
  const [subTab,    setSubTab]    = useState('overview')
  const [newLabel,  setNewLabel]  = useState('')
  const [busy,      setBusy]      = useState(false)
  const [copied,    setCopied]    = useState(null)

  const API = apiBase
  const hdr = { Authorization: `Bearer ${auth.token}` }

  const load = async () => {
    const [s, u, l, a] = await Promise.all([
      fetch(`${API}/admin/stats`,      { headers: hdr }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/admin/users`,      { headers: hdr }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/admin/demo-links`, { headers: hdr }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/admin/audit-logs`, { headers: hdr }).then(r => r.ok ? r.json() : null),
    ])
    setStats(s); setUsers(u); setLinks(l); setAuditLogs(a)
  }

  useEffect(() => {
    if (!auth.isAuthed) return
    if (auth.user?.role !== 'admin') { navigate('/resumen'); return }
    load()
  }, [auth.isAuthed])

  const createLink = async () => {
    setBusy(true)
    const res = await fetch(`${API}/admin/demo-links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...hdr },
      body: JSON.stringify({ label: newLabel.trim() || 'Demo 3 días' }),
    })
    if (res.ok) { setNewLabel(''); await load() }
    setBusy(false)
  }
  const removeLink = async id => {
    await fetch(`${API}/admin/demo-links/${id}`, { method: 'DELETE', headers: hdr })
    load()
  }
  const copy = (link, id) => {
    navigator.clipboard.writeText(link)
    setCopied(id); setTimeout(() => setCopied(null), 2000)
  }
  const fmtDate  = ts => new Date(ts * 1000).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  const fmtShort = ts => new Date(ts * 1000).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })

  if (!auth.isAuthed) return null

  const cardS = { background: 'var(--card-bg)', borderRadius: 16, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }
  const PLAN_COLOR = { free: 'var(--text-faint)', familiar: '#007aff', pro: '#ff9500', trial: '#30d158', admin: '#ff3b30' }
  const TABS = [['overview', 'Resumen'], ['users', `Usuarios (${users.length})`], ['demos', `Links demo (${links.length})`], ['audit', 'Auditoría']]

  return (
    <div style={{ minHeight: '100vh', background: 'var(--field-bg)' }}>
      {/* Header */}
      <div style={{ background: 'var(--card-bg)', borderBottom: '1px solid var(--border)', padding: '0 clamp(16px, 3vw, 48px)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => navigate('/resumen')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', fontSize: 13, fontFamily: 'inherit', padding: '4px 0' }}>
              ← App
            </button>
            <span style={{ color: '#d1d1d6' }}>|</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.5 }}>Manabi</span>
            <span style={{ fontSize: 11, background: '#ff3b30', color: '#fff', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>ADMIN</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-faint)' }}>{auth.user?.email}</p>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px clamp(16px, 3vw, 48px) 60px' }}>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: 'var(--track)', borderRadius: 10, padding: 3, width: 'fit-content' }}>
          {TABS.map(([key, lbl]) => (
            <button key={key} onClick={() => setSubTab(key)} style={{
              padding: '7px 16px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', background: subTab === key ? 'var(--seg-active)' : 'transparent',
              color: 'var(--text)', boxShadow: subTab === key ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              transition: 'all 0.15s',
            }}>{lbl}</button>
          ))}
        </div>

        {/* OVERVIEW */}
        {subTab === 'overview' && stats && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* User counters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
              {[
                ['Total',      stats.users?.total   ?? '—', '#007aff'],
                ['Pro',        stats.users?.pro     ?? '—', '#ff9500'],
                ['Trial',      stats.users?.trial   ?? '—', '#30d158'],
                ['Free',       stats.users?.free    ?? '—', 'var(--text-faint)'],
                ['Activos 7d', stats.users?.recent7 ?? '—', '#ff3b30'],
              ].map(([label, val, color]) => (
                <div key={label} style={{ ...cardS, textAlign: 'center' }}>
                  <p style={{ fontSize: 28, fontWeight: 800, color, margin: '0 0 4px' }}>{val}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</p>
                </div>
              ))}
            </div>
            {/* SQLite stats */}
            {stats.sqlite && (
              <div style={cardS}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Actividad</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
                  {[
                    ['Leads', stats.sqlite.totalLeads],
                    ['Leads convertidos', stats.sqlite.convertedLeads],
                    ['Demos activos', stats.sqlite.activeDemos],
                    ['Seguidores', stats.sqlite.followers],
                  ].map(([lbl, val]) => (
                    <div key={lbl} style={{ background: 'var(--field-bg)', borderRadius: 10, padding: '10px 14px' }}>
                      <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 2 }}>{val ?? '—'}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-faint)' }}>{lbl}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* BigQuery tables */}
            {stats.bigquery && Object.keys(stats.bigquery).length > 0 && (
              <div style={cardS}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>BigQuery</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                  {Object.entries(stats.bigquery).map(([table, data]) => (
                    <div key={table} style={{ background: 'var(--field-bg)', borderRadius: 10, padding: '10px 14px' }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{table}</p>
                      <p style={{ fontSize: 12, color: 'var(--text-faint)' }}>{Number(data.rows ?? 0).toLocaleString()} filas</p>
                      <p style={{ fontSize: 11, color: 'var(--text-fainter)' }}>{((data.bytes ?? 0) / 1e6).toFixed(1)} MB</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* USERS */}
        {subTab === 'users' && (
          <div style={cardS}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>Usuarios registrados</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-soft)' }}>
                    {['Nombre', 'Email', 'Plan', 'Rol', 'Trial hasta', 'Registro'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{u.name}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: (PLAN_COLOR[u.plan] || 'var(--text-faint)') + '22', color: PLAN_COLOR[u.plan] || 'var(--text-faint)', textTransform: 'uppercase' }}>{u.plan}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: u.role === 'admin' ? '#ff3b30' : 'var(--text-faint)', fontWeight: u.role === 'admin' ? 700 : 400 }}>{u.role || 'user'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-faint)', fontSize: 12 }}>{u.trial_until ? fmtShort(u.trial_until) : '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-fainter)', fontSize: 12 }}>{u.created_at ? fmtShort(u.created_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DEMO LINKS */}
        {subTab === 'demos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={cardS}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Crear link de demo (3 días)</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={newLabel} onChange={e => setNewLabel(e.target.value)}
                  placeholder="Etiqueta, ej: María López"
                  onKeyDown={e => e.key === 'Enter' && createLink()}
                  style={{ flex: 1, background: 'var(--chip-bg)', border: 'none', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
                />
                <button onClick={createLink} disabled={busy} style={{
                  background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1,
                }}>{busy ? '…' : 'Crear'}</button>
              </div>
            </div>
            <div style={cardS}>
              {links.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-faint)' }}>Sin links creados.</p>}
              {links.map(l => {
                const expired = l.expires_at < Math.floor(Date.now() / 1000)
                return (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderTop: '1px solid var(--border-soft)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: expired ? 'var(--text-faint)' : 'var(--text)', marginBottom: 2 }}>
                        {l.label} {expired && <span style={{ fontSize: 11, color: '#ff3b30', fontWeight: 500 }}>vencido</span>}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text-faint)' }}>Vence: {fmtDate(l.expires_at)} · Activado {l.activated_count}x</p>
                      <p style={{ fontSize: 11, color: 'var(--text-fainter)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.link}</p>
                    </div>
                    <button onClick={() => copy(l.link, l.id)} style={{ background: copied === l.id ? '#34c759' : 'var(--chip-bg)', color: copied === l.id ? 'var(--seg-active-text-inv)' : 'var(--text)', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
                      {copied === l.id ? '✓' : 'Copiar'}
                    </button>
                    <button onClick={() => removeLink(l.id)} style={{ background: '#fff1f0', color: '#ff3b30', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Borrar
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* AUDIT LOGS */}
        {subTab === 'audit' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Logins section */}
            <div style={cardS}>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Historial de Inicios de Sesión (Últimos 100)</p>
              {!auditLogs?.logins?.length ? (
                <p style={{ fontSize: 13, color: 'var(--text-faint)' }}>Sin registros de login.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-soft)' }}>
                        {['Fecha', 'Email', 'Usuario', 'Estado', 'IP', 'Dispositivo'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.logins.map(l => {
                        const isSuccess = l.status === 'success'
                        const isWindows = /windows/i.test(l.user_agent)
                        const isApple = /iphone|ipad|macintosh/i.test(l.user_agent)
                        const isAndroid = /android/i.test(l.user_agent)
                        const devLabel = isWindows ? 'Windows' : isApple ? 'Apple' : isAndroid ? 'Android' : 'Móvil'
                        return (
                          <tr key={l.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--text)' }}>{l.email}</td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{l.user_name || '—'}</td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, background: isSuccess ? 'rgba(52,199,89,0.15)' : 'rgba(255,59,48,0.15)', color: isSuccess ? '#34c759' : '#ff3b30' }}>
                                {isSuccess ? 'EXITOSO' : 'FALLIDO'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-faint)', fontFamily: 'monospace' }}>{l.ip_address || '—'}</td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 11.5 }} title={l.user_agent}>{devLabel}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Activity logs section */}
            <div style={cardS}>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Registro de Actividad Reciente (Últimos 100)</p>
              {!auditLogs?.activity?.length ? (
                <p style={{ fontSize: 13, color: 'var(--text-faint)' }}>Sin registros de actividad.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-soft)' }}>
                        {['Fecha', 'Usuario', 'Email', 'Acción', 'Detalles', 'IP', 'Dispositivo'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.activity.map(a => {
                        const isWindows = /windows/i.test(a.user_agent)
                        const isApple = /iphone|ipad|macintosh/i.test(a.user_agent)
                        const isAndroid = /android/i.test(a.user_agent)
                        const devLabel = isWindows ? 'Windows' : isApple ? 'Apple' : isAndroid ? 'Android' : 'Móvil'
                        let detailText = ''
                        try {
                          const parsed = typeof a.details === 'string' ? JSON.parse(a.details) : a.details
                          detailText = Object.entries(parsed).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')
                        } catch (_) {
                          detailText = String(a.details || '')
                        }
                        return (
                          <tr key={a.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--text)' }}>{a.user_name}</td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-faint)' }}>{a.user_email}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                              <span style={{ background: 'var(--chip-bg)', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: 11 }}>{a.action}</span>
                            </td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 11.5, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={detailText}>{detailText || '—'}</td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-faint)', fontFamily: 'monospace' }}>{a.ip_address || '—'}</td>
                            <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 11.5 }} title={a.user_agent}>{devLabel}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
